import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import ImageCropCompressModal from '../components/ImageCropCompressModal';
import io from 'socket.io-client';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import { 
  getStats, 
  getStatsJournalieres,
  getStatsFamillesJournalieres,
  getMembresInscritsAujourdhui,
  getCotisationsPayeesAujourdhui,
  getPaiementsGlobaux,
  getCotisationsPayees,
  getCotisationsImpayees,
  getStatsFamillesMensuelles,
  getStatsFamillesGlobales,
  getStatsEvolution,
  getEvolutionCotisations,
  getComparaisonMois,
  getAdmins, 
  createAdmin, 
  updateAdmin, 
  deleteAdmin,
  getRecolteurs,
  createRecolteur,
  updateRecolteur,
  deleteRecolteur,
  getGerers,
  createGerer,
  updateGerer,
  deleteGerer,
  getFinances,
  createFinances,
  updateFinances,
  deleteFinances,
  getRecolteurStats,
  getMembres,
  getMembre,
  updateMembre,
  deleteMembre,
  getCotisations,
  getCotisationsByMembre,
  getPaiements,
  deletePaiement,
  getMontantsMensuels,
  createMontantMensuel,
  updateMontantMensuel,
  deleteMontantMensuel,
  getMontantsMensuelsFamilles,
  createMontantMensuelFamille,
  updateMontantMensuelFamille,
  getCotisationsFamilles,
  createCotisationFamille,
  getPaiementsFamilles,
  createPaiementFamille,
  deletePaiementFamille,
  getCentresSante,
  createCentreSante,
  updateCentreSante,
  deleteCentreSante,
  declarerCentres,
  declarerCentresFamilles,
  getAdminActivities,
  getStatsHebdomadaires,
  createBackup,
  listBackups,
  downloadBackup,
  deleteBackup,
  restoreBackup,
  verifyAdminPassword,
  getBackupSchedule,
  updateBackupSchedule,
  getSystemSettings,
  updateSystemSettings,
  resetSystemSettings,
  uploadLogo,
  uploadTeamPhoto,
  uploadIndexImage,
  getMessageTemplates,
  createMessageTemplate,
  updateMessageTemplate,
  deleteMessageTemplate,
  sendNotifications,
  getNotificationHistory,
  getContactMessages,
  getContactMessage,
  updateContactMessageStatus,
  addContactMessageResponse,
  sendContactReplyEmail,
  deleteContactMessage,
  getSiteActivities,
  createSiteActivity,
  updateSiteActivity,
  deleteSiteActivity,
  uploadActivityImage,
  getFamilles,
  getFamille,
  createFamille,
  updateFamille,
  deleteFamille,
  createMembreFamille,
  updateMembreFamille,
  deleteMembreFamille,
  regenererNumeroFamille,
  getEnfantsMembre,
  createEnfantMembre,
  updateEnfantMembre,
  deleteEnfantMembre
} from '../services/api';
import CarteMembre from '../components/CarteMembre';
import CarteFamille from '../components/CarteFamille';
import BonTraitement from '../components/BonTraitement';
import FicheAdhesion from '../components/FicheAdhesion';
import DetailMembre from '../components/DetailMembre';
import WebcamCapture from '../components/WebcamCapture';
import DetailRecolteur from '../components/DetailRecolteur';
import NotificationBell from '../components/NotificationBell';
import Footer from '../components/Footer';
import AdminInstallPrompt from '../components/AdminInstallPrompt';
import RapportsFinanciers from '../components/RapportsFinanciers';
import { notificationService } from '../utils/notifications';
import { generateRecolteurUsername, generateRecolteurPassword, generateRecolteurMatricule } from '../utils/generateRecolteurCredentials';
import './DashboardAdmin.css';

// Enregistrer les composants Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const DashboardAdmin = () => {
  const navigate = useNavigate();
  const admin = JSON.parse(localStorage.getItem('admin_user') || '{}');
  
  // Fonction helper pour vérifier si l'admin connecté est principal
  const isPrincipal = () => {
    return admin.is_principal === true || admin.is_principal === 1 || admin.is_principal === '1';
  };
  
  // Impression des listes modales
  const handlePrintModal = (e) => {
    e.stopPropagation();
    const overlay = e.currentTarget.closest('.modal-list-print');
    if (!overlay) return;
    const header = overlay.querySelector('.modal-header');
    const body = overlay.querySelector('.modal-body');
    const headerClone = header ? header.cloneNode(true) : null;
    const bodyClone = body ? body.cloneNode(true) : null;
    if (headerClone) headerClone.querySelector('.modal-close')?.remove();
    const contentHtml = (headerClone?.outerHTML || '') + (bodyClone?.outerHTML || '');
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Impression - MUTU NDE ESPOIR</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; margin: 0; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { padding: 8px; border: 1px solid #ddd; text-align: left; }
            th { background: #f8f9fa; font-weight: 600; }
            .modal-header h2 { margin: 0 0 15px 0; font-size: 18px; }
            @media print { body { padding: 10px; } }
          </style>
        </head>
        <body>${contentHtml}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 300);
  };

  // Debug: Afficher les informations de l'admin connecté
  useEffect(() => {
    console.log('🔍 Admin connecté:', admin);
    console.log('🔍 is_principal:', admin.is_principal);
    console.log('🔍 isPrincipal():', isPrincipal());
  }, [admin]);
  
  // Fonctions utilitaires pour la pagination
  const paginate = (array, currentPage, itemsPerPage) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return array.slice(startIndex, endIndex);
  };
  
  const getTotalPages = (array, itemsPerPage) => {
    return Math.ceil(array.length / itemsPerPage);
  };
  
  // Composant de pagination réutilisable
  const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;
    
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        gap: '5px', 
        marginTop: '20px',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          style={{
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            backgroundColor: currentPage === 1 ? '#f5f5f5' : 'white',
            color: currentPage === 1 ? '#999' : '#333',
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
            fontSize: '14px'
          }}
        >
          ««
        </button>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          style={{
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            backgroundColor: currentPage === 1 ? '#f5f5f5' : 'white',
            color: currentPage === 1 ? '#999' : '#333',
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
            fontSize: '14px'
          }}
        >
          «
        </button>
        {pages.map(page => (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: currentPage === page ? '#007bff' : 'white',
              color: currentPage === page ? 'white' : '#333',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: currentPage === page ? 'bold' : 'normal'
            }}
          >
            {page}
          </button>
        ))}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          style={{
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            backgroundColor: currentPage === totalPages ? '#f5f5f5' : 'white',
            color: currentPage === totalPages ? '#999' : '#333',
            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
            fontSize: '14px'
          }}
        >
          »
        </button>
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          style={{
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            backgroundColor: currentPage === totalPages ? '#f5f5f5' : 'white',
            color: currentPage === totalPages ? '#999' : '#333',
            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
            fontSize: '14px'
          }}
        >
          »»
        </button>
        <span style={{ marginLeft: '10px', fontSize: '14px', color: '#666' }}>
          Page {currentPage} sur {totalPages}
        </span>
      </div>
    );
  };
  
  const [stats, setStats] = useState({});
  const [loadingStats, setLoadingStats] = useState(false);
  const [statsJournalieres, setStatsJournalieres] = useState({
    membresAujourdhui: 0,
    paiementsAujourdhui: 0,
    montantCotisationsAujourdhui: 0,
    famillesAujourdhui: 0,
    cotisationsPayeesAujourdhui: 0,
    nombrePaiementsAujourdhui: 0,
    cartesPayeesAujourdhui: 0,
    montantCartesAujourdhui: 0,
    date: ''
  });
  const [loadingStatsJournalieres, setLoadingStatsJournalieres] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [recolteurs, setRecolteurs] = useState([]);
  const [gerers, setGerers] = useState([]);
  const [finances, setFinances] = useState([]);
  const [membres, setMembres] = useState([]);
  const [familles, setFamilles] = useState([]);
  const [cotisations, setCotisations] = useState([]);
  const [paiements, setPaiements] = useState([]);
  
  // États de pagination pour toutes les listes
  const [currentPageAdmins, setCurrentPageAdmins] = useState(1);
  const [currentPageRecolteurs, setCurrentPageRecolteurs] = useState(1);
  const [currentPageGerers, setCurrentPageGerers] = useState(1);
  const [currentPageFinances, setCurrentPageFinances] = useState(1);
  const [currentPageMembres, setCurrentPageMembres] = useState(1);
  const [currentPageFamilles, setCurrentPageFamilles] = useState(1);
  const [currentPageCotisations, setCurrentPageCotisations] = useState(1);
  const [currentPagePaiements, setCurrentPagePaiements] = useState(1);
  const [currentPageMontants, setCurrentPageMontants] = useState(1);
  const [currentPageCentresSante, setCurrentPageCentresSante] = useState(1);
  const [currentPageContactMessages, setCurrentPageContactMessages] = useState(1);
  const [currentPageNotificationHistory, setCurrentPageNotificationHistory] = useState(1);
  const [currentPageMessageTemplates, setCurrentPageMessageTemplates] = useState(1);
  const [currentPageActivities, setCurrentPageActivities] = useState(1);
  const [currentPageBackups, setCurrentPageBackups] = useState(1);
  
  const itemsPerPage = 10;
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('stats');
  const [siteSection, setSiteSection] = useState('infos'); // 'infos' | 'index' | 'apropos' | 'historique' — sous-menu Gestion du site
  const [showAdminForm, setShowAdminForm] = useState(false);
  const [showRecolteurForm, setShowRecolteurForm] = useState(false);
  const [showGererForm, setShowGererForm] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [editingRecolteur, setEditingRecolteur] = useState(null);
  const [editingGerer, setEditingGerer] = useState(null);
  const [adminView, setAdminView] = useState('admins'); // 'admins', 'recolteurs' ou 'gerers'
  const [selectedMembre, setSelectedMembre] = useState(null);
  const [selectedMembreFiche, setSelectedMembreFiche] = useState(null);
  const [selectedMembreDetail, setSelectedMembreDetail] = useState(null);
  const [selectedFamilleCarte, setSelectedFamilleCarte] = useState(null);
  const [selectedPersonneBon, setSelectedPersonneBon] = useState(null);
  const [typePersonneBon, setTypePersonneBon] = useState(null);
  const [cotisationsBon, setCotisationsBon] = useState([]);
  const [editingMembre, setEditingMembre] = useState(null);
  const [editingFamille, setEditingFamille] = useState(null);
  const [showFamilleForm, setShowFamilleForm] = useState(false);
  const [showGestionMembres, setShowGestionMembres] = useState(false);
  const [famillePourMembres, setFamillePourMembres] = useState(null);
  const [searchFamilles, setSearchFamilles] = useState('');
  const [showFamilleDetails, setShowFamilleDetails] = useState(false);
  const [selectedFamilleDetails, setSelectedFamilleDetails] = useState(null);
  const [showMembresInscritsAujourdhuiModal, setShowMembresInscritsAujourdhuiModal] = useState(false);
  const [membresInscritsAujourdhui, setMembresInscritsAujourdhui] = useState({ membresIndividuels: [], membresFamilles: [] });
  const [loadingMembresInscritsAujourdhui, setLoadingMembresInscritsAujourdhui] = useState(false);
  const [showCotisationsPayeesAujourdhuiModal, setShowCotisationsPayeesAujourdhuiModal] = useState(false);
  const [cotisationsPayeesAujourdhui, setCotisationsPayeesAujourdhui] = useState({ cotisationsMembres: [], cotisationsFamilles: [] });
  const [loadingCotisationsPayeesAujourdhui, setLoadingCotisationsPayeesAujourdhui] = useState(false);
  const [showPaiementsGlobauxModal, setShowPaiementsGlobauxModal] = useState(false);
  const [paiementsGlobaux, setPaiementsGlobaux] = useState({ paiementsMembres: [], paiementsFamilles: [] });
  const [loadingPaiementsGlobaux, setLoadingPaiementsGlobaux] = useState(false);
  const [showMontantCollecteAujourdhuiModal, setShowMontantCollecteAujourdhuiModal] = useState(false);
  const [montantCollecteAujourdhui, setMontantCollecteAujourdhui] = useState({ paiementsMembres: [], paiementsFamilles: [] });
  const [loadingMontantCollecteAujourdhui, setLoadingMontantCollecteAujourdhui] = useState(false);
  const [showCotisationsPayeesModal, setShowCotisationsPayeesModal] = useState(false);
  const [cotisationsPayees, setCotisationsPayees] = useState({ cotisationsMembres: [], cotisationsFamilles: [] });
  const [loadingCotisationsPayees, setLoadingCotisationsPayees] = useState(false);
  const [searchCotisationsPayees, setSearchCotisationsPayees] = useState('');
  const [showCotisationsImpayeesModal, setShowCotisationsImpayeesModal] = useState(false);
  const [cotisationsImpayees, setCotisationsImpayees] = useState({ cotisationsMembres: [], cotisationsFamilles: [] });
  const [loadingCotisationsImpayees, setLoadingCotisationsImpayees] = useState(false);
  const [searchCotisationsImpayees, setSearchCotisationsImpayees] = useState('');
  
  // États pour les cotisations et paiements des familles
  const [cotisationsFamille, setCotisationsFamille] = useState([]);
  const [paiementsFamille, setPaiementsFamille] = useState([]);
  
  // Liste combinée des paiements (membres + familles)
  const paiementsCombines = useMemo(() => {
    const paiementsMembres = (paiements || []).map(p => ({
      ...p,
      type_paiement_source: 'membre',
      source_id: p.membre_id,
      source: p.membre,
      source_type: 'membre'
    }));
    
    const paiementsFamilles = (paiementsFamille || []).map(p => ({
      ...p,
      type_paiement_source: 'famille',
      source_id: p.famille_id,
      source: p.famille,
      source_type: 'famille',
      membre_id: null, // Pas de membre_id pour les paiements de famille
      membre: null
    }));
    
    // Combiner et trier par date (plus récent en premier)
    return [...paiementsMembres, ...paiementsFamilles].sort((a, b) => {
      const dateA = new Date(a.date_paiement || a.created_at || 0);
      const dateB = new Date(b.date_paiement || b.created_at || 0);
      return dateB - dateA;
    });
  }, [paiements, paiementsFamille]);
  
  // Calculs combinés pour les statistiques globales (membres + familles)
  const statsCombinees = useMemo(() => {
    // Total paiements : membres + familles (en excluant les paiements de cartes)
    const totalPaiementsMembres = parseFloat(stats?.totalPaiements || 0);
    // Exclure les paiements de cartes des familles (type_paiement: 'autre' et montant: 5000)
    const totalPaiementsFamilles = (paiementsFamille || []).filter(p => 
      !(p.type_paiement === 'autre' && parseFloat(p.montant || 0) === 5000)
    ).reduce((sum, p) => sum + parseFloat(p.montant || 0), 0);
    const totalPaiementsCombines = totalPaiementsMembres + totalPaiementsFamilles;
    
    // Cotisations payées : membres + familles
    const cotisationsPayeesMembres = stats?.cotisationsPayees || 0;
    const cotisationsPayeesFamilles = (cotisationsFamille || []).filter(c => c.statut === 'paye').length;
    const cotisationsPayeesCombines = cotisationsPayeesMembres + cotisationsPayeesFamilles;
    
    // Cotisations impayées : membres + familles
    const cotisationsImpayeesMembres = stats?.cotisationsImpayees || 0;
    const cotisationsImpayeesFamilles = (cotisationsFamille || []).filter(c => c.statut === 'impaye').length;
    const cotisationsImpayeesCombines = cotisationsImpayeesMembres + cotisationsImpayeesFamilles;
    
    // Montant total des cartes : membres + familles (type_paiement: 'autre' et montant: 5000)
    const montantCartesMembres = parseFloat(stats?.montantTotalCartes || 0);
    const montantCartesFamilles = (paiementsFamille || []).filter(p => 
      p.type_paiement === 'autre' && parseFloat(p.montant || 0) === 5000
    ).reduce((sum, p) => sum + parseFloat(p.montant || 0), 0);
    const montantTotalCartesCombines = montantCartesMembres + montantCartesFamilles;
    
    return {
      totalPaiements: totalPaiementsCombines,
      cotisationsPayees: cotisationsPayeesCombines,
      cotisationsImpayees: cotisationsImpayeesCombines,
      montantTotalCartes: montantTotalCartesCombines
    };
  }, [stats, paiementsFamille, cotisationsFamille]);
  
  const [loadingCotisationsFamille, setLoadingCotisationsFamille] = useState(false);
  const [loadingPaiementsFamille, setLoadingPaiementsFamille] = useState(false);
  const [showPaiementFamilleForm, setShowPaiementFamilleForm] = useState(false);
  const [paiementFamilleForm, setPaiementFamilleForm] = useState({
    montant: '',
    date_paiement: new Date().toISOString().split('T')[0],
    numero_recu: '',
    cotisation_ids: []
  });
  const [selectedAssociationFamilles, setSelectedAssociationFamilles] = useState('');
  const [statsFamillesJournalieres, setStatsFamillesJournalieres] = useState(null);
  const [statsFamillesMensuelles, setStatsFamillesMensuelles] = useState(null);
  const [statsFamillesGlobales, setStatsFamillesGlobales] = useState(null);
  const [loadingStatsFamilles, setLoadingStatsFamilles] = useState(false);
  const [evolutionData, setEvolutionData] = useState(null);
  const [loadingEvolution, setLoadingEvolution] = useState(false);
  const [nbMoisEvolution, setNbMoisEvolution] = useState(6);
  const [evolutionCombineeData, setEvolutionCombineeData] = useState(null);
  const [loadingEvolutionCombinee, setLoadingEvolutionCombinee] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false); // Pour mobile
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false); // Pour desktop
  const [selectedRecolteur, setSelectedRecolteur] = useState(null);
  const [recolteurStats, setRecolteurStats] = useState(null);
  const [loadingRecolteurStats, setLoadingRecolteurStats] = useState(null); // Stocker l'ID du récolteur en cours de chargement
  const [membreForm, setMembreForm] = useState({
    nom: '',
    postnom: '',
    prenom: '',
    date_naissance: '',
    lieu_naissance: '',
    quartier: '',
    commune: '',
    telephone: '',
    profession: '',
    association: '',
    centre_sante: '',
    nom_epouse: '',
    date_adhesion: ''
  });
  const [familleForm, setFamilleForm] = useState({
    chef_famille_nom: '',
    nom_famille: '',
    adresse: '',
    quartier: '',
    commune: '',
    telephone: '',
    observations: '',
    statut: 'active'
  });
  const [photoChefFamille, setPhotoChefFamille] = useState(null);
  const [membresFamille, setMembresFamille] = useState([]);
  const [nouveauMembre, setNouveauMembre] = useState({ nom: '', prenom: '', date_naissance: '', lien: '' });
  const [photoMembreFamille, setPhotoMembreFamille] = useState(null);
  const [webcamKeyMembreFamille, setWebcamKeyMembreFamille] = useState(0);
  const [enfantsMembre, setEnfantsMembre] = useState([]);
  const [nouvelEnfant, setNouvelEnfant] = useState({ nom: '', prenom: '', date_naissance: '', lien: '' });
  const [photoEnfant, setPhotoEnfant] = useState(null);
  const [membrePourEnfants, setMembrePourEnfants] = useState(null);
  const [webcamKeyEnfant, setWebcamKeyEnfant] = useState(0); // Pour forcer la réinitialisation du WebcamCapture
  const [searchTerm, setSearchTerm] = useState('');
  const [searchAdmins, setSearchAdmins] = useState('');
  const [searchRecolteurs, setSearchRecolteurs] = useState('');
  const [searchGerers, setSearchGerers] = useState('');
  const [searchFinances, setSearchFinances] = useState('');
  const [searchCotisations, setSearchCotisations] = useState('');
  const [membresExpandes, setMembresExpandes] = useState({}); // Pour gérer l'ouverture/fermeture des membres dans la liste des cotisations
  const [searchPaiements, setSearchPaiements] = useState('');
  const [membresExpandesPaiements, setMembresExpandesPaiements] = useState({}); // Pour gérer l'ouverture/fermeture des membres dans la liste des paiements
  const [searchMontants, setSearchMontants] = useState('');
  const [selectedAssociation, setSelectedAssociation] = useState('');
  const [selectedTypeMembre, setSelectedTypeMembre] = useState(''); // 'individuel', 'famille', ou '' pour tous
  const [selectedAssociationRecolteurs, setSelectedAssociationRecolteurs] = useState('');
  const [selectedAssociationCotisations, setSelectedAssociationCotisations] = useState('');
  const [selectedAssociationPaiements, setSelectedAssociationPaiements] = useState('');
  const [searchCotisationsFamilles, setSearchCotisationsFamilles] = useState('');
  const [famillesExpandees, setFamillesExpandees] = useState({}); // Pour gérer l'ouverture/fermeture des familles dans la liste des cotisations
  const [selectedCommuneCotisationsFamilles, setSelectedCommuneCotisationsFamilles] = useState('');
  const [currentPageCotisationsFamilles, setCurrentPageCotisationsFamilles] = useState(1);
  const [searchCentresSante, setSearchCentresSante] = useState('');
  const [montantsMensuels, setMontantsMensuels] = useState([]);
  const [centresSante, setCentresSante] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(''); // Format YYYY-MM pour le filtre mensuel
  const [selectedDay, setSelectedDay] = useState(''); // Format 'tous' ou nombre du jour (1-31)
  const [showMontantForm, setShowMontantForm] = useState(false);
  const [editingMontant, setEditingMontant] = useState(null);
  
  // États pour les montants mensuels des familles
  const [montantsMensuelsFamilles, setMontantsMensuelsFamilles] = useState([]);
  const [editingMontantFamille, setEditingMontantFamille] = useState(null);
  const [searchMontantsFamilles, setSearchMontantsFamilles] = useState('');
  const [currentPageMontantsFamilles, setCurrentPageMontantsFamilles] = useState(1);
  const [showMontantFamilleForm, setShowMontantFamilleForm] = useState(false);
  const [montantFamilleForm, setMontantFamilleForm] = useState({
    mois: new Date().getMonth() + 1,
    annee: new Date().getFullYear(),
    montant_mensuel: ''
  });
  const [showCentreSanteForm, setShowCentreSanteForm] = useState(false);
  const [editingCentreSante, setEditingCentreSante] = useState(null);
  const [declarationForm, setDeclarationForm] = useState({
    association: '',
    commune: '',
    nombreMembres: '',
    centre_sante: ''
  });
  const [declarationFormFamilles, setDeclarationFormFamilles] = useState({
    nom_famille: '',
    centre_sante: ''
  });
  const [membresDisponibles, setMembresDisponibles] = useState(0);
  const [famillesDisponibles, setFamillesDisponibles] = useState(0);
  const [montantForm, setMontantForm] = useState({
    mois: new Date().getMonth() + 1,
    annee: new Date().getFullYear(),
    montant_mensuel: ''
  });
  const [adminForm, setAdminForm] = useState({
    username: '',
    password: '',
    nom_complet: ''
  });
  const [gererForm, setGererForm] = useState({
    username: '',
    password: '',
    nom_complet: ''
  });
  const [financesForm, setFinancesForm] = useState({
    username: '',
    password: '',
    nom_complet: ''
  });
  const [showFinancesForm, setShowFinancesForm] = useState(false);
  const [editingFinances, setEditingFinances] = useState(null);
  const [recolteurForm, setRecolteurForm] = useState({
    username: '',
    password: '',
    nom_complet: '',
    association: '',
    commune: '',
    matricule: '',
    telephone: ''
  });
  const [centreSanteForm, setCentreSanteForm] = useState({
    nom: '',
    adresse: '',
    commune: '',
    telephone: ''
  });
  // États pour les rapports
  const [rapportCotisationsForm, setRapportCotisationsForm] = useState({
    association: '',
    commune: '',
    periodeType: 'mois', // 'mois' ou 'semaine'
    mois: new Date().getMonth() + 1,
    annee: new Date().getFullYear(),
    semaine: 1,
    statut: 'tous' // 'tous', 'paye', 'impaye'
  });
  const [rapportMembresRecoltesForm, setRapportMembresRecoltesForm] = useState({
    association: '',
    commune: ''
  });
  // États pour les activités
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [activityFilters, setActivityFilters] = useState({
    action_type: '',
    entity_type: ''
  });
  // États pour les paramètres système
  // eslint-disable-next-line no-unused-vars
  const [systemSettings, setSystemSettings] = useState({
    general: {},
    sms: {},
    amounts: {},
    print: {}
  });
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState({
    general: {},
    sms: {},
    amounts: {},
    print: {}
  });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [imageCropModal, setImageCropModal] = useState({ open: false, file: null, aspect: undefined, title: '', onConfirm: null });
  // États pour les notifications
  const [messageTemplates, setMessageTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    category: 'custom',
    subject: '',
    message: '',
    variables: '',
    is_active: true
  });
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [loadingNotificationHistory, setLoadingNotificationHistory] = useState(false);
  const [notificationFilters] = useState({
    type: '',
    status: '',
    date_from: '',
    date_to: ''
  });
  const [showSendNotificationModal, setShowSendNotificationModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [sendingNotification, setSendingNotification] = useState(false);
  // États pour les statistiques hebdomadaires
  const [statsHebdomadaires, setStatsHebdomadaires] = useState(null);
  const [loadingStatsHebdomadaires, setLoadingStatsHebdomadaires] = useState(false);
  // États pour les exports
  const [exportForm, setExportForm] = useState({
    type: 'membres', // 'membres', 'paiements', 'cotisations'
    association: '',
    commune: '',
    dateDebut: '',
    dateFin: '',
    format: 'excel' // 'excel' ou 'csv'
  });
  // États pour les graphiques
  const [comparaisonData, setComparaisonData] = useState(null);
  const [loadingComparaison, setLoadingComparaison] = useState(false);
  const [comparaisonForm, setComparaisonForm] = useState({
    mois1: new Date().getMonth() + 1,
    annee1: new Date().getFullYear(),
    mois2: new Date().getMonth() || 12,
    annee2: new Date().getMonth() === 0 ? new Date().getFullYear() - 1 : new Date().getFullYear()
  });
  // États pour les sauvegardes
  const [backups, setBackups] = useState([]);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [backupSchedule, setBackupSchedule] = useState({
    enabled: false,
    frequency: 'daily',
    time: '02:00'
  });
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  // États pour le modal de confirmation de restauration
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [restorePassword, setRestorePassword] = useState('');
  const [restoreFilename, setRestoreFilename] = useState('');
  const [verifyingPassword, setVerifyingPassword] = useState(false);
  // États pour les messages de contact
  const [contactMessages, setContactMessages] = useState([]);
  const [loadingContactMessages, setLoadingContactMessages] = useState(false);
  const [selectedContactMessage, setSelectedContactMessage] = useState(null);
  const [contactMessageFilters, setContactMessageFilters] = useState({
    statut: '',
    page: 1,
    limit: 20
  });
  const [contactResponse, setContactResponse] = useState('');
  const [showContactResponseModal, setShowContactResponseModal] = useState(false);
  // Activités du site (page publique /activites)
  const [siteActivities, setSiteActivities] = useState([]);
  const [loadingSiteActivities, setLoadingSiteActivities] = useState(false);
  const [siteActivityForm, setSiteActivityForm] = useState({ id: null, title: '', summary: '', content: '', image_url: '', activity_date: '', published: true });
  const [showSiteActivityForm, setShowSiteActivityForm] = useState(false);

  // Référence pour Socket.io
  const socketRef = useRef(null);


  useEffect(() => {
    if (!admin.id) {
      navigate('/login-admin');
      return;
    }
    if (admin.role !== 'admin') {
      navigate('/login-admin');
      return;
    }
    
    // Charger les données
    loadData();

    // Connexion Socket.io pour les mises à jour en temps réel
    // S'assurer d'utiliser le bon port (5000 pour le backend, pas 3000)
    let apiBaseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
    
    // S'assurer que l'URL ne contient pas le port 3000 (port du frontend React)
    if (apiBaseUrl.includes(':3000')) {
      apiBaseUrl = apiBaseUrl.replace(':3000', ':5000');
    }
    
    // S'assurer que l'URL est complète avec le protocole
    if (!apiBaseUrl.startsWith('http://') && !apiBaseUrl.startsWith('https://')) {
      apiBaseUrl = `http://${apiBaseUrl}`;
    }
    
    // En production (mundes.org), utiliser uniquement le polling pour éviter l'erreur
    // "Unrecognized frame opcode: 3" due aux proxies (cPanel/Nginx) qui ne gèrent pas bien le WebSocket
    const usePollingOnly = apiBaseUrl.includes('mundes.org') || process.env.NODE_ENV === 'production';
    console.log('🔌 Connexion Socket.io à:', apiBaseUrl, usePollingOnly ? '(polling seul)' : '');

    const socket = io(apiBaseUrl, {
      transports: usePollingOnly ? ['polling'] : ['polling', 'websocket'],
      upgrade: !usePollingOnly,
      // Options de reconnexion
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
      timeout: 20000,
      forceNew: false,
      // Path Socket.io
      path: '/socket.io/',
      // Désactiver la compression pour éviter les problèmes avec les proxies
      compression: false,
      // Options de compatibilité
      rememberUpgrade: false,
      // Gérer les erreurs de transport
      autoConnect: true
    });
    
    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('✅ Connecté au serveur Socket.io');
      // Rejoindre la room admin après connexion
      socket.emit('join-admin');
    });

    socket.on('connect_error', (error) => {
      // Ne pas spammer la console : log une seule fois (le serveur peut être arrêté en dev)
      if (!socketRef.current._connectErrorLogged) {
        socketRef.current._connectErrorLogged = true;
        console.warn('Socket.io: serveur indisponible (temps réel désactivé). Démarrez le backend sur le port 5000 pour l\'activer.');
      }
      if (error.message && (error.message.includes('opcode') || error.message.includes('websocket'))) {
        socket.io.opts.transports = ['polling'];
        socket.disconnect();
        socket.connect();
      }
    });

    socket.on('disconnect', (reason) => {
      if (reason === 'io server disconnect') {
        socket.connect();
      }
    });

    // Erreurs de transport : log unique pour éviter le spam
    socket.io.on('error', (error) => {
      if (!socketRef.current._transportErrorLogged) {
        socketRef.current._transportErrorLogged = true;
        console.warn('Socket.io transport:', error.message || error);
      }
      if (error.message && error.message.includes('opcode')) {
        socket.io.opts.transports = ['polling'];
        socket.disconnect();
        setTimeout(() => socket.connect(), 1000);
      }
    });

    // Écouter les événements de création/modification
    socket.on('membre:created', async (data) => {
      console.log('📥 Nouveau membre créé:', data);
      notificationService.info('Nouveau membre', `Un nouveau membre a été enregistré: ${data.membre.nom} ${data.membre.prenom}`);
      // Recharger les membres
      try {
        const membresRes = await getMembres();
        setMembres(membresRes.data || []);
        // Recharger aussi les stats
        const statsRes = await loadStats();
        if (statsRes && statsRes.data) {
          setStats(statsRes.data);
        }
      } catch (error) {
        console.error('Erreur lors du rechargement:', error);
      }
    });

    socket.on('paiement:created', async (data) => {
      console.log('📥 Nouveau paiement créé:', data);
      notificationService.info('Nouveau paiement', `Un nouveau paiement de ${parseFloat(data.paiement.montant).toLocaleString('fr-FR')} CDF a été enregistré`);
      // Recharger les paiements
      try {
        const paiementsRes = await getPaiements();
        setPaiements(paiementsRes.data || []);
        // Recharger aussi les stats
        const statsRes = await loadStats();
        if (statsRes && statsRes.data) {
          setStats(statsRes.data);
        }
        const statsJournalieresRes = await getStatsJournalieres();
        if (statsJournalieresRes && statsJournalieresRes.data) {
          setStatsJournalieres(statsJournalieresRes.data);
        }
      } catch (error) {
        console.error('Erreur lors du rechargement:', error);
      }
    });

    socket.on('cotisation:updated', async (data) => {
      console.log('📥 Cotisation mise à jour:', data);
      // Recharger les cotisations
      try {
        const cotisationsRes = await getCotisations();
        setCotisations(cotisationsRes.data || []);
        // Recharger aussi les stats
        const statsRes = await loadStats();
        if (statsRes && statsRes.data) {
          setStats(statsRes.data);
        }
      } catch (error) {
        console.error('Erreur lors du rechargement:', error);
      }
    });

    // Nettoyer la connexion à la déconnexion
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };

    // Nettoyer les event listeners
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin.id, admin.role, navigate]);

  // Charger les graphiques quand l'onglet stats est actif
  useEffect(() => {
    if (activeTab === 'stats') {
      loadEvolutionCotisations();
    }
  }, [activeTab, nbMoisEvolution, cotisationsFamille]); // eslint-disable-line react-hooks/exhaustive-deps

  // Charger les statistiques d'évolution quand l'onglet evolution est actif
  useEffect(() => {
    if (activeTab === 'evolution') {
      loadStatsEvolution();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, nbMoisEvolution]);

  // Charger les sauvegardes quand l'onglet backup est actif
  useEffect(() => {
    if (activeTab === 'backup') {
      loadBackups();
      loadBackupSchedule();
    }
  }, [activeTab]);

  // Recharger les membres quand on active l'onglet Membres
  useEffect(() => {
    if (activeTab === 'membres') {
      const reloadMembres = async () => {
        try {
          console.log('🔄 Rechargement automatique des membres (onglet actif)...');
          const membresRes = await getMembres();
          setMembres(membresRes.data || []);
          console.log('✅ Membres rechargés:', membresRes.data?.length || 0);
        } catch (err) {
          console.error('❌ Erreur lors du rechargement des membres:', err);
        }
      };
      // Attendre un peu pour éviter les rechargements multiples
      const timeoutId = setTimeout(reloadMembres, 100);
      return () => clearTimeout(timeoutId);
    }
    if (activeTab === 'familles') {
      const reloadFamilles = async () => {
        try {
          console.log('🔄 Rechargement automatique des familles (onglet actif)...');
          const famillesRes = await getFamilles();
          const famillesArray = Array.isArray(famillesRes) ? famillesRes : (Array.isArray(famillesRes?.data) ? famillesRes.data : []);
          setFamilles(famillesArray);
          console.log('✅ Familles rechargées:', famillesArray.length, 'famille(s)');
          
          // Charger les statistiques des familles
          await loadStatsFamilles();
        } catch (err) {
          console.error('❌ Erreur lors du rechargement des familles:', err);
          setFamilles([]);
        }
      };
      // Attendre un peu pour éviter les rechargements multiples
      const timeoutId = setTimeout(reloadFamilles, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [activeTab]);

  // Vérifier les rappels de paiement après le chargement des données
  useEffect(() => {
    if (!membres.length || !cotisations.length) return;
    
    // Vérifier immédiatement
    try {
      checkRappelsPaiement();
    } catch (err) {
      console.error('Erreur lors de la vérification des rappels:', err);
    }
    
    // Puis vérifier périodiquement toutes les 5 minutes
    const rappelInterval = setInterval(() => {
      try {
        checkRappelsPaiement();
      } catch (err) {
        console.error('Erreur lors de la vérification des rappels:', err);
      }
    }, 300000); // 5 minutes

    return () => {
      clearInterval(rappelInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membres, cotisations]);

  // Charger les activités automatiquement quand l'onglet est ouvert
  useEffect(() => {
    if (activeTab === 'activities') {
      loadActivities();
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Charger les notifications automatiquement quand l'onglet est ouvert
  useEffect(() => {
    if (activeTab === 'notifications') {
      loadMessageTemplates();
      loadNotificationHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Charger les messages de contact automatiquement quand l'onglet est ouvert
  useEffect(() => {
    if (activeTab === 'contact') {
      loadContactMessages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, contactMessageFilters]);

  // Charger les activités du site quand la section est ouverte
  const loadSiteActivitiesList = useCallback(async () => {
    setLoadingSiteActivities(true);
    try {
      const res = await getSiteActivities();
      setSiteActivities(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      setSiteActivities([]);
      notificationService.error('Erreur', 'Impossible de charger les activités.');
    } finally {
      setLoadingSiteActivities(false);
    }
  }, []);
  useEffect(() => {
    if (activeTab === 'site' && siteSection === 'activites') {
      loadSiteActivitiesList();
    }
  }, [activeTab, siteSection, loadSiteActivitiesList]);

  // Fonction pour vérifier si un membre a payé la carte de membre
  const membreACartePayee = (membreId) => {
    return paiements.some(p => 
      p.membre_id === membreId && 
      p.type_paiement === 'autre' && 
      parseFloat(p.montant) === 5000
    );
  };

  // Recalculer les membres disponibles quand l'association ou la commune change
  useEffect(() => {
    if (declarationForm.association && declarationForm.commune) {
      // Normaliser les valeurs pour la comparaison (trim et insensible à la casse)
      const associationRecherchee = declarationForm.association.trim();
      const communeRecherchee = declarationForm.commune.trim();
      const isToutesAssociations = associationRecherchee === '__TOUTES__';
      
      console.log('🔍 ====== DÉBUT DU FILTRAGE ======');
      console.log('🔍 Critères de recherche:', {
        association: isToutesAssociations ? 'TOUTES LES ASSOCIATIONS' : `"${associationRecherchee}"`,
        commune: `"${communeRecherchee}"`
      });
      
      // Filtrer les membres avec log détaillé
      const membresFiltres = membres.filter(m => {
        const mAssociation = (m.association || '').trim();
        const mCommune = (m.commune || '').trim();
        const mCentreSante = m.centre_sante;
        
        // Comparaison insensible à la casse
        // Si "Toutes les associations", ignorer le filtre association
        const matchAssociation = isToutesAssociations || mAssociation.toLowerCase() === associationRecherchee.toLowerCase();
        const matchCommune = mCommune.toLowerCase() === communeRecherchee.toLowerCase();
        
        // Vérifier si le membre n'a pas de centre de santé (null, undefined, ou chaîne vide)
        const sansCentre = !mCentreSante || mCentreSante === '' || mCentreSante === null || mCentreSante === undefined;
        
        const match = matchAssociation && matchCommune && sansCentre;
        
        return match;
      });
      
      console.log('✅ ====== RÉSULTAT DU FILTRAGE ======');
      console.log('📊 Résumé:', {
        association: isToutesAssociations ? 'TOUTES' : associationRecherchee,
        commune: communeRecherchee,
        totalMembres: membres.length,
        membresFiltres: membresFiltres.length
      });
      
      setMembresDisponibles(membresFiltres.length);
    } else {
      setMembresDisponibles(0);
    }
  }, [declarationForm.association, declarationForm.commune, membres]);

  // Calculer si la famille sélectionnée a déjà un centre de santé
  useEffect(() => {
    if (declarationFormFamilles.nom_famille) {
      const familleSelectionnee = (Array.isArray(familles) ? familles : []).find(f => {
        const fNom = (f.nom_famille || '').trim();
        return fNom.toLowerCase() === declarationFormFamilles.nom_famille.trim().toLowerCase() && f.statut === 'active';
      });
      
      if (familleSelectionnee) {
        setFamillesDisponibles(familleSelectionnee.centre_sante ? 0 : 1);
      } else {
        setFamillesDisponibles(0);
      }
    } else {
      setFamillesDisponibles(0);
    }
  }, [declarationFormFamilles.nom_famille, familles]);

  // Fonction pour charger les stats avec filtre mensuel et jour optionnels
  const loadStats = async (mois, annee, jour = null) => {
    try {
      console.log('📊 Chargement stats...', mois && annee ? `(mois: ${mois}, année: ${annee}${jour ? `, jour: ${jour}` : ''})` : '(toutes)');
      const r = await getStats(mois, annee, jour);
      console.log('✅ Stats chargées');
      return r;
    } catch (error) {
      console.error('❌ Erreur lors du chargement des stats:', error);
      throw error;
    }
  };

  // Fonction pour gérer le changement de mois et recharger les stats
  const handleMonthChange = async (event) => {
    const monthValue = event.target.value;
    console.log('📅 Changement de mois sélectionné:', monthValue);
    setSelectedMonth(monthValue);
    setSelectedDay(''); // Réinitialiser le jour quand on change de mois
    setLoadingStats(true);
    
    try {
      if (monthValue) {
        // Extraire mois et année du format YYYY-MM
        const [annee, mois] = monthValue.split('-');
        const moisInt = parseInt(mois);
        const anneeInt = parseInt(annee);
        console.log(`📅 Extraction: mois=${moisInt}, année=${anneeInt}`);
        
        console.log('🔄 Chargement des stats pour le mois sélectionné...');
        const statsRes = await loadStats(moisInt, anneeInt);
        console.log('✅ Stats reçues:', statsRes);
        if (statsRes && statsRes.data) {
          console.log('📊 Données stats:', statsRes.data);
          setStats(statsRes.data);
        } else {
          console.warn('⚠️ Aucune donnée dans la réponse');
          setStats({});
        }
        
        // Charger les statistiques hebdomadaires
        await loadStatsHebdomadaires(moisInt, anneeInt);
      } else {
        // Si aucun mois sélectionné, charger toutes les stats
        console.log('🔄 Chargement de toutes les stats (sans filtre)...');
        const statsRes = await loadStats();
        if (statsRes && statsRes.data) {
          setStats(statsRes.data);
        } else {
          setStats({});
        }
        setStatsHebdomadaires(null);
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des stats:', error);
      console.error('Détails:', error.response?.data || error.message);
      setStats({});
    } finally {
      setLoadingStats(false);
    }
  };

  const handleDayChange = async (event) => {
    const dayValue = event.target.value;
    console.log('📅 Changement de jour sélectionné:', dayValue);
    setSelectedDay(dayValue);
    setLoadingStats(true);
    
    try {
      if (selectedMonth) {
        const [annee, mois] = selectedMonth.split('-');
        const moisInt = parseInt(mois);
        const anneeInt = parseInt(annee);
        
        console.log('🔄 Chargement des stats pour le jour sélectionné...');
        const jourInt = dayValue === 'tous' ? null : parseInt(dayValue);
        const statsRes = await loadStats(moisInt, anneeInt, jourInt);
        if (statsRes && statsRes.data) {
          setStats(statsRes.data);
        } else {
          setStats({});
        }
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des stats:', error);
      setStats({});
    } finally {
      setLoadingStats(false);
    }
  };

  // Fonction pour obtenir les jours disponibles dans le mois sélectionné
  const getDaysInMonth = () => {
    if (!selectedMonth) return [];
    const [annee, mois] = selectedMonth.split('-');
    const year = parseInt(annee);
    const month = parseInt(mois);
    const daysInMonth = new Date(year, month, 0).getDate();
    return Array.from({ length: daysInMonth }, (_, i) => i + 1);
  };

  // Fonction pour charger les statistiques hebdomadaires
  const loadStatsHebdomadaires = async (mois, annee) => {
    console.log('🔄 Chargement des stats hebdomadaires pour:', mois, annee);
    setLoadingStatsHebdomadaires(true);
    try {
      const response = await getStatsHebdomadaires(mois, annee);
      console.log('✅ Réponse stats hebdomadaires:', response);
      console.log('✅ Données stats hebdomadaires:', response.data);
      if (response && response.data) {
        setStatsHebdomadaires(response.data);
        console.log('✅ Stats hebdomadaires définies:', response.data);
      } else {
        console.warn('⚠️ Réponse invalide pour stats hebdomadaires');
        setStatsHebdomadaires(null);
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des stats hebdomadaires:', error);
      console.error('❌ Type:', error.name);
      console.error('❌ Message:', error.message);
      console.error('❌ Response:', error.response?.data);
      notificationService.error('Erreur', `Impossible de charger les statistiques hebdomadaires: ${error.response?.data?.message || error.message}`);
      setStatsHebdomadaires(null);
    } finally {
      console.log('✅ Fin du chargement des stats hebdomadaires');
      setLoadingStatsHebdomadaires(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setLoadingStatsJournalieres(true);
      console.log('🔄 Début du chargement des données...');
      
      // Charger les données avec logs pour chaque requête
      console.log('📡 Envoi des requêtes API...');
      
      const [statsRes, statsJournalieresRes, adminsRes, recolteursRes, gerersRes, financesRes, membresRes, famillesRes, cotisationsRes, paiementsRes, paiementsFamillesRes, montantsRes, montantsFamillesRes, centresSanteRes, cotisationsFamillesRes] = await Promise.allSettled([
        (async () => { return await loadStats(); })(),
        (async () => { console.log('📅 Chargement stats journalières...'); const r = await getStatsJournalieres(); console.log('✅ Stats journalières chargées'); return r; })(),
        (async () => { console.log('👥 Chargement admins...'); const r = await getAdmins(); console.log('✅ Admins chargés'); return r; })(),
        (async () => { console.log('👤 Chargement récolteurs...'); const r = await getRecolteurs(); console.log('✅ Récolteurs chargés'); return r; })(),
        (async () => { console.log('👨‍💼 Chargement gestionnaires...'); const r = await getGerers(); console.log('✅ Gestionnaires chargés'); return r; })(),
        (async () => { console.log('💰 Chargement finances...'); const r = await getFinances(); console.log('✅ Finances chargés'); return r; })(),
        (async () => { console.log('👨‍👩‍👧 Chargement membres...'); const r = await getMembres(); console.log('✅ Membres chargés'); return r; })(),
        (async () => { console.log('👨‍👩‍👧‍👦 Chargement familles...'); const r = await getFamilles(); console.log('✅ Familles chargées'); return r; })(),
        (async () => { console.log('💰 Chargement cotisations...'); const r = await getCotisations(); console.log('✅ Cotisations chargées'); return r; })(),
        (async () => { console.log('💵 Chargement paiements...'); const r = await getPaiements(); console.log('✅ Paiements chargés'); return r; })(),
        (async () => { console.log('💵 Chargement paiements familles...'); const r = await getPaiementsFamilles(); console.log('✅ Paiements familles chargés'); return r; })(),
        (async () => { console.log('📅 Chargement montants...'); const r = await getMontantsMensuels(); console.log('✅ Montants chargés'); return r; })(),
        (async () => { console.log('📅 Chargement montants familles...'); const r = await getMontantsMensuelsFamilles(); console.log('✅ Montants familles chargés'); return r; })(),
        (async () => { console.log('🏥 Chargement centres de santé...'); const r = await getCentresSante(); console.log('✅ Centres de santé chargés'); return r; })(),
        (async () => { console.log('💰 Chargement cotisations familles...'); const r = await getCotisationsFamilles(); console.log('✅ Cotisations familles chargées'); return r; })()
      ]);
      
      console.log('✅ Toutes les requêtes terminées');
      
      // Traiter chaque résultat
      if (statsRes.status === 'fulfilled') {
        setStats(statsRes.value.data);
      } else {
        console.error('❌ Erreur stats:', statsRes.reason);
        setStats({});
      }
      
      if (statsJournalieresRes.status === 'fulfilled') {
        try {
          // Axios enveloppe la réponse dans response.data
          const responseData = statsJournalieresRes.value;
          const data = responseData?.data || responseData || {};
          
          console.log('📊 Données statistiques journalières reçues:', data);
          console.log('📊 Réponse complète:', statsJournalieresRes.value);
          console.log('📊 Type de données:', typeof data, Array.isArray(data) ? 'array' : 'object');
          
          // Vérifier si data est un objet valide
          if (data && typeof data === 'object' && !Array.isArray(data)) {
            setStatsJournalieres({
              membresAujourdhui: parseInt(data.membresAujourdhui) || 0,
              paiementsAujourdhui: parseFloat(data.paiementsAujourdhui) || 0,
              cotisationsPayeesAujourdhui: parseInt(data.cotisationsPayeesAujourdhui) || 0,
              nombrePaiementsAujourdhui: parseInt(data.nombrePaiementsAujourdhui) || 0,
              cartesPayeesAujourdhui: parseInt(data.cartesPayeesAujourdhui) || 0,
              montantCartesAujourdhui: parseFloat(data.montantCartesAujourdhui) || 0,
              date: data.date || new Date().toISOString().split('T')[0]
            });
          } else {
            console.warn('⚠️ Format de données inattendu pour les statistiques journalières:', data);
            setStatsJournalieres({
              membresAujourdhui: 0,
              paiementsAujourdhui: 0,
              cotisationsPayeesAujourdhui: 0,
              nombrePaiementsAujourdhui: 0,
              cartesPayeesAujourdhui: 0,
              montantCartesAujourdhui: 0,
              date: new Date().toISOString().split('T')[0]
            });
          }
        } catch (err) {
          console.error('❌ Erreur lors du traitement des statistiques journalières:', err);
          setStatsJournalieres({
            membresAujourdhui: 0,
            paiementsAujourdhui: 0,
            cotisationsPayeesAujourdhui: 0,
            nombrePaiementsAujourdhui: 0,
            cartesPayeesAujourdhui: 0,
            montantCartesAujourdhui: 0,
            date: new Date().toISOString().split('T')[0]
          });
        }
      } else {
        console.error('❌ Erreur stats journalières:', statsJournalieresRes.reason);
        console.error('❌ Détails de l\'erreur:', statsJournalieresRes.reason?.response?.data || statsJournalieresRes.reason?.message || statsJournalieresRes.reason);
        setStatsJournalieres({
          membresAujourdhui: 0,
          paiementsAujourdhui: 0,
          cotisationsPayeesAujourdhui: 0,
          nombrePaiementsAujourdhui: 0,
          cartesPayeesAujourdhui: 0,
          montantCartesAujourdhui: 0,
          date: new Date().toISOString().split('T')[0]
        });
      }
      
      if (adminsRes.status === 'fulfilled') {
        setAdmins(adminsRes.value.data);
      } else {
        console.error('❌ Erreur admins:', adminsRes.reason);
        setAdmins([]);
      }
      
      if (recolteursRes.status === 'fulfilled') {
        setRecolteurs(recolteursRes.value.data);
      } else {
        console.error('❌ Erreur récolteurs:', recolteursRes.reason);
        setRecolteurs([]);
      }
      
      if (gerersRes.status === 'fulfilled') {
        setGerers(gerersRes.value.data);
      } else {
        console.error('❌ Erreur gestionnaires:', gerersRes.reason);
        setGerers([]);
      }

      if (financesRes && financesRes.status === 'fulfilled') {
        setFinances(financesRes.value.data);
      } else if (financesRes) {
        console.error('❌ Erreur finances:', financesRes.reason);
        setFinances([]);
      }
      
      if (membresRes.status === 'fulfilled') {
        setMembres(membresRes.value.data);
      } else {
        console.error('❌ Erreur membres:', membresRes.reason);
        setMembres([]);
      }
      
      if (famillesRes.status === 'fulfilled') {
        // L'API axios retourne les données dans response.data (comme pour les membres)
        const famillesData = famillesRes.value.data;
        const famillesArray = Array.isArray(famillesData) ? famillesData : [];
        setFamilles(famillesArray);
        console.log('✅ Familles chargées:', famillesArray.length, 'famille(s)');
        if (famillesArray.length > 0) {
          console.log('📋 Première famille:', famillesArray[0]);
        } else {
          console.warn('⚠️ Aucune famille trouvée dans la réponse. Réponse complète:', famillesRes.value);
        }
      } else {
        console.error('❌ Erreur familles:', famillesRes.reason);
        setFamilles([]);
      }
      
      if (cotisationsRes.status === 'fulfilled') {
        setCotisations(cotisationsRes.value.data);
      } else {
        console.error('❌ Erreur cotisations:', cotisationsRes.reason);
        setCotisations([]);
      }
      
      if (cotisationsFamillesRes.status === 'fulfilled') {
        setCotisationsFamille(cotisationsFamillesRes.value.data || []);
      } else {
        console.error('❌ Erreur cotisations familles:', cotisationsFamillesRes.reason);
        setCotisationsFamille([]);
      }
      
      if (paiementsRes.status === 'fulfilled') {
        setPaiements(paiementsRes.value.data);
      } else {
        console.error('❌ Erreur paiements:', paiementsRes.reason);
        setPaiements([]);
      }
      
      if (paiementsFamillesRes.status === 'fulfilled') {
        setPaiementsFamille(paiementsFamillesRes.value.data || []);
      } else {
        console.error('❌ Erreur paiements familles:', paiementsFamillesRes.reason);
        setPaiementsFamille([]);
      }
      
      if (montantsRes.status === 'fulfilled') {
        setMontantsMensuels(montantsRes.value.data);
      } else {
        console.error('❌ Erreur montants mensuels:', montantsRes.reason);
        setMontantsMensuels([]);
      }
      
      if (montantsFamillesRes.status === 'fulfilled') {
        setMontantsMensuelsFamilles(montantsFamillesRes.value.data);
      } else {
        console.error('❌ Erreur montants mensuels familles:', montantsFamillesRes.reason);
        setMontantsMensuelsFamilles([]);
      }
      
      if (centresSanteRes.status === 'fulfilled') {
        setCentresSante(centresSanteRes.value.data);
      } else {
        console.error('❌ Erreur centres de santé:', centresSanteRes.reason);
        setCentresSante([]);
      }
    } catch (err) {
      console.error('❌ Erreur critique lors du chargement des données:', err);
      // Initialiser tous les états avec des valeurs par défaut en cas d'erreur critique
      setStats({});
      setAdmins([]);
      setRecolteurs([]);
      setMembres([]);
      setCotisations([]);
      setPaiements([]);
      setMontantsMensuels([]);
      setCentresSante([]);
    } finally {
      console.log('✅ Chargement terminé, masquage du loader');
      setLoading(false);
      setLoadingStatsJournalieres(false);
    }
    
    // Charger les statistiques des familles
    loadStatsFamilles();
  };

  // Fonction pour vérifier et envoyer des rappels de paiement (suspendu pour éviter 429/QUIC sur l'API notifications)
  const checkRappelsPaiement = () => {
    return; // Suspendu : réactiver en supprimant cette ligne si besoin
    // Vérifier que les données sont chargées et sont des tableaux
    if (!membres || !Array.isArray(membres) || !cotisations || !Array.isArray(cotisations)) {
      return;
    }
    if (!membres.length || !cotisations.length) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Grouper les cotisations impayées par membre
    const membresAvecImpayees = {};
    
    cotisations.forEach(cotisation => {
      if (cotisation.statut === 'impaye') {
        const membreId = cotisation.membre_id;
        if (!membresAvecImpayees[membreId]) {
          membresAvecImpayees[membreId] = {
            membre: membres.find(m => m.id === membreId),
            cotisationsImpayees: []
          };
        }
        membresAvecImpayees[membreId].cotisationsImpayees.push(cotisation);
      }
    });

    // Vérifier les échéances proches (dans les 3 prochains jours)
    Object.values(membresAvecImpayees).forEach(({ membre, cotisationsImpayees }) => {
      if (!membre) return;

      const cotisationsEcheantes = cotisationsImpayees.filter(c => {
        if (!c.date_echeance) return false;
        const dateEcheance = new Date(c.date_echeance);
        dateEcheance.setHours(0, 0, 0, 0);
        const joursRestants = Math.ceil((dateEcheance.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        return joursRestants >= 0 && joursRestants <= 3;
      });

      if (cotisationsEcheantes.length > 0) {
        notificationService.rappelPaiement(membre, cotisationsImpayees.length);
      }
    });
  };

  const handleAdminSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAdmin) {
        // Ne pas envoyer le mot de passe s'il est vide lors de la modification
        const updateData = { ...adminForm };
        if (!updateData.password || updateData.password.trim() === '') {
          delete updateData.password;
        }
        await updateAdmin(editingAdmin.id, updateData);
        notificationService.success('Succès', 'Administrateur mis à jour');
      } else {
          await createAdmin(adminForm);
          notificationService.success('Succès', 'Administrateur créé');
      }
      setAdminForm({ username: '', password: '', nom_complet: '' });
      setShowAdminForm(false);
      setEditingAdmin(null);
      loadData();
    } catch (error) {
      notificationService.error('Erreur', error.response?.data?.message || 'Erreur lors de l\'opération');
    }
  };

  const handleRecolteurSubmit = async (e) => {
    e.preventDefault();
    
    // Validation des champs requis
    if (!recolteurForm.nom_complet || recolteurForm.nom_complet.trim() === '') {
      notificationService.error('Erreur', 'Le nom complet est requis');
      return;
    }
    
    if (!editingRecolteur) {
      // Vérifier que les identifiants sont bien générés
      if (!recolteurForm.username || !recolteurForm.password) {
        notificationService.error('Erreur', 'Les identifiants n\'ont pas été générés. Veuillez fermer et rouvrir le formulaire.');
        return;
      }
    }
    
    try {
      // Préparer les données à envoyer
      const dataToSend = {
        nom_complet: recolteurForm.nom_complet.trim(),
        association: recolteurForm.association?.trim() || null,
        commune: recolteurForm.commune || null,
        matricule: recolteurForm.matricule?.trim() || null,
        telephone: recolteurForm.telephone?.trim() || null
      };
      
      if (!editingRecolteur) {
        // Création : inclure username et password (obligatoires)
        if (!recolteurForm.username || !recolteurForm.password) {
          notificationService.error('Erreur', 'Username et password sont requis');
          return;
        }
        dataToSend.username = recolteurForm.username.trim();
        dataToSend.password = recolteurForm.password.trim();
      } else {
        // Modification : inclure username (ne peut pas changer) et password si fourni
        dataToSend.username = recolteurForm.username.trim();
        if (recolteurForm.password && recolteurForm.password.trim() !== '') {
          dataToSend.password = recolteurForm.password.trim();
        }
        if (recolteurForm.matricule && recolteurForm.matricule.trim() !== '') {
          dataToSend.matricule = recolteurForm.matricule.trim();
        }
      }
      
      // Debug: afficher les données envoyées
      console.log('Données envoyées:', dataToSend);
      
      if (editingRecolteur) {
        await updateRecolteur(editingRecolteur.id, dataToSend);
        notificationService.success('Succès', 'Récolteur mis à jour');
      } else {
          await createRecolteur(dataToSend);
          // Afficher les identifiants générés
          const message = `Récolteur créé avec succès!\n\nIdentifiants générés:\nUsername: ${recolteurForm.username}\nMot de passe: ${recolteurForm.password}\nMatricule: ${recolteurForm.matricule}`;
          notificationService.success('Récolteur créé', message);
          // Afficher aussi dans une alerte pour faciliter la copie
          setTimeout(() => {
            alert(`Récolteur créé avec succès!\n\nIdentifiants générés:\nUsername: ${recolteurForm.username}\nMot de passe: ${recolteurForm.password}\nMatricule: ${recolteurForm.matricule}\n\nNotez ces informations pour le récolteur.`);
          }, 500);
      }
      setRecolteurForm({ username: '', password: '', nom_complet: '', association: '', commune: '', matricule: '', telephone: '' });
      setShowRecolteurForm(false);
      setEditingRecolteur(null);
      loadData();
    } catch (error) {
      notificationService.error('Erreur', error.response?.data?.message || 'Erreur lors de l\'opération');
    }
  };

  const handleEditRecolteur = (recolteur) => {
    if (!isPrincipal()) {
      notificationService.warning('Accès refusé', 'Seul l\'administrateur principal peut modifier les récolteurs.');
      return;
    }
    setEditingRecolteur(recolteur);
    setRecolteurForm({
      username: recolteur.username,
      password: '',
      nom_complet: recolteur.nom_complet,
      association: recolteur.association || '',
      commune: recolteur.commune || '',
      matricule: recolteur.matricule || '',
      telephone: recolteur.telephone || ''
    });
    setShowRecolteurForm(true);
  };

  const handleGererSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingGerer) {
        await updateGerer(editingGerer.id, gererForm);
        notificationService.success('Succès', 'Gestionnaire mis à jour');
      } else {
          await createGerer(gererForm);
          notificationService.success('Succès', 'Gestionnaire créé');
      }
      setGererForm({ username: '', password: '', nom_complet: '' });
      setShowGererForm(false);
      setEditingGerer(null);
      loadData();
    } catch (error) {
      notificationService.error('Erreur', error.response?.data?.message || 'Erreur lors de l\'opération');
    }
  };

  const handleEditGerer = (gerer) => {
    if (!isPrincipal()) {
      notificationService.warning('Accès refusé', 'Seul l\'administrateur principal peut modifier les gestionnaires.');
      return;
    }
    setEditingGerer(gerer);
    setGererForm({
      username: gerer.username,
      password: '',
      nom_complet: gerer.nom_complet
    });
    setShowGererForm(true);
  };

  const handleDeleteGerer = async (id) => {
    if (!isPrincipal()) {
      notificationService.warning('Accès refusé', 'Seul l\'administrateur principal peut supprimer les gestionnaires.');
      return;
    }
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce gestionnaire ?')) {
      return;
    }
    try {
      await deleteGerer(id);
      loadData();
      notificationService.success('Succès', 'Gestionnaire supprimé');
    } catch (error) {
      notificationService.error('Erreur', error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const handleFinancesSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingFinances) {
        await updateFinances(editingFinances.id, financesForm);
        notificationService.success('Succès', 'Utilisateur Finances mis à jour');
      } else {
        await createFinances(financesForm);
        notificationService.success('Succès', 'Utilisateur Finances créé');
      }
      setFinancesForm({ username: '', password: '', nom_complet: '' });
      setShowFinancesForm(false);
      setEditingFinances(null);
      loadData();
    } catch (error) {
      notificationService.error('Erreur', error.response?.data?.message || 'Erreur lors de l\'opération');
    }
  };

  const handleEditFinances = (financesUser) => {
    if (!isPrincipal()) {
      notificationService.warning('Accès refusé', 'Seul l\'administrateur principal peut modifier les utilisateurs Finances.');
      return;
    }
    setEditingFinances(financesUser);
    setFinancesForm({
      username: financesUser.username,
      password: '',
      nom_complet: financesUser.nom_complet
    });
    setShowFinancesForm(true);
  };

  const handleDeleteFinances = async (id) => {
    if (!isPrincipal()) {
      notificationService.warning('Accès refusé', 'Seul l\'administrateur principal peut supprimer les utilisateurs Finances.');
      return;
    }
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur Finances ?')) {
      return;
    }
    try {
      await deleteFinances(id);
      loadData();
      notificationService.success('Succès', 'Utilisateur Finances supprimé');
    } catch (error) {
      notificationService.error('Erreur', error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const handleDeleteRecolteur = async (id) => {
    if (!isPrincipal()) {
      notificationService.warning('Accès refusé', 'Seul l\'administrateur principal peut supprimer les récolteurs.');
      return;
    }
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce récolteur ?')) {
      return;
    }
    try {
      await deleteRecolteur(id);
      loadData();
      notificationService.success('Succès', 'Récolteur supprimé');
    } catch (error) {
      notificationService.error('Erreur', error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const handleEditAdmin = (admin) => {
    setEditingAdmin(admin);
    setAdminForm({
      username: admin.username,
      password: '',
      nom_complet: admin.nom_complet
    });
    setShowAdminForm(true);
  };

  const handleDeleteAdmin = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet administrateur ?')) {
      return;
    }
    try {
      await deleteAdmin(id);
      loadData();
      notificationService.success('Succès', 'Administrateur supprimé');
    } catch (error) {
      notificationService.error('Erreur', error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const handleEditMembre = async (membre) => {
    if (!isPrincipal()) {
      notificationService.warning('Accès refusé', 'Seul l\'administrateur principal peut modifier les membres.');
      return;
    }
    setEditingMembre(membre);
    setMembreForm({
      nom: membre.nom || '',
      postnom: membre.postnom || '',
      prenom: membre.prenom || '',
      date_naissance: membre.date_naissance ? membre.date_naissance.split('T')[0] : '',
      lieu_naissance: membre.lieu_naissance || '',
      quartier: membre.quartier || '',
      commune: membre.commune || '',
      telephone: membre.telephone || '',
      profession: membre.profession || '',
      association: membre.association || '',
      centre_sante: membre.centre_sante || '',
      nom_epouse: membre.nom_epouse || '',
      date_adhesion: membre.date_adhesion ? membre.date_adhesion.split('T')[0] : ''
    });
    
    // Charger les enfants du membre
    setMembrePourEnfants(membre);
    await loadEnfantsMembre(membre.id);
  };

  const handleAddEnfantsMembre = async (membre) => {
    if (!isPrincipal()) {
      notificationService.warning('Accès refusé', 'Seul l\'administrateur principal peut ajouter des enfants aux membres.');
      return;
    }
    // Ouvrir le formulaire d'édition et charger les enfants
    setEditingMembre(membre);
    setMembreForm({
      nom: membre.nom || '',
      postnom: membre.postnom || '',
      prenom: membre.prenom || '',
      date_naissance: membre.date_naissance ? membre.date_naissance.split('T')[0] : '',
      lieu_naissance: membre.lieu_naissance || '',
      quartier: membre.quartier || '',
      commune: membre.commune || '',
      telephone: membre.telephone || '',
      profession: membre.profession || '',
      association: membre.association || '',
      centre_sante: membre.centre_sante || '',
      nom_epouse: membre.nom_epouse || '',
      date_adhesion: membre.date_adhesion ? membre.date_adhesion.split('T')[0] : ''
    });
    
    // Charger les enfants du membre
    setMembrePourEnfants(membre);
    await loadEnfantsMembre(membre.id);
  };

  const handleDeleteMembre = async (membreId) => {
    if (!isPrincipal()) {
      notificationService.warning('Accès refusé', 'Seul l\'administrateur principal peut supprimer les membres.');
      return;
    }
    const membre = membres.find(m => m.id === membreId);
    const membreNom = membre ? `${membre.nom} ${membre.prenom}` : 'ce membre';
    const message = `Êtes-vous sûr de vouloir supprimer le membre ${membreNom} (Matricule: ${membre?.numero_matricule}) ?\n\n⚠️ Cette action est irréversible et supprimera également toutes les cotisations et paiements associés.`;
    
    if (!window.confirm(message)) {
      return;
    }
    
    try {
      await deleteMembre(membreId);
      notificationService.success('Succès', 'Membre supprimé avec succès');
      loadData();
    } catch (error) {
      notificationService.error('Erreur', error.response?.data?.message || 'Erreur lors de la suppression du membre');
    }
  };

  const handleMembreSubmit = async (e) => {
    e.preventDefault();
    if (!editingMembre) return;

    try {
      const formData = new FormData();
      
      // Ajouter tous les champs du formulaire
      Object.keys(membreForm).forEach(key => {
        if (membreForm[key] !== null && membreForm[key] !== undefined && membreForm[key] !== '') {
          formData.append(key, membreForm[key]);
        }
      });

      await updateMembre(editingMembre.id, formData);
      notificationService.success('Succès', 'Membre mis à jour avec succès');
      setEditingMembre(null);
      setMembrePourEnfants(null);
      setEnfantsMembre([]);
      setNouvelEnfant({ nom: '', prenom: '', date_naissance: '', lien: '' });
      setPhotoEnfant(null);
      setWebcamKeyEnfant(prev => prev + 1); // Réinitialiser le WebcamCapture
      setMembreForm({
        nom: '',
        postnom: '',
        prenom: '',
        date_naissance: '',
        lieu_naissance: '',
        quartier: '',
        commune: '',
        telephone: '',
        profession: '',
        association: '',
        centre_sante: '',
        nom_epouse: '',
        date_adhesion: ''
      });
      loadData();
    } catch (error) {
      notificationService.error('Erreur', error.response?.data?.message || 'Erreur lors de la mise à jour du membre');
    }
  };

  // Fonctions de gestion des familles
  const handleEditFamille = (famille) => {
    if (!isPrincipal()) {
      notificationService.warning('Accès refusé', 'Seul l\'administrateur principal peut modifier les familles.');
      return;
    }
    setEditingFamille(famille);
    setFamilleForm({
      chef_famille_nom: famille.chef_famille_nom || '',
      nom_famille: famille.nom_famille || '',
      adresse: famille.adresse || '',
      quartier: famille.quartier || '',
      commune: famille.commune || '',
      telephone: famille.telephone || '',
      observations: famille.observations || '',
      statut: famille.statut || 'active'
    });
    setPhotoChefFamille(null); // Réinitialiser la photo lors de l'édition
    setShowFamilleForm(true);
  };

  const handleAddMembreFamille = () => {
    if (!nouveauMembre.nom || !nouveauMembre.prenom) {
      notificationService.warning('Attention', 'Veuillez remplir au moins le nom et le prénom');
      return;
    }
    setMembresFamille([...membresFamille, { ...nouveauMembre, id: Date.now() }]);
    setNouveauMembre({ nom: '', prenom: '', date_naissance: '', lien: '' });
  };

  const handleRemoveMembreFamille = (id) => {
    setMembresFamille(membresFamille.filter(m => m.id !== id));
  };

  const handleDeleteFamille = async (familleId) => {
    if (!isPrincipal()) {
      notificationService.warning('Accès refusé', 'Seul l\'administrateur principal peut supprimer les familles.');
      return;
    }
    const famillesArray = Array.isArray(familles) ? familles : [];
    const famille = famillesArray.find(f => f.id === familleId);
    const familleNom = famille ? famille.nom_famille : 'cette famille';
    const nombreMembres = famille?.membres ? famille.membres.length : (famille?.nombre_membres || 0);
    const message = `Êtes-vous sûr de vouloir supprimer la famille ${familleNom} (Numéro: ${famille?.numero_famille}) ?\n\n⚠️ Cette action est irréversible.\n\n${nombreMembres > 0 ? `⚠️ ATTENTION: ${nombreMembres} membre(s) lié(s) à cette famille seront également supprimé(s).` : ''}`;
    
    if (!window.confirm(message)) {
      return;
    }
    
    try {
      await deleteFamille(familleId);
      notificationService.success('Succès', 'Famille supprimée avec succès');
      const famillesRes = await getFamilles();
      const famillesArray = Array.isArray(famillesRes) ? famillesRes : (Array.isArray(famillesRes?.data) ? famillesRes.data : []);
      setFamilles(famillesArray);
      console.log('✅ Familles rechargées après suppression:', famillesArray.length, 'famille(s)');
    } catch (error) {
      notificationService.error('Erreur', error.response?.data?.message || 'Erreur lors de la suppression de la famille');
    }
  };

  const loadStatsFamilles = async () => {
    setLoadingStatsFamilles(true);
    try {
      const [journalieresRes, mensuellesRes, globalesRes] = await Promise.allSettled([
        getStatsFamillesJournalieres(),
        getStatsFamillesMensuelles(new Date().getMonth() + 1, new Date().getFullYear()),
        getStatsFamillesGlobales()
      ]);
      
      if (journalieresRes.status === 'fulfilled' && journalieresRes.value?.data) {
        setStatsFamillesJournalieres(journalieresRes.value.data);
      }
      
      if (mensuellesRes.status === 'fulfilled' && mensuellesRes.value?.data) {
        setStatsFamillesMensuelles(mensuellesRes.value.data);
      }
      
      if (globalesRes.status === 'fulfilled' && globalesRes.value?.data) {
        setStatsFamillesGlobales(globalesRes.value.data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques des familles:', error);
    } finally {
      setLoadingStatsFamilles(false);
    }
  };

  const handleRegenererCarteFamille = async (familleId) => {
    try {
      const response = await regenererNumeroFamille(familleId);
      const familleMiseAJour = response.data?.famille || response.data;
      notificationService.success('Succès', 'Carte de famille prête à l\'affichage.');
      
      // Afficher la carte directement
      setSelectedFamilleCarte(familleMiseAJour);
    } catch (error) {
      notificationService.error('Erreur', error.response?.data?.message || 'Erreur lors de la préparation de la carte de famille');
    }
  };

  const handlePhotoChefCapture = (file, fieldName) => {
    setPhotoChefFamille(file);
    console.log('📸 Photo du chef de famille capturée:', file);
  };

  const handlePhotoEnfantCapture = (file, fieldName) => {
    setPhotoEnfant(file);
    console.log('📸 Photo de l\'enfant capturée:', file);
  };

  const handleFamilleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const formData = new FormData();
      
      // Ajouter tous les champs du formulaire
      Object.keys(familleForm).forEach(key => {
        if (familleForm[key] !== null && familleForm[key] !== undefined && familleForm[key] !== '') {
          formData.append(key, familleForm[key]);
        }
      });
      
      // Ajouter la photo du chef de famille si elle existe
      if (photoChefFamille) {
        formData.append('photo_chef', photoChefFamille);
      }
      
      if (editingFamille) {
        await updateFamille(editingFamille.id, formData);
        notificationService.success('Succès', 'Famille mise à jour avec succès');
      } else {
        const nouvelleFamille = await createFamille(formData);
        const familleData = nouvelleFamille?.data?.famille || nouvelleFamille?.data || nouvelleFamille;
        
        // Créer automatiquement le paiement de carte de famille (5000 CDF)
        const montantCarte = 5000;
        try {
          console.log('💳 Création automatique du paiement de carte de famille:', {
            famille_id: familleData.id,
            montant: montantCarte,
            type_paiement: 'cotisation_famille'
          });

          const paiementData = {
            famille_id: familleData.id,
            montant: montantCarte,
            date_paiement: new Date().toISOString().split('T')[0],
            numero_recu: `PAY-FAM-${Date.now()}`,
            type_paiement: 'autre'
          };

          await createPaiementFamille(paiementData);
          
          console.log('✅ Paiement de carte de famille créé automatiquement');
          notificationService.success('Succès', `Famille créée avec succès! Paiement de la carte (${montantCarte.toLocaleString('fr-FR')} CDF) enregistré.`);
        } catch (paiementErr) {
          console.error('❌ Erreur lors de la création automatique du paiement de carte:', paiementErr);
          // Ne pas bloquer l'enregistrement de la famille si le paiement échoue
          notificationService.success('Succès', 'Famille créée avec succès');
          notificationService.warning('Avertissement', 'Le paiement de la carte n\'a pas pu être enregistré automatiquement. Vous pouvez l\'enregistrer manuellement.');
        }
      }
      
      setShowFamilleForm(false);
      setEditingFamille(null);
      setPhotoChefFamille(null);
      setFamilleForm({
        chef_famille_nom: '',
        nom_famille: '',
        adresse: '',
        quartier: '',
        commune: '',
        telephone: '',
        observations: '',
        statut: 'active'
      });
      
      const famillesRes = await getFamilles();
      // L'API retourne directement un tableau de familles (comme pour les membres)
      const famillesArray = Array.isArray(famillesRes) ? famillesRes : (Array.isArray(famillesRes?.data) ? famillesRes.data : []);
      setFamilles(famillesArray);
      console.log('✅ Familles rechargées après création/modification:', famillesArray.length, 'famille(s)');
    } catch (error) {
      console.error('❌ Erreur lors du rechargement des familles:', error);
      notificationService.error('Erreur', error.response?.data?.message || 'Erreur lors de l\'enregistrement de la famille');
    }
  };

  const handleGestionMembres = async (famille) => {
    try {
      const familleRes = await getFamille(famille.id);
      // L'API retourne directement la famille (comme pour les membres)
      const familleData = familleRes?.data || familleRes;
      setFamillePourMembres(familleData);
      setMembresFamille(familleData?.membres || []);
      setShowGestionMembres(true);
    } catch (error) {
      notificationService.error('Erreur', 'Erreur lors du chargement de la famille');
    }
  };

  const handleViewFamilleDetails = async (famille) => {
    // Charger les détails complets de la famille
    try {
      const familleRes = await getFamille(famille.id);
      // L'API retourne directement la famille (comme pour les membres)
      const familleData = familleRes?.data || familleRes;
      setSelectedFamilleDetails(familleData);
      setShowFamilleDetails(true);
    } catch (error) {
      console.error('Erreur lors du chargement des détails de la famille:', error);
      notificationService.error('Erreur', 'Erreur lors du chargement des détails de la famille');
    }
  };

  const handleImprimerBonTraitement = async (personneId, type) => {
    try {
      if (type === 'membre') {
        const [membreResponse, cotisationsResponse] = await Promise.all([
          getMembre(personneId),
          getCotisationsByMembre(personneId).catch(() => ({ data: [] }))
        ]);
        setSelectedPersonneBon(membreResponse?.data || membreResponse);
        setTypePersonneBon('membre');
        setCotisationsBon(cotisationsResponse?.data || []);
      } else {
        const [familleResponse, cotisationsResponse] = await Promise.all([
          getFamille(personneId),
          getCotisationsFamilles({ famille_id: personneId }).catch(() => ({ data: [] }))
        ]);
        setSelectedPersonneBon(familleResponse?.data || familleResponse);
        setTypePersonneBon('famille');
        setCotisationsBon(cotisationsResponse?.data || []);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération pour le bon de traitement:', error);
      notificationService.error('Erreur', 'Impossible de charger les données pour le bon de traitement.');
    }
  };

  const handlePhotoMembreFamilleCapture = (file, fieldName) => {
    if (fieldName === 'photo') {
      setPhotoMembreFamille(file);
      console.log('📸 Photo du membre de famille capturée:', file);
    }
  };

  const handleAddMembreFamilleSubmit = async () => {
    if (!nouveauMembre.nom || !nouveauMembre.prenom) {
      notificationService.warning('Attention', 'Veuillez remplir au moins le nom et le prénom');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('nom', nouveauMembre.nom);
      formData.append('prenom', nouveauMembre.prenom);
      if (nouveauMembre.date_naissance) {
        formData.append('date_naissance', nouveauMembre.date_naissance);
      }
      if (nouveauMembre.lien) {
        formData.append('lien', nouveauMembre.lien);
      }
      if (photoMembreFamille) {
        formData.append('photo', photoMembreFamille);
      }

      await createMembreFamille(famillePourMembres.id, formData);
      notificationService.success('Succès', 'Membre ajouté avec succès');
      
      // Recharger les membres
      const familleRes = await getFamille(famillePourMembres.id);
      const familleData = familleRes?.data || familleRes;
      setFamillePourMembres(familleData);
      setMembresFamille(familleData?.membres || []);
      setNouveauMembre({ nom: '', prenom: '', date_naissance: '', lien: '' });
      setPhotoMembreFamille(null);
      setWebcamKeyMembreFamille(prev => prev + 1);
      
      // Recharger la liste des familles
      const famillesRes = await getFamilles();
      const famillesData = famillesRes?.data;
      setFamilles(Array.isArray(famillesData) ? famillesData : []);
    } catch (error) {
      notificationService.error('Erreur', error.response?.data?.message || 'Erreur lors de l\'ajout du membre');
    }
  };

  const handleDeleteMembreFamille = async (membreId) => {
    if (!isPrincipal()) {
      notificationService.warning('Accès refusé', 'Seul l\'administrateur principal peut supprimer les membres de famille.');
      return;
    }
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce membre ?')) {
      return;
    }

    try {
      await deleteMembreFamille(famillePourMembres.id, membreId);
      notificationService.success('Succès', 'Membre supprimé avec succès');
      
      // Recharger les membres
      const familleRes = await getFamille(famillePourMembres.id);
      const familleData = familleRes?.data || familleRes;
      setFamillePourMembres(familleData);
      setMembresFamille(familleData?.membres || []);
      
      // Recharger la liste des familles
      const famillesRes = await getFamilles();
      const famillesData = famillesRes?.data;
      setFamilles(Array.isArray(famillesData) ? famillesData : []);
    } catch (error) {
      notificationService.error('Erreur', error.response?.data?.message || 'Erreur lors de la suppression du membre');
    }
  };

  // Fonctions pour gérer les enfants des membres
  const loadEnfantsMembre = async (membreId) => {
    try {
      const response = await getEnfantsMembre(membreId);
      const enfants = response?.data || response || [];
      setEnfantsMembre(Array.isArray(enfants) ? enfants : []);
    } catch (error) {
      console.error('Erreur lors du chargement des enfants:', error);
      console.error('Response:', error.response?.data);
      
      // Afficher le message d'erreur détaillé
      const errorMessage = error.response?.data?.message || 'Erreur lors du chargement des enfants';
      const errorDetails = error.response?.data?.details;
      
      console.error('❌ Détails de l\'erreur:', error.response?.data);
      
      if (error.response?.data?.message?.includes('n\'existe pas') || 
          error.response?.data?.errorName === 'SequelizeDatabaseError') {
        notificationService.warning(
          'Table manquante', 
          errorMessage
        );
      } else {
        notificationService.error(
          'Erreur', 
          errorMessage
        );
      }
      setEnfantsMembre([]);
    }
  };

  const handleAddEnfantMembreSubmit = async () => {
    if (!nouvelEnfant.nom || !nouvelEnfant.prenom) {
      notificationService.warning('Attention', 'Veuillez remplir au moins le nom et le prénom');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('nom', nouvelEnfant.nom);
      formData.append('prenom', nouvelEnfant.prenom);
      if (nouvelEnfant.date_naissance) {
        formData.append('date_naissance', nouvelEnfant.date_naissance);
      }
      if (nouvelEnfant.lien) {
        formData.append('lien', nouvelEnfant.lien);
      }
      if (photoEnfant) {
        formData.append('photo', photoEnfant);
      }

      console.log('📤 Envoi des données:', {
        membreId: membrePourEnfants.id,
        nom: nouvelEnfant.nom,
        prenom: nouvelEnfant.prenom,
        date_naissance: nouvelEnfant.date_naissance,
        lien: nouvelEnfant.lien,
        hasPhoto: !!photoEnfant
      });
      
      const response = await createEnfantMembre(membrePourEnfants.id, formData);
      notificationService.success('Succès', 'Enfant ajouté avec succès');
      
      // Recharger les enfants
      await loadEnfantsMembre(membrePourEnfants.id);
      
      // Réinitialiser complètement le formulaire
      setNouvelEnfant({ nom: '', prenom: '', date_naissance: '', lien: '' });
      setPhotoEnfant(null);
      setWebcamKeyEnfant(prev => prev + 1); // Forcer la réinitialisation du WebcamCapture
      
      // Recharger le membre pour mettre à jour le nombre d'enfants
      const membreRes = await getMembre(membrePourEnfants.id);
      const membreData = membreRes?.data || membreRes;
      if (membreData) {
        setMembrePourEnfants(membreData);
      }
    } catch (error) {
      console.error('❌ Erreur lors de l\'ajout de l\'enfant:', error);
      console.error('❌ Response:', error.response);
      console.error('❌ Data:', error.response?.data);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Erreur lors de l\'ajout de l\'enfant';
      notificationService.error('Erreur', errorMessage);
    }
  };

  const handleDeleteEnfantMembre = async (enfantId) => {
    if (!isPrincipal()) {
      notificationService.warning('Accès refusé', 'Seul l\'administrateur principal peut supprimer les enfants.');
      return;
    }
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet enfant ?')) {
      return;
    }

    try {
      await deleteEnfantMembre(membrePourEnfants.id, enfantId);
      notificationService.success('Succès', 'Enfant supprimé avec succès');
      
      // Recharger les enfants
      await loadEnfantsMembre(membrePourEnfants.id);
      
      // Recharger le membre pour mettre à jour le nombre d'enfants
      const membreRes = await getMembre(membrePourEnfants.id);
      const membreData = membreRes?.data || membreRes;
      if (membreData) {
        setMembrePourEnfants(membreData);
      }
    } catch (error) {
      notificationService.error('Erreur', error.response?.data?.message || 'Erreur lors de la suppression de l\'enfant');
    }
  };

  // Réinitialiser la photo lors de l'édition d'un membre
  useEffect(() => {
    if (!editingMembre) {
      setPhotoEnfant(null);
    }
  }, [editingMembre]);

  const handleMontantSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMontant) {
        // Mode modification
        await updateMontantMensuel(editingMontant.id, {
          montant_mensuel: montantForm.montant_mensuel
        });
        
        notificationService.success(
          'Montant modifié',
          `Le montant mensuel pour ${montantForm.mois}/${montantForm.annee} a été modifié avec succès.`
        );
        
        setShowMontantForm(false);
        setEditingMontant(null);
        setMontantForm({
          mois: new Date().getMonth() + 1,
          annee: new Date().getFullYear(),
          montant_mensuel: ''
        });
        loadData();
      } else {
        // Mode création
        const response = await createMontantMensuel(montantForm);
        
        // Notification de déclaration de montant mensuel
        notificationService.montantMensuelDefini(
          response.data.montant_mensuel,
          response.data.statistiques.cotisations_creees
        );
        
        // Notification du montant hebdomadaire calculé
        notificationService.montantHebdomadaireCalcule(
          response.data.montant_mensuel.montant_hebdomadaire
        );
        
        setShowMontantForm(false);
        setMontantForm({
          mois: new Date().getMonth() + 1,
          annee: new Date().getFullYear(),
          montant_mensuel: ''
        });
        loadData();
      }
    } catch (err) {
      console.error('Erreur:', err);
      const errorMessage = err.response?.data?.message || (editingMontant ? 'Erreur lors de la modification du montant mensuel' : 'Erreur lors de la fixation du montant mensuel');
      notificationService.error('Erreur', errorMessage);
      alert(errorMessage);
    }
  };

  const handleEditMontant = (montant) => {
    if (!isPrincipal()) {
      notificationService.warning('Accès refusé', 'Seul l\'administrateur principal peut modifier les montants mensuels.');
      return;
    }
    setEditingMontant(montant);
    setMontantForm({
      mois: montant.mois,
      annee: montant.annee,
      montant_mensuel: montant.montant_mensuel.toString()
    });
    setShowMontantForm(true);
  };

  // Handlers pour les montants mensuels des familles
  const handleMontantFamilleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMontantFamille) {
        // Mode modification
        await updateMontantMensuelFamille(editingMontantFamille.id, {
          montant_mensuel: montantFamilleForm.montant_mensuel
        });
        
        notificationService.success(
          'Montant modifié',
          `Le montant mensuel famille pour ${montantFamilleForm.mois}/${montantFamilleForm.annee} a été modifié avec succès.`
        );
        
        setShowMontantFamilleForm(false);
        setEditingMontantFamille(null);
        setMontantFamilleForm({
          mois: new Date().getMonth() + 1,
          annee: new Date().getFullYear(),
          montant_mensuel: ''
        });
        loadData();
      } else {
        // Mode création
        const response = await createMontantMensuelFamille(montantFamilleForm);
        
        notificationService.success(
          'Montant fixé',
          `Le montant mensuel famille pour ${montantFamilleForm.mois}/${montantFamilleForm.annee} a été fixé avec succès. ${response.data.stats?.cotisationsCreees || 0} cotisation(s) créée(s).`
        );
        
        setShowMontantFamilleForm(false);
        setMontantFamilleForm({
          mois: new Date().getMonth() + 1,
          annee: new Date().getFullYear(),
          montant_mensuel: ''
        });
        loadData();
      }
    } catch (err) {
      console.error('Erreur:', err);
      const errorMessage = err.response?.data?.message || (editingMontantFamille ? 'Erreur lors de la modification du montant mensuel famille' : 'Erreur lors de la fixation du montant mensuel famille');
      notificationService.error('Erreur', errorMessage);
    }
  };

  const handleEditMontantFamille = (montant) => {
    if (!isPrincipal()) {
      notificationService.warning('Accès refusé', 'Seul l\'administrateur principal peut modifier les montants mensuels des familles.');
      return;
    }
    setEditingMontantFamille(montant);
    setMontantFamilleForm({
      mois: montant.mois,
      annee: montant.annee,
      montant_mensuel: montant.montant_mensuel.toString()
    });
    setShowMontantFamilleForm(true);
  };

  const handleCentreSanteSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCentreSante) {
        await updateCentreSante(editingCentreSante.id, centreSanteForm);
        notificationService.success('Succès', 'Centre de santé mis à jour');
      } else {
        await createCentreSante(centreSanteForm);
        notificationService.success('Succès', 'Centre de santé créé');
      }
      setShowCentreSanteForm(false);
      setEditingCentreSante(null);
      setCentreSanteForm({
        nom: '',
        adresse: '',
        commune: '',
        telephone: ''
      });
      loadData();
    } catch (error) {
      notificationService.error('Erreur', error.response?.data?.message || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleDeleteMontant = async (montant) => {
    if (!isPrincipal()) {
      notificationService.warning('Accès refusé', 'Seul l\'administrateur principal peut supprimer les montants mensuels.');
      return;
    }
    if (!montant || !montant.id) {
      console.error('❌ Montant invalide:', montant);
      notificationService.error('Erreur', 'Montant invalide');
      return;
    }

    const moisNoms = [
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];
    const moisNom = moisNoms[montant.mois - 1] || montant.mois;
    
    const confirmMessage = `Êtes-vous sûr de vouloir supprimer le montant mensuel pour ${moisNom} ${montant.annee} ?\n\nCette action supprimera également toutes les cotisations impayées pour ce mois/année.\n\nLes cotisations déjà payées ne seront pas supprimées.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      console.log('🗑️ Suppression du montant ID:', montant.id);
      const response = await deleteMontantMensuel(montant.id);
      console.log('✅ Réponse de suppression:', response);
      
      if (response && response.data) {
        notificationService.success(
          'Montant supprimé',
          `Le montant mensuel pour ${moisNom} ${montant.annee} a été supprimé avec succès.${response.data.cotisations_supprimees > 0 ? ` ${response.data.cotisations_supprimees} cotisation(s) impayée(s) supprimée(s).` : ''}`
        );
      } else {
        notificationService.success(
          'Montant supprimé',
          `Le montant mensuel pour ${moisNom} ${montant.annee} a été supprimé avec succès.`
        );
      }
      
      await loadData();
    } catch (err) {
      console.error('❌ Erreur lors de la suppression:', err);
      console.error('❌ Détails de l\'erreur:', {
        message: err.message,
        response: err.response,
        data: err.response?.data,
        status: err.response?.status
      });
      const errorMessage = err.response?.data?.message || err.message || 'Erreur lors de la suppression du montant mensuel';
      notificationService.error('Erreur de suppression', errorMessage);
    }
  };

  const handlePrintMontants = () => {
    // Ouvrir la page d'impression dans une nouvelle fenêtre
    const printWindow = window.open(
      `/admin/montants/print?search=${encodeURIComponent(searchMontants)}`,
      '_blank',
      'width=1200,height=800,scrollbars=yes,resizable=yes'
    );
    
    if (!printWindow) {
      notificationService.error('Erreur', 'Veuillez autoriser les pop-ups pour ouvrir la page d\'impression.');
    }
  };

  const handleDownloadPDFMontants = () => {
    // Ouvrir la page d'impression dans une nouvelle fenêtre pour le PDF
    const printWindow = window.open(
      `/admin/montants/print?search=${encodeURIComponent(searchMontants)}`,
      '_blank',
      'width=1200,height=800,scrollbars=yes,resizable=yes'
    );
    
    if (!printWindow) {
      notificationService.error('Erreur', 'Veuillez autoriser les pop-ups pour ouvrir la page d\'impression.');
    }
  };

  const handlePrintMembres = () => {
    const params = new URLSearchParams();
    if (selectedAssociation) {
      params.append('association', selectedAssociation);
    }
    if (selectedTypeMembre) {
      params.append('type', selectedTypeMembre);
    }
    if (searchTerm) {
      params.append('search', searchTerm);
    }
    
    const printWindow = window.open(
      `/admin/membres/print?${params.toString()}`,
      '_blank',
      'width=1200,height=800,scrollbars=yes,resizable=yes'
    );
    
    if (!printWindow) {
      notificationService.error('Erreur', 'Veuillez autoriser les pop-ups pour ouvrir la page d\'impression.');
    }
  };

  const handleDownloadPDFMembres = () => {
    const params = new URLSearchParams();
    if (selectedAssociation) {
      params.append('association', selectedAssociation);
    }
    if (selectedTypeMembre) {
      params.append('type', selectedTypeMembre);
    }
    if (searchTerm) {
      params.append('search', searchTerm);
    }
    
    const printWindow = window.open(
      `/admin/membres/print?${params.toString()}`,
      '_blank',
      'width=1200,height=800,scrollbars=yes,resizable=yes'
    );
    
    if (!printWindow) {
      notificationService.error('Erreur', 'Veuillez autoriser les pop-ups pour ouvrir la page d\'impression.');
    }
  };

  const handlePrintRecolteurs = () => {
    const params = new URLSearchParams();
    if (selectedAssociationRecolteurs) {
      params.append('association', selectedAssociationRecolteurs);
    }
    if (searchRecolteurs) {
      params.append('search', searchRecolteurs);
    }
    
    const printWindow = window.open(
      `/admin/recolteurs/print?${params.toString()}`,
      '_blank',
      'width=1200,height=800,scrollbars=yes,resizable=yes'
    );
    
    if (!printWindow) {
      notificationService.error('Erreur', 'Veuillez autoriser les pop-ups pour ouvrir la page d\'impression.');
    }
  };

  const handleDownloadPDFRecolteurs = () => {
    const params = new URLSearchParams();
    if (selectedAssociationRecolteurs) {
      params.append('association', selectedAssociationRecolteurs);
    }
    if (searchRecolteurs) {
      params.append('search', searchRecolteurs);
    }
    
    const printWindow = window.open(
      `/admin/recolteurs/print?${params.toString()}`,
      '_blank',
      'width=1200,height=800,scrollbars=yes,resizable=yes'
    );
    
    if (!printWindow) {
      notificationService.error('Erreur', 'Veuillez autoriser les pop-ups pour ouvrir la page d\'impression.');
    }
  };

  const handlePrintCotisations = () => {
    const params = new URLSearchParams();
    if (selectedAssociationCotisations) {
      params.append('association', selectedAssociationCotisations);
    }
    if (searchCotisations) {
      params.append('search', searchCotisations);
    }
    
    const printWindow = window.open(
      `/admin/cotisations/print?${params.toString()}`,
      '_blank',
      'width=1200,height=800,scrollbars=yes,resizable=yes'
    );
    
    if (!printWindow) {
      notificationService.error('Erreur', 'Veuillez autoriser les pop-ups pour ouvrir la page d\'impression.');
    }
  };

  const handleDownloadPDFCotisations = () => {
    const params = new URLSearchParams();
    if (selectedAssociationCotisations) {
      params.append('association', selectedAssociationCotisations);
    }
    if (searchCotisations) {
      params.append('search', searchCotisations);
    }
    
    const printWindow = window.open(
      `/admin/cotisations/print?${params.toString()}`,
      '_blank',
      'width=1200,height=800,scrollbars=yes,resizable=yes'
    );
    
    if (!printWindow) {
      notificationService.error('Erreur', 'Veuillez autoriser les pop-ups pour ouvrir la page d\'impression.');
    }
  };

  const handlePrintPaiements = () => {
    const params = new URLSearchParams();
    if (selectedAssociationPaiements) {
      params.append('association', selectedAssociationPaiements);
    }
    if (searchPaiements) {
      params.append('search', searchPaiements);
    }
    
    const printWindow = window.open(
      `/admin/paiements/print?${params.toString()}`,
      '_blank',
      'width=1200,height=800,scrollbars=yes,resizable=yes'
    );
    
    if (!printWindow) {
      notificationService.error('Erreur', 'Veuillez autoriser les pop-ups pour ouvrir la page d\'impression.');
    }
  };

  const handleDownloadPDFPaiements = () => {
    const params = new URLSearchParams();
    if (selectedAssociationPaiements) {
      params.append('association', selectedAssociationPaiements);
    }
    if (searchPaiements) {
      params.append('search', searchPaiements);
    }
    
    const printWindow = window.open(
      `/admin/paiements/print?${params.toString()}`,
      '_blank',
      'width=1200,height=800,scrollbars=yes,resizable=yes'
    );
    
    if (!printWindow) {
      notificationService.error('Erreur', 'Veuillez autoriser les pop-ups pour ouvrir la page d\'impression.');
    }
  };

  // Fonctions pour les rapports
  const handlePrintMembresCotisations = () => {
    const params = new URLSearchParams();
    params.append('association', rapportCotisationsForm.association);
    params.append('commune', rapportCotisationsForm.commune);
    params.append('periodeType', rapportCotisationsForm.periodeType);
    params.append('mois', rapportCotisationsForm.mois);
    params.append('annee', rapportCotisationsForm.annee);
    if (rapportCotisationsForm.periodeType === 'semaine') {
      params.append('semaine', rapportCotisationsForm.semaine);
    }
    params.append('statut', rapportCotisationsForm.statut);
    
    const printWindow = window.open(
      `/admin/rapports/membres-cotisations/print?${params.toString()}`,
      '_blank',
      'width=1200,height=800,scrollbars=yes,resizable=yes'
    );
    
    if (!printWindow) {
      notificationService.error('Erreur', 'Veuillez autoriser les pop-ups pour ouvrir la page d\'impression.');
    }
  };

  const handleDownloadPDFMembresCotisations = () => {
    const params = new URLSearchParams();
    params.append('association', rapportCotisationsForm.association);
    params.append('commune', rapportCotisationsForm.commune);
    params.append('periodeType', rapportCotisationsForm.periodeType);
    params.append('mois', rapportCotisationsForm.mois);
    params.append('annee', rapportCotisationsForm.annee);
    if (rapportCotisationsForm.periodeType === 'semaine') {
      params.append('semaine', rapportCotisationsForm.semaine);
    }
    params.append('statut', rapportCotisationsForm.statut);
    
    const printWindow = window.open(
      `/admin/rapports/membres-cotisations/print?${params.toString()}`,
      '_blank',
      'width=1200,height=800,scrollbars=yes,resizable=yes'
    );
    
    if (!printWindow) {
      notificationService.error('Erreur', 'Veuillez autoriser les pop-ups pour ouvrir la page d\'impression.');
    }
  };

  const handlePrintMembresRecoltes = () => {
    const params = new URLSearchParams();
    params.append('association', rapportMembresRecoltesForm.association);
    params.append('commune', rapportMembresRecoltesForm.commune);
    
    const printWindow = window.open(
      `/admin/rapports/membres-recoltes/print?${params.toString()}`,
      '_blank',
      'width=1200,height=800,scrollbars=yes,resizable=yes'
    );
    
    if (!printWindow) {
      notificationService.error('Erreur', 'Veuillez autoriser les pop-ups pour ouvrir la page d\'impression.');
    }
  };

  const handleDownloadPDFMembresRecoltes = () => {
    const params = new URLSearchParams();
    params.append('association', rapportMembresRecoltesForm.association);
    params.append('commune', rapportMembresRecoltesForm.commune);
    
    const printWindow = window.open(
      `/admin/rapports/membres-recoltes/print?${params.toString()}`,
      '_blank',
      'width=1200,height=800,scrollbars=yes,resizable=yes'
    );
    
    if (!printWindow) {
      notificationService.error('Erreur', 'Veuillez autoriser les pop-ups pour ouvrir la page d\'impression.');
    }
  };

  // Fonctions d'export Excel/CSV
  const exportToExcel = (data, filename, sheetName = 'Données') => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, `${filename}.xlsx`);
  };

  const exportToCSV = (data, filename) => {
    if (data.length === 0) {
      notificationService.error('Erreur', 'Aucune donnée à exporter');
      return;
    }

    const headers = Object.keys(data[0]);
    const csvHeaders = headers.map(h => `"${h}"`).join(',');
    const csvRows = data.map(row => {
      return headers.map(header => {
        const value = row[header];
        if (value === null || value === undefined) return '""';
        const stringValue = String(value).replace(/"/g, '""');
        return `"${stringValue}"`;
      }).join(',');
    });
    
    const csvContent = [csvHeaders, ...csvRows].join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportMembres = () => {
    let membresFiltres = [...membres];

    if (exportForm.association) {
      membresFiltres = membresFiltres.filter(m => m.association === exportForm.association);
    }

    if (exportForm.commune && exportForm.commune !== 'toutes') {
      membresFiltres = membresFiltres.filter(m => m.commune === exportForm.commune);
    }

    if (exportForm.dateDebut) {
      const dateDebut = new Date(exportForm.dateDebut);
      dateDebut.setHours(0, 0, 0, 0);
      membresFiltres = membresFiltres.filter(m => {
        const dateMembre = new Date(m.created_at);
        return dateMembre >= dateDebut;
      });
    }

    if (exportForm.dateFin) {
      const dateFin = new Date(exportForm.dateFin);
      dateFin.setHours(23, 59, 59, 999);
      membresFiltres = membresFiltres.filter(m => {
        const dateMembre = new Date(m.created_at);
        return dateMembre <= dateFin;
      });
    }

    const dataToExport = membresFiltres.map(m => ({
      'Numéro Matricule': m.numero_matricule,
      'Nom': m.nom,
      'Postnom': m.postnom,
      'Prénom': m.prenom,
      'Date de Naissance': m.date_naissance,
      'Lieu de Naissance': m.lieu_naissance,
      'Quartier': m.quartier,
      'Commune': m.commune,
      'Téléphone': m.telephone,
      'Profession': m.profession,
      'Association': m.association,
      'Centre de Santé': m.centre_sante,
      'Nom Épouse': m.nom_epouse,
      'Nombre Enfants': m.enfants ? m.enfants.length : (m.nombre_enfants || 0),
      'Enfants': m.enfants ? m.enfants.map(e => `${e.nom} ${e.prenom}`).join(', ') : (m.noms_enfants || ''),
      'Date Adhésion': m.date_adhesion,
      'Date Création': m.created_at ? new Date(m.created_at).toLocaleDateString('fr-FR') : ''
    }));

    if (dataToExport.length === 0) {
      notificationService.error('Erreur', 'Aucun membre ne correspond aux critères sélectionnés');
      return;
    }

    const filename = `membres_${exportForm.association || 'tous'}_${exportForm.commune !== 'toutes' ? exportForm.commune : 'toutes'}_${new Date().toISOString().split('T')[0]}`;
    
    if (exportForm.format === 'excel') {
      exportToExcel(dataToExport, filename, 'Membres');
      notificationService.success('Succès', `${dataToExport.length} membre(s) exporté(s) en Excel`);
    } else {
      exportToCSV(dataToExport, filename);
      notificationService.success('Succès', `${dataToExport.length} membre(s) exporté(s) en CSV`);
    }
  };

  const handleExportPaiements = () => {
    let paiementsFiltres = [...paiements];

    if (exportForm.association) {
      paiementsFiltres = paiementsFiltres.filter(p => p.membre?.association === exportForm.association);
    }

    if (exportForm.commune && exportForm.commune !== 'toutes') {
      paiementsFiltres = paiementsFiltres.filter(p => p.membre?.commune === exportForm.commune);
    }

    if (exportForm.dateDebut) {
      const dateDebut = new Date(exportForm.dateDebut);
      paiementsFiltres = paiementsFiltres.filter(p => {
        const datePaiement = new Date(p.date_paiement);
        return datePaiement >= dateDebut;
      });
    }

    if (exportForm.dateFin) {
      const dateFin = new Date(exportForm.dateFin);
      paiementsFiltres = paiementsFiltres.filter(p => {
        const datePaiement = new Date(p.date_paiement);
        return datePaiement <= dateFin;
      });
    }

    const dataToExport = paiementsFiltres.map(p => ({
      'Numéro Reçu': p.numero_recu,
      'Date Paiement': p.date_paiement,
      'Montant': parseFloat(p.montant || 0).toLocaleString('fr-FR'),
      'Type Paiement': p.type_paiement,
      'Semaine': p.semaine || '',
      'Matricule Membre': p.membre?.numero_matricule || '',
      'Nom Membre': `${p.membre?.nom || ''} ${p.membre?.prenom || ''}`.trim(),
      'Association': p.membre?.association || '',
      'Commune': p.membre?.commune || ''
    }));

    if (dataToExport.length === 0) {
      notificationService.error('Erreur', 'Aucun paiement ne correspond aux critères sélectionnés');
      return;
    }

    const filename = `paiements_${exportForm.association || 'tous'}_${exportForm.commune !== 'toutes' ? exportForm.commune : 'toutes'}_${new Date().toISOString().split('T')[0]}`;
    
    if (exportForm.format === 'excel') {
      exportToExcel(dataToExport, filename, 'Paiements');
      notificationService.success('Succès', `${dataToExport.length} paiement(s) exporté(s) en Excel`);
    } else {
      exportToCSV(dataToExport, filename);
      notificationService.success('Succès', `${dataToExport.length} paiement(s) exporté(s) en CSV`);
    }
  };

  const handleExportCotisations = () => {
    let cotisationsFiltres = [...cotisations];

    if (exportForm.association) {
      cotisationsFiltres = cotisationsFiltres.filter(c => c.membre?.association === exportForm.association);
    }

    if (exportForm.commune && exportForm.commune !== 'toutes') {
      cotisationsFiltres = cotisationsFiltres.filter(c => c.membre?.commune === exportForm.commune);
    }

    if (exportForm.dateDebut) {
      const dateDebut = new Date(exportForm.dateDebut);
      const moisDebut = dateDebut.getMonth() + 1;
      const anneeDebut = dateDebut.getFullYear();
      cotisationsFiltres = cotisationsFiltres.filter(c => {
        if (c.annee < anneeDebut) return false;
        if (c.annee === anneeDebut && c.mois < moisDebut) return false;
        return true;
      });
    }

    if (exportForm.dateFin) {
      const dateFin = new Date(exportForm.dateFin);
      const moisFin = dateFin.getMonth() + 1;
      const anneeFin = dateFin.getFullYear();
      cotisationsFiltres = cotisationsFiltres.filter(c => {
        if (c.annee > anneeFin) return false;
        if (c.annee === anneeFin && c.mois > moisFin) return false;
        return true;
      });
    }

    const dataToExport = cotisationsFiltres.map(c => ({
      'Mois': c.mois,
      'Année': c.annee,
      'Semaine': c.semaine || '',
      'Montant': parseFloat(c.montant || 0).toLocaleString('fr-FR'),
      'Statut': c.statut === 'paye' ? 'Payé' : 'Impayé',
      'Date Paiement': c.date_paiement || '',
      'Matricule Membre': c.membre?.numero_matricule || '',
      'Nom Membre': `${c.membre?.nom || ''} ${c.membre?.prenom || ''}`.trim(),
      'Association': c.membre?.association || '',
      'Commune': c.membre?.commune || ''
    }));

    if (dataToExport.length === 0) {
      notificationService.error('Erreur', 'Aucune cotisation ne correspond aux critères sélectionnés');
      return;
    }

    const filename = `cotisations_${exportForm.association || 'tous'}_${exportForm.commune !== 'toutes' ? exportForm.commune : 'toutes'}_${new Date().toISOString().split('T')[0]}`;
    
    if (exportForm.format === 'excel') {
      exportToExcel(dataToExport, filename, 'Cotisations');
      notificationService.success('Succès', `${dataToExport.length} cotisation(s) exportée(s) en Excel`);
    } else {
      exportToCSV(dataToExport, filename);
      notificationService.success('Succès', `${dataToExport.length} cotisation(s) exportée(s) en CSV`);
    }
  };

  const handleExport = () => {
    if (exportForm.type === 'membres') {
      handleExportMembres();
    } else if (exportForm.type === 'paiements') {
      handleExportPaiements();
    } else if (exportForm.type === 'cotisations') {
      handleExportCotisations();
    }
  };

  // Fonctions pour charger les données de graphiques
  const loadEvolutionCotisations = async () => {
    setLoadingEvolution(true);
    try {
      // Charger les données d'évolution des membres
      const response = await getEvolutionCotisations(nbMoisEvolution);
      const membresData = response.data;
      
      // Calculer les données d'évolution des familles
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1;
      const moisNoms = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 
                       'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
      
      const famillesDataByMonth = {};
      
      // Grouper les cotisations des familles par mois/année
      (cotisationsFamille || []).forEach(cotisation => {
        const key = `${cotisation.annee}-${cotisation.mois}`;
        if (!famillesDataByMonth[key]) {
          famillesDataByMonth[key] = {
            mois: cotisation.mois,
            annee: cotisation.annee,
            cotisationsPayees: 0,
            cotisationsImpayees: 0,
            montantTotal: 0
          };
        }
        
        if (cotisation.statut === 'paye') {
          famillesDataByMonth[key].cotisationsPayees++;
          famillesDataByMonth[key].montantTotal += parseFloat(cotisation.montant || 0);
        } else if (cotisation.statut === 'impaye') {
          famillesDataByMonth[key].cotisationsImpayees++;
        }
      });
      
      // Combiner les données des membres et des familles
      const combinedData = (membresData?.data || []).map(membreData => {
        const key = `${membreData.annee}-${membreData.mois}`;
        const familleData = famillesDataByMonth[key] || {
          cotisationsPayees: 0,
          cotisationsImpayees: 0,
          montantTotal: 0
        };
        
        return {
          ...membreData,
          cotisationsPayees: (membreData.cotisationsPayees || 0) + familleData.cotisationsPayees,
          cotisationsImpayees: (membreData.cotisationsImpayees || 0) + familleData.cotisationsImpayees,
          montantTotal: parseFloat(membreData.montantTotal || 0) + parseFloat(familleData.montantTotal || 0)
        };
      });
      
      // Ajouter les mois qui n'existent que pour les familles
      Object.keys(famillesDataByMonth).forEach(key => {
        const familleData = famillesDataByMonth[key];
        const existsInMembres = (membresData?.data || []).some(m => 
          m.mois === familleData.mois && m.annee === familleData.annee
        );
        
        if (!existsInMembres) {
          combinedData.push({
            mois: familleData.mois,
            annee: familleData.annee,
            moisLabel: `${moisNoms[familleData.mois - 1]} ${familleData.annee}`,
            cotisationsPayees: familleData.cotisationsPayees,
            cotisationsImpayees: familleData.cotisationsImpayees,
            montantTotal: parseFloat(familleData.montantTotal || 0)
          });
        }
      });
      
      // Trier par année et mois
      combinedData.sort((a, b) => {
        if (a.annee !== b.annee) return a.annee - b.annee;
        return a.mois - b.mois;
      });
      
      setEvolutionData({ data: combinedData });
    } catch (error) {
      // Ne pas afficher d'erreur pour les erreurs 429 (rate limiting)
      // Ces erreurs sont temporaires et se résolvent automatiquement
      if (error.response?.status === 429) {
        console.warn('⚠️ Rate limit atteint pour l\'évolution des cotisations, réessai automatique dans quelques secondes');
        // Réessayer après 5 secondes
        setTimeout(() => {
          loadEvolutionCotisations();
        }, 5000);
        return;
      }
      console.error('Erreur lors du chargement de l\'évolution:', error);
      notificationService.error('Erreur', 'Impossible de charger l\'évolution des cotisations');
      setEvolutionData(null);
    } finally {
      setLoadingEvolution(false);
    }
  };

  const loadStatsEvolution = async () => {
    try {
      setLoadingEvolutionCombinee(true);
      const response = await getStatsEvolution(nbMoisEvolution);
      setEvolutionCombineeData(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques d\'évolution:', error);
      notificationService.error('Erreur', 'Impossible de charger les statistiques d\'évolution');
      setEvolutionCombineeData(null);
    } finally {
      setLoadingEvolutionCombinee(false);
    }
  };

  const loadComparaisonMois = async () => {
    setLoadingComparaison(true);
    try {
      // Charger les données de comparaison des membres
      const response = await getComparaisonMois(
        comparaisonForm.mois1,
        comparaisonForm.annee1,
        comparaisonForm.mois2,
        comparaisonForm.annee2
      );
      const membresData = response.data;
      
      // Calculer les données des familles pour chaque mois
      const calculerStatsFamillesMois = (mois, annee) => {
        const cotisationsFamillesMois = (cotisationsFamille || []).filter(c => 
          c.mois === mois && c.annee === annee
        );
        
        const cotisationsPayees = cotisationsFamillesMois.filter(c => c.statut === 'paye').length;
        const cotisationsImpayees = cotisationsFamillesMois.filter(c => c.statut === 'impaye').length;
        const montantTotal = cotisationsFamillesMois
          .filter(c => c.statut === 'paye')
          .reduce((sum, c) => sum + parseFloat(c.montant || 0), 0);
        
        return {
          cotisationsPayees,
          cotisationsImpayees,
          montantTotal: parseFloat(montantTotal || 0)
        };
      };
      
      // Calculer les stats des familles pour chaque mois
      const famillesStats1 = calculerStatsFamillesMois(comparaisonForm.mois1, comparaisonForm.annee1);
      const famillesStats2 = calculerStatsFamillesMois(comparaisonForm.mois2, comparaisonForm.annee2);
      
      // Calculer les paiements des familles pour chaque mois
      const calculerPaiementsFamillesMois = (mois, annee) => {
        const startDate = `${annee}-${String(mois).padStart(2, '0')}-01`;
        const endDate = `${annee}-${String(mois).padStart(2, '0')}-${String(new Date(annee, mois, 0).getDate()).padStart(2, '0')}`;
        
        const paiementsFamillesMois = (paiementsFamille || []).filter(p => {
          const datePaiement = p.date_paiement || p.created_at;
          if (!datePaiement) return false;
          const dateStr = datePaiement.split('T')[0]; // Extraire la date sans l'heure
          return dateStr >= startDate && dateStr <= endDate;
        });
        
        const montantPaiements = paiementsFamillesMois.reduce((sum, p) => 
          sum + parseFloat(p.montant || 0), 0
        );
        
        return parseFloat(montantPaiements || 0);
      };
      
      const paiementsFamilles1 = calculerPaiementsFamillesMois(comparaisonForm.mois1, comparaisonForm.annee1);
      const paiementsFamilles2 = calculerPaiementsFamillesMois(comparaisonForm.mois2, comparaisonForm.annee2);
      
      // Combiner les données des membres et des familles
      const combinedData = {
        mois1: {
          ...membresData.mois1,
          cotisationsPayees: (membresData.mois1?.cotisationsPayees || 0) + famillesStats1.cotisationsPayees,
          cotisationsImpayees: (membresData.mois1?.cotisationsImpayees || 0) + famillesStats1.cotisationsImpayees,
          montantTotal: parseFloat(membresData.mois1?.montantTotal || 0) + famillesStats1.montantTotal,
          montantPaiements: parseFloat(membresData.mois1?.montantPaiements || 0) + paiementsFamilles1
        },
        mois2: {
          ...membresData.mois2,
          cotisationsPayees: (membresData.mois2?.cotisationsPayees || 0) + famillesStats2.cotisationsPayees,
          cotisationsImpayees: (membresData.mois2?.cotisationsImpayees || 0) + famillesStats2.cotisationsImpayees,
          montantTotal: parseFloat(membresData.mois2?.montantTotal || 0) + famillesStats2.montantTotal,
          montantPaiements: parseFloat(membresData.mois2?.montantPaiements || 0) + paiementsFamilles2
        }
      };
      
      setComparaisonData(combinedData);
    } catch (error) {
      console.error('Erreur lors du chargement de la comparaison:', error);
      notificationService.error('Erreur', 'Impossible de charger la comparaison des mois');
      setComparaisonData(null);
    } finally {
      setLoadingComparaison(false);
    }
  };

  // Fonctions pour gérer les sauvegardes
  const loadBackups = async () => {
    setLoadingBackups(true);
    try {
      const response = await listBackups();
      setBackups(response.data.backups || []);
    } catch (error) {
      console.error('Erreur lors du chargement des sauvegardes:', error);
      notificationService.error('Erreur', 'Impossible de charger la liste des sauvegardes');
      setBackups([]);
    } finally {
      setLoadingBackups(false);
    }
  };

  const loadBackupSchedule = async () => {
    setLoadingSchedule(true);
    try {
      const response = await getBackupSchedule();
      setBackupSchedule(response.data.schedule || { enabled: false, frequency: 'daily', time: '02:00' });
    } catch (error) {
      console.error('Erreur lors du chargement de la planification:', error);
      notificationService.error('Erreur', 'Impossible de charger la planification');
    } finally {
      setLoadingSchedule(false);
    }
  };

  const handleCreateBackup = async () => {
    try {
      notificationService.info('Information', 'Création de la sauvegarde en cours...');
      await createBackup();
      notificationService.success('Succès', 'Sauvegarde créée avec succès');
      await loadBackups();
    } catch (error) {
      console.error('Erreur lors de la création de la sauvegarde:', error);
      notificationService.error('Erreur', `Impossible de créer la sauvegarde: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleDownloadBackup = async (filename) => {
    try {
      const response = await downloadBackup(filename);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      notificationService.success('Succès', 'Téléchargement démarré');
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
      notificationService.error('Erreur', `Impossible de télécharger la sauvegarde: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleDeleteBackup = async (filename) => {
    if (!isPrincipal()) {
      notificationService.warning('Accès refusé', 'Seul l\'administrateur principal peut supprimer les sauvegardes.');
      return;
    }
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer la sauvegarde "${filename}" ?`)) {
      return;
    }
    
    try {
      await deleteBackup(filename);
      notificationService.success('Succès', 'Sauvegarde supprimée avec succès');
      await loadBackups();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      notificationService.error('Erreur', `Impossible de supprimer la sauvegarde: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleRestoreBackup = (filename) => {
    setRestoreFilename(filename);
    setRestorePassword('');
    setShowRestoreModal(true);
  };

  const handleConfirmRestore = async () => {
    if (!restorePassword) {
      notificationService.error('Erreur', 'Veuillez entrer votre mot de passe');
      return;
    }

    setVerifyingPassword(true);
    try {
      // Vérifier le mot de passe d'abord
      const verifyRes = await verifyAdminPassword(restorePassword);
      
      if (!verifyRes.data.success) {
        notificationService.error('Erreur', 'Mot de passe incorrect');
        setVerifyingPassword(false);
        return;
      }

      // Si le mot de passe est correct, procéder à la restauration
      notificationService.info('Information', 'Restauration en cours... Cela peut prendre quelques instants.');
      await restoreBackup(restoreFilename, restorePassword);
      
      setShowRestoreModal(false);
      setRestorePassword('');
      setRestoreFilename('');
      
      notificationService.success('Succès', 'Base de données restaurée avec succès. Veuillez actualiser la page.');
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      console.error('Erreur lors de la restauration:', error);
      const errorMessage = error.response?.data?.message || error.message;
      if (errorMessage.includes('Mot de passe incorrect') || errorMessage.includes('password')) {
        notificationService.error('Erreur', 'Mot de passe incorrect');
      } else {
        notificationService.error('Erreur', `Impossible de restaurer la sauvegarde: ${errorMessage}`);
      }
    } finally {
      setVerifyingPassword(false);
    }
  };

  const handleUpdateSchedule = async () => {
    try {
      await updateBackupSchedule(backupSchedule);
      notificationService.success('Succès', 'Planification mise à jour avec succès');
      await loadBackupSchedule();
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la planification:', error);
      notificationService.error('Erreur', `Impossible de mettre à jour la planification: ${error.response?.data?.message || error.message}`);
    }
  };

  // Fonction pour charger les activités
  const loadSystemSettings = async () => {
    setLoadingSettings(true);
    try {
      const response = await getSystemSettings();
      if (response && response.data && response.data.settings) {
        setSystemSettings(response.data.settings);
        setSettingsForm(response.data.settings);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des paramètres:', error);
      notificationService.error('Erreur', 'Impossible de charger les paramètres système');
    } finally {
      setLoadingSettings(false);
    }
  };

  const handleSaveSettings = async (category) => {
    try {
      const settingsToSave = {};
      const source = settingsForm[category] || {};
      const siteKeys = ['site_name', 'site_tagline', 'site_logo', 'site_email', 'site_phone', 'site_address', 'maintenance_mode', 'legal_mentions', 'historique_title', 'historique_subtitle', 'historique_timeline', 'historique_achievements_title', 'historique_achievements', 'index_hero_description', 'index_about_title', 'index_about_p1', 'index_about_p2', 'index_about_image', 'index_services_title', 'index_services', 'index_values_title', 'index_values', 'index_membership_title', 'index_membership_benefits_title', 'index_membership_benefits', 'index_membership_cta', 'apropos_title', 'apropos_subtitle', 'apropos_mission_title', 'apropos_mission_p1', 'apropos_mission_p2', 'apropos_vision_title', 'apropos_vision_p1', 'apropos_vision_p2', 'apropos_values_title', 'apropos_values', 'apropos_team_title', 'apropos_team_intro', 'apropos_team', 'apropos_location_title', 'apropos_location_text', 'contact_title', 'contact_subtitle', 'contact_info_section_title', 'contact_address_label', 'contact_email_label', 'contact_phone_label', 'contact_hours_label', 'contact_hours_text', 'contact_social_title', 'contact_social_links', 'contact_form_title', 'contact_success_title', 'contact_success_text'];
      const keys = category === 'site' ? siteKeys : Object.keys(source);
      keys.forEach(key => {
        const val = source[key];
        const value = val?.value !== undefined ? val.value : val;
        settingsToSave[key] = {
          value: value !== undefined && value !== null ? value : (category === 'site' && key === 'site_name' ? 'MUTU NDE ESPOIR ASBL' : (key === 'maintenance_mode' ? false : '')),
          category,
          description: val?.description || ''
        };
      });

      // Si on sauvegarde le logo de l'organisation, synchroniser automatiquement avec le logo d'impression
      if (category === 'general' && settingsToSave.organization_logo) {
        const logoPath = settingsToSave.organization_logo.value;
        settingsToSave.print_logo_path = {
          value: logoPath,
          category: 'print',
          description: 'Chemin du logo pour l\'impression'
        };
      }

      await updateSystemSettings(settingsToSave);
      await loadSystemSettings();
      notificationService.success('Succès', 'Paramètres sauvegardés avec succès');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des paramètres:', error);
      notificationService.error('Erreur', error.response?.data?.message || 'Erreur lors de la sauvegarde');
    }
  };

  const handleResetSettings = async (category) => {
    const label = category === 'site' ? 'du site internet' : category;
    if (!window.confirm(`Êtes-vous sûr de vouloir réinitialiser les paramètres ${label} ?`)) {
      return;
    }
    try {
      await resetSystemSettings(category);
      await loadSystemSettings();
      notificationService.success('Succès', 'Paramètres réinitialisés avec succès');
    } catch (error) {
      console.error('Erreur lors de la réinitialisation:', error);
      notificationService.error('Erreur', error.response?.data?.message || 'Erreur lors de la réinitialisation');
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Vérifier le type de fichier
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      notificationService.error('Erreur', 'Format de fichier non supporté. Utilisez JPEG, PNG, GIF, SVG ou WebP.');
      return;
    }

    // Vérifier la taille (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      notificationService.error('Erreur', 'Le fichier est trop volumineux. Taille maximale: 2MB.');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);

      const response = await uploadLogo(formData);
      if (response && response.data && response.data.success) {
        await loadSystemSettings();
        // Synchroniser le logo d'impression avec le logo de l'organisation
        const logoPath = response.data.logoPath;
        setSettingsForm(prev => ({
          ...prev,
          general: {
            ...prev.general,
            organization_logo: { ...prev.general?.organization_logo, value: logoPath }
          },
          print: {
            ...prev.print,
            print_logo_path: { ...prev.print?.print_logo_path, value: logoPath }
          }
        }));
        notificationService.success('Succès', 'Logo uploadé avec succès');
        // Réinitialiser l'input file
        e.target.value = '';
      }
    } catch (error) {
      console.error('Erreur lors de l\'upload du logo:', error);
      notificationService.error('Erreur', error.response?.data?.message || 'Erreur lors de l\'upload du logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  // Fonctions pour les notifications
  const loadMessageTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const response = await getMessageTemplates();
      console.log('📝 Réponse modèles:', response);
      
      // Gérer différentes structures de réponse
      let templatesData = [];
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          templatesData = response.data;
        } else if (response.data.templates && Array.isArray(response.data.templates)) {
          templatesData = response.data.templates;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          templatesData = response.data.data;
        }
      } else if (Array.isArray(response)) {
        templatesData = response;
      }
      
      setMessageTemplates(templatesData);
    } catch (error) {
      console.error('❌ Erreur lors du chargement des modèles:', error);
      console.error('❌ Détails de l\'erreur:', error.response?.data || error);
      setMessageTemplates([]); // S'assurer que c'est toujours un tableau
      notificationService.error('Erreur', error.response?.data?.message || 'Impossible de charger les modèles de messages');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    try {
      await createMessageTemplate(templateForm);
      notificationService.success('Succès', 'Modèle créé avec succès');
      setShowTemplateForm(false);
      setTemplateForm({
        name: '',
        category: 'custom',
        subject: '',
        message: '',
        variables: '',
        is_active: true
      });
      await loadMessageTemplates();
    } catch (error) {
      console.error('Erreur lors de la création du modèle:', error);
      notificationService.error('Erreur', error.response?.data?.message || 'Erreur lors de la création du modèle');
    }
  };

  const handleUpdateTemplate = async (e) => {
    e.preventDefault();
    if (!editingTemplate) return;
    try {
      await updateMessageTemplate(editingTemplate.id, templateForm);
      notificationService.success('Succès', 'Modèle mis à jour avec succès');
      setShowTemplateForm(false);
      setEditingTemplate(null);
      setTemplateForm({
        name: '',
        category: 'custom',
        subject: '',
        message: '',
        variables: '',
        is_active: true
      });
      await loadMessageTemplates();
    } catch (error) {
      console.error('Erreur lors de la mise à jour du modèle:', error);
      notificationService.error('Erreur', error.response?.data?.message || 'Erreur lors de la mise à jour du modèle');
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce modèle ?')) {
      return;
    }
    try {
      await deleteMessageTemplate(id);
      notificationService.success('Succès', 'Modèle supprimé avec succès');
      await loadMessageTemplates();
    } catch (error) {
      console.error('Erreur lors de la suppression du modèle:', error);
      notificationService.error('Erreur', error.response?.data?.message || 'Erreur lors de la suppression du modèle');
    }
  };

  const handleEditTemplate = (template) => {
    if (!isPrincipal()) {
      notificationService.warning('Accès refusé', 'Seul l\'administrateur principal peut modifier les modèles de messages.');
      return;
    }
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name || '',
      category: template.category || 'custom',
      subject: template.subject || '',
      message: template.message || '',
      variables: template.variables || '',
      is_active: template.is_active !== undefined ? template.is_active : true
    });
    setShowTemplateForm(true);
  };

  const loadNotificationHistory = async () => {
    setLoadingNotificationHistory(true);
    try {
      const params = {};
      if (notificationFilters.type) params.type = notificationFilters.type;
      if (notificationFilters.status) params.status = notificationFilters.status;
      if (notificationFilters.date_from) params.date_from = notificationFilters.date_from;
      if (notificationFilters.date_to) params.date_to = notificationFilters.date_to;
      
      const response = await getNotificationHistory(params);
      console.log('📱 Réponse notifications:', response);
      
      // Gérer différentes structures de réponse
      let historyData = [];
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          historyData = response.data;
        } else if (response.data.history && Array.isArray(response.data.history)) {
          historyData = response.data.history;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          historyData = response.data.data;
        }
      } else if (Array.isArray(response)) {
        historyData = response;
      }
      
      setNotificationHistory(historyData);
    } catch (error) {
      console.error('❌ Erreur lors du chargement de l\'historique:', error);
      console.error('❌ Détails de l\'erreur:', error.response?.data || error);
      setNotificationHistory([]); // S'assurer que c'est toujours un tableau
      notificationService.error('Erreur', error.response?.data?.message || 'Impossible de charger l\'historique des notifications');
    } finally {
      setLoadingNotificationHistory(false);
    }
  };

  const handleSendNotification = async () => {
    if (!notificationMessage.trim()) {
      notificationService.error('Erreur', 'Veuillez entrer un message');
      return;
    }
    if (selectedMembers.length === 0) {
      notificationService.error('Erreur', 'Veuillez sélectionner au moins un membre');
      return;
    }

    setSendingNotification(true);
    try {
      const response = await sendNotifications({
        template_id: selectedTemplate?.id || null,
        member_ids: selectedMembers.map(m => m.id),
        message: notificationMessage,
        type: 'sms'
      });
      
      if (response && response.data && response.data.success) {
        notificationService.success('Succès', `Notifications envoyées à ${response.data.sent_count || selectedMembers.length} membre(s)`);
        setShowSendNotificationModal(false);
        setSelectedTemplate(null);
        setSelectedMembers([]);
        setNotificationMessage('');
        await loadNotificationHistory();
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi des notifications:', error);
      notificationService.error('Erreur', error.response?.data?.message || 'Erreur lors de l\'envoi des notifications');
    } finally {
      setSendingNotification(false);
    }
  };

  const loadContactMessages = async () => {
    setLoadingContactMessages(true);
    try {
      const params = {
        page: contactMessageFilters.page,
        limit: contactMessageFilters.limit
      };
      if (contactMessageFilters.statut) params.statut = contactMessageFilters.statut;
      
      const response = await getContactMessages(params);
      if (response && response.data) {
        setContactMessages(response.data.messages || []);
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des messages de contact:', error);
      notificationService.error('Erreur', 'Impossible de charger les messages de contact.');
    } finally {
      setLoadingContactMessages(false);
    }
  };

  const loadActivities = async () => {
    setLoadingActivities(true);
    try {
      const params = {};
      if (activityFilters.action_type) params.action_type = activityFilters.action_type;
      if (activityFilters.entity_type) params.entity_type = activityFilters.entity_type;
      params.limit = 100; // Réduire à 100 pour améliorer les performances
      
      console.log('🔄 Chargement des activités avec params:', params);
      
      // Ajouter un timeout pour éviter un blocage infini (augmenté à 60 secondes)
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout: La requête a pris plus de 60 secondes')), 60000)
      );
      
      const response = await Promise.race([
        getAdminActivities(params),
        timeoutPromise
      ]);
      
      console.log('✅ Réponse complète:', response);
      console.log('✅ Données activités:', response.data);
      console.log('✅ Liste activités:', response.data?.activities);
      
      if (response && response.data) {
        setActivities(response.data.activities || []);
        console.log(`✅ ${response.data.activities?.length || 0} activité(s) chargée(s)`);
      } else {
        console.warn('⚠️ Réponse invalide:', response);
        setActivities([]);
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des activités:', error);
      console.error('❌ Type d\'erreur:', error.name);
      console.error('❌ Message:', error.message);
      console.error('❌ Stack:', error.stack);
      if (error.response) {
        console.error('❌ Status:', error.response.status);
        console.error('❌ Data:', error.response.data);
      }
      const errorMessage = error.response?.data?.message || error.message || 'Erreur inconnue';
      notificationService.error('Erreur', `Impossible de charger les activités: ${errorMessage}`);
      setActivities([]);
    } finally {
      console.log('✅ Fin du chargement des activités');
      setLoadingActivities(false);
    }
  };

  // Obtenir la liste unique des associations
  // Associations des membres individuels + communes des familles
  const associationsMembres = useMemo(() => {
    return membres.filter(m => m.type === 'individuel').map(m => m.association).filter(Boolean);
  }, [membres]);
  const associationsFamilles = useMemo(() => {
    return membres.filter(m => m.type === 'famille' && m.famille?.commune).map(m => m.famille.commune).filter(Boolean);
  }, [membres]);
  const associations = useMemo(() => {
    return [...new Set([...associationsMembres, ...associationsFamilles])].sort();
  }, [associationsMembres, associationsFamilles]);
  const associationsRecolteurs = useMemo(() => {
    return [...new Set(recolteurs.map(r => r.association).filter(Boolean))].sort();
  }, [recolteurs]);
  const associationsCotisations = useMemo(() => {
    return [...new Set(cotisations.map(c => c.membre?.association).filter(Boolean))].sort();
  }, [cotisations]);
  const associationsPaiements = useMemo(() => {
    return [...new Set([
      ...paiements.map(p => p.membre?.association).filter(Boolean),
      ...paiementsFamille.map(p => p.famille?.commune).filter(Boolean)
    ])].sort();
  }, [paiements, paiementsFamille]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/login-admin');
  };

  // Fermer le sidebar quand on change d'onglet sur mobile
  useEffect(() => {
    if (window.innerWidth <= 768) {
      setSidebarOpen(false);
    }
  }, [activeTab]);

  // Empêcher le scroll du body quand le sidebar est ouvert
  useEffect(() => {
    if (sidebarOpen) {
      document.body.classList.add('sidebar-open');
    } else {
      document.body.classList.remove('sidebar-open');
    }
    return () => {
      document.body.classList.remove('sidebar-open');
    };
  }, [sidebarOpen]);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <React.Fragment>
    <div className="App">
      {/* Bouton hamburger pour mobile */}
      <button
        className={`mobile-menu-toggle ${sidebarOpen ? 'active' : ''}`}
        onClick={() => setSidebarOpen(!sidebarOpen)}
        aria-label="Toggle menu"
        aria-expanded={sidebarOpen}
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      <div className="header">
        <div className="header-content">
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <img src="/logo.png" alt="Logo" style={{ height: '50px', width: '50px', objectFit: 'contain', borderRadius: '50%' }} />
            <div>
              <h1>MUTU NDE ESPOIR ASBL - Administration</h1>
              <p style={{ margin: '5px 0 0 0', fontSize: '16px', fontWeight: '500', color: 'rgba(255, 255, 255, 0.95)' }}>
                Bienvenue, <strong style={{ color: '#fff', fontWeight: '700', fontSize: '17px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{admin.nom_complet || 'Administrateur'}</strong>
              </p>
            </div>
          </div>
          <div className="header-actions">
            <NotificationBell />
          </div>
        </div>
      </div>

      {/* Overlay pour fermer le sidebar - placé après le header mais avant la sidebar */}
      <div
        className={`sidebar-overlay ${sidebarOpen ? 'active' : ''}`}
        onClick={() => setSidebarOpen(false)}
        aria-hidden="true"
      />

      <div className="sidebar-container">
        <aside className={`sidebar ${sidebarOpen ? 'mobile-open' : ''} ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <div className="sidebar-header">
            <h3 className={sidebarCollapsed ? 'hidden' : ''}>Menu Principal</h3>
            {/* Bouton pour masquer/démasquer en desktop */}
            <button
              className="sidebar-toggle-desktop"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              aria-label={sidebarCollapsed ? 'Afficher le menu' : 'Masquer le menu'}
              title={sidebarCollapsed ? 'Afficher le menu' : 'Masquer le menu'}
            >
              {sidebarCollapsed ? '▶' : '◀'}
            </button>
          </div>
          <nav className="sidebar-menu">
            <div className="sidebar-menu-item">
              <div 
                className={`sidebar-menu-link ${activeTab === 'stats' ? 'active' : ''}`}
                onClick={() => setActiveTab('stats')}
              >
                <span className="sidebar-menu-icon">📊</span>
                <span className="sidebar-menu-text">
                  <span>Statistiques</span>
                </span>
              </div>
            </div>
            <div className="sidebar-menu-item">
              <div 
                className={`sidebar-menu-link ${activeTab === 'evolution' ? 'active' : ''}`}
                onClick={() => setActiveTab('evolution')}
              >
                <span className="sidebar-menu-icon">📈</span>
                <span className="sidebar-menu-text">
                  <span>Évolution</span>
                </span>
              </div>
            </div>
            <div className="sidebar-menu-item">
              <div 
                className={`sidebar-menu-link ${activeTab === 'admins' ? 'active' : ''}`}
                onClick={() => setActiveTab('admins')}
              >
                <span className="sidebar-menu-icon">👥</span>
                <span className="sidebar-menu-text">
                  <span>Administrateurs & Récolteurs</span>
                  <span className="sidebar-badge">{(admins.length || 0) + (recolteurs.length || 0)}</span>
                </span>
              </div>
            </div>
            <div className="sidebar-menu-item">
              <div 
                className={`sidebar-menu-link ${activeTab === 'membres' ? 'active' : ''}`}
                onClick={async () => {
                  setActiveTab('membres');
                  try {
                    console.log('🔄 Rechargement des membres...');
                    const membresRes = await getMembres();
                    setMembres(membresRes.data || []);
                    console.log('✅ Membres rechargés:', membresRes.data?.length || 0);
                  } catch (err) {
                    console.error('❌ Erreur lors du rechargement des membres:', err);
                  }
                }}
              >
                <span className="sidebar-menu-icon">👤</span>
                <span className="sidebar-menu-text">
                  <span>Membres</span>
                  <span className="sidebar-badge">{membres.length || 0}</span>
                </span>
              </div>
            </div>
            <div className="sidebar-menu-item">
              <div 
                className={`sidebar-menu-link ${activeTab === 'familles' ? 'active' : ''}`}
                onClick={async () => {
                  setActiveTab('familles');
                  try {
                    console.log('🔄 Rechargement des familles...');
          const famillesRes = await getFamilles();
          // L'API axios retourne les données dans response.data
          const famillesData = famillesRes?.data || famillesRes;
          const famillesArray = Array.isArray(famillesData) ? famillesData : [];
          setFamilles(famillesArray);
          console.log('✅ Familles rechargées:', famillesArray.length, 'famille(s)');
          if (famillesArray.length === 0) {
            console.warn('⚠️ Aucune famille trouvée. Réponse complète:', famillesRes);
          }
                  } catch (err) {
                    console.error('❌ Erreur lors du rechargement des familles:', err);
                  }
                }}
              >
                <span className="sidebar-menu-icon">👨‍👩‍👧‍👦</span>
                <span className="sidebar-menu-text">
                  <span>Familles</span>
                  <span className="sidebar-badge">{Array.isArray(familles) ? familles.length : 0}</span>
                </span>
              </div>
            </div>
            <div className="sidebar-menu-item">
              <div 
                className={`sidebar-menu-link ${activeTab === 'cotisations' ? 'active' : ''}`}
                onClick={() => setActiveTab('cotisations')}
              >
                <span className="sidebar-menu-icon">📋</span>
                <span className="sidebar-menu-text">
                  <span>Cotisations</span>
                  <span className="sidebar-badge">{cotisations.length || 0}</span>
                </span>
              </div>
            </div>
            <div className="sidebar-menu-item">
              <div 
                className={`sidebar-menu-link ${activeTab === 'paiements' ? 'active' : ''}`}
                onClick={() => setActiveTab('paiements')}
              >
                <span className="sidebar-menu-icon">💰</span>
                <span className="sidebar-menu-text">
                  <span>Paiements</span>
                  <span className="sidebar-badge">{paiements.length || 0}</span>
                </span>
              </div>
            </div>
            <div className="sidebar-menu-item">
              <div 
                className={`sidebar-menu-link ${activeTab === 'montants' ? 'active' : ''}`}
                onClick={() => setActiveTab('montants')}
              >
                <span className="sidebar-menu-icon">💵</span>
                <span className="sidebar-menu-text">
                  <span>Montants Mensuels</span>
                  <span className="sidebar-badge">{montantsMensuels.length || 0}</span>
                </span>
              </div>
            </div>
            <div className="sidebar-menu-item">
              <div 
                className={`sidebar-menu-link ${activeTab === 'montants-familles' ? 'active' : ''}`}
                onClick={() => setActiveTab('montants-familles')}
              >
                <span className="sidebar-menu-icon">👨‍👩‍👧‍👦</span>
                <span className="sidebar-menu-text">
                  <span>Montants Familles</span>
                  <span className="sidebar-badge">{montantsMensuelsFamilles.length || 0}</span>
                </span>
              </div>
            </div>
            <div className="sidebar-menu-item">
              <div 
                className={`sidebar-menu-link ${activeTab === 'centres-sante' ? 'active' : ''}`}
                onClick={() => setActiveTab('centres-sante')}
              >
                <span className="sidebar-menu-icon">🏥</span>
                <span className="sidebar-menu-text">
                  <span>Centres de santé</span>
                  <span className="sidebar-badge">{centresSante.length || 0}</span>
                </span>
              </div>
            </div>
            <div className="sidebar-menu-item">
              <div 
                className={`sidebar-menu-link ${activeTab === 'declaration-centres' ? 'active' : ''}`}
                onClick={() => setActiveTab('declaration-centres')}
              >
                <span className="sidebar-menu-icon">🏥</span>
                <span className="sidebar-menu-text">
                  <span>Déclaration des centres</span>
                </span>
              </div>
            </div>
            <div className="sidebar-menu-item">
              <div 
                className={`sidebar-menu-link ${activeTab === 'rapports' ? 'active' : ''}`}
                onClick={() => setActiveTab('rapports')}
              >
                <span className="sidebar-menu-icon">📄</span>
                <span className="sidebar-menu-text">
                  <span>Rapports</span>
                </span>
              </div>
            </div>
            <div className="sidebar-menu-item">
              <div 
                className={`sidebar-menu-link ${activeTab === 'impressions' ? 'active' : ''}`}
                onClick={() => setActiveTab('impressions')}
              >
                <span className="sidebar-menu-icon">🖨️</span>
                <span className="sidebar-menu-text">
                  <span>Impressions</span>
                </span>
              </div>
            </div>
            <div className="sidebar-menu-item">
              <div 
                className={`sidebar-menu-link ${activeTab === 'activities' ? 'active' : ''}`}
                onClick={async () => {
                  setActiveTab('activities');
                  await loadActivities();
                }}
              >
                <span className="sidebar-menu-icon">📋</span>
                <span className="sidebar-menu-text">
                  <span>Activités</span>
                </span>
              </div>
            </div>
            <div className="sidebar-menu-item">
              <div 
                className={`sidebar-menu-link ${activeTab === 'backup' ? 'active' : ''}`}
                onClick={() => setActiveTab('backup')}
              >
                <span className="sidebar-menu-icon">💾</span>
                <span className="sidebar-menu-text">
                  <span>Sauvegardes</span>
                </span>
              </div>
            </div>
            <div className="sidebar-menu-item">
              <div 
                className={`sidebar-menu-link ${activeTab === 'settings' ? 'active' : ''}`}
                onClick={async () => {
                  setActiveTab('settings');
                  await loadSystemSettings();
                }}
              >
                <span className="sidebar-menu-icon">⚙️</span>
                <span className="sidebar-menu-text">
                  <span>Paramètres</span>
                </span>
              </div>
            </div>
            <div className="sidebar-menu-item">
              <div 
                className={`sidebar-menu-link ${activeTab === 'site' ? 'active' : ''}`}
                onClick={async () => {
                  setActiveTab('site');
                  await loadSystemSettings();
                }}
              >
                <span className="sidebar-menu-icon">🌐</span>
                <span className="sidebar-menu-text">
                  <span>Site internet</span>
                </span>
              </div>
            </div>
            <div className="sidebar-menu-item">
              <div 
                className={`sidebar-menu-link ${activeTab === 'contact' ? 'active' : ''}`}
                onClick={async () => {
                  setActiveTab('contact');
                  await loadContactMessages();
                }}
              >
                <span className="sidebar-menu-icon">📧</span>
                <span className="sidebar-menu-text">
                  <span>Gestion des mails professionnels</span>
                  <span className="sidebar-badge">
                    {contactMessages.filter(m => m.statut === 'nouveau').length || 0}
                  </span>
                </span>
              </div>
            </div>
            <div className="sidebar-menu-item">
              <div 
                className={`sidebar-menu-link ${activeTab === 'notifications' ? 'active' : ''}`}
                onClick={async () => {
                  setActiveTab('notifications');
                  await loadMessageTemplates();
                  await loadNotificationHistory();
                }}
              >
                <span className="sidebar-menu-icon">📱</span>
                <span className="sidebar-menu-text">
                  <span>Notifications</span>
                </span>
              </div>
            </div>
            <div className="sidebar-menu-item" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <div 
                className="sidebar-menu-link"
                onClick={handleLogout}
                style={{ color: '#e74c3c', cursor: 'pointer' }}
              >
                <span className="sidebar-menu-icon">🚪</span>
                <span className="sidebar-menu-text">
                  <span>Déconnexion</span>
                </span>
              </div>
            </div>
          </nav>
        </aside>

        <main className="main-content">
          <React.Fragment>
          <div>
        {activeTab === 'stats' && (
          <div>
            {/* Sélecteur de vue statistiques */}
            <div className="stats-view-selector" style={{ marginBottom: '20px', display: 'flex', gap: '10px', borderBottom: '2px solid #e0e0e0', flexWrap: 'wrap' }}>
              <button
                className="btn btn-primary"
                style={{ 
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '5px 5px 0 0',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  flex: '1 1 auto',
                  minWidth: '150px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <span className="stats-icon">📊</span>
                <span className="stats-text">Statistiques</span>
              </button>
            </div>

            {/* Statistiques Journalières */}
            <div style={{ marginBottom: '40px' }}>
              <h3 className="stats-section-title" style={{ marginBottom: '20px', color: '#2c3e50', display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span>📅</span>
                  <span>Statistiques Journalières</span>
                  {statsJournalieres.date && (
                    <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#666', marginLeft: '10px' }}>
                      ({new Date(statsJournalieres.date).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })})
                    </span>
                  )}
                  {!statsJournalieres.date && !loadingStatsJournalieres && (
                    <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#666', marginLeft: '10px' }}>
                      ({new Date().toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })})
                    </span>
                  )}
                </div>
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={async () => {
                    setLoadingStatsJournalieres(true);
                    try {
                      const response = await getStatsJournalieres();
                      if (response && response.data) {
                        const data = response.data;
                        setStatsJournalieres({
                          membresAujourdhui: data.membresAujourdhui || 0,
                          paiementsAujourdhui: data.paiementsAujourdhui || 0,
                          cotisationsPayeesAujourdhui: data.cotisationsPayeesAujourdhui || 0,
                          nombrePaiementsAujourdhui: data.nombrePaiementsAujourdhui || 0,
                          cartesPayeesAujourdhui: data.cartesPayeesAujourdhui || 0,
                          montantCartesAujourdhui: data.montantCartesAujourdhui || 0,
                          date: data.date || new Date().toISOString().split('T')[0]
                        });
                        notificationService.success('Succès', 'Statistiques journalières actualisées');
                      }
                    } catch (error) {
                      console.error('Erreur lors du rechargement des statistiques journalières:', error);
                      notificationService.error('Erreur', 'Impossible de recharger les statistiques journalières');
                    } finally {
                      setLoadingStatsJournalieres(false);
                    }
                  }}
                  disabled={loadingStatsJournalieres}
                  style={{ fontSize: '12px', padding: '5px 10px' }}
                >
                  {loadingStatsJournalieres ? '⏳ Chargement...' : '🔄 Actualiser'}
                </button>
              </h3>
              {loadingStatsJournalieres ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
                  <p>Chargement des statistiques journalières...</p>
                </div>
              ) : (
                <div className="stats-grid">
                  <div 
                    className="stat-card" 
                    style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }}
                    onClick={async () => {
                      setShowMembresInscritsAujourdhuiModal(true);
                      setLoadingMembresInscritsAujourdhui(true);
                      try {
                        const response = await getMembresInscritsAujourdhui();
                        if (response?.data) {
                          setMembresInscritsAujourdhui(response.data);
                        }
                      } catch (err) {
                        console.error('Erreur chargement membres inscrits aujourd\'hui:', err);
                        notificationService.error('Erreur', 'Impossible de charger la liste des membres');
                      } finally {
                        setLoadingMembresInscritsAujourdhui(false);
                      }
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.02)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.15)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = ''; }}
                  >
                    <h3 style={{ color: 'white' }}>Membres inscrits aujourd'hui (Tous)</h3>
                    <div className="value" style={{ color: 'white', fontSize: '2.5em' }}>{(statsJournalieres.membresAujourdhui || 0) + (statsFamillesJournalieres?.membresFamilleAujourdhui || 0)}</div>
                    <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.9)', marginTop: '5px' }}>Membres individuels + membres de familles • Cliquez pour voir la liste</div>
                  </div>
                  <div className="stat-card" style={{ background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', color: '#2c3e50' }}>
                    <h3 style={{ color: '#2c3e50' }}>Membres individuels inscrits aujourd'hui</h3>
                    <div className="value" style={{ color: '#2c3e50', fontSize: '2.5em' }}>{statsJournalieres.membresAujourdhui || 0}</div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>Membres individuels uniquement (sans familles)</div>
                  </div>
                  <div 
                    className="stat-card" 
                    style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white', cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }}
                    onClick={async () => {
                      setShowMontantCollecteAujourdhuiModal(true);
                      setLoadingMontantCollecteAujourdhui(true);
                      try {
                        const todayStr = new Date().toISOString().split('T')[0];
                        const response = await getPaiementsGlobaux(null, null, todayStr);
                        if (response?.data) {
                          setMontantCollecteAujourdhui(response.data);
                        }
                      } catch (err) {
                        console.error('Erreur chargement montant collecté aujourd\'hui:', err);
                        notificationService.error('Erreur', 'Impossible de charger la liste des paiements');
                      } finally {
                        setLoadingMontantCollecteAujourdhui(false);
                      }
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.02)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.15)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = ''; }}
                  >
                    <h3 style={{ color: 'white' }}>Montant collecté aujourd'hui</h3>
                    <div className="value" style={{ color: 'white', fontSize: '2.5em' }}>{parseFloat((statsJournalieres.montantCotisationsAujourdhui || statsJournalieres.paiementsAujourdhui - (statsJournalieres.montantCartesAujourdhui || 0) || 0) + (statsFamillesJournalieres?.paiementsFamillesAujourdhui || 0)).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF</div>
                    <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.9)', marginTop: '5px' }}>Montant des cotisations membres + familles (sans cartes) • Cliquez pour voir la liste</div>
                  </div>
                  <div 
                    className="stat-card" 
                    style={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white', cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }}
                    onClick={async () => {
                      setShowCotisationsPayeesAujourdhuiModal(true);
                      setLoadingCotisationsPayeesAujourdhui(true);
                      try {
                        const response = await getCotisationsPayeesAujourdhui();
                        if (response?.data) {
                          setCotisationsPayeesAujourdhui(response.data);
                        }
                      } catch (err) {
                        console.error('Erreur chargement cotisations payées aujourd\'hui:', err);
                        notificationService.error('Erreur', 'Impossible de charger la liste des cotisations');
                      } finally {
                        setLoadingCotisationsPayeesAujourdhui(false);
                      }
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.02)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.15)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = ''; }}
                  >
                    <h3 style={{ color: 'white' }}>Cotisations payées aujourd'hui</h3>
                    <div className="value" style={{ color: 'white', fontSize: '2.5em' }}>{statsJournalieres.cotisationsPayeesAujourdhui || 0}</div>
                    <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.9)', marginTop: '5px' }}>Cotisations réglées aujourd'hui • Cliquez pour voir la liste</div>
                  </div>
                  <div className="stat-card" style={{ background: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)', color: 'white' }}>
                    <h3 style={{ color: 'white' }}>Montant cartes aujourd'hui</h3>
                    <div className="value" style={{ color: 'white', fontSize: '2.5em' }}>
                      {(() => {
                        const montantCartesMembres = parseFloat(statsJournalieres.montantCartesAujourdhui || 0);
                        const todayStr = new Date().toISOString().split('T')[0];
                        const montantCartesFamilles = (paiementsFamille || []).filter(p => {
                          const datePaiement = p.date_paiement || p.created_at;
                          if (!datePaiement) return false;
                          const dateStr = datePaiement.split('T')[0];
                          return dateStr === todayStr && p.type_paiement === 'autre' && parseFloat(p.montant || 0) === 5000;
                        }).reduce((sum, p) => sum + parseFloat(p.montant || 0), 0);
                        return (montantCartesMembres + montantCartesFamilles).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
                      })()} CDF
                    </div>
                    <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.9)', marginTop: '5px' }}>Total collecté pour les cartes (membres + familles)</div>
                  </div>
                  <div className="stat-card" style={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
                    <h3 style={{ color: 'white' }}>Familles enregistrées aujourd'hui</h3>
                    <div className="value" style={{ color: 'white', fontSize: '2.5em' }}>{statsFamillesJournalieres?.famillesAujourdhui || 0}</div>
                    <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.9)', marginTop: '5px' }}>Nouvelles familles enregistrées</div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal - Liste des membres inscrits aujourd'hui */}
            {showMembresInscritsAujourdhuiModal && (
              <div className="modal-overlay modal-list-print" onClick={() => setShowMembresInscritsAujourdhuiModal(false)}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', padding: 0 }}>
                  <div className="modal-header">
                    <h2>👥 Membres inscrits aujourd'hui (Tous)</h2>
                    <button className="modal-close" onClick={() => setShowMembresInscritsAujourdhuiModal(false)}>✕</button>
                  </div>
                  <div className="modal-body" style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
                    {loadingMembresInscritsAujourdhui ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
                        <p>Chargement de la liste...</p>
                      </div>
                    ) : (
                      <>
                        <div style={{ marginBottom: '25px' }}>
                          <h4 style={{ color: '#667eea', marginBottom: '15px', borderBottom: '2px solid #667eea', paddingBottom: '8px' }}>
                            Membres individuels ({membresInscritsAujourdhui.membresIndividuels?.length || 0})
                          </h4>
                          {(!membresInscritsAujourdhui.membresIndividuels || membresInscritsAujourdhui.membresIndividuels.length === 0) ? (
                            <p style={{ color: '#666', fontStyle: 'italic' }}>Aucun membre individuel inscrit aujourd'hui</p>
                          ) : (
                            <div style={{ overflowX: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                                <thead>
                                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>#</th>
                                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Matricule</th>
                                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Nom complet</th>
                                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Téléphone</th>
                                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Association</th>
                                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Récolteur</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {membresInscritsAujourdhui.membresIndividuels.map((m, idx) => (
                                    <tr key={m.id} style={{ borderBottom: '1px solid #eee' }}>
                                      <td style={{ padding: '10px' }}>{idx + 1}</td>
                                      <td style={{ padding: '10px' }}>{m.numero_matricule || '-'}</td>
                                      <td style={{ padding: '10px' }}>{[m.nom, m.postnom, m.prenom].filter(Boolean).join(' ')}</td>
                                      <td style={{ padding: '10px' }}>{m.telephone || '-'}</td>
                                      <td style={{ padding: '10px' }}>{m.association || '-'}</td>
                                      <td style={{ padding: '10px' }}>{m.recolteur?.nom_complet || m.nom_recolteur || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                        <div>
                          <h4 style={{ color: '#764ba2', marginBottom: '15px', borderBottom: '2px solid #764ba2', paddingBottom: '8px' }}>
                            Membres de familles ({membresInscritsAujourdhui.membresFamilles?.length || 0})
                          </h4>
                          {(!membresInscritsAujourdhui.membresFamilles || membresInscritsAujourdhui.membresFamilles.length === 0) ? (
                            <p style={{ color: '#666', fontStyle: 'italic' }}>Aucun membre de famille inscrit aujourd'hui</p>
                          ) : (
                            <div style={{ overflowX: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                                <thead>
                                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>#</th>
                                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Nom</th>
                                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Lien</th>
                                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Famille</th>
                                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>N° Famille</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {membresInscritsAujourdhui.membresFamilles.map((m, idx) => (
                                    <tr key={m.id} style={{ borderBottom: '1px solid #eee' }}>
                                      <td style={{ padding: '10px' }}>{idx + 1}</td>
                                      <td style={{ padding: '10px' }}>{[m.prenom, m.nom].filter(Boolean).join(' ')}</td>
                                      <td style={{ padding: '10px' }}>{m.lien || '-'}</td>
                                      <td style={{ padding: '10px' }}>{m.famille?.nom_famille || '-'}</td>
                                      <td style={{ padding: '10px' }}>{m.famille?.numero_famille || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="modal-footer" style={{ padding: '15px 20px', borderTop: '1px solid #ddd', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button className="btn btn-secondary" onClick={handlePrintModal}>🖨️ Imprimer</button>
                    <button className="btn btn-primary" onClick={() => setShowMembresInscritsAujourdhuiModal(false)}>Fermer</button>
                  </div>
                </div>
              </div>
            )}

            {/* Modal - Liste des cotisations payées aujourd'hui */}
            {showCotisationsPayeesAujourdhuiModal && (
              <div className="modal-overlay modal-list-print" onClick={() => setShowCotisationsPayeesAujourdhuiModal(false)}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', padding: 0 }}>
                  <div className="modal-header">
                    <h2>💰 Cotisations payées aujourd'hui</h2>
                    <button className="modal-close" onClick={() => setShowCotisationsPayeesAujourdhuiModal(false)}>✕</button>
                  </div>
                  <div className="modal-body" style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
                    {loadingCotisationsPayeesAujourdhui ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
                        <p>Chargement de la liste...</p>
                      </div>
                    ) : (
                      <>
                        <div style={{ marginBottom: '25px' }}>
                          <h4 style={{ color: '#43e97b', marginBottom: '15px', borderBottom: '2px solid #43e97b', paddingBottom: '8px' }}>
                            Membres avec cotisations payées aujourd'hui ({cotisationsPayeesAujourdhui.cotisationsMembres?.length || 0})
                          </h4>
                          {(!cotisationsPayeesAujourdhui.cotisationsMembres || cotisationsPayeesAujourdhui.cotisationsMembres.length === 0) ? (
                            <p style={{ color: '#666', fontStyle: 'italic' }}>Aucun membre avec cotisation payée aujourd'hui</p>
                          ) : (
                            <div style={{ overflowX: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                <thead>
                                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>#</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Membre</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Matricule</th>
                                    <th style={{ padding: '8px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Nb</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Mois</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Semaines</th>
                                    <th style={{ padding: '8px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Montant total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {cotisationsPayeesAujourdhui.cotisationsMembres.map((c, idx) => (
                                    <tr key={c.membre_id} style={{ borderBottom: '1px solid #eee' }}>
                                      <td style={{ padding: '8px' }}>{idx + 1}</td>
                                      <td style={{ padding: '8px' }}>{c.membre ? [c.membre.nom, c.membre.postnom, c.membre.prenom].filter(Boolean).join(' ') : '-'}</td>
                                      <td style={{ padding: '8px' }}>{c.membre?.numero_matricule || '-'}</td>
                                      <td style={{ padding: '8px', textAlign: 'center' }}>{c.nbCotisations || 0}</td>
                                      <td style={{ padding: '8px' }}>{c.moisLabel || '-'}</td>
                                      <td style={{ padding: '8px' }}>{c.semaineLabel || '-'}</td>
                                      <td style={{ padding: '8px', textAlign: 'right' }}>{parseFloat(c.montant || 0).toLocaleString('fr-FR')} CDF</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                        <div>
                          <h4 style={{ color: '#38f9d7', marginBottom: '15px', borderBottom: '2px solid #38f9d7', paddingBottom: '8px' }}>
                            Familles avec cotisations payées aujourd'hui ({cotisationsPayeesAujourdhui.cotisationsFamilles?.length || 0})
                          </h4>
                          {(!cotisationsPayeesAujourdhui.cotisationsFamilles || cotisationsPayeesAujourdhui.cotisationsFamilles.length === 0) ? (
                            <p style={{ color: '#666', fontStyle: 'italic' }}>Aucune famille avec cotisation payée aujourd'hui</p>
                          ) : (
                            <div style={{ overflowX: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                <thead>
                                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>#</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Famille</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>N° Famille</th>
                                    <th style={{ padding: '8px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Nb</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Mois</th>
                                    <th style={{ padding: '8px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Montant total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {cotisationsPayeesAujourdhui.cotisationsFamilles.map((c, idx) => (
                                    <tr key={c.famille_id} style={{ borderBottom: '1px solid #eee' }}>
                                      <td style={{ padding: '8px' }}>{idx + 1}</td>
                                      <td style={{ padding: '8px' }}>{c.famille?.nom_famille || '-'}</td>
                                      <td style={{ padding: '8px' }}>{c.famille?.numero_famille || '-'}</td>
                                      <td style={{ padding: '8px', textAlign: 'center' }}>{c.nbCotisations || 0}</td>
                                      <td style={{ padding: '8px' }}>{c.moisLabel || '-'}</td>
                                      <td style={{ padding: '8px', textAlign: 'right' }}>{parseFloat(c.montant || 0).toLocaleString('fr-FR')} CDF</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="modal-footer" style={{ padding: '15px 20px', borderTop: '1px solid #ddd', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button className="btn btn-secondary" onClick={handlePrintModal}>🖨️ Imprimer</button>
                    <button className="btn btn-primary" onClick={() => setShowCotisationsPayeesAujourdhuiModal(false)}>Fermer</button>
                  </div>
                </div>
              </div>
            )}

            {/* Modal - Liste du montant collecté aujourd'hui */}
            {showMontantCollecteAujourdhuiModal && (
              <div className="modal-overlay modal-list-print" onClick={() => setShowMontantCollecteAujourdhuiModal(false)}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1000px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', padding: 0 }}>
                  <div className="modal-header">
                    <h2>💰 Montant collecté aujourd'hui</h2>
                    <button className="modal-close" onClick={() => setShowMontantCollecteAujourdhuiModal(false)}>✕</button>
                  </div>
                  <div className="modal-body" style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
                    {loadingMontantCollecteAujourdhui ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
                        <p>Chargement de la liste...</p>
                      </div>
                    ) : (
                      <>
                        <div style={{ marginBottom: '25px' }}>
                          <h4 style={{ color: '#4facfe', marginBottom: '15px', borderBottom: '2px solid #4facfe', paddingBottom: '8px' }}>
                            Paiements membres ({montantCollecteAujourdhui.paiementsMembres?.length || 0})
                          </h4>
                          {(!montantCollecteAujourdhui.paiementsMembres || montantCollecteAujourdhui.paiementsMembres.length === 0) ? (
                            <p style={{ color: '#666', fontStyle: 'italic' }}>Aucun paiement membre aujourd'hui</p>
                          ) : (
                            <div style={{ overflowX: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                <thead>
                                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>#</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Membre</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Matricule</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Mois</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Semaines</th>
                                    <th style={{ padding: '8px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Montant</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {montantCollecteAujourdhui.paiementsMembres.map((p, idx) => (
                                    <tr key={`m-${p.membre_id}-${p.mois}`} style={{ borderBottom: '1px solid #eee' }}>
                                      <td style={{ padding: '8px' }}>{idx + 1}</td>
                                      <td style={{ padding: '8px' }}>{p.membre ? [p.membre.nom, p.membre.postnom, p.membre.prenom].filter(Boolean).join(' ') : '-'}</td>
                                      <td style={{ padding: '8px' }}>{p.membre?.numero_matricule || '-'}</td>
                                      <td style={{ padding: '8px' }}>{p.moisLabel || '-'}</td>
                                      <td style={{ padding: '8px' }}>{p.semaineLabel || '-'}</td>
                                      <td style={{ padding: '8px', textAlign: 'right' }}>{parseFloat(p.montant || 0).toLocaleString('fr-FR')} CDF</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                        <div>
                          <h4 style={{ color: '#00f2fe', marginBottom: '15px', borderBottom: '2px solid #00f2fe', paddingBottom: '8px' }}>
                            Paiements familles ({montantCollecteAujourdhui.paiementsFamilles?.length || 0})
                          </h4>
                          {(!montantCollecteAujourdhui.paiementsFamilles || montantCollecteAujourdhui.paiementsFamilles.length === 0) ? (
                            <p style={{ color: '#666', fontStyle: 'italic' }}>Aucun paiement famille aujourd'hui</p>
                          ) : (
                            <div style={{ overflowX: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                <thead>
                                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>#</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Famille</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>N° Famille</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Mois</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Semaines</th>
                                    <th style={{ padding: '8px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Montant</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {montantCollecteAujourdhui.paiementsFamilles.map((p, idx) => (
                                    <tr key={`f-${p.famille_id}-${p.mois}`} style={{ borderBottom: '1px solid #eee' }}>
                                      <td style={{ padding: '8px' }}>{idx + 1}</td>
                                      <td style={{ padding: '8px' }}>{p.famille?.nom_famille || '-'}</td>
                                      <td style={{ padding: '8px' }}>{p.famille?.numero_famille || '-'}</td>
                                      <td style={{ padding: '8px' }}>{p.moisLabel || '-'}</td>
                                      <td style={{ padding: '8px' }}>{p.semaineLabel || '-'}</td>
                                      <td style={{ padding: '8px', textAlign: 'right' }}>{parseFloat(p.montant || 0).toLocaleString('fr-FR')} CDF</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                        <p style={{ fontSize: '12px', color: '#999', marginTop: '15px' }}>* Paiements collectés aujourd'hui (membres + familles), regroupés par membre/famille et mois. Les paiements de cartes (5 000 CDF) sont exclus.</p>
                      </>
                    )}
                  </div>
                  <div className="modal-footer" style={{ padding: '15px 20px', borderTop: '1px solid #ddd', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button className="btn btn-secondary" onClick={handlePrintModal}>🖨️ Imprimer</button>
                    <button className="btn btn-primary" onClick={() => setShowMontantCollecteAujourdhuiModal(false)}>Fermer</button>
                  </div>
                </div>
              </div>
            )}

            {/* Modal - Liste des paiements globaux */}
            {showPaiementsGlobauxModal && (
              <div className="modal-overlay modal-list-print" onClick={() => setShowPaiementsGlobauxModal(false)}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1000px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', padding: 0 }}>
                  <div className="modal-header">
                    <h2>💰 {selectedMonth ? `Paiements - ${new Date(selectedMonth + '-01').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}` : 'Total Paiements globaux'}</h2>
                    <button className="modal-close" onClick={() => setShowPaiementsGlobauxModal(false)}>✕</button>
                  </div>
                  <div className="modal-body" style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
                    {loadingPaiementsGlobaux ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
                        <p>Chargement de la liste...</p>
                      </div>
                    ) : (
                      <>
                        <div style={{ marginBottom: '25px' }}>
                          <h4 style={{ color: '#4facfe', marginBottom: '15px', borderBottom: '2px solid #4facfe', paddingBottom: '8px' }}>
                            Paiements membres ({paiementsGlobaux.paiementsMembres?.length || 0})
                          </h4>
                          {(!paiementsGlobaux.paiementsMembres || paiementsGlobaux.paiementsMembres.length === 0) ? (
                            <p style={{ color: '#666', fontStyle: 'italic' }}>Aucun paiement membre</p>
                          ) : (
                            <div style={{ overflowX: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                <thead>
                                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>#</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Membre</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Matricule</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Mois</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Semaines</th>
                                    <th style={{ padding: '8px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Montant</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {paiementsGlobaux.paiementsMembres.map((p, idx) => (
                                    <tr key={`m-${p.membre_id}-${p.mois}`} style={{ borderBottom: '1px solid #eee' }}>
                                      <td style={{ padding: '8px' }}>{idx + 1}</td>
                                      <td style={{ padding: '8px' }}>{p.membre ? [p.membre.nom, p.membre.postnom, p.membre.prenom].filter(Boolean).join(' ') : '-'}</td>
                                      <td style={{ padding: '8px' }}>{p.membre?.numero_matricule || '-'}</td>
                                      <td style={{ padding: '8px' }}>{p.moisLabel || '-'}</td>
                                      <td style={{ padding: '8px' }}>{p.semaineLabel || '-'}</td>
                                      <td style={{ padding: '8px', textAlign: 'right' }}>{parseFloat(p.montant || 0).toLocaleString('fr-FR')} CDF</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                        <div>
                          <h4 style={{ color: '#00f2fe', marginBottom: '15px', borderBottom: '2px solid #00f2fe', paddingBottom: '8px' }}>
                            Paiements familles ({paiementsGlobaux.paiementsFamilles?.length || 0})
                          </h4>
                          {(!paiementsGlobaux.paiementsFamilles || paiementsGlobaux.paiementsFamilles.length === 0) ? (
                            <p style={{ color: '#666', fontStyle: 'italic' }}>Aucun paiement famille</p>
                          ) : (
                            <div style={{ overflowX: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                <thead>
                                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>#</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Famille</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>N° Famille</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Mois</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Semaines</th>
                                    <th style={{ padding: '8px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Montant</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {paiementsGlobaux.paiementsFamilles.map((p, idx) => (
                                    <tr key={`f-${p.famille_id}-${p.mois}`} style={{ borderBottom: '1px solid #eee' }}>
                                      <td style={{ padding: '8px' }}>{idx + 1}</td>
                                      <td style={{ padding: '8px' }}>{p.famille?.nom_famille || '-'}</td>
                                      <td style={{ padding: '8px' }}>{p.famille?.numero_famille || '-'}</td>
                                      <td style={{ padding: '8px' }}>{p.moisLabel || '-'}</td>
                                      <td style={{ padding: '8px' }}>{p.semaineLabel || '-'}</td>
                                      <td style={{ padding: '8px', textAlign: 'right' }}>{parseFloat(p.montant || 0).toLocaleString('fr-FR')} CDF</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                        <p style={{ fontSize: '12px', color: '#999', marginTop: '15px' }}>* Paiements regroupés par membre/famille et mois (semaines sur une ligne), triés par ordre alphabétique. Les paiements de cartes (5 000 CDF) sont exclus.</p>
                      </>
                    )}
                  </div>
                  <div className="modal-footer" style={{ padding: '15px 20px', borderTop: '1px solid #ddd', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button className="btn btn-secondary" onClick={handlePrintModal}>🖨️ Imprimer</button>
                    <button className="btn btn-primary" onClick={() => setShowPaiementsGlobauxModal(false)}>Fermer</button>
                  </div>
                </div>
              </div>
            )}

            {/* Modal - Liste des cotisations payées */}
            {showCotisationsPayeesModal && (
              <div className="modal-overlay modal-list-print" onClick={() => setShowCotisationsPayeesModal(false)}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1000px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', padding: 0 }}>
                  <div className="modal-header">
                    <h2>✅ {selectedMonth ? `Cotisations payées - ${new Date(selectedMonth + '-01').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}` : 'Cotisations Payées'}</h2>
                    <button className="modal-close" onClick={() => setShowCotisationsPayeesModal(false)}>✕</button>
                  </div>
                  <div className="modal-body" style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
                    {loadingCotisationsPayees ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
                        <p>Chargement de la liste...</p>
                      </div>
                    ) : (
                      <>
                        <div style={{ marginBottom: '20px' }}>
                          <input
                            type="text"
                            placeholder="🔍 Rechercher par nom, matricule, famille..."
                            value={searchCotisationsPayees}
                            onChange={(e) => setSearchCotisationsPayees(e.target.value)}
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              fontSize: '14px',
                              border: '2px solid #ddd',
                              borderRadius: '8px',
                              outline: 'none',
                              transition: 'border-color 0.2s'
                            }}
                            onFocus={(e) => { e.target.style.borderColor = '#28a745'; }}
                            onBlur={(e) => { e.target.style.borderColor = '#ddd'; }}
                          />
                        </div>
                        <div style={{ marginBottom: '25px' }}>
                          <h4 style={{ color: '#28a745', marginBottom: '15px', borderBottom: '2px solid #28a745', paddingBottom: '8px' }}>
                            Membres avec cotisations payées ({(() => {
                              const term = searchCotisationsPayees.trim().toLowerCase();
                              const list = cotisationsPayees.cotisationsMembres || [];
                              const filtered = term ? list.filter(c => {
                                const nom = [c.membre?.nom, c.membre?.postnom, c.membre?.prenom].filter(Boolean).join(' ').toLowerCase();
                                const matricule = (c.membre?.numero_matricule || '').toLowerCase();
                                return nom.includes(term) || matricule.includes(term) || (c.moisLabel || '').toLowerCase().includes(term);
                              }) : list;
                              return filtered.length;
                            })()})
                          </h4>
                          {(!cotisationsPayees.cotisationsMembres || cotisationsPayees.cotisationsMembres.length === 0) ? (
                            <p style={{ color: '#666', fontStyle: 'italic' }}>Aucun membre avec cotisations payées</p>
                          ) : (() => {
                            const term = searchCotisationsPayees.trim().toLowerCase();
                            const list = cotisationsPayees.cotisationsMembres || [];
                            const filteredMembres = term ? list.filter(c => {
                              const nom = [c.membre?.nom, c.membre?.postnom, c.membre?.prenom].filter(Boolean).join(' ').toLowerCase();
                              const matricule = (c.membre?.numero_matricule || '').toLowerCase();
                              return nom.includes(term) || matricule.includes(term) || (c.moisLabel || '').toLowerCase().includes(term);
                            }) : list;
                            return filteredMembres.length === 0 ? (
                              <p style={{ color: '#666', fontStyle: 'italic' }}>Aucun résultat pour &quot;{searchCotisationsPayees}&quot;</p>
                            ) : (
                            <div style={{ overflowX: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                <thead>
                                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>#</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Membre</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Matricule</th>
                                    <th style={{ padding: '8px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Nb</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Mois</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Semaines</th>
                                    <th style={{ padding: '8px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Montant total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filteredMembres.map((c, idx) => (
                                    <tr key={c.membre_id} style={{ borderBottom: '1px solid #eee' }}>
                                      <td style={{ padding: '8px' }}>{idx + 1}</td>
                                      <td style={{ padding: '8px' }}>{c.membre ? [c.membre.nom, c.membre.postnom, c.membre.prenom].filter(Boolean).join(' ') : '-'}</td>
                                      <td style={{ padding: '8px' }}>{c.membre?.numero_matricule || '-'}</td>
                                      <td style={{ padding: '8px', textAlign: 'center' }}>{c.nbCotisations || 0}</td>
                                      <td style={{ padding: '8px' }}>{c.moisLabel || '-'}</td>
                                      <td style={{ padding: '8px' }}>{c.semaineLabel || '-'}</td>
                                      <td style={{ padding: '8px', textAlign: 'right' }}>{parseFloat(c.montant || 0).toLocaleString('fr-FR')} CDF</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            );
                          })()}
                        </div>
                        <div>
                          <h4 style={{ color: '#20c997', marginBottom: '15px', borderBottom: '2px solid #20c997', paddingBottom: '8px' }}>
                            Familles avec cotisations payées ({(() => {
                              const term = searchCotisationsPayees.trim().toLowerCase();
                              const list = cotisationsPayees.cotisationsFamilles || [];
                              const filtered = term ? list.filter(c => {
                                const nom = (c.famille?.nom_famille || '').toLowerCase();
                                const numero = (c.famille?.numero_famille || '').toLowerCase();
                                return nom.includes(term) || numero.includes(term) || (c.moisLabel || '').toLowerCase().includes(term);
                              }) : list;
                              return filtered.length;
                            })()})
                          </h4>
                          {(!cotisationsPayees.cotisationsFamilles || cotisationsPayees.cotisationsFamilles.length === 0) ? (
                            <p style={{ color: '#666', fontStyle: 'italic' }}>Aucune famille avec cotisations payées</p>
                          ) : (() => {
                            const term = searchCotisationsPayees.trim().toLowerCase();
                            const list = cotisationsPayees.cotisationsFamilles || [];
                            const filteredFamilles = term ? list.filter(c => {
                              const nom = (c.famille?.nom_famille || '').toLowerCase();
                              const numero = (c.famille?.numero_famille || '').toLowerCase();
                              return nom.includes(term) || numero.includes(term) || (c.moisLabel || '').toLowerCase().includes(term);
                            }) : list;
                            return filteredFamilles.length === 0 ? (
                              <p style={{ color: '#666', fontStyle: 'italic' }}>Aucun résultat pour &quot;{searchCotisationsPayees}&quot;</p>
                            ) : (
                            <div style={{ overflowX: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                <thead>
                                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>#</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Famille</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>N° Famille</th>
                                    <th style={{ padding: '8px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Nb</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Mois</th>
                                    <th style={{ padding: '8px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Montant total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filteredFamilles.map((c, idx) => (
                                    <tr key={c.famille_id} style={{ borderBottom: '1px solid #eee' }}>
                                      <td style={{ padding: '8px' }}>{idx + 1}</td>
                                      <td style={{ padding: '8px' }}>{c.famille?.nom_famille || '-'}</td>
                                      <td style={{ padding: '8px' }}>{c.famille?.numero_famille || '-'}</td>
                                      <td style={{ padding: '8px', textAlign: 'center' }}>{c.nbCotisations || 0}</td>
                                      <td style={{ padding: '8px' }}>{c.moisLabel || '-'}</td>
                                      <td style={{ padding: '8px', textAlign: 'right' }}>{parseFloat(c.montant || 0).toLocaleString('fr-FR')} CDF</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            );
                          })()}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="modal-footer" style={{ padding: '15px 20px', borderTop: '1px solid #ddd', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button className="btn btn-secondary" onClick={handlePrintModal}>🖨️ Imprimer</button>
                    <button className="btn btn-primary" onClick={() => setShowCotisationsPayeesModal(false)}>Fermer</button>
                  </div>
                </div>
              </div>
            )}

            {/* Modal - Liste des cotisations impayées */}
            {showCotisationsImpayeesModal && (
              <div className="modal-overlay modal-list-print" onClick={() => setShowCotisationsImpayeesModal(false)}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1000px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', padding: 0 }}>
                  <div className="modal-header">
                    <h2>⚠️ {selectedMonth ? `Cotisations impayées - ${new Date(selectedMonth + '-01').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}` : 'Cotisations Impayées'}</h2>
                    <button className="modal-close" onClick={() => setShowCotisationsImpayeesModal(false)}>✕</button>
                  </div>
                  <div className="modal-body" style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
                    {loadingCotisationsImpayees ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
                        <p>Chargement de la liste...</p>
                      </div>
                    ) : (
                      <>
                        <div style={{ marginBottom: '20px' }}>
                          <input
                            type="text"
                            placeholder="🔍 Rechercher par nom, matricule, famille..."
                            value={searchCotisationsImpayees}
                            onChange={(e) => setSearchCotisationsImpayees(e.target.value)}
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              fontSize: '14px',
                              border: '2px solid #ddd',
                              borderRadius: '8px',
                              outline: 'none',
                              transition: 'border-color 0.2s'
                            }}
                            onFocus={(e) => { e.target.style.borderColor = '#dc3545'; }}
                            onBlur={(e) => { e.target.style.borderColor = '#ddd'; }}
                          />
                        </div>
                        <div style={{ marginBottom: '25px' }}>
                          <h4 style={{ color: '#dc3545', marginBottom: '15px', borderBottom: '2px solid #dc3545', paddingBottom: '8px' }}>
                            Membres avec cotisations impayées ({(() => {
                              const term = searchCotisationsImpayees.trim().toLowerCase();
                              const list = cotisationsImpayees.cotisationsMembres || [];
                              const filtered = term ? list.filter(c => {
                                const nom = [c.membre?.nom, c.membre?.postnom, c.membre?.prenom].filter(Boolean).join(' ').toLowerCase();
                                const matricule = (c.membre?.numero_matricule || '').toLowerCase();
                                return nom.includes(term) || matricule.includes(term) || (c.moisLabel || '').toLowerCase().includes(term);
                              }) : list;
                              return filtered.length;
                            })()})
                          </h4>
                          {(!cotisationsImpayees.cotisationsMembres || cotisationsImpayees.cotisationsMembres.length === 0) ? (
                            <p style={{ color: '#666', fontStyle: 'italic' }}>Aucun membre avec cotisations impayées</p>
                          ) : (() => {
                            const term = searchCotisationsImpayees.trim().toLowerCase();
                            const list = cotisationsImpayees.cotisationsMembres || [];
                            const filteredMembres = term ? list.filter(c => {
                              const nom = [c.membre?.nom, c.membre?.postnom, c.membre?.prenom].filter(Boolean).join(' ').toLowerCase();
                              const matricule = (c.membre?.numero_matricule || '').toLowerCase();
                              return nom.includes(term) || matricule.includes(term) || (c.moisLabel || '').toLowerCase().includes(term);
                            }) : list;
                            return filteredMembres.length === 0 ? (
                              <p style={{ color: '#666', fontStyle: 'italic' }}>Aucun résultat pour &quot;{searchCotisationsImpayees}&quot;</p>
                            ) : (
                            <div style={{ overflowX: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                <thead>
                                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>#</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Membre</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Matricule</th>
                                    <th style={{ padding: '8px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Nb</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Mois</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Semaines</th>
                                    <th style={{ padding: '8px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Montant total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filteredMembres.map((c, idx) => (
                                    <tr key={c.membre_id} style={{ borderBottom: '1px solid #eee' }}>
                                      <td style={{ padding: '8px' }}>{idx + 1}</td>
                                      <td style={{ padding: '8px' }}>{c.membre ? [c.membre.nom, c.membre.postnom, c.membre.prenom].filter(Boolean).join(' ') : '-'}</td>
                                      <td style={{ padding: '8px' }}>{c.membre?.numero_matricule || '-'}</td>
                                      <td style={{ padding: '8px', textAlign: 'center' }}>{c.nbCotisations || 0}</td>
                                      <td style={{ padding: '8px' }}>{c.moisLabel || '-'}</td>
                                      <td style={{ padding: '8px' }}>{c.semaineLabel || '-'}</td>
                                      <td style={{ padding: '8px', textAlign: 'right' }}>{parseFloat(c.montant || 0).toLocaleString('fr-FR')} CDF</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            );
                          })()}
                        </div>
                        <div>
                          <h4 style={{ color: '#c82333', marginBottom: '15px', borderBottom: '2px solid #c82333', paddingBottom: '8px' }}>
                            Familles avec cotisations impayées ({(() => {
                              const term = searchCotisationsImpayees.trim().toLowerCase();
                              const list = cotisationsImpayees.cotisationsFamilles || [];
                              const filtered = term ? list.filter(c => {
                                const nom = (c.famille?.nom_famille || '').toLowerCase();
                                const numero = (c.famille?.numero_famille || '').toLowerCase();
                                return nom.includes(term) || numero.includes(term) || (c.moisLabel || '').toLowerCase().includes(term);
                              }) : list;
                              return filtered.length;
                            })()})
                          </h4>
                          {(!cotisationsImpayees.cotisationsFamilles || cotisationsImpayees.cotisationsFamilles.length === 0) ? (
                            <p style={{ color: '#666', fontStyle: 'italic' }}>Aucune famille avec cotisations impayées</p>
                          ) : (() => {
                            const term = searchCotisationsImpayees.trim().toLowerCase();
                            const list = cotisationsImpayees.cotisationsFamilles || [];
                            const filteredFamilles = term ? list.filter(c => {
                              const nom = (c.famille?.nom_famille || '').toLowerCase();
                              const numero = (c.famille?.numero_famille || '').toLowerCase();
                              return nom.includes(term) || numero.includes(term) || (c.moisLabel || '').toLowerCase().includes(term);
                            }) : list;
                            return filteredFamilles.length === 0 ? (
                              <p style={{ color: '#666', fontStyle: 'italic' }}>Aucun résultat pour &quot;{searchCotisationsImpayees}&quot;</p>
                            ) : (
                            <div style={{ overflowX: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                <thead>
                                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>#</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Famille</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>N° Famille</th>
                                    <th style={{ padding: '8px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Nb</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Mois</th>
                                    <th style={{ padding: '8px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Montant total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filteredFamilles.map((c, idx) => (
                                    <tr key={c.famille_id} style={{ borderBottom: '1px solid #eee' }}>
                                      <td style={{ padding: '8px' }}>{idx + 1}</td>
                                      <td style={{ padding: '8px' }}>{c.famille?.nom_famille || '-'}</td>
                                      <td style={{ padding: '8px' }}>{c.famille?.numero_famille || '-'}</td>
                                      <td style={{ padding: '8px', textAlign: 'center' }}>{c.nbCotisations || 0}</td>
                                      <td style={{ padding: '8px' }}>{c.moisLabel || '-'}</td>
                                      <td style={{ padding: '8px', textAlign: 'right' }}>{parseFloat(c.montant || 0).toLocaleString('fr-FR')} CDF</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            );
                          })()}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="modal-footer" style={{ padding: '15px 20px', borderTop: '1px solid #ddd', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button className="btn btn-secondary" onClick={handlePrintModal}>🖨️ Imprimer</button>
                    <button className="btn btn-primary" onClick={() => setShowCotisationsImpayeesModal(false)}>Fermer</button>
                  </div>
                </div>
              </div>
            )}

            {/* Statistiques Globales */}
            <div>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  marginBottom: '20px', 
                  flexWrap: 'wrap', 
                  gap: '15px',
                  padding: '15px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0'
                }}>
                  <h3 className="stats-section-title" style={{ margin: 0, color: '#2c3e50', fontSize: '20px', fontWeight: '600' }}>
                    📊 Vue d'ensemble globale
                  </h3>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px',
                    flexWrap: 'wrap'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      padding: '8px 12px',
                      backgroundColor: 'white',
                      borderRadius: '6px',
                      border: '1px solid #ddd',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                      <label htmlFor="month-selector" style={{ 
                        fontSize: '14px', 
                        color: '#495057', 
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <span>📅</span>
                        <span>Sélectionner un mois :</span>
                      </label>
                      <input
                        type="month"
                        id="month-selector"
                        value={selectedMonth}
                        onChange={handleMonthChange}
                        style={{
                          padding: '10px 14px',
                          border: '2px solid #007bff',
                          borderRadius: '6px',
                          fontSize: '15px',
                          cursor: 'pointer',
                          backgroundColor: 'white',
                          minWidth: '180px',
                          fontWeight: '500',
                          color: '#495057',
                          transition: 'all 0.3s ease'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#0056b3';
                          e.target.style.boxShadow = '0 0 0 3px rgba(0,123,255,0.25)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#007bff';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                      {selectedMonth && (
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px',
                          padding: '8px 12px',
                          backgroundColor: 'white',
                          borderRadius: '6px',
                          border: '1px solid #ddd',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                          <label htmlFor="day-selector" style={{ 
                            fontSize: '14px', 
                            color: '#495057', 
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            <span>📆</span>
                            <span>Sélectionner un jour :</span>
                          </label>
                          <select
                            id="day-selector"
                            value={selectedDay}
                            onChange={handleDayChange}
                            style={{
                              padding: '10px 14px',
                              border: '2px solid #007bff',
                              borderRadius: '6px',
                              fontSize: '15px',
                              cursor: 'pointer',
                              backgroundColor: 'white',
                              minWidth: '150px',
                              fontWeight: '500',
                              color: '#495057',
                              transition: 'all 0.3s ease'
                            }}
                            onFocus={(e) => {
                              e.target.style.borderColor = '#0056b3';
                              e.target.style.boxShadow = '0 0 0 3px rgba(0,123,255,0.25)';
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = '#007bff';
                              e.target.style.boxShadow = 'none';
                            }}
                          >
                            <option value="tous">Tous les jours</option>
                            {getDaysInMonth().map(day => (
                              <option key={day} value={day}>Jour {day}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                    {selectedMonth && (
                      <button
                        type="button"
                        onClick={async () => {
                          setSelectedMonth('');
                          setSelectedDay('');
                          setLoadingStats(true);
                          try {
                            const statsRes = await loadStats();
                            if (statsRes && statsRes.data) {
                              setStats(statsRes.data);
                            }
                          } catch (err) {
                            console.error('❌ Erreur lors du rechargement des stats:', err);
                            setStats({});
                          } finally {
                            setLoadingStats(false);
                          }
                        }}
                        style={{
                          padding: '10px 16px',
                          border: '1px solid #6c757d',
                          borderRadius: '6px',
                          fontSize: '14px',
                          cursor: 'pointer',
                          backgroundColor: '#6c757d',
                          color: 'white',
                          fontWeight: '500',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#5a6268';
                          e.target.style.borderColor = '#5a6268';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#6c757d';
                          e.target.style.borderColor = '#6c757d';
                        }}
                      >
                        🔄 Afficher tout
                      </button>
                    )}
                  </div>
                </div>
                {selectedMonth && (
                  <div style={{ 
                    marginBottom: '20px', 
                    padding: '15px 20px', 
                    backgroundColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '8px', 
                    fontSize: '15px', 
                    color: 'white',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}>
                    <span style={{ fontSize: '20px' }}>📅</span>
                    <span>
                      <strong>Affichage des statistiques pour :</strong> {new Date(selectedMonth + '-01').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                      {selectedDay && selectedDay !== 'tous' && (
                        <span> - Jour {selectedDay}</span>
                      )}
                      {selectedDay === 'tous' && (
                        <span> - Tous les jours</span>
                      )}
                    </span>
                  </div>
                )}
                {!selectedMonth && (
                  <div style={{ 
                    marginBottom: '20px', 
                    padding: '12px 20px', 
                    backgroundColor: '#e7f3ff',
                    borderRadius: '8px', 
                    fontSize: '14px', 
                    color: '#0066cc',
                    border: '1px solid #b3d9ff',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span>ℹ️</span>
                    <span>Les statistiques affichées concernent <strong>toutes les périodes</strong>. Utilisez le sélecteur ci-dessus pour filtrer par mois.</span>
                  </div>
                )}
                {loadingStats ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                    <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
                    <p>Chargement des statistiques...</p>
                  </div>
                ) : (
                  <div className="stats-grid">
                  <div className="stat-card">
                    <h3>{selectedMonth ? 'Total Membres (Tous)' : 'Total Membres (Tous)'}</h3>
                    <div className="value">{(stats.totalMembres || 0) + (statsFamillesGlobales?.totalMembres || 0)}</div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                      {selectedMonth 
                        ? `Membres individuels + membres de familles en ${new Date(selectedMonth + '-01').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}`
                        : 'Membres individuels + membres de toutes les familles'
                      }
                    </div>
                  </div>
                  <div className="stat-card">
                    <h3>{selectedMonth ? 'Membres individuels du mois' : 'Total Membres Individuels'}</h3>
                    <div className="value">{stats.totalMembres || 0}</div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                      {selectedMonth 
                        ? `Membres individuels uniquement en ${new Date(selectedMonth + '-01').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}`
                        : 'Membres individuels uniquement (sans familles)'
                      }
                    </div>
                  </div>
                  <div 
                    className="stat-card" 
                    style={{ cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }}
                    onClick={async () => {
                      setShowPaiementsGlobauxModal(true);
                      setLoadingPaiementsGlobaux(true);
                      try {
                        const [annee, mois] = selectedMonth ? selectedMonth.split('-') : [null, null];
                        const response = await getPaiementsGlobaux(mois, annee);
                        if (response?.data) {
                          setPaiementsGlobaux(response.data);
                        }
                      } catch (err) {
                        console.error('Erreur chargement paiements globaux:', err);
                        notificationService.error('Erreur', 'Impossible de charger la liste des paiements');
                      } finally {
                        setLoadingPaiementsGlobaux(false);
                      }
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.02)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.1)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = ''; }}
                  >
                    <h3>{selectedMonth ? 'Paiements du mois' : 'Total Paiements'}</h3>
                    <div className="value">{parseFloat(statsCombinees.totalPaiements || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF</div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                      {selectedMonth 
                        ? `Montant collecté en ${new Date(selectedMonth + '-01').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })} • Cliquez pour voir la liste`
                        : 'Montant total collecté (membres + familles) • Cliquez pour voir la liste'
                      }
                    </div>
                  </div>
                  <div 
                    className="stat-card" 
                    style={{ cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }}
                    onClick={async () => {
                      setShowCotisationsPayeesModal(true);
                      setSearchCotisationsPayees('');
                      setLoadingCotisationsPayees(true);
                      try {
                        const [annee, mois] = selectedMonth ? selectedMonth.split('-') : [null, null];
                        const response = await getCotisationsPayees(mois, annee);
                        if (response?.data) {
                          setCotisationsPayees(response.data);
                        }
                      } catch (err) {
                        console.error('Erreur chargement cotisations payées:', err);
                        notificationService.error('Erreur', 'Impossible de charger la liste des cotisations payées');
                      } finally {
                        setLoadingCotisationsPayees(false);
                      }
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.02)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.1)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = ''; }}
                  >
                    <h3>{selectedMonth ? 'Cotisations payées du mois' : 'Cotisations Payées'}</h3>
                    <div className="value">{statsCombinees.cotisationsPayees || 0}</div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                      {selectedMonth 
                        ? `Cotisations réglées en ${new Date(selectedMonth + '-01').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })} • Cliquez pour voir la liste`
                        : 'Cotisations réglées (membres + familles) • Cliquez pour voir la liste'
                      }
                    </div>
                  </div>
                  <div 
                    className="stat-card" 
                    style={{ cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }}
                    onClick={async () => {
                      setShowCotisationsImpayeesModal(true);
                      setSearchCotisationsImpayees('');
                      setLoadingCotisationsImpayees(true);
                      try {
                        const [annee, mois] = selectedMonth ? selectedMonth.split('-') : [null, null];
                        const response = await getCotisationsImpayees(mois, annee);
                        if (response?.data) {
                          setCotisationsImpayees(response.data);
                        }
                      } catch (err) {
                        console.error('Erreur chargement cotisations impayées:', err);
                        notificationService.error('Erreur', 'Impossible de charger la liste des cotisations impayées');
                      } finally {
                        setLoadingCotisationsImpayees(false);
                      }
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.02)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.1)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = ''; }}
                  >
                    <h3>{selectedMonth ? 'Cotisations impayées du mois' : 'Cotisations Impayées'}</h3>
                    <div className="value">{statsCombinees.cotisationsImpayees || 0}</div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                      {selectedMonth 
                        ? `Cotisations en attente pour ${new Date(selectedMonth + '-01').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })} • Cliquez pour voir la liste`
                        : 'Cotisations en attente (membres + familles) • Cliquez pour voir la liste'
                      }
                    </div>
                  </div>
                  <div className="stat-card">
                    <h3>Administrateurs</h3>
                    <div className="value">{stats.totalAdmins || 0}</div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>Comptes administrateurs (global)</div>
                  </div>
                  <div className="stat-card">
                    <h3>Récolteurs</h3>
                    <div className="value">{stats.totalRecolteurs || 0}</div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>Comptes récolteurs (global)</div>
                  </div>
                  <div className="stat-card">
                    <h3>{selectedMonth ? 'Cartes payées du mois' : 'Cartes Payées'}</h3>
                    <div className="value">{stats.cartesPayees || 0}</div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                      {selectedMonth 
                        ? `Cartes payées en ${new Date(selectedMonth + '-01').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}`
                        : 'Membres ayant payé la carte'
                      }
                    </div>
                  </div>
                  <div className="stat-card">
                    <h3>{selectedMonth ? 'Montant cartes du mois' : 'Montant Cartes'}</h3>
                    <div className="value">{parseFloat(statsCombinees.montantTotalCartes || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF</div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                      {selectedMonth 
                        ? `Total cartes collecté en ${new Date(selectedMonth + '-01').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}`
                        : 'Total collecté pour les cartes (membres + familles)'
                      }
                    </div>
                  </div>
                  <div className="stat-card">
                    <h3>Total Familles</h3>
                    <div className="value">{statsFamillesGlobales?.totalFamilles || 0}</div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>Toutes les familles enregistrées</div>
                  </div>
                </div>
                )}

                {/* Rapport Hebdomadaire */}
                {selectedMonth && (
                  <div style={{ marginBottom: '40px' }}>
                    <h3 
                      className="stats-section-title" 
                      style={{
                        marginBottom: '20px',
                        color: '#2c3e50',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}>
                      <span>📅</span>
                      <span>Rapport Hebdomadaire - {new Date(selectedMonth + '-01').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}</span>
                    </h3>
                    {loadingStatsHebdomadaires ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
                        <p>Chargement du rapport hebdomadaire...</p>
                      </div>
                    ) : statsHebdomadaires && statsHebdomadaires.semaines && statsHebdomadaires.semaines.length > 0 ? (
                      <div className="table-wrapper" style={{ overflowX: 'auto' }}>
                        <table className="table" style={{ width: '100%' }}>
                          <thead>
                            <tr>
                              <th>Semaine</th>
                              <th>Total Membres</th>
                              <th>Montant Total Collecté</th>
                              <th>Cotisations en Attente</th>
                              <th>Total Collecté pour les Cartes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {statsHebdomadaires.semaines.map((semaine, index) => (
                              <tr key={index}>
                                <td><strong>Semaine {semaine.semaine}</strong></td>
                                <td>{semaine.totalMembres || 0}</td>
                                <td>{parseFloat(semaine.montantTotalCollecte || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF</td>
                                <td>{semaine.cotisationsEnAttente || 0}</td>
                                <td>{parseFloat(semaine.totalCartes || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF</td>
                              </tr>
                            ))}
                            <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                              <td><strong>Total</strong></td>
                              <td><strong>{statsHebdomadaires.semaines.reduce((sum, s) => sum + (s.totalMembres || 0), 0)}</strong></td>
                              <td><strong>{parseFloat(statsHebdomadaires.semaines.reduce((sum, s) => sum + (s.montantTotalCollecte || 0), 0)).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF</strong></td>
                              <td><strong>{statsHebdomadaires.semaines.reduce((sum, s) => sum + (s.cotisationsEnAttente || 0), 0)}</strong></td>
                              <td><strong>{parseFloat(statsHebdomadaires.semaines.reduce((sum, s) => sum + (s.totalCartes || 0), 0)).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF</strong></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                        <p style={{ fontSize: '16px', marginBottom: '10px' }}>📊 Aucune donnée hebdomadaire disponible</p>
                        <p style={{ fontSize: '14px', color: '#999' }}>
                          {loadingStatsHebdomadaires === false && statsHebdomadaires === null 
                            ? 'Les données n\'ont pas pu être chargées. Vérifiez la console pour plus de détails.'
                            : 'Aucune activité enregistrée pour ce mois.'}
                        </p>
                        {statsHebdomadaires && (
                          <p style={{ fontSize: '12px', color: '#999', marginTop: '10px', fontStyle: 'italic' }}>
                            Données reçues: {JSON.stringify(statsHebdomadaires)}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Graphiques et Visualisations */}
                <div style={{ marginTop: '40px' }}>
                  <h3 className="stats-section-title" style={{ marginBottom: '30px', color: '#2c3e50', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span>📈</span>
                    <span>Graphiques et Visualisations</span>
                  </h3>

                  {/* Évolution des cotisations */}
                  <div className="card" style={{ marginBottom: '30px' }}>
                    <h4 style={{ marginBottom: '20px', color: '#2c3e50' }}>📊 Évolution des Cotisations (Membres + Familles)</h4>
                    <div style={{ marginBottom: '15px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <label style={{ fontWeight: '600' }}>Nombre de mois à afficher :</label>
                      <select
                        value={nbMoisEvolution}
                        onChange={(e) => {
                          setNbMoisEvolution(parseInt(e.target.value));
                        }}
                        style={{
                          padding: '8px 12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                      >
                        <option value="3">3 mois</option>
                        <option value="6">6 mois</option>
                        <option value="12">12 mois</option>
                      </select>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={loadEvolutionCotisations}
                        style={{ padding: '8px 16px' }}
                      >
                        🔄 Actualiser
                      </button>
                    </div>
                    {loadingEvolution ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
                        <p>Chargement des données...</p>
                      </div>
                    ) : evolutionData && evolutionData.data && evolutionData.data.length > 0 ? (
                      <div>
                        {/* Graphique en barres - Cotisations payées vs impayées */}
                        <div style={{ marginBottom: '30px' }}>
                          <h5 style={{ marginBottom: '15px', color: '#34495e' }}>Cotisations Payées vs Impayées</h5>
                          <Bar
                            data={{
                              labels: evolutionData.data.map(d => d.moisLabel),
                              datasets: [
                                {
                                  label: 'Cotisations Payées',
                                  data: evolutionData.data.map(d => d.cotisationsPayees),
                                  backgroundColor: 'rgba(40, 167, 69, 0.7)',
                                  borderColor: 'rgba(40, 167, 69, 1)',
                                  borderWidth: 1
                                },
                                {
                                  label: 'Cotisations Impayées',
                                  data: evolutionData.data.map(d => d.cotisationsImpayees),
                                  backgroundColor: 'rgba(220, 53, 69, 0.7)',
                                  borderColor: 'rgba(220, 53, 69, 1)',
                                  borderWidth: 1
                                }
                              ]
                            }}
                            options={{
                              responsive: true,
                              maintainAspectRatio: true,
                              plugins: {
                                legend: {
                                  position: 'top',
                                },
                                title: {
                                  display: false
                                }
                              },
                              scales: {
                                y: {
                                  beginAtZero: true,
                                  ticks: {
                                    stepSize: 1
                                  }
                                }
                              }
                            }}
                          />
                        </div>

                        {/* Graphique linéaire - Montant total des cotisations */}
                        <div>
                          <h5 style={{ marginBottom: '15px', color: '#34495e' }}>Évolution du Montant Total des Cotisations (Membres + Familles)</h5>
                          <Line
                            data={{
                              labels: evolutionData.data.map(d => d.moisLabel),
                              datasets: [
                                {
                                  label: 'Montant Total (CDF)',
                                  data: evolutionData.data.map(d => d.montantTotal),
                                  borderColor: 'rgba(0, 123, 255, 1)',
                                  backgroundColor: 'rgba(0, 123, 255, 0.1)',
                                  borderWidth: 2,
                                  fill: true,
                                  tension: 0.4
                                }
                              ]
                            }}
                            options={{
                              responsive: true,
                              maintainAspectRatio: true,
                              plugins: {
                                legend: {
                                  position: 'top',
                                },
                                title: {
                                  display: false
                                },
                                tooltip: {
                                  callbacks: {
                                    label: function(context) {
                                      return `Montant: ${parseFloat(context.parsed.y).toLocaleString('fr-FR')} CDF`;
                                    }
                                  }
                                }
                              },
                              scales: {
                                y: {
                                  beginAtZero: true,
                                  ticks: {
                                    callback: function(value) {
                                      return parseFloat(value).toLocaleString('fr-FR') + ' CDF';
                                    }
                                  }
                                }
                              }
                            }}
                          />
                        </div>
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                        <p>Aucune donnée disponible pour l'évolution des cotisations</p>
                      </div>
                    )}
                  </div>

                  {/* Comparaison mois vs mois */}
                  <div className="card">
                    <h4 style={{ marginBottom: '20px', color: '#2c3e50' }}>⚖️ Comparaison Mois vs Mois (Membres + Familles)</h4>
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      loadComparaisonMois();
                    }} style={{ marginBottom: '20px' }}>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Mois 1</label>
                          <select
                            value={comparaisonForm.mois1}
                            onChange={(e) => setComparaisonForm({ ...comparaisonForm, mois1: parseInt(e.target.value) })}
                            style={{ marginRight: '10px' }}
                          >
                            {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                              <option key={m} value={m}>
                                {['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'][m-1]}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Année 1</label>
                          <input
                            type="number"
                            value={comparaisonForm.annee1}
                            onChange={(e) => setComparaisonForm({ ...comparaisonForm, annee1: parseInt(e.target.value) })}
                            min="2020"
                            max="2100"
                            style={{ marginRight: '10px' }}
                          />
                        </div>
                        <div className="form-group">
                          <label>Mois 2</label>
                          <select
                            value={comparaisonForm.mois2}
                            onChange={(e) => setComparaisonForm({ ...comparaisonForm, mois2: parseInt(e.target.value) })}
                            style={{ marginRight: '10px' }}
                          >
                            {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                              <option key={m} value={m}>
                                {['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'][m-1]}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Année 2</label>
                          <input
                            type="number"
                            value={comparaisonForm.annee2}
                            onChange={(e) => setComparaisonForm({ ...comparaisonForm, annee2: parseInt(e.target.value) })}
                            min="2020"
                            max="2100"
                            style={{ marginRight: '10px' }}
                          />
                        </div>
                        <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                          <button type="submit" className="btn btn-primary">
                            🔍 Comparer
                          </button>
                        </div>
                      </div>
                    </form>
                    {loadingComparaison ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
                        <p>Chargement de la comparaison...</p>
                      </div>
                    ) : comparaisonData && comparaisonData.mois1 && comparaisonData.mois2 ? (
                      <div>
                        {/* Graphique en barres comparatif */}
                        <div style={{ marginBottom: '30px' }}>
                          <h5 style={{ marginBottom: '15px', color: '#34495e' }}>Comparaison des Indicateurs</h5>
                          <Bar
                            data={{
                              labels: ['Cotisations Payées', 'Cotisations Impayées', 'Montant Total', 'Membres Inscrits', 'Montant Paiements'],
                              datasets: [
                                {
                                  label: comparaisonData.mois1?.label || 'Mois 1',
                                  data: [
                                    comparaisonData.mois1?.cotisationsPayees || 0,
                                    comparaisonData.mois1?.cotisationsImpayees || 0,
                                    comparaisonData.mois1?.montantTotal || 0,
                                    comparaisonData.mois1?.membresInscrits || 0,
                                    comparaisonData.mois1?.montantPaiements || 0
                                  ],
                                  backgroundColor: 'rgba(0, 123, 255, 0.7)',
                                  borderColor: 'rgba(0, 123, 255, 1)',
                                  borderWidth: 1
                                },
                                {
                                  label: comparaisonData.mois2?.label || 'Mois 2',
                                  data: [
                                    comparaisonData.mois2?.cotisationsPayees || 0,
                                    comparaisonData.mois2?.cotisationsImpayees || 0,
                                    comparaisonData.mois2?.montantTotal || 0,
                                    comparaisonData.mois2?.membresInscrits || 0,
                                    comparaisonData.mois2?.montantPaiements || 0
                                  ],
                                  backgroundColor: 'rgba(40, 167, 69, 0.7)',
                                  borderColor: 'rgba(40, 167, 69, 1)',
                                  borderWidth: 1
                                }
                              ]
                            }}
                            options={{
                              responsive: true,
                              maintainAspectRatio: true,
                              plugins: {
                                legend: {
                                  position: 'top',
                                },
                                title: {
                                  display: false
                                },
                                tooltip: {
                                  callbacks: {
                                    label: function(context) {
                                      let label = context.dataset.label || '';
                                      if (label) {
                                        label += ': ';
                                      }
                                      if (context.dataIndex >= 2) {
                                        // Montant Total et Montant Paiements
                                        label += parseFloat(context.parsed.y).toLocaleString('fr-FR') + ' CDF';
                                      } else {
                                        label += context.parsed.y;
                                      }
                                      return label;
                                    }
                                  }
                                }
                              },
                              scales: {
                                y: {
                                  beginAtZero: true
                                }
                              }
                            }}
                          />
                        </div>

                        {/* Graphique en donut - Répartition cotisations */}
                        <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap' }}>
                          <div style={{ flex: '1', minWidth: '300px' }}>
                            <h5 style={{ marginBottom: '15px', color: '#34495e', textAlign: 'center' }}>
                              {comparaisonData.mois1?.label || 'Mois 1'}
                            </h5>
                            <Doughnut
                              data={{
                                labels: ['Payées', 'Impayées'],
                                datasets: [{
                                  data: [
                                    comparaisonData.mois1?.cotisationsPayees || 0,
                                    comparaisonData.mois1?.cotisationsImpayees || 0
                                  ],
                                  backgroundColor: [
                                    'rgba(40, 167, 69, 0.7)',
                                    'rgba(220, 53, 69, 0.7)'
                                  ],
                                  borderColor: [
                                    'rgba(40, 167, 69, 1)',
                                    'rgba(220, 53, 69, 1)'
                                  ],
                                  borderWidth: 1
                                }]
                              }}
                              options={{
                                responsive: true,
                                maintainAspectRatio: true,
                                plugins: {
                                  legend: {
                                    position: 'bottom',
                                  }
                                }
                              }}
                            />
                          </div>
                          <div style={{ flex: '1', minWidth: '300px' }}>
                            <h5 style={{ marginBottom: '15px', color: '#34495e', textAlign: 'center' }}>
                              {comparaisonData.mois2?.label || 'Mois 2'}
                            </h5>
                            <Doughnut
                              data={{
                                labels: ['Payées', 'Impayées'],
                                datasets: [{
                                  data: [
                                    comparaisonData.mois2?.cotisationsPayees || 0,
                                    comparaisonData.mois2?.cotisationsImpayees || 0
                                  ],
                                  backgroundColor: [
                                    'rgba(40, 167, 69, 0.7)',
                                    'rgba(220, 53, 69, 0.7)'
                                  ],
                                  borderColor: [
                                    'rgba(40, 167, 69, 1)',
                                    'rgba(220, 53, 69, 1)'
                                  ],
                                  borderWidth: 1
                                }]
                              }}
                              options={{
                                responsive: true,
                                maintainAspectRatio: true,
                                plugins: {
                                  legend: {
                                    position: 'bottom',
                                  }
                                }
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                        <p>Sélectionnez deux mois et cliquez sur "Comparer" pour voir la comparaison</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
          </div>
        )}

        {activeTab === 'evolution' && (
          <div>
            <div style={{ marginBottom: '30px' }}>
              <h2 style={{ marginBottom: '20px', color: '#2c3e50', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span>📈</span>
                <span>Évolution - Statistiques Combinées (Membres + Familles)</span>
              </h2>
              
              <div style={{ display: 'flex', gap: '15px', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap' }}>
                <label style={{ fontWeight: '600' }}>Période :</label>
                <select
                  value={nbMoisEvolution}
                  onChange={(e) => {
                    setNbMoisEvolution(parseInt(e.target.value));
                    loadStatsEvolution();
                  }}
                  className="form-control"
                  style={{ width: 'auto', minWidth: '150px' }}
                >
                  <option value={3}>3 derniers mois</option>
                  <option value={6}>6 derniers mois</option>
                  <option value={12}>12 derniers mois</option>
                  <option value={24}>24 derniers mois</option>
                </select>
                  <button
                  className="btn btn-secondary btn-sm"
                  onClick={loadStatsEvolution}
                  disabled={loadingEvolutionCombinee}
                >
                  {loadingEvolutionCombinee ? '⏳ Chargement...' : '🔄 Actualiser'}
                </button>
              </div>

              {loadingEvolutionCombinee ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
                  <p>Chargement des statistiques d'évolution...</p>
                </div>
              ) : evolutionCombineeData && evolutionCombineeData.data && evolutionCombineeData.data.length > 0 ? (
                <div>
                  {/* Statistiques globales */}
                  {evolutionCombineeData.globales && (
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                      gap: '20px', 
                      marginBottom: '30px' 
                    }}>
                      <div className="stat-card" style={{ 
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        padding: '20px',
                        borderRadius: '10px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                      }}>
                        <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', opacity: 0.9 }}>Total Membres</h4>
                        <p style={{ margin: 0, fontSize: '32px', fontWeight: 'bold' }}>
                          {evolutionCombineeData.globales.totalMembres || 0}
                        </p>
                      </div>
                      <div className="stat-card" style={{ 
                        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                        color: 'white',
                        padding: '20px',
                        borderRadius: '10px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                      }}>
                        <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', opacity: 0.9 }}>Total Familles</h4>
                        <p style={{ margin: 0, fontSize: '32px', fontWeight: 'bold' }}>
                          {evolutionCombineeData.globales.totalFamilles || 0}
                        </p>
                      </div>
                      <div className="stat-card" style={{ 
                        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                        color: 'white',
                        padding: '20px',
                        borderRadius: '10px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                      }}>
                        <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', opacity: 0.9 }}>Total Combiné</h4>
                        <p style={{ margin: 0, fontSize: '32px', fontWeight: 'bold' }}>
                          {evolutionCombineeData.globales.totalMembresCombine || 0}
                        </p>
                        <p style={{ margin: '5px 0 0 0', fontSize: '12px', opacity: 0.8 }}>
                          (Membres + Familles + Membres de famille)
                        </p>
                      </div>
                      <div className="stat-card" style={{ 
                        background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                        color: 'white',
                        padding: '20px',
                        borderRadius: '10px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                      }}>
                        <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', opacity: 0.9 }}>Total Paiements</h4>
                        <p style={{ margin: 0, fontSize: '28px', fontWeight: 'bold' }}>
                          {parseFloat(evolutionCombineeData.globales.totalMontantPaiements || 0).toLocaleString('fr-FR')} CDF
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Graphique 1: Évolution des membres et familles */}
                  <div style={{ marginBottom: '30px', backgroundColor: 'white', padding: '20px', borderRadius: '10px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    <h4 style={{ marginBottom: '20px', color: '#2c3e50' }}>Évolution des Membres et Familles</h4>
                    <Line
                      data={{
                        labels: evolutionCombineeData.data.map(d => d.moisLabel),
                        datasets: [
                          {
                            label: 'Membres',
                            data: evolutionCombineeData.data.map(d => d.membres),
                            borderColor: 'rgba(102, 126, 234, 1)',
                            backgroundColor: 'rgba(102, 126, 234, 0.1)',
                            tension: 0.4
                          },
                          {
                            label: 'Familles',
                            data: evolutionCombineeData.data.map(d => d.familles),
                            borderColor: 'rgba(245, 87, 108, 1)',
                            backgroundColor: 'rgba(245, 87, 108, 0.1)',
                            tension: 0.4
                          },
                          {
                            label: 'Total Combiné',
                            data: evolutionCombineeData.data.map(d => d.totalMembres),
                            borderColor: 'rgba(67, 233, 123, 1)',
                            backgroundColor: 'rgba(67, 233, 123, 0.1)',
                            tension: 0.4,
                            borderWidth: 3
                          }
                        ]
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: true,
                        plugins: {
                          legend: {
                            position: 'top',
                          },
                          title: {
                            display: false
                          }
                        },
                        scales: {
                          y: {
                            beginAtZero: true
                          }
                        }
                      }}
                    />
                  </div>

                  {/* Graphique 2: Évolution des paiements */}
                  <div style={{ marginBottom: '30px', backgroundColor: 'white', padding: '20px', borderRadius: '10px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    <h4 style={{ marginBottom: '20px', color: '#2c3e50' }}>Évolution des Paiements</h4>
                    <Bar
                      data={{
                        labels: evolutionCombineeData.data.map(d => d.moisLabel),
                        datasets: [
                          {
                            label: 'Montant des Paiements (CDF)',
                            data: evolutionCombineeData.data.map(d => d.montantPaiements),
                            backgroundColor: 'rgba(67, 233, 123, 0.7)',
                            borderColor: 'rgba(67, 233, 123, 1)',
                            borderWidth: 1
                          }
                        ]
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: true,
                        plugins: {
                          legend: {
                            position: 'top',
                          }
                        },
                        scales: {
                          y: {
                            beginAtZero: true,
                            ticks: {
                              callback: function(value) {
                                return value.toLocaleString('fr-FR') + ' CDF';
                              }
                            }
                          }
                        }
                      }}
                    />
                  </div>

                  {/* Graphique 3: Cotisations payées vs impayées */}
                  <div style={{ marginBottom: '30px', backgroundColor: 'white', padding: '20px', borderRadius: '10px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    <h4 style={{ marginBottom: '20px', color: '#2c3e50' }}>Cotisations Payées vs Impayées</h4>
                    <Bar
                      data={{
                        labels: evolutionCombineeData.data.map(d => d.moisLabel),
                        datasets: [
                          {
                            label: 'Cotisations Payées',
                            data: evolutionCombineeData.data.map(d => d.cotisationsPayees),
                            backgroundColor: 'rgba(40, 167, 69, 0.7)',
                            borderColor: 'rgba(40, 167, 69, 1)',
                            borderWidth: 1
                          },
                          {
                            label: 'Cotisations Impayées',
                            data: evolutionCombineeData.data.map(d => d.cotisationsImpayees),
                            backgroundColor: 'rgba(220, 53, 69, 0.7)',
                            borderColor: 'rgba(220, 53, 69, 1)',
                            borderWidth: 1
                          }
                        ]
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: true,
                        plugins: {
                          legend: {
                            position: 'top',
                          }
                        },
                        scales: {
                          y: {
                            beginAtZero: true
                          }
                        }
                      }}
                    />
                  </div>

                  {/* Tableau récapitulatif */}
                  <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '10px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    <h4 style={{ marginBottom: '20px', color: '#2c3e50' }}>Tableau Récapitulatif</h4>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Mois</th>
                            <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Membres</th>
                            <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Familles</th>
                            <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Membres Famille</th>
                            <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Total</th>
                            <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Paiements (CDF)</th>
                            <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Cot. Payées</th>
                            <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Cot. Impayées</th>
                          </tr>
                        </thead>
                        <tbody>
                          {evolutionCombineeData.data.map((item, index) => (
                            <tr key={index} style={{ borderBottom: '1px solid #dee2e6' }}>
                              <td style={{ padding: '12px', fontWeight: '600' }}>{item.moisLabel}</td>
                              <td style={{ padding: '12px', textAlign: 'center' }}>{item.membres}</td>
                              <td style={{ padding: '12px', textAlign: 'center' }}>{item.familles}</td>
                              <td style={{ padding: '12px', textAlign: 'center' }}>{item.membresFamille}</td>
                              <td style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>{item.totalMembres}</td>
                              <td style={{ padding: '12px', textAlign: 'right' }}>
                                {parseFloat(item.montantPaiements).toLocaleString('fr-FR')} CDF
                              </td>
                              <td style={{ padding: '12px', textAlign: 'center', color: '#28a745' }}>{item.cotisationsPayees}</td>
                              <td style={{ padding: '12px', textAlign: 'center', color: '#dc3545' }}>{item.cotisationsImpayees}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  <p>Aucune donnée disponible pour la période sélectionnée</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'admins' && (
          <div>
            {/* Sous-onglets pour Admins et Récolteurs */}
            <div style={{ 
              display: 'flex', 
              gap: '10px', 
              marginBottom: '20px',
              borderBottom: '2px solid #e0e0e0',
              paddingBottom: '10px'
            }}>
              <button
                className={`btn ${adminView === 'admins' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setAdminView('admins')}
                style={{ minWidth: '150px' }}
              >
                👤 Administrateurs
              </button>
              <button
                className={`btn ${adminView === 'recolteurs' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setAdminView('recolteurs')}
                style={{ minWidth: '150px' }}
              >
                👷 Récolteurs
              </button>
              <button
                className={`btn ${adminView === 'gerers' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setAdminView('gerers')}
                style={{ minWidth: '200px' }}
              >
                🖨️ Gestion - Impression & Rapports
              </button>
            </div>

            {/* Section Administrateurs */}
            {adminView === 'admins' && (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
                <h2 style={{ flex: '1 1 100%', marginBottom: '10px' }}>Gestion des administrateurs</h2>
                <button 
                  className="btn btn-primary"
                  onClick={() => {
                    setEditingAdmin(null);
                    setAdminForm({ username: '', password: '', nom_complet: '' });
                    setShowAdminForm(true);
                  }}
                  style={{ minWidth: '150px' }}
                >
                  + Nouvel Administrateur
                </button>
              </div>

              {showAdminForm && (
                <div className="card" style={{ marginBottom: '20px', background: '#f8f9fa' }}>
                  <h3>{editingAdmin ? 'Modifier l\'administrateur' : 'Créer un administrateur'}</h3>
                  <form onSubmit={handleAdminSubmit}>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Nom d'utilisateur *</label>
                        <input
                          type="text"
                          value={adminForm.username}
                          onChange={(e) => setAdminForm({ ...adminForm, username: e.target.value })}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Mot de passe {editingAdmin ? '(laisser vide pour ne pas changer)' : '*'}</label>
                        <input
                          type="password"
                          value={adminForm.password}
                          onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
                          required={!editingAdmin}
                        />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Nom complet *</label>
                        <input
                          type="text"
                          value={adminForm.nom_complet}
                          onChange={(e) => setAdminForm({ ...adminForm, nom_complet: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <button type="submit" className="btn btn-primary">Enregistrer</button>
                      <button 
                        type="button" 
                        className="btn btn-secondary"
                        onClick={() => {
                          setShowAdminForm(false);
                          setEditingAdmin(null);
                        }}
                        style={{ marginLeft: '10px' }}
                      >
                        Annuler
                      </button>
                    </div>
                  </form>
                </div>
              )}

              <div style={{ marginTop: '20px' }}>
                <h3 style={{ marginBottom: '15px' }}>Liste des administrateurs ({admins.length})</h3>
                <div style={{ marginBottom: '15px' }}>
                  <input
                    type="text"
                    placeholder="🔍 Rechercher un administrateur (nom, username)..."
                    value={searchAdmins}
                    onChange={(e) => {
                      setSearchAdmins(e.target.value);
                      setCurrentPageAdmins(1); // Réinitialiser à la page 1 lors de la recherche
                    }}
                    style={{
                      width: '100%',
                      maxWidth: '500px',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px'
                    }}
                  />
                  {searchAdmins && (
                    <button
                      onClick={() => setSearchAdmins('')}
                      style={{
                        marginLeft: '10px',
                        padding: '10px 15px',
                        background: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      ✕ Effacer
                    </button>
                  )}
                </div>
                <div className="table-wrapper" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th style={{ whiteSpace: 'nowrap' }}>Nom d'utilisateur</th>
                      <th style={{ whiteSpace: 'nowrap' }}>Nom complet</th>
                      <th style={{ whiteSpace: 'nowrap' }}>Statut</th>
                      <th style={{ whiteSpace: 'nowrap' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const filteredAdmins = admins.filter(adminItem => {
                        if (!searchAdmins) return true;
                        const search = searchAdmins.toLowerCase();
                        return (
                          adminItem.username?.toLowerCase().includes(search) ||
                          adminItem.nom_complet?.toLowerCase().includes(search)
                        );
                      });
                      const paginatedAdmins = paginate(filteredAdmins, currentPageAdmins, itemsPerPage);
                      return paginatedAdmins.map(adminItem => (
                      <tr key={adminItem.id}>
                        <td data-label="Nom d'utilisateur"><strong>{adminItem.username}</strong></td>
                        <td data-label="Nom complet">{adminItem.nom_complet}</td>
                        <td data-label="Statut">
                          {adminItem.is_principal ? (
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '600',
                              backgroundColor: '#d4edda',
                              color: '#155724',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '5px'
                            }}>
                              👑 Principal
                            </span>
                          ) : (
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '600',
                              backgroundColor: '#e7f3ff',
                              color: '#2c3e50',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '5px'
                            }}>
                              👤 Secondaire
                            </span>
                          )}
                        </td>
                        <td data-label="Actions">
                          {(() => {
                            // Vérifier si l'admin connecté est principal (support booléen et numérique)
                            const isPrincipal = admin.is_principal === true || admin.is_principal === 1 || admin.is_principal === '1';
                            console.log('🔍 Vérification is_principal:', { 
                              'admin.is_principal': admin.is_principal, 
                              'typeof': typeof admin.is_principal,
                              'isPrincipal': isPrincipal,
                              'admin.id': admin.id,
                              'admin.username': admin.username
                            });
                            return isPrincipal;
                          })() ? (
                            <>
                              <button 
                                className="btn btn-secondary"
                                onClick={() => handleEditAdmin(adminItem)}
                                style={{ marginRight: '10px', fontSize: '14px', padding: '5px 10px' }}
                              >
                                Modifier
                              </button>
                              {adminItem.id !== admin.id && !(adminItem.is_principal === true || adminItem.is_principal === 1 || adminItem.is_principal === '1') && (
                                <button 
                                  className="btn btn-danger"
                                  onClick={() => handleDeleteAdmin(adminItem.id)}
                                  style={{ fontSize: '14px', padding: '5px 10px' }}
                                >
                                  Supprimer
                                </button>
                              )}
                            </>
                          ) : (
                            <span style={{ color: '#999', fontStyle: 'italic', fontSize: '13px' }}>
                              Seul l'administrateur principal peut modifier/supprimer
                            </span>
                          )}
                        </td>
                      </tr>
                      ));
                    })()}
                    {(() => {
                      const filteredAdmins = admins.filter(adminItem => {
                        if (!searchAdmins) return true;
                        const search = searchAdmins.toLowerCase();
                        return (
                          adminItem.username?.toLowerCase().includes(search) ||
                          adminItem.nom_complet?.toLowerCase().includes(search)
                        );
                      });
                      if (filteredAdmins.length === 0) {
                        return (
                          <tr>
                            <td colSpan="5" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                              {searchAdmins ? 'Aucun administrateur trouvé' : 'Aucun administrateur enregistré. Cliquez sur "Nouvel Administrateur" pour en créer.'}
                            </td>
                          </tr>
                        );
                      }
                      return null;
                    })()}
                    {admins.length === 0 && !searchAdmins && (
                      <tr>
                        <td colSpan="5" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                          Aucun administrateur enregistré. Cliquez sur "Nouvel Administrateur" pour en créer.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                </div>
                {(() => {
                  const filteredAdmins = admins.filter(adminItem => {
                    if (!searchAdmins) return true;
                    const search = searchAdmins.toLowerCase();
                    return (
                      adminItem.username?.toLowerCase().includes(search) ||
                      adminItem.nom_complet?.toLowerCase().includes(search)
                    );
                  });
                  const totalPages = getTotalPages(filteredAdmins, itemsPerPage);
                  return (
                    <Pagination 
                      currentPage={currentPageAdmins} 
                      totalPages={totalPages} 
                      onPageChange={(page) => {
                        setCurrentPageAdmins(page);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }} 
                    />
                  );
                })()}
              </div>
            </div>
            )}

            {/* Section Récolteurs */}
            {adminView === 'recolteurs' && (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
                <h2 style={{ flex: '1 1 100%', marginBottom: '10px' }}>Gestion des récolteurs</h2>
                <button 
                  className="btn btn-success"
                  onClick={() => {
                    setEditingRecolteur(null);
                    // Générer automatiquement username, password et matricule
                    const existingUsernames = recolteurs.map(r => r.username).filter(Boolean);
                    const existingMatricules = recolteurs.map(r => r.matricule).filter(Boolean);
                    const username = generateRecolteurUsername(existingUsernames);
                    const password = generateRecolteurPassword();
                    const matricule = generateRecolteurMatricule(existingMatricules);
                    
                    setRecolteurForm({ 
                      username, 
                      password, 
                      nom_complet: '', 
                      association: '', 
                      commune: '', 
                      matricule,
                      telephone: ''
                    });
                    setShowRecolteurForm(true);
                  }}
                  style={{ minWidth: '150px' }}
                >
                  + Nouveau Récolteur
                </button>
              </div>

              {showRecolteurForm && (
                <div className="card" style={{ marginBottom: '20px', background: '#f8f9fa' }}>
                  <h3>{editingRecolteur ? 'Modifier le récolteur' : 'Créer un récolteur'}</h3>
                  <form onSubmit={handleRecolteurSubmit}>
                    {/* Section 1: Champs à remplir par l'administrateur */}
                    <div style={{ 
                      marginBottom: '30px', 
                      padding: '20px', 
                      backgroundColor: '#fff', 
                      borderRadius: '8px',
                      border: '2px solid #e0e0e0'
                    }}>
                      <h4 style={{ marginTop: 0, marginBottom: '20px', color: '#2c3e50', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span>📝</span>
                        <span>Informations à compléter</span>
                      </h4>
                      
                      <div className="form-row">
                        <div className="form-group" style={{ flex: '1 1 100%' }}>
                          <label>Nom complet *</label>
                          <input
                            type="text"
                            value={recolteurForm.nom_complet}
                            onChange={(e) => setRecolteurForm({ ...recolteurForm, nom_complet: e.target.value })}
                            required
                            placeholder="Ex: Jean KABONGO"
                          />
                        </div>
                      </div>
                      
                      <div className="form-row">
                        <div className="form-group">
                          <label>Association</label>
                          <input
                            type="text"
                            value={recolteurForm.association}
                            onChange={(e) => setRecolteurForm({ ...recolteurForm, association: e.target.value })}
                            placeholder="Nom de l'association"
                          />
                        </div>
                        <div className="form-group">
                          <label>Commune</label>
                          <select
                            value={recolteurForm.commune}
                            onChange={(e) => setRecolteurForm({ ...recolteurForm, commune: e.target.value })}
                          >
                            <option value="">Sélectionner une commune</option>
                            <option value="MAKISO">MAKISO</option>
                            <option value="KABONDO">KABONDO</option>
                            <option value="MANGOBO">MANGOBO</option>
                            <option value="LUBUNGA">LUBUNGA</option>
                            <option value="TSHOPO">TSHOPO</option>
                            <option value="KISANGANI">KISANGANI</option>
                          </select>
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>Téléphone</label>
                          <input
                            type="tel"
                            value={recolteurForm.telephone}
                            onChange={(e) => setRecolteurForm({ ...recolteurForm, telephone: e.target.value })}
                            placeholder="Ex: +243 900 000 000"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Section 2: Identifiants générés automatiquement */}
                    {!editingRecolteur && (
                      <div style={{ 
                        marginBottom: '30px', 
                        padding: '20px', 
                        backgroundColor: '#e8f5e9', 
                        borderRadius: '8px',
                        border: '2px solid #4caf50'
                      }}>
                        <h4 style={{ marginTop: 0, marginBottom: '20px', color: '#2e7d32', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span>🔐</span>
                          <span>Identifiants générés automatiquement</span>
                        </h4>
                        <div style={{ 
                          padding: '15px', 
                          backgroundColor: '#fff', 
                          borderRadius: '6px',
                          marginBottom: '15px',
                          border: '1px solid #c8e6c9'
                        }}>
                          <div style={{ fontSize: '13px', color: '#666', marginBottom: '10px', fontStyle: 'italic' }}>
                            ⚙️ Ces identifiants ont été générés automatiquement. Notez-les pour le récolteur.
                          </div>
                        </div>
                        
                        <div className="form-row">
                          <div className="form-group">
                            <label style={{ color: '#2e7d32', fontWeight: 'bold' }}>
                              Nom d'utilisateur * 
                              <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#666', marginLeft: '5px' }}>
                                (Généré automatiquement)
                              </span>
                            </label>
                            <div style={{ position: 'relative' }}>
                              <input
                                type="text"
                                value={recolteurForm.username}
                                readOnly
                                required
                                style={{ 
                                  backgroundColor: '#f0f0f0',
                                  cursor: 'not-allowed',
                                  fontFamily: 'monospace',
                                  fontWeight: 'bold',
                                  fontSize: '16px',
                                  color: '#2e7d32'
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  navigator.clipboard.writeText(recolteurForm.username);
                                  notificationService.success('Copié', 'Nom d\'utilisateur copié');
                                }}
                                style={{
                                  position: 'absolute',
                                  right: '10px',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  background: '#4caf50',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '4px 8px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                                title="Copier le nom d'utilisateur"
                              >
                                📋 Copier
                              </button>
                            </div>
                          </div>
                          <div className="form-group">
                            <label style={{ color: '#2e7d32', fontWeight: 'bold' }}>
                              Mot de passe * 
                              <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#666', marginLeft: '5px' }}>
                                (Généré automatiquement)
                              </span>
                            </label>
                            <div style={{ position: 'relative' }}>
                              <input
                                type="text"
                                value={recolteurForm.password}
                                readOnly
                                required
                                style={{ 
                                  backgroundColor: '#f0f0f0',
                                  cursor: 'not-allowed',
                                  fontFamily: 'monospace',
                                  fontWeight: 'bold',
                                  letterSpacing: '3px',
                                  fontSize: '18px',
                                  color: '#d32f2f'
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  navigator.clipboard.writeText(recolteurForm.password);
                                  notificationService.success('Copié', 'Mot de passe copié');
                                }}
                                style={{
                                  position: 'absolute',
                                  right: '10px',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  background: '#4caf50',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '4px 8px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                                title="Copier le mot de passe"
                              >
                                📋 Copier
                              </button>
                            </div>
                          </div>
                        </div>
                        
                        <div className="form-row">
                          <div className="form-group">
                            <label style={{ color: '#2e7d32', fontWeight: 'bold' }}>
                              Matricule 
                              <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#666', marginLeft: '5px' }}>
                                (Généré automatiquement)
                              </span>
                            </label>
                            <div style={{ position: 'relative' }}>
                              <input
                                type="text"
                                value={recolteurForm.matricule}
                                readOnly
                                style={{ 
                                  backgroundColor: '#f0f0f0',
                                  cursor: 'not-allowed',
                                  fontFamily: 'monospace',
                                  fontWeight: 'bold',
                                  fontSize: '16px',
                                  color: '#2e7d32'
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  navigator.clipboard.writeText(recolteurForm.matricule);
                                  notificationService.success('Copié', 'Matricule copié');
                                }}
                                style={{
                                  position: 'absolute',
                                  right: '10px',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  background: '#4caf50',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '4px 8px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                                title="Copier le matricule"
                              >
                                📋 Copier
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Section pour la modification */}
                    {editingRecolteur && (
                      <div style={{ 
                        marginBottom: '30px', 
                        padding: '20px', 
                        backgroundColor: '#fff3cd', 
                        borderRadius: '8px',
                        border: '2px solid #ffc107'
                      }}>
                        <h4 style={{ marginTop: 0, marginBottom: '20px', color: '#856404', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span>🔧</span>
                          <span>Modifier les identifiants (optionnel)</span>
                        </h4>
                        
                        <div className="form-row">
                          <div className="form-group">
                            <label>Nom d'utilisateur *</label>
                            <input
                              type="text"
                              value={recolteurForm.username}
                              readOnly
                              required
                              style={{ 
                                backgroundColor: '#f0f0f0',
                                cursor: 'not-allowed'
                              }}
                            />
                            <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
                              Le nom d'utilisateur ne peut pas être modifié
                            </small>
                          </div>
                          <div className="form-group">
                            <label>Mot de passe (laisser vide pour ne pas changer)</label>
                            <input
                              type="password"
                              value={recolteurForm.password}
                              onChange={(e) => setRecolteurForm({ ...recolteurForm, password: e.target.value })}
                              placeholder="Nouveau mot de passe"
                            />
                          </div>
                        </div>
                        
                        <div className="form-row">
                          <div className="form-group">
                            <label>Matricule</label>
                            <input
                              type="text"
                              value={recolteurForm.matricule}
                              onChange={(e) => setRecolteurForm({ ...recolteurForm, matricule: e.target.value })}
                              placeholder="RCL-001"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    <div>
                      <button type="submit" className="btn btn-primary">Enregistrer</button>
                      <button 
                        type="button" 
                        className="btn btn-secondary"
                        onClick={() => {
                          setShowRecolteurForm(false);
                          setEditingRecolteur(null);
                        }}
                        style={{ marginLeft: '10px' }}
                      >
                        Annuler
                      </button>
                    </div>
                  </form>
                </div>
              )}

              <div style={{ marginTop: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '15px' }}>
                  <h3 style={{ margin: 0 }}>Liste des récolteurs ({recolteurs.length})</h3>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', width: '100%', alignItems: 'center' }}>
                    <div style={{ position: 'relative', flex: '1', minWidth: '200px', maxWidth: '300px' }}>
                      <input
                        type="text"
                        placeholder="🔍 Rechercher un récolteur..."
                        value={searchRecolteurs}
                        onChange={(e) => {
                          setSearchRecolteurs(e.target.value);
                          setCurrentPageRecolteurs(1);
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 40px 10px 15px',
                          border: '2px solid #ddd',
                          borderRadius: '5px',
                          fontSize: '14px',
                          outline: 'none',
                          transition: 'border-color 0.3s'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#2c3e50'}
                        onBlur={(e) => e.target.style.borderColor = '#ddd'}
                      />
                      {searchRecolteurs && (
                        <button
                          onClick={() => setSearchRecolteurs('')}
                          style={{
                            position: 'absolute',
                            right: '10px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '18px',
                            color: '#999'
                          }}
                          title="Effacer la recherche"
                        >
                          ×
                        </button>
                      )}
                    </div>
                    <div style={{ minWidth: '200px', maxWidth: '300px' }}>
                      <select
                        value={selectedAssociationRecolteurs}
                        onChange={(e) => {
                          setSelectedAssociationRecolteurs(e.target.value);
                          setCurrentPageRecolteurs(1);
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 15px',
                          border: '2px solid #ddd',
                          borderRadius: '5px',
                          fontSize: '14px',
                          outline: 'none',
                          transition: 'border-color 0.3s',
                          backgroundColor: 'white'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#2c3e50'}
                        onBlur={(e) => e.target.style.borderColor = '#ddd'}
                      >
                        <option value="">Toutes les associations</option>
                        {associationsRecolteurs.map(asso => (
                          <option key={asso} value={asso}>{asso}</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      <button
                        className="btn btn-primary"
                        onClick={handlePrintRecolteurs}
                        style={{ fontSize: '14px', padding: '8px 15px' }}
                        title="Imprimer la liste"
                      >
                        🖨️ Imprimer
                      </button>
                      <button
                        className="btn btn-success"
                        onClick={handleDownloadPDFRecolteurs}
                        style={{ fontSize: '14px', padding: '8px 15px' }}
                        title="Télécharger en PDF"
                      >
                        📥 Télécharger PDF
                      </button>
                    </div>
                  </div>
                </div>
                {(searchRecolteurs || selectedAssociationRecolteurs) && (
                  <div style={{ marginBottom: '15px', color: '#666', fontSize: '14px' }}>
                    {(() => {
                      const filtered = recolteurs.filter(r => {
                        if (selectedAssociationRecolteurs && r.association !== selectedAssociationRecolteurs) return false;
                        if (!searchRecolteurs) return true;
                        const search = searchRecolteurs.toLowerCase();
                        return (
                          r.username?.toLowerCase().includes(search) ||
                          r.nom_complet?.toLowerCase().includes(search) ||
                          r.telephone?.toLowerCase().includes(search) ||
                          r.matricule?.toLowerCase().includes(search) ||
                          r.association?.toLowerCase().includes(search) ||
                          r.commune?.toLowerCase().includes(search) ||
                          r.centre_sante?.toLowerCase().includes(search)
                        );
                      });
                      return `${filtered.length} récolteur(s) ${selectedAssociationRecolteurs ? `de l'association "${selectedAssociationRecolteurs}"` : ''} ${searchRecolteurs ? 'trouvé(s)' : ''}`;
                    })()}
                  </div>
                )}
                <div className="table-wrapper" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table className="table" style={{ tableLayout: 'auto', width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={{ whiteSpace: 'nowrap' }}>Nom d'utilisateur</th>
                      <th style={{ whiteSpace: 'nowrap' }}>Mot de passe</th>
                      <th style={{ whiteSpace: 'nowrap' }}>Nom complet</th>
                      <th style={{ whiteSpace: 'nowrap' }}>Téléphone</th>
                      <th style={{ whiteSpace: 'nowrap' }}>Matricule</th>
                      <th style={{ whiteSpace: 'nowrap' }}>Association</th>
                      <th style={{ whiteSpace: 'nowrap' }}>Commune</th>
                      <th style={{ whiteSpace: 'nowrap' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const filteredRecolteurs = recolteurs.filter(recolteur => {
                        // Filtre par association
                        if (selectedAssociationRecolteurs && recolteur.association !== selectedAssociationRecolteurs) {
                          return false;
                        }
                        
                        // Filtre par recherche
                        if (!searchRecolteurs) return true;
                        const search = searchRecolteurs.toLowerCase();
                        return (
                          recolteur.username?.toLowerCase().includes(search) ||
                          recolteur.nom_complet?.toLowerCase().includes(search) ||
                          recolteur.telephone?.toLowerCase().includes(search) ||
                          recolteur.matricule?.toLowerCase().includes(search) ||
                          recolteur.association?.toLowerCase().includes(search) ||
                          recolteur.commune?.toLowerCase().includes(search) ||
                          recolteur.centre_sante?.toLowerCase().includes(search)
                        );
                      });
                      if (filteredRecolteurs.length === 0) {
                        return (
                          <tr key="no-recolteurs">
                            <td colSpan="7" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                              {searchRecolteurs ? 'Aucun récolteur trouvé' : 'Aucun récolteur enregistré. Cliquez sur "Nouveau Récolteur" pour en créer.'}
                            </td>
                          </tr>
                        );
                      }
                      const paginatedRecolteurs = paginate(filteredRecolteurs, currentPageRecolteurs, itemsPerPage);
                      return paginatedRecolteurs.map(recolteur => (
                        <tr key={recolteur.id}>
                        <td data-label="Nom d'utilisateur" style={{ whiteSpace: 'nowrap' }}><strong>{recolteur.username}</strong></td>
                        <td data-label="Mot de passe" style={{ whiteSpace: 'nowrap' }}>
                          {recolteur.password_plain ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
                              <code style={{ 
                                fontFamily: 'monospace', 
                                fontWeight: 'bold', 
                                letterSpacing: '2px',
                                fontSize: '14px',
                                color: '#d32f2f',
                                backgroundColor: '#ffebee',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                whiteSpace: 'nowrap'
                              }}>
                                {recolteur.password_plain}
                              </code>
                              <button
                                type="button"
                                onClick={() => {
                                  navigator.clipboard.writeText(recolteur.password_plain);
                                  notificationService.success('Copié', 'Mot de passe copié');
                                }}
                                style={{
                                  background: '#4caf50',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '4px 8px',
                                  cursor: 'pointer',
                                  fontSize: '11px',
                                  whiteSpace: 'nowrap',
                                  flexShrink: 0
                                }}
                                title="Copier le mot de passe"
                              >
                                {'📋 Copier'}
                              </button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', whiteSpace: 'nowrap' }}>
                              <span style={{ color: '#999', fontStyle: 'italic', fontSize: '12px' }}>Non disponible</span>
                              <small style={{ color: '#ff9800', fontSize: '11px' }}>
                                (Créé avant l'ajout de cette fonctionnalité)
                              </small>
                            </div>
                          )}
                        </td>
                        <td data-label="Nom complet" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '200px' }} title={recolteur.nom_complet}>{recolteur.nom_complet}</td>
                        <td data-label="Téléphone" style={{ whiteSpace: 'nowrap' }}>{recolteur.telephone || '-'}</td>
                        <td data-label="Matricule" style={{ whiteSpace: 'nowrap' }}>{recolteur.matricule || '-'}</td>
                        <td data-label="Association" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '200px' }} title={recolteur.association || ''}>{recolteur.association || '-'}</td>
                        <td data-label="Commune" style={{ whiteSpace: 'nowrap' }}>{recolteur.commune || '-'}</td>
                        <td data-label="Actions" style={{ whiteSpace: 'nowrap' }}>
                          <select
                            onChange={async (e) => {
                              const action = e.target.value;
                              if (action === 'details') {
                                if (loadingRecolteurStats === recolteur.id) return;
                              setLoadingRecolteurStats(recolteur.id);
                              try {
                                console.log('📊 Chargement des statistiques pour le récolteur:', recolteur.id);
                                
                                const timeoutPromise = new Promise((_, reject) => {
                                    setTimeout(() => reject(new Error('Timeout: La requête a pris trop de temps')), 30000);
                                });
                                
                                const apiPromise = getRecolteurStats(recolteur.id);
                                const response = await Promise.race([apiPromise, timeoutPromise]);
                                
                                console.log('✅ Statistiques reçues:', response.data);
                                if (response && response.data) {
                                  setRecolteurStats(response.data);
                                  setSelectedRecolteur(recolteur);
                                } else {
                                  throw new Error('Réponse invalide du serveur');
                                }
                              } catch (error) {
                                console.error('❌ Erreur lors du chargement des statistiques:', error);
                                let errorMessage = 'Impossible de charger les statistiques du récolteur';
                                if (error.message.includes('Timeout')) {
                                  errorMessage = 'La requête a pris trop de temps. Vérifiez votre connexion et réessayez.';
                                } else if (error.response?.status === 401 || error.response?.status === 403) {
                                  errorMessage = 'Vous n\'êtes pas autorisé à accéder à ces statistiques.';
                                } else if (error.response?.data?.message) {
                                  errorMessage = error.response.data.message;
                                } else if (error.message) {
                                  errorMessage = error.message;
                                }
                                notificationService.error('Erreur', errorMessage);
                                setSelectedRecolteur(null);
                                setRecolteurStats(null);
                              } finally {
                                setLoadingRecolteurStats(null);
                              }
                              } else if (action === 'modifier') {
                                handleEditRecolteur(recolteur);
                              } else if (action === 'supprimer') {
                                handleDeleteRecolteur(recolteur.id);
                              }
                              // Réinitialiser la sélection
                              e.target.value = '';
                            }}
                            style={{
                              padding: '8px 12px',
                              fontSize: '13px',
                              borderRadius: '5px',
                              border: '1px solid #2c3e50',
                              backgroundColor: 'white',
                              cursor: 'pointer',
                              minWidth: '180px',
                              fontWeight: '500',
                              color: '#2c3e50'
                            }}
                            defaultValue=""
                            title="Sélectionner une action"
                            disabled={loadingRecolteurStats === recolteur.id}
                          >
                            <option value="" disabled hidden>⚙️ Actions...</option>
                            <option value="details" disabled={loadingRecolteurStats === recolteur.id}>
                              {loadingRecolteurStats === recolteur.id ? '⏳ Chargement...' : '📊 Voir Détails/Statistiques'}
                            </option>
                            {isPrincipal() && <option value="modifier">✏️ Modifier</option>}
                            {isPrincipal() && <option value="supprimer" style={{ color: '#dc3545', fontWeight: '600' }}>🗑️ Supprimer</option>}
                          </select>
                </td>
              </tr>
                      ));
                    })()}
                  </tbody>
                </table>
                </div>
                {(() => {
                  const filteredGerers = gerers.filter(gerer => {
                    if (!searchGerers) return true;
                    const search = searchGerers.toLowerCase();
                    return (
                      gerer.username?.toLowerCase().includes(search) ||
                      gerer.nom_complet?.toLowerCase().includes(search)
                    );
                  });
                  const totalPages = getTotalPages(filteredGerers, itemsPerPage);
                  return (
                    <Pagination 
                      currentPage={currentPageGerers} 
                      totalPages={totalPages} 
                      onPageChange={(page) => {
                        setCurrentPageGerers(page);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }} 
                    />
                  );
                })()}
              </div>
            </div>
            )}
          </div>
        )}

            {/* Section Gestionnaires (Gerer) */}
            {adminView === 'gerers' && (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
                <h2 style={{ flex: '1 1 100%', marginBottom: '10px' }}>Gestion des utilisateurs - Impression & Rapports</h2>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button 
                    className="btn btn-primary"
                    onClick={() => {
                      setEditingGerer(null);
                      setGererForm({ username: '', password: '', nom_complet: '' });
                      setShowGererForm(true);
                    }}
                    style={{ minWidth: '200px' }}
                  >
                    + Nouvel Utilisateur Gestionnaire
                  </button>
                  <button 
                    className="btn btn-success"
                    onClick={() => {
                      setEditingFinances(null);
                      setFinancesForm({ username: '', password: '', nom_complet: '' });
                      setShowFinancesForm(true);
                    }}
                    style={{ minWidth: '200px' }}
                  >
                    + Nouvel Utilisateur Finances
                  </button>
                </div>
              </div>

              {showGererForm && (
                <div className="card" style={{ marginBottom: '20px', background: '#f8f9fa' }}>
                  <h3>{editingGerer ? 'Modifier le gestionnaire' : 'Créer un gestionnaire'}</h3>
                  <form onSubmit={handleGererSubmit}>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Nom d'utilisateur *</label>
                        <input
                          type="text"
                          value={gererForm.username}
                          onChange={(e) => setGererForm({ ...gererForm, username: e.target.value })}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Mot de passe {editingGerer ? '(laisser vide pour ne pas changer)' : '*'}</label>
                        <input
                          type="password"
                          value={gererForm.password}
                          onChange={(e) => setGererForm({ ...gererForm, password: e.target.value })}
                          required={!editingGerer}
                        />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Nom complet *</label>
                        <input
                          type="text"
                          value={gererForm.nom_complet}
                          onChange={(e) => setGererForm({ ...gererForm, nom_complet: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <button type="submit" className="btn btn-primary">Enregistrer</button>
                      <button 
                        type="button" 
                        className="btn btn-secondary"
                        onClick={() => {
                          setShowGererForm(false);
                          setEditingGerer(null);
                        }}
                        style={{ marginLeft: '10px' }}
                      >
                        Annuler
                      </button>
                    </div>
                  </form>
                </div>
              )}

              <div style={{ marginTop: '20px' }}>
                <h3 style={{ marginBottom: '15px' }}>Liste des gestionnaires ({gerers.length})</h3>
                <div style={{ marginBottom: '15px' }}>
                  <input
                    type="text"
                    placeholder="🔍 Rechercher un gestionnaire (nom, username)..."
                    value={searchGerers}
                    onChange={(e) => setSearchGerers(e.target.value)}
                    style={{
                      width: '100%',
                      maxWidth: '500px',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px'
                    }}
                  />
                  {searchGerers && (
                    <button
                      onClick={() => setSearchGerers('')}
                      style={{
                        marginLeft: '10px',
                        padding: '10px 15px',
                        background: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      ✕ Effacer
                    </button>
                  )}
                </div>
                <div className="table-wrapper" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th style={{ whiteSpace: 'nowrap' }}>Nom d'utilisateur</th>
                      <th style={{ whiteSpace: 'nowrap' }}>Nom complet</th>
                      <th style={{ whiteSpace: 'nowrap' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const filteredGerers = gerers.filter(gerer => {
                        if (!searchGerers) return true;
                        const search = searchGerers.toLowerCase();
                        return (
                          gerer.username?.toLowerCase().includes(search) ||
                          gerer.nom_complet?.toLowerCase().includes(search)
                        );
                      });
                      const paginatedGerers = paginate(filteredGerers, currentPageGerers, itemsPerPage);
                      return paginatedGerers.map(gerer => (
                      <tr key={gerer.id}>
                        <td data-label="Nom d'utilisateur"><strong>{gerer.username}</strong></td>
                        <td data-label="Nom complet">{gerer.nom_complet}</td>
                        
                        <td data-label="Actions">
                          <button 
                            className="btn btn-secondary"
                            onClick={() => handleEditGerer(gerer)}
                            style={{ marginRight: '10px', fontSize: '14px', padding: '5px 10px' }}
                          >
                            Modifier
                          </button>
                          <button 
                            className="btn btn-danger"
                            onClick={() => handleDeleteGerer(gerer.id)}
                            style={{ fontSize: '14px', padding: '5px 10px' }}
                          >
                            Supprimer
                          </button>
                        </td>
                      </tr>
                      ));
                    })()}
                    {(() => {
                      const filteredGerers = gerers.filter(gerer => {
                        if (!searchGerers) return true;
                        const search = searchGerers.toLowerCase();
                        return (
                          gerer.username?.toLowerCase().includes(search) ||
                          gerer.nom_complet?.toLowerCase().includes(search)
                        );
                      });
                      if (filteredGerers.length === 0) {
                        return (
                          <tr>
                            <td colSpan="4" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                              {searchGerers ? 'Aucun gestionnaire trouvé' : 'Aucun gestionnaire enregistré. Cliquez sur "Nouvel Utilisateur Gestionnaire" pour en créer.'}
                            </td>
                          </tr>
                        );
                      }
                      return null;
                    })()}
                    {gerers.length === 0 && !searchGerers && (
                      <tr>
                        <td colSpan="4" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                          Aucun gestionnaire enregistré. Cliquez sur "Nouvel Utilisateur Gestionnaire" pour en créer.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                </div>
              </div>

              {/* Section Utilisateurs Finances */}
              <div style={{ marginTop: '40px', paddingTop: '30px', borderTop: '2px solid #ddd' }}>
                <h3 style={{ marginBottom: '20px', color: '#28a745' }}>💰 Utilisateurs Finances</h3>
                
                {showFinancesForm && (
                  <div className="card" style={{ marginBottom: '20px', background: '#f8f9fa' }}>
                    <h3>{editingFinances ? 'Modifier l\'utilisateur Finances' : 'Créer un utilisateur Finances'}</h3>
                    <form onSubmit={handleFinancesSubmit}>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Nom d'utilisateur *</label>
                          <input
                            type="text"
                            value={financesForm.username}
                            onChange={(e) => setFinancesForm({ ...financesForm, username: e.target.value })}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Mot de passe {editingFinances ? '(laisser vide pour ne pas changer)' : '*'}</label>
                          <input
                            type="password"
                            value={financesForm.password}
                            onChange={(e) => setFinancesForm({ ...financesForm, password: e.target.value })}
                            required={!editingFinances}
                          />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Nom complet *</label>
                          <input
                            type="text"
                            value={financesForm.nom_complet}
                            onChange={(e) => setFinancesForm({ ...financesForm, nom_complet: e.target.value })}
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <button type="submit" className="btn btn-success">Enregistrer</button>
                        <button 
                          type="button" 
                          className="btn btn-secondary"
                          onClick={() => {
                            setShowFinancesForm(false);
                            setEditingFinances(null);
                          }}
                          style={{ marginLeft: '10px' }}
                        >
                          Annuler
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                <div style={{ marginTop: '20px' }}>
                  <h4 style={{ marginBottom: '15px' }}>Liste des utilisateurs Finances ({finances.length})</h4>
                  <div style={{ marginBottom: '15px' }}>
                    <input
                      type="text"
                      placeholder="🔍 Rechercher un utilisateur Finances (nom, username)..."
                      value={searchFinances}
                      onChange={(e) => setSearchFinances(e.target.value)}
                      style={{
                        width: '100%',
                        maxWidth: '500px',
                        padding: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '5px',
                        fontSize: '14px'
                      }}
                    />
                    {searchFinances && (
                      <button
                        onClick={() => setSearchFinances('')}
                        style={{
                          marginLeft: '10px',
                          padding: '10px 15px',
                          background: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '5px',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        ✕ Effacer
                      </button>
                    )}
                  </div>
                  <div className="table-wrapper" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    <table className="table">
                      <thead>
                        <tr>
                          <th style={{ whiteSpace: 'nowrap' }}>Nom d'utilisateur</th>
                          <th style={{ whiteSpace: 'nowrap' }}>Nom complet</th>
                          <th style={{ whiteSpace: 'nowrap' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const filteredFinances = finances.filter(finance => {
                            if (!searchFinances) return true;
                            const search = searchFinances.toLowerCase();
                            return (
                              finance.username?.toLowerCase().includes(search) ||
                              finance.nom_complet?.toLowerCase().includes(search)
                            );
                          });
                          const paginatedFinances = paginate(filteredFinances, currentPageFinances, itemsPerPage);
                          return paginatedFinances.map(finance => (
                            <tr key={finance.id}>
                              <td data-label="Nom d'utilisateur"><strong>{finance.username}</strong></td>
                              <td data-label="Nom complet">{finance.nom_complet}</td>
                              <td data-label="Actions">
                                <button 
                                  className="btn btn-secondary"
                                  onClick={() => handleEditFinances(finance)}
                                  style={{ marginRight: '10px', fontSize: '14px', padding: '5px 10px' }}
                                >
                                  Modifier
                                </button>
                                <button 
                                  className="btn btn-danger"
                                  onClick={() => handleDeleteFinances(finance.id)}
                                  style={{ fontSize: '14px', padding: '5px 10px' }}
                                >
                                  Supprimer
                                </button>
                              </td>
                            </tr>
                          ));
                        })()}
                        {(() => {
                          const filteredFinances = finances.filter(finance => {
                            if (!searchFinances) return true;
                            const search = searchFinances.toLowerCase();
                            return (
                              finance.username?.toLowerCase().includes(search) ||
                              finance.nom_complet?.toLowerCase().includes(search)
                            );
                          });
                          if (filteredFinances.length === 0) {
                            return (
                              <tr>
                                <td colSpan="3" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                                  {searchFinances ? 'Aucun utilisateur Finances trouvé' : 'Aucun utilisateur Finances enregistré. Cliquez sur "Nouvel Utilisateur Finances" pour en créer.'}
                                </td>
                              </tr>
                            );
                          }
                          return null;
                        })()}
                        {finances.length === 0 && !searchFinances && (
                          <tr>
                            <td colSpan="3" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                              Aucun utilisateur Finances enregistré. Cliquez sur "Nouvel Utilisateur Finances" pour en créer.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {(() => {
                    const filteredFinances = finances.filter(finance => {
                      if (!searchFinances) return true;
                      const search = searchFinances.toLowerCase();
                      return (
                        finance.username?.toLowerCase().includes(search) ||
                        finance.nom_complet?.toLowerCase().includes(search)
                      );
                    });
                    const totalPagesFinances = Math.ceil(filteredFinances.length / itemsPerPage);
                    if (totalPagesFinances > 1) {
                      return (
                        <div className="pagination" style={{ marginTop: '15px' }}>
                          <button
                            className="btn btn-sm"
                            onClick={() => setCurrentPageFinances(prev => Math.max(1, prev - 1))}
                            disabled={currentPageFinances === 1}
                          >
                            Précédent
                          </button>
                          <span>Page {currentPageFinances} sur {totalPagesFinances}</span>
                          <button
                            className="btn btn-sm"
                            onClick={() => setCurrentPageFinances(prev => Math.min(totalPagesFinances, prev + 1))}
                            disabled={currentPageFinances === totalPagesFinances}
                          >
                            Suivant
                          </button>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
            </div>
            )}

        {activeTab === 'membres' && (
          <div className="card">
            {editingMembre && (
              <div className="card" style={{ marginBottom: '20px', background: '#f8f9fa' }}>
                <h3>Modifier le membre: {editingMembre.nom} {editingMembre.prenom}</h3>
                <form onSubmit={handleMembreSubmit}>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Nom *</label>
                      <input
                        type="text"
                        value={membreForm.nom}
                        onChange={(e) => setMembreForm({ ...membreForm, nom: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Postnom</label>
                      <input
                        type="text"
                        value={membreForm.postnom}
                        onChange={(e) => setMembreForm({ ...membreForm, postnom: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Prénom *</label>
                      <input
                        type="text"
                        value={membreForm.prenom}
                        onChange={(e) => setMembreForm({ ...membreForm, prenom: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Date de naissance *</label>
                      <input
                        type="date"
                        value={membreForm.date_naissance}
                        onChange={(e) => setMembreForm({ ...membreForm, date_naissance: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Lieu de naissance</label>
                      <input
                        type="text"
                        value={membreForm.lieu_naissance}
                        onChange={(e) => setMembreForm({ ...membreForm, lieu_naissance: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Date d'adhésion *</label>
                      <input
                        type="date"
                        value={membreForm.date_adhesion}
                        onChange={(e) => setMembreForm({ ...membreForm, date_adhesion: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Avenue, numéro, Quartier</label>
                      <input
                        type="text"
                        value={membreForm.quartier}
                        onChange={(e) => setMembreForm({ ...membreForm, quartier: e.target.value })}
                        placeholder="Avenue, numéro, Quartier"
                      />
                    </div>
                    <div className="form-group">
                      <label>Commune</label>
                      <select
                        value={membreForm.commune}
                        onChange={(e) => setMembreForm({ ...membreForm, commune: e.target.value })}
                      >
                        <option value="">Sélectionner une commune</option>
                        <option value="MAKISO">MAKISO</option>
                        <option value="KABONDO">KABONDO</option>
                        <option value="MANGOBO">MANGOBO</option>
                        <option value="LUBUNGA">LUBUNGA</option>
                        <option value="KISANGANI">KISANGANI</option>
                        <option value="TSHOPO">TSHOPO</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Téléphone</label>
                      <input
                        type="text"
                        value={membreForm.telephone}
                        onChange={(e) => setMembreForm({ ...membreForm, telephone: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Profession</label>
                      <input
                        type="text"
                        value={membreForm.profession}
                        onChange={(e) => setMembreForm({ ...membreForm, profession: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Association</label>
                      <input
                        type="text"
                        value={membreForm.association}
                        onChange={(e) => setMembreForm({ ...membreForm, association: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Centre de santé</label>
                      <select
                        value={membreForm.centre_sante}
                        onChange={(e) => setMembreForm({ ...membreForm, centre_sante: e.target.value })}
                      >
                        <option value="">Sélectionner un centre de santé</option>
                        {centresSante.map((centre) => (
                          <option key={centre.id} value={centre.nom}>
                            {centre.nom}{centre.commune ? (' - ' + centre.commune) : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="form-row">
                    {/* Champ "Nom de l'épouse/époux" masqué */}
                    {/* Gestion des enfants du membre */}
                    {membrePourEnfants && (
                      <div className="form-group" style={{ gridColumn: '1 / -1', marginTop: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '5px', border: '1px solid #ddd' }}>
                        <h4 style={{ marginBottom: '15px', color: '#2c3e50' }}>👶 Enfants du membre ({enfantsMembre.length})</h4>
                        
                        {/* Formulaire pour ajouter un enfant */}
                        <div style={{ marginBottom: '20px', padding: '15px', background: '#fff', borderRadius: '5px', border: '1px solid #ddd' }}>
                          <h5 style={{ marginBottom: '10px', color: '#2c3e50', fontSize: '14px' }}>Ajouter un enfant</h5>
                          <div className="form-row">
                            <div className="form-group">
                              <label>Nom *</label>
                              <input
                                type="text"
                                value={nouvelEnfant.nom}
                                onChange={(e) => setNouvelEnfant({ ...nouvelEnfant, nom: e.target.value })}
                                placeholder="Nom"
                                required
                              />
                            </div>
                            <div className="form-group">
                              <label>Prénom *</label>
                              <input
                                type="text"
                                value={nouvelEnfant.prenom}
                                onChange={(e) => setNouvelEnfant({ ...nouvelEnfant, prenom: e.target.value })}
                                placeholder="Prénom"
                                required
                              />
                            </div>
                            <div className="form-group">
                              <label>Date de naissance</label>
                              <input
                                type="date"
                                value={nouvelEnfant.date_naissance}
                                onChange={(e) => setNouvelEnfant({ ...nouvelEnfant, date_naissance: e.target.value })}
                              />
                            </div>
                            <div className="form-group">
                              <label>Lien</label>
                              <select
                                value={nouvelEnfant.lien}
                                onChange={(e) => setNouvelEnfant({ ...nouvelEnfant, lien: e.target.value })}
                              >
                                <option value="">Sélectionner</option>
                                <option value="Épouse">Épouse</option>
                                <option value="Fils">Fils</option>
                                <option value="Fille">Fille</option>
                                <option value="Autre">Autre</option>
                              </select>
                            </div>
                          </div>
                          <div className="form-group" style={{ marginTop: '15px' }}>
                            <WebcamCapture
                              key={`enfant-photo-${webcamKeyEnfant}`}
                              onCapture={handlePhotoEnfantCapture}
                              label="Photo de l'enfant"
                              fieldName="photo"
                              photoType="passeport"
                            />
                          </div>
                          <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', marginTop: '15px' }}>
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={handleAddEnfantMembreSubmit}
                              style={{ width: '100%' }}
                            >
                              ➕ Ajouter
                            </button>
                          </div>
                        </div>

                        {/* Liste des enfants existants */}
                        {enfantsMembre.length > 0 ? (
                          <div className="table-wrapper">
                            <table className="table">
                              <thead>
                                <tr>
                                  <th>Photo</th>
                                  <th>Nom</th>
                                  <th>Prénom</th>
                                  <th>Date de naissance</th>
                                  <th>Lien</th>
                                  <th>Date d'enregistrement</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {enfantsMembre.map((enfant) => (
                                  <tr key={enfant.id}>
                                    <td>
                                      {enfant.photo ? (
                                        <img 
                                          src={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${enfant.photo}`} 
                                          alt={`${enfant.nom} ${enfant.prenom}`}
                                          style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px' }}
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                          }}
                                        />
                                      ) : (
                                        <span style={{ color: '#999', fontSize: '12px' }}>Pas de photo</span>
                                      )}
                                    </td>
                                    <td>{enfant.nom}</td>
                                    <td>{enfant.prenom}</td>
                                    <td>{enfant.date_naissance || '-'}</td>
                                    <td>{enfant.lien || '-'}</td>
                                    <td>{enfant.created_at ? new Date(enfant.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'}</td>
                                    <td>
                                      <button
                                        type="button"
                                        className="btn btn-danger"
                                        onClick={() => handleDeleteEnfantMembre(enfant.id)}
                                        style={{ padding: '5px 10px', fontSize: '12px' }}
                                        disabled={!isPrincipal()}
                                        title={!isPrincipal() ? 'Seul l\'administrateur principal peut supprimer' : 'Supprimer cet enfant'}
                                      >
                                        🗑️ Supprimer
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div style={{ textAlign: 'center', padding: '20px', color: '#999', fontStyle: 'italic', background: '#fff', borderRadius: '5px', border: '1px solid #ddd' }}>
                            Aucun enfant enregistré pour ce membre
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div>
                    <button type="submit" className="btn btn-primary">Enregistrer les modifications</button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setEditingMembre(null);
                        setMembreForm({
                          nom: '',
                          postnom: '',
                          prenom: '',
                          date_naissance: '',
                          lieu_naissance: '',
                          quartier: '',
                          commune: '',
                          telephone: '',
                          profession: '',
                          association: '',
                          centre_sante: '',
                          nom_epouse: '',
                          date_adhesion: ''
                        });
                        setMembrePourEnfants(null);
                        setEnfantsMembre([]);
                        setNouvelEnfant({ nom: '', prenom: '', date_naissance: '', lien: '' });
                        setPhotoEnfant(null);
                        setWebcamKeyEnfant(prev => prev + 1); // Réinitialiser le WebcamCapture
                      }}
                      style={{ marginLeft: '10px' }}
                    >
                      Annuler
                    </button>
                  </div>
                </form>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
              <h2 style={{ flex: '1 1 100%', marginBottom: '10px' }}>Liste des membres ({membres.length})</h2>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', width: '100%', alignItems: 'center' }}>
                <div style={{ position: 'relative', flex: '1', minWidth: '200px', maxWidth: '300px' }}>
                  <input
                    type="text"
                    placeholder="🔍 Rechercher un membre..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPageMembres(1);
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 40px 10px 15px',
                      border: '2px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'border-color 0.3s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#2c3e50'}
                    onBlur={(e) => e.target.style.borderColor = '#ddd'}
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '18px',
                        color: '#999'
                      }}
                      title="Effacer la recherche"
                    >
                      ×
                    </button>
                  )}
                </div>
                <div style={{ minWidth: '200px', maxWidth: '300px' }}>
                  <select
                    value={selectedAssociation}
                    onChange={(e) => {
                      setSelectedAssociation(e.target.value);
                      setCurrentPageMembres(1);
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 15px',
                      border: '2px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'border-color 0.3s',
                      backgroundColor: 'white'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#2c3e50'}
                    onBlur={(e) => e.target.style.borderColor = '#ddd'}
                  >
                    <option value="">Toutes les associations</option>
                    {associations.map(asso => (
                      <option key={asso} value={asso}>{asso}</option>
                    ))}
                  </select>
                </div>
                <div style={{ minWidth: '200px', maxWidth: '300px' }}>
                  <select
                    value={selectedTypeMembre}
                    onChange={(e) => {
                      setSelectedTypeMembre(e.target.value);
                      setCurrentPageMembres(1);
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 15px',
                      border: '2px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'border-color 0.3s',
                      backgroundColor: 'white'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#2c3e50'}
                    onBlur={(e) => e.target.style.borderColor = '#ddd'}
                  >
                    <option value="">Tous les types</option>
                    <option value="individuel">👤 Membres individuels</option>
                    <option value="famille">👨‍👩‍👧‍👦 Membres de famille</option>
                  </select>
                </div>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button
                    className="btn btn-primary"
                    onClick={handlePrintMembres}
                    style={{ fontSize: '14px', padding: '8px 15px' }}
                    title="Imprimer la liste"
                  >
                    🖨️ Imprimer
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={handleDownloadPDFMembres}
                    style={{ fontSize: '14px', padding: '8px 15px' }}
                    title="Télécharger en PDF"
                  >
                    📥 Télécharger PDF
                  </button>
                </div>
              </div>
            </div>
            {(() => {
              const filteredMembres = membres.filter(membre => {
                // Filtre par type (individuel/famille)
                if (selectedTypeMembre && membre.type !== selectedTypeMembre) {
                  return false;
                }
                
                // Filtre par association
                if (selectedAssociation) {
                  // Pour les membres individuels, vérifier l'association
                  if (membre.type === 'individuel' && membre.association !== selectedAssociation) {
                    return false;
                  }
                  // Pour les membres de famille, vérifier la commune de la famille
                  if (membre.type === 'famille' && membre.famille && membre.famille.commune !== selectedAssociation) {
                    return false;
                  }
                }
                
                // Filtre par recherche
                if (!searchTerm) return true;
                const search = searchTerm.toLowerCase();
                return (
                  (membre.nom && membre.nom.toLowerCase().includes(search)) ||
                  (membre.postnom && membre.postnom.toLowerCase().includes(search)) ||
                  (membre.prenom && membre.prenom.toLowerCase().includes(search)) ||
                  (membre.numero_matricule && membre.numero_matricule.toLowerCase().includes(search)) ||
                  (membre.telephone && membre.telephone.includes(search)) ||
                  (membre.association && membre.association.toLowerCase().includes(search)) ||
                  (membre.quartier && membre.quartier.toLowerCase().includes(search)) ||
                  (membre.commune && membre.commune.toLowerCase().includes(search)) ||
                  (membre.nom_recolteur && membre.nom_recolteur.toLowerCase().includes(search)) ||
                  (membre.centre_sante && membre.centre_sante.toLowerCase().includes(search)) ||
                  // Recherche dans les informations de famille
                  (membre.type === 'famille' && membre.famille && (
                    (membre.famille.nom_famille && membre.famille.nom_famille.toLowerCase().includes(search)) ||
                    (membre.famille.chef_famille_nom && membre.famille.chef_famille_nom.toLowerCase().includes(search)) ||
                    (membre.famille.numero_famille && membre.famille.numero_famille.toLowerCase().includes(search)) ||
                    (membre.lien && membre.lien.toLowerCase().includes(search))
                  ))
                );
              });
              return (
                <>
                  {(searchTerm || selectedAssociation || selectedTypeMembre) && (
                    <div style={{ marginBottom: '15px', color: '#666', fontSize: '14px' }}>
                      {filteredMembres.length} membre(s) {selectedTypeMembre === 'individuel' ? 'individuel(s)' : selectedTypeMembre === 'famille' ? 'de famille' : ''} {selectedAssociation ? `de l'association "${selectedAssociation}"` : ''} {searchTerm ? 'trouvé(s)' : ''}
                    </div>
                  )}
                  <div className="table-wrapper" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    <table className="table">
                      <thead>
                        <tr>
                          <th style={{ whiteSpace: 'nowrap' }}>Type</th>
                          <th style={{ whiteSpace: 'nowrap' }}>Matricule</th>
                          <th style={{ whiteSpace: 'nowrap' }}>Nom complet</th>
                          <th style={{ whiteSpace: 'nowrap' }}>Date adhésion</th>
                          <th style={{ whiteSpace: 'nowrap' }}>Famille</th>
                          <th style={{ whiteSpace: 'nowrap' }}>Récolteur</th>
                          <th style={{ whiteSpace: 'nowrap' }}>Centre de santé</th>
                          <th style={{ whiteSpace: 'nowrap' }}>Nombre d'enfants</th>
                          <th style={{ whiteSpace: 'nowrap' }}>Détails</th>
                          <th style={{ whiteSpace: 'nowrap' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginate(filteredMembres, currentPageMembres, itemsPerPage).map(membre => (
                          <tr key={membre.id} style={{ backgroundColor: membre.type === 'famille' ? '#f0f8ff' : 'transparent' }}>
                            <td data-label="Type">
                              {membre.type === 'famille' ? (
                                <span style={{ 
                                  padding: '4px 8px', 
                                  borderRadius: '4px', 
                                  fontSize: '11px', 
                                  backgroundColor: '#e3f2fd', 
                                  color: '#1976d2',
                                  fontWeight: 'bold'
                                }}>
                                  👨‍👩‍👧‍👦 Famille
                                </span>
                              ) : (
                                <span style={{ 
                                  padding: '4px 8px', 
                                  borderRadius: '4px', 
                                  fontSize: '11px', 
                                  backgroundColor: '#f3e5f5', 
                                  color: '#7b1fa2',
                                  fontWeight: 'bold'
                                }}>
                                  👤 Individuel
                                </span>
                              )}
                            </td>
                            <td data-label="Matricule">{membre.numero_matricule}</td>
                            <td data-label="Nom complet">
                              {membre.nom} {membre.postnom || ''} {membre.prenom}
                              {membre.type === 'famille' && membre.lien && (
                                <small style={{ display: 'block', color: '#666', fontSize: '11px', marginTop: '2px' }}>
                                  ({membre.lien})
                                </small>
                              )}
                            </td>
                            <td data-label="Date adhésion">{membre.date_adhesion ? new Date(membre.date_adhesion).toLocaleDateString('fr-FR') : '-'}</td>
                            <td data-label="Famille" style={{ whiteSpace: 'nowrap' }}>
                              {membre.type === 'famille' && membre.famille ? (
                                <span style={{ fontSize: '12px' }}>
                                  {membre.famille.nom_famille || membre.famille.numero_famille}
                                  <br />
                                  <small style={{ color: '#666' }}>Chef: {membre.famille.chef_famille_nom}</small>
                                </span>
                              ) : '-'}
                            </td>
                            <td data-label="Récolteur" style={{ whiteSpace: 'nowrap' }}>{membre.nom_recolteur || '-'}</td>
                            <td data-label="Centre de santé" style={{ whiteSpace: 'nowrap' }}>{membre.centre_sante || '-'}</td>
                            <td data-label="Nombre d'enfants" style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
                              {membre.enfants ? membre.enfants.length : 0}
                            </td>
                            <td data-label="Détails" style={{ whiteSpace: 'nowrap' }}>
                              {membre.type === 'famille' ? (
                                <button 
                                  className="btn btn-info"
                                  onClick={() => {
                                    if (membre.famille) {
                                      const famille = familles.find(f => f.id === membre.famille.id);
                                      if (famille) {
                                        handleViewFamilleDetails(famille);
                                      }
                                    }
                                  }}
                                  style={{ fontSize: '12px', padding: '6px 12px' }}
                                  title="Voir les détails de la famille"
                                >
                                  👨‍👩‍👧‍👦
                                </button>
                              ) : (
                                <button 
                                  className="btn btn-info"
                                  onClick={() => setSelectedMembreDetail(membre)}
                                  style={{ fontSize: '12px', padding: '6px 12px' }}
                                  title="Voir les détails du membre"
                                >
                                  👁️
                                </button>
                              )}
                            </td>
                            <td data-label="Actions" style={{ whiteSpace: 'nowrap' }}>
                              {membre.type === 'famille' ? (
                                <select
                                  onChange={async (e) => {
                                    const action = e.target.value;
                                    if (membre.famille) {
                                      const famille = familles.find(f => f.id === membre.famille.id);
                                      if (famille) {
                                        if (action === 'details') {
                                          handleViewFamilleDetails(famille);
                                        } else if (action === 'bon_traitement') {
                                          handleImprimerBonTraitement(famille.id, 'famille');
                                        } else if (action === 'imprimer') {
                                          try {
                                            const response = await getFamille(famille.id);
                                            const familleData = response?.data || response;
                                            setSelectedFamilleCarte(familleData);
                                          } catch (error) {
                                            console.error('Erreur lors du chargement de la famille pour impression:', error);
                                            notificationService.error('Erreur', "Impossible de charger les détails de la famille pour l'impression.");
                                          }
                                        } else if (action === 'modifier') {
                                          handleEditFamille(famille);
                                        } else if (action === 'membres') {
                                          handleGestionMembres(famille);
                                        } else if (action === 'supprimer') {
                                          handleDeleteFamille(famille.id);
                                        }
                                      }
                                    }
                                    e.target.value = '';
                                  }}
                                  style={{
                                    padding: '8px 12px',
                                    fontSize: '13px',
                                    borderRadius: '5px',
                                    border: '1px solid #2c3e50',
                                    backgroundColor: 'white',
                                    cursor: 'pointer',
                                    minWidth: '180px',
                                    fontWeight: '500',
                                    color: '#2c3e50'
                                  }}
                                  defaultValue=""
                                  title="Sélectionner une action"
                                >
                                  <option value="" disabled hidden>⚙️ Actions...</option>
                                  <option value="details">👁️ Voir détails</option>
                                  <option value="bon_traitement">📄 Imprimer bon de traitement</option>
                                  <option value="imprimer">🖨️ Imprimer la carte</option>
                                </select>
                              ) : (
                                <select
                                  onChange={async (e) => {
                                    const action = e.target.value;
                                    if (action === 'bon_traitement') {
                                      handleImprimerBonTraitement(membre.id, 'membre');
                                    } else if (action === 'imprimer') {
                                      try {
                                        const response = await getMembre(membre.id);
                                        setSelectedMembre(response.data);
                                      } catch (error) {
                                        console.error('Erreur lors du chargement du membre pour impression:', error);
                                        notificationService.error('Erreur', "Impossible de charger les détails du membre pour l'impression.");
                                      }
                                    } else if (action === 'payer_cotisation') {
                                      if (membre.type === 'famille' && membre.famille && membre.famille.id) {
                                        window.open(`/confirmation-paiements-famille/${membre.famille.id}`, '_blank');
                                      } else {
                                        notificationService.error('Erreur', 'Impossible de trouver la famille associée à ce membre.');
                                      }
                                    } else if (action === 'modifier') {
                                      handleEditMembre(membre);
                                    } else if (action === 'ajouter_enfants') {
                                      handleAddEnfantsMembre(membre);
                                    } else if (action === 'supprimer') {
                                      handleDeleteMembre(membre.id);
                                    }
                                    e.target.value = '';
                                  }}
                                style={{
                                  padding: '8px 12px',
                                  fontSize: '13px',
                                  borderRadius: '5px',
                                  border: '1px solid #2c3e50',
                                  backgroundColor: 'white',
                                  cursor: 'pointer',
                                  minWidth: '180px',
                                  fontWeight: '500',
                                  color: '#2c3e50'
                                }}
                                defaultValue=""
                                title="Sélectionner une action"
                              >
                                <option value="" disabled hidden>⚙️ Actions...</option>
                                <option value="bon_traitement">📄 Imprimer bon de traitement</option>
                                <option value="imprimer">🖨️ Imprimer la carte</option>
                                {membre.type === 'famille' && membre.famille && (
                                  <option value="payer_cotisation">💰 Payer cotisation</option>
                                )}
                                {isPrincipal() && <option value="ajouter_enfants">👶 Ajouter les enfants</option>}
                                {isPrincipal() && <option value="modifier">✏️ Modifier</option>}
                                {isPrincipal() && <option value="supprimer" style={{ color: '#dc3545', fontWeight: '600' }}>🗑️ Supprimer</option>}
                              </select>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {(() => {
                    const filteredMembres = membres.filter(membre => {
                      if (selectedAssociation && membre.association !== selectedAssociation) {
                        return false;
                      }
                      if (!searchTerm) return true;
                      const search = searchTerm.toLowerCase();
                      return (
                        membre.numero_matricule?.toLowerCase().includes(search) ||
                        membre.nom?.toLowerCase().includes(search) ||
                        membre.postnom?.toLowerCase().includes(search) ||
                        membre.prenom?.toLowerCase().includes(search) ||
                        (membre.association && membre.association.toLowerCase().includes(search)) ||
                        (membre.quartier && membre.quartier.toLowerCase().includes(search)) ||
                        (membre.commune && membre.commune.toLowerCase().includes(search)) ||
                        (membre.nom_recolteur && membre.nom_recolteur.toLowerCase().includes(search)) ||
                        (membre.centre_sante && membre.centre_sante.toLowerCase().includes(search))
                      );
                    });
                    const totalPages = getTotalPages(filteredMembres, itemsPerPage);
                    return (
                      <Pagination 
                        currentPage={currentPageMembres} 
                        totalPages={totalPages} 
                        onPageChange={(page) => {
                          setCurrentPageMembres(page);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }} 
                      />
                    );
                  })()}
                </>
              );
            })()}
          </div>
        )}

        {activeTab === 'familles' && (
          <>
          <div className="card">
            {editingFamille && (
              <div className="card" style={{ marginBottom: '20px', background: '#f8f9fa' }}>
                <h3>Modifier la famille: {editingFamille.nom_famille}</h3>
                <form onSubmit={handleFamilleSubmit}>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Chef de famille *</label>
                      <input
                        type="text"
                        value={familleForm.chef_famille_nom}
                        onChange={(e) => setFamilleForm({ ...familleForm, chef_famille_nom: e.target.value })}
                        required
                        placeholder="Nom complet du chef de famille"
                      />
                    </div>
                    <div className="form-group">
                      <label>Nom de la famille *</label>
                      <input
                        type="text"
                        value={familleForm.nom_famille}
                        onChange={(e) => setFamilleForm({ ...familleForm, nom_famille: e.target.value })}
                        required
                        placeholder="Nom de la famille"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Adresse</label>
                      <input
                        type="text"
                        value={familleForm.adresse}
                        onChange={(e) => setFamilleForm({ ...familleForm, adresse: e.target.value })}
                        placeholder="Adresse complète"
                      />
                    </div>
                    <div className="form-group">
                      <label>Quartier</label>
                      <input
                        type="text"
                        value={familleForm.quartier}
                        onChange={(e) => setFamilleForm({ ...familleForm, quartier: e.target.value })}
                        placeholder="Quartier"
                      />
                    </div>
                    <div className="form-group">
                      <label>Commune</label>
                      <select
                        value={familleForm.commune}
                        onChange={(e) => setFamilleForm({ ...familleForm, commune: e.target.value })}
                      >
                        <option value="">Sélectionner une commune</option>
                        <option value="MAKISO">MAKISO</option>
                        <option value="KABONDO">KABONDO</option>
                        <option value="MANGOBO">MANGOBO</option>
                        <option value="LUBUNGA">LUBUNGA</option>
                        <option value="KISANGANI">KISANGANI</option>
                        <option value="TSHOPO">TSHOPO</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Téléphone</label>
                      <input
                        type="text"
                        value={familleForm.telephone}
                        onChange={(e) => setFamilleForm({ ...familleForm, telephone: e.target.value })}
                        placeholder="Numéro de téléphone"
                      />
                    </div>
                    <div className="form-group">
                      <label>Statut *</label>
                      <select
                        value={familleForm.statut || 'active'}
                        onChange={(e) => setFamilleForm({ ...familleForm, statut: e.target.value })}
                        required
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                    <div className="form-group" style={{ flex: '1' }}>
                      <label>Observations</label>
                      <textarea
                        value={familleForm.observations}
                        onChange={(e) => setFamilleForm({ ...familleForm, observations: e.target.value })}
                        rows="3"
                        placeholder="Notes et observations sur la famille"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <button type="submit" className="btn btn-primary">💾 Enregistrer</button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setEditingFamille(null);
                        setShowFamilleForm(false);
                        setFamilleForm({
                          chef_famille_nom: '',
                          nom_famille: '',
                          adresse: '',
                          quartier: '',
                          commune: '',
                          telephone: '',
                          observations: '',
                          statut: 'active'
                        });
                      }}
                      style={{ marginLeft: '10px' }}
                    >
                      Annuler
                    </button>
                  </div>
                </form>
              </div>
            )}
            {!editingFamille && !showFamilleForm && (
              <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setShowFamilleForm(true);
                    setFamilleForm({
                      chef_famille_nom: '',
                      nom_famille: '',
                      adresse: '',
                      quartier: '',
                      commune: '',
                      telephone: '',
                      observations: ''
                    });
                    setMembresFamille([]);
                    setNouveauMembre({ nom: '', prenom: '', date_naissance: '', lien: '' });
                  }}
                >
                  ➕ Ajouter une famille
                </button>
                <button
                  className="btn btn-success"
                  onClick={() => {
                    const params = new URLSearchParams();
                    if (searchFamilles) {
                      params.append('search', searchFamilles);
                    }
                    if (selectedAssociationFamilles && selectedAssociationFamilles !== 'toutes') {
                      params.append('commune', selectedAssociationFamilles);
                    }
                    
                    const printWindow = window.open(
                      `/admin/familles/print?${params.toString()}`,
                      '_blank',
                      'width=1200,height=800,scrollbars=yes,resizable=yes'
                    );
                    
                    if (!printWindow) {
                      notificationService.error('Erreur', 'Veuillez autoriser les pop-ups pour ouvrir la page d\'impression.');
                    }
                  }}
                >
                  🖨️ Imprimer / Télécharger le rapport
                </button>
              </div>
            )}
            {showFamilleForm && !editingFamille && (
              <div className="card" style={{ marginBottom: '20px', background: '#f8f9fa' }}>
                <h3>Nouvelle famille</h3>
                <form onSubmit={handleFamilleSubmit}>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Chef de famille *</label>
                      <input
                        type="text"
                        value={familleForm.chef_famille_nom}
                        onChange={(e) => setFamilleForm({ ...familleForm, chef_famille_nom: e.target.value })}
                        required
                        placeholder="Nom complet du chef de famille"
                      />
                    </div>
                    <div className="form-group">
                      <label>Nom de la famille *</label>
                      <input
                        type="text"
                        value={familleForm.nom_famille}
                        onChange={(e) => setFamilleForm({ ...familleForm, nom_famille: e.target.value })}
                        required
                        placeholder="Nom de la famille"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Adresse</label>
                      <input
                        type="text"
                        value={familleForm.adresse}
                        onChange={(e) => setFamilleForm({ ...familleForm, adresse: e.target.value })}
                        placeholder="Adresse complète"
                      />
                    </div>
                    <div className="form-group">
                      <label>Quartier</label>
                      <input
                        type="text"
                        value={familleForm.quartier}
                        onChange={(e) => setFamilleForm({ ...familleForm, quartier: e.target.value })}
                        placeholder="Quartier"
                      />
                    </div>
                    <div className="form-group">
                      <label>Commune</label>
                      <select
                        value={familleForm.commune}
                        onChange={(e) => setFamilleForm({ ...familleForm, commune: e.target.value })}
                      >
                        <option value="">Sélectionner une commune</option>
                        <option value="MAKISO">MAKISO</option>
                        <option value="KABONDO">KABONDO</option>
                        <option value="MANGOBO">MANGOBO</option>
                        <option value="LUBUNGA">LUBUNGA</option>
                        <option value="KISANGANI">KISANGANI</option>
                        <option value="TSHOPO">TSHOPO</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Téléphone</label>
                      <input
                        type="text"
                        value={familleForm.telephone}
                        onChange={(e) => setFamilleForm({ ...familleForm, telephone: e.target.value })}
                        placeholder="Numéro de téléphone"
                      />
                    </div>
                    <div className="form-group" style={{ flex: '1' }}>
                      <label>Observations</label>
                      <textarea
                        value={familleForm.observations}
                        onChange={(e) => setFamilleForm({ ...familleForm, observations: e.target.value })}
                        rows="3"
                        placeholder="Notes et observations sur la famille"
                      />
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <WebcamCapture
                        onCapture={handlePhotoChefCapture}
                        label="Photo du chef de famille"
                        fieldName="photo_chef"
                        photoType="passeport"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <button type="submit" className="btn btn-primary">💾 Enregistrer</button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowFamilleForm(false);
                        setPhotoChefFamille(null);
                        setFamilleForm({
                          chef_famille_nom: '',
                          nom_famille: '',
                          adresse: '',
                          quartier: '',
                          commune: '',
                          telephone: '',
                          observations: '',
                          statut: 'active'
                        });
                        setMembresFamille([]);
                        setNouveauMembre({ nom: '', prenom: '', date_naissance: '', lien: '' });
                      }}
                      style={{ marginLeft: '10px' }}
                    >
                      Annuler
                    </button>
                  </div>
                </form>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
              <h2 style={{ flex: '1 1 100%', marginBottom: '10px' }}>Liste des familles ({Array.isArray(familles) ? familles.length : 0})</h2>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', width: '100%', alignItems: 'center' }}>
                <div style={{ position: 'relative', flex: '1', minWidth: '200px', maxWidth: '300px' }}>
                  <input
                    type="text"
                    placeholder="🔍 Rechercher une famille..."
                    value={searchFamilles}
                    onChange={(e) => {
                      setSearchFamilles(e.target.value);
                      setCurrentPageFamilles(1);
                    }}
                            style={{ 
                      width: '100%',
                      padding: '10px 40px 10px 15px',
                      border: '2px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'border-color 0.3s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#2c3e50'}
                    onBlur={(e) => e.target.style.borderColor = '#ddd'}
                  />
                  {searchFamilles && (
                    <button
                      onClick={() => setSearchFamilles('')}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '18px',
                        color: '#999'
                      }}
                      title="Effacer la recherche"
                    >
                      ×
                    </button>
                  )}
                </div>
                <div style={{ minWidth: '200px', maxWidth: '300px' }}>
                  <select
                    value={selectedAssociationFamilles}
                    onChange={(e) => {
                      setSelectedAssociationFamilles(e.target.value);
                      setCurrentPageFamilles(1);
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 15px',
                      border: '2px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'border-color 0.3s',
                      backgroundColor: 'white'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#2c3e50'}
                    onBlur={(e) => e.target.style.borderColor = '#ddd'}
                  >
                    <option value="">Toutes les communes</option>
                    {[...new Set((Array.isArray(familles) ? familles : []).map(f => f.commune).filter(Boolean))].sort().map(commune => (
                      <option key={commune} value={commune}>{commune}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            {(() => {
              const famillesArray = Array.isArray(familles) ? familles : [];
              const filteredFamilles = famillesArray.filter(famille => {
                // Filtre par commune
                if (selectedAssociationFamilles && famille.commune !== selectedAssociationFamilles) {
                  return false;
                }
                
                // Filtre par recherche
                if (!searchFamilles) return true;
                const search = searchFamilles.toLowerCase();
                return (
                  (famille.nom_famille && famille.nom_famille.toLowerCase().includes(search)) ||
                  (famille.numero_famille && famille.numero_famille.toLowerCase().includes(search)) ||
                  (famille.telephone && famille.telephone.includes(search)) ||
                  (famille.quartier && famille.quartier.toLowerCase().includes(search)) ||
                  (famille.commune && famille.commune.toLowerCase().includes(search)) ||
                  (famille.chef_famille_nom && famille.chef_famille_nom.toLowerCase().includes(search))
                );
              });
              return (
                <>
                  {(searchFamilles || selectedAssociationFamilles) && (
                    <div style={{ marginBottom: '15px', color: '#666', fontSize: '14px' }}>
                      {filteredFamilles.length} famille(s) {selectedAssociationFamilles ? `de la commune "${selectedAssociationFamilles}"` : ''} {searchFamilles ? 'trouvée(s)' : ''}
                    </div>
                  )}
                  {filteredFamilles.length === 0 ? (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '40px', 
                      color: '#999', 
                      fontStyle: 'italic', 
                      background: '#fff', 
                      borderRadius: '5px', 
                      border: '1px solid #ddd',
                      marginTop: '20px'
                    }}>
                      {famillesArray.length === 0 ? (
                        <div>
                          <p style={{ fontSize: '18px', marginBottom: '10px' }}>📋 Aucune famille enregistrée</p>
                          <p>Cliquez sur "➕ Ajouter une famille" pour créer votre première famille.</p>
                        </div>
                      ) : (
                        <div>
                          <p style={{ fontSize: '18px', marginBottom: '10px' }}>🔍 Aucune famille trouvée</p>
                          <p>Essayez de modifier vos critères de recherche ou de filtre.</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="table-wrapper" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                      <table className="table">
                        <thead>
                          <tr>
                            <th style={{ whiteSpace: 'nowrap' }}>Numéro</th>
                            <th style={{ whiteSpace: 'nowrap' }}>Nom de la famille</th>
                            <th style={{ whiteSpace: 'nowrap' }}>Chef de famille</th>
                            <th style={{ whiteSpace: 'nowrap' }}>Commune</th>
                            <th style={{ whiteSpace: 'nowrap' }}>Centre de santé</th>
                            <th style={{ whiteSpace: 'nowrap' }}>Téléphone</th>
                            <th style={{ whiteSpace: 'nowrap' }}>Membres</th>
                            <th style={{ whiteSpace: 'nowrap' }}>Statut</th>
                            <th style={{ whiteSpace: 'nowrap' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginate(filteredFamilles, currentPageFamilles, itemsPerPage).map(famille => {
                            return (
                              <tr key={famille.id}>
                                <td data-label="Numéro">{famille.numero_famille || '-'}</td>
                                <td data-label="Nom de la famille">{famille.nom_famille || '-'}</td>
                                <td data-label="Chef de famille">{famille.chef_famille_nom || '-'}</td>
                                <td data-label="Commune">{famille.commune || '-'}</td>
                                <td data-label="Centre de santé" style={{ whiteSpace: 'nowrap' }}>
                                  {famille.centre_sante ? (
                                    <span style={{
                                      padding: '4px 8px',
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      fontWeight: '500',
                                      backgroundColor: '#e7f3ff',
                                      color: '#2c3e50'
                                    }}>
                                      🏥 {famille.centre_sante}
                                    </span>
                                  ) : (
                                    <span style={{ color: '#999', fontStyle: 'italic' }}>-</span>
                                  )}
                                </td>
                                <td data-label="Téléphone">{famille.telephone || '-'}</td>
                                <td data-label="Membres">{famille.nombre_membres || 1}</td>
                                <td data-label="Statut">
                                  <span style={{
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    backgroundColor: (famille.statut === 'active' || !famille.statut) ? '#d4edda' : '#f8d7da',
                                    color: (famille.statut === 'active' || !famille.statut) ? '#155724' : '#721c24'
                                  }}>
                                    {(famille.statut === 'active' || !famille.statut) ? 'Active' : 'Inactive'}
                                  </span>
                                </td>
                                <td data-label="Actions" style={{ whiteSpace: 'nowrap' }}>
                                  <select
                                    onChange={async (e) => {
                                      const action = e.target.value;
                                      if (action === 'details') {
                                        handleViewFamilleDetails(famille);
                                      } else if (action === 'bon_traitement') {
                                        handleImprimerBonTraitement(famille.id, 'famille');
                                      } else if (action === 'payer_cotisation') {
                                        window.open(`/confirmation-paiements-famille/${famille.id}`, '_blank');
                                      } else if (action === 'imprimer') {
                                        try {
                                          const response = await getFamille(famille.id);
                                          const familleData = response?.data || response;
                                          setSelectedFamilleCarte(familleData);
                                        } catch (error) {
                                          console.error('Erreur lors du chargement de la famille pour impression:', error);
                                          notificationService.error('Erreur', "Impossible de charger les détails de la famille pour l'impression.");
                                        }
                                      } else if (action === 'modifier') {
                                        handleEditFamille(famille);
                                      } else if (action === 'membres') {
                                        handleGestionMembres(famille);
                                      } else if (action === 'supprimer') {
                                        handleDeleteFamille(famille.id);
                                      }
                                      e.target.value = '';
                                    }}
                                    style={{
                                      padding: '8px 12px',
                                      fontSize: '13px',
                                      borderRadius: '5px',
                                      border: '1px solid #2c3e50',
                                      backgroundColor: 'white',
                                      cursor: 'pointer',
                                      minWidth: '180px',
                                      fontWeight: '500',
                                      color: '#2c3e50'
                                    }}
                                    defaultValue=""
                                    title="Sélectionner une action"
                                  >
                                    <option value="" disabled hidden>⚙️ Actions...</option>
                                    <option value="details">👁️ Voir détails</option>
                                    <option value="bon_traitement">📄 Imprimer bon de traitement</option>
                                    <option value="payer_cotisation">💰 Payer cotisation</option>
                                    {isPrincipal() && <option value="modifier">✏️ Modifier</option>}
                                    <option value="membres">👨‍👩‍👧‍👦 Gérer les membres</option>
                                    <option value="imprimer">🖨️ Imprimer la carte</option>
                                    {isPrincipal() && <option value="supprimer" style={{ color: '#dc3545', fontWeight: '600' }}>🗑️ Supprimer</option>}
                                  </select>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {(() => {
                    const famillesArray = Array.isArray(familles) ? familles : [];
                    const filteredFamilles = famillesArray.filter(famille => {
                      if (selectedAssociationFamilles && famille.commune !== selectedAssociationFamilles) {
                        return false;
                      }
                      if (!searchFamilles) return true;
                      const search = searchFamilles.toLowerCase();
                      return (
                        (famille.nom_famille && famille.nom_famille.toLowerCase().includes(search)) ||
                        (famille.numero_famille && famille.numero_famille.toLowerCase().includes(search)) ||
                        (famille.telephone && famille.telephone.includes(search)) ||
                        (famille.quartier && famille.quartier.toLowerCase().includes(search)) ||
                        (famille.commune && famille.commune.toLowerCase().includes(search)) ||
                        (famille.chef_famille_nom && famille.chef_famille_nom.toLowerCase().includes(search))
                      );
                    });
                    const totalPages = getTotalPages(filteredFamilles, itemsPerPage);
                    return (
                      <Pagination 
                        currentPage={currentPageFamilles} 
                        totalPages={totalPages} 
                        onPageChange={(page) => {
                          setCurrentPageFamilles(page);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }} 
                      />
                    );
                  })()}
                </>
              );
            })()}
          </div>

          {/* Section des cotisations des familles */}
          <div className="card" style={{ marginTop: '30px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
              <h3 style={{ flex: '1 1 100%', marginBottom: '10px' }}>💰 Cotisations des familles</h3>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', width: '100%', alignItems: 'center' }}>
                <div style={{ position: 'relative', flex: '1', minWidth: '200px', maxWidth: '300px' }}>
                  <input
                    type="text"
                    placeholder="🔍 Rechercher une famille..."
                    value={searchCotisationsFamilles}
                    onChange={(e) => {
                      setSearchCotisationsFamilles(e.target.value);
                      setCurrentPageCotisationsFamilles(1);
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 40px 10px 15px',
                      border: '2px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'border-color 0.3s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#2c3e50'}
                    onBlur={(e) => e.target.style.borderColor = '#ddd'}
                  />
                  {searchCotisationsFamilles && (
                    <button
                      onClick={() => setSearchCotisationsFamilles('')}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '18px',
                        color: '#999'
                      }}
                      title="Effacer la recherche"
                    >
                      ×
                    </button>
                  )}
                </div>
                <div style={{ minWidth: '200px', maxWidth: '300px' }}>
                  <select
                    value={selectedCommuneCotisationsFamilles}
                    onChange={(e) => {
                      setSelectedCommuneCotisationsFamilles(e.target.value);
                      setCurrentPageCotisationsFamilles(1);
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 15px',
                      border: '2px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'border-color 0.3s',
                      backgroundColor: 'white'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#2c3e50'}
                    onBlur={(e) => e.target.style.borderColor = '#ddd'}
                  >
                    <option value="">Toutes les communes</option>
                    {[...new Set((Array.isArray(familles) ? familles : []).map(f => f.commune).filter(Boolean))].sort().map(commune => (
                      <option key={commune} value={commune}>{commune}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            {(searchCotisationsFamilles || selectedCommuneCotisationsFamilles) && (
              <div style={{ marginBottom: '15px', color: '#666', fontSize: '14px' }}>
                {(() => {
                  const filtered = cotisationsFamille.filter(c => {
                    if (selectedCommuneCotisationsFamilles && c.famille?.commune !== selectedCommuneCotisationsFamilles) return false;
                    if (!searchCotisationsFamilles) return true;
                    const search = searchCotisationsFamilles.toLowerCase();
                    const moisNoms = [
                      'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
                      'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
                    ];
                    const moisNom = moisNoms[c.mois - 1] || '';
                    return (
                      c.famille?.numero_famille?.toLowerCase().includes(search) ||
                      c.famille?.nom_famille?.toLowerCase().includes(search) ||
                      c.famille?.chef_famille_nom?.toLowerCase().includes(search) ||
                      moisNom.includes(search) ||
                      c.annee?.toString().includes(search) ||
                      c.statut?.toLowerCase().includes(search) ||
                      c.montant?.toString().includes(search)
                    );
                  });
                  return `${filtered.length} cotisation(s) ${selectedCommuneCotisationsFamilles ? `de la commune "${selectedCommuneCotisationsFamilles}"` : ''} ${searchCotisationsFamilles ? 'trouvée(s)' : ''}`;
                })()}
              </div>
            )}
            
            {(() => {
              // Filtrer les cotisations selon la recherche et la commune
              const filteredCotisations = cotisationsFamille.filter(cotisation => {
                // Filtre par commune
                if (selectedCommuneCotisationsFamilles && cotisation.famille?.commune !== selectedCommuneCotisationsFamilles) {
                  return false;
                }
                
                // Filtre par recherche
                if (!searchCotisationsFamilles) return true;
                const search = searchCotisationsFamilles.toLowerCase();
                const moisNoms = [
                  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
                  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
                ];
                const moisNom = moisNoms[cotisation.mois - 1] || '';
                return (
                  cotisation.famille?.numero_famille?.toLowerCase().includes(search) ||
                  cotisation.famille?.nom_famille?.toLowerCase().includes(search) ||
                  cotisation.famille?.chef_famille_nom?.toLowerCase().includes(search) ||
                  moisNom.includes(search) ||
                  cotisation.annee?.toString().includes(search) ||
                  cotisation.statut?.toLowerCase().includes(search) ||
                  cotisation.montant?.toString().includes(search)
                );
              });

              if (filteredCotisations.length === 0) {
                return (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                    <p style={{ fontSize: '16px' }}>
                      {searchCotisationsFamilles ? 'Aucune cotisation trouvée' : 'Aucune cotisation enregistrée'}
                    </p>
                  </div>
                );
              }

              // Grouper les cotisations par famille
              const cotisationsParFamille = {};
              filteredCotisations.forEach(cotisation => {
                const familleId = cotisation.famille_id;
                if (!cotisationsParFamille[familleId]) {
                  cotisationsParFamille[familleId] = {
                    famille: cotisation.famille,
                    cotisations: []
                  };
                }
                cotisationsParFamille[familleId].cotisations.push(cotisation);
              });

              // Trier les familles par nom
              const famillesIds = Object.keys(cotisationsParFamille).sort((a, b) => {
                const familleA = cotisationsParFamille[a].famille;
                const familleB = cotisationsParFamille[b].famille;
                const nomA = familleA?.nom_famille || '';
                const nomB = familleB?.nom_famille || '';
                return nomA.localeCompare(nomB);
              });

              const moisNoms = [
                'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
                'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
              ];

              const paginatedFamillesIds = paginate(famillesIds, currentPageCotisationsFamilles, itemsPerPage);
              return (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {paginatedFamillesIds.map(familleId => {
                    const { famille, cotisations: cotisationsFamille } = cotisationsParFamille[familleId];
                    const isExpanded = famillesExpandees[familleId] || false;
                    const cotisationsPayees = cotisationsFamille.filter(c => c.statut === 'paye').length;
                    const cotisationsImpayees = cotisationsFamille.filter(c => c.statut === 'impaye').length;
                    const totalMontant = cotisationsFamille.reduce((sum, c) => sum + parseFloat(c.montant || 0), 0);

                    return (
                      <div
                        key={familleId}
                        style={{
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          backgroundColor: '#fff'
                        }}
                      >
                        <div
                          style={{
                            padding: '15px',
                            backgroundColor: cotisationsImpayees > 0 ? '#fff3cd' : '#d4edda',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            transition: 'background-color 0.2s'
                          }}
                          onClick={() => setFamillesExpandees(prev => ({ ...prev, [familleId]: !isExpanded }))}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = cotisationsImpayees > 0 ? '#ffe69c' : '#c3e6cb'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = cotisationsImpayees > 0 ? '#fff3cd' : '#d4edda'}
                        >
                          <div style={{ flex: 1 }}>
                            <h3 style={{ margin: 0, fontSize: '16px', color: '#2c3e50' }}>
                              {famille?.numero_famille || '-'} - {famille?.nom_famille || ''}
                            </h3>
                            <div style={{ fontSize: '13px', color: '#666', marginTop: '5px' }}>
                              {cotisationsFamille.length} cotisation(s) • 
                              <span style={{ color: '#28a745', marginLeft: '5px' }}>{cotisationsPayees} payée(s)</span> • 
                              <span style={{ color: '#dc3545', marginLeft: '5px' }}>{cotisationsImpayees} impayée(s)</span> • 
                              <span style={{ fontWeight: 'bold', marginLeft: '5px' }}>
                                Total: {totalMontant.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF
                              </span>
                            </div>
                          </div>
                          <div style={{ fontSize: '20px', color: '#666', marginLeft: '15px' }}>
                            {isExpanded ? '▼' : '▶'}
                          </div>
                        </div>

                        {isExpanded && (
                          <div style={{ padding: '15px', backgroundColor: '#f8f9fa' }}>
                            <div className="table-wrapper" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                              <table className="table" style={{ margin: 0 }}>
                                <thead>
                                  <tr>
                                    <th style={{ whiteSpace: 'nowrap' }}>Mois/Année</th>
                                    <th style={{ whiteSpace: 'nowrap' }}>Semaine</th>
                                    <th style={{ whiteSpace: 'nowrap' }}>Montant</th>
                                    <th style={{ whiteSpace: 'nowrap' }}>Statut</th>
                                    <th style={{ whiteSpace: 'nowrap' }}>Date paiement</th>
                                    <th style={{ whiteSpace: 'nowrap' }}>Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {cotisationsFamille
                                    .sort((a, b) => {
                                      if (a.annee !== b.annee) return b.annee - a.annee;
                                      if (a.mois !== b.mois) return b.mois - a.mois;
                                      return (a.semaine || 0) - (b.semaine || 0);
                                    })
                                    .map(cotisation => (
                                      <tr key={cotisation.id} style={{
                                        backgroundColor: cotisation.statut === 'paye' ? '#d4edda' : 'transparent'
                                      }}>
                                        <td data-label="Mois/Année" style={{ whiteSpace: 'nowrap' }}>
                                          {moisNoms[cotisation.mois - 1]} {cotisation.annee}
                                        </td>
                                        <td data-label="Semaine" style={{ whiteSpace: 'nowrap' }}>
                                          {cotisation.semaine ? `Semaine ${cotisation.semaine}` : '-'}
                                        </td>
                                        <td data-label="Montant" style={{ whiteSpace: 'nowrap' }}>
                                          {parseFloat(cotisation.montant).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF
                                        </td>
                                        <td data-label="Statut" style={{ whiteSpace: 'nowrap' }}>
                                          {cotisation.statut === 'paye' ? (
                                            <span className="badge badge-success">Payée</span>
                                          ) : (
                                            <span className="badge badge-danger">Impayée</span>
                                          )}
                                        </td>
                                        <td data-label="Date paiement" style={{ whiteSpace: 'nowrap' }}>
                                          {cotisation.date_paiement ? new Date(cotisation.date_paiement).toLocaleDateString('fr-FR') : '-'}
                                        </td>
                                        <td data-label="Actions" style={{ whiteSpace: 'nowrap' }}>
                                          {cotisation.statut === 'impaye' && (
                                            <button
                                              className="btn btn-success btn-sm"
                                              onClick={() => window.open(`/confirmation-paiements-famille/${familleId}`, '_blank')}
                                              style={{ fontSize: '12px', padding: '5px 10px' }}
                                              title="Payer la cotisation"
                                            >
                                              💰 Payer
                                            </button>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  </div>
                  {(() => {
                    const filtered = cotisationsFamille.filter(c => {
                      if (selectedCommuneCotisationsFamilles && c.famille?.commune !== selectedCommuneCotisationsFamilles) {
                        return false;
                      }
                      if (!searchCotisationsFamilles) return true;
                      const search = searchCotisationsFamilles.toLowerCase();
                      return (
                        c.famille?.numero_famille?.toLowerCase().includes(search) ||
                        c.famille?.nom_famille?.toLowerCase().includes(search) ||
                        c.famille?.chef_famille_nom?.toLowerCase().includes(search)
                      );
                    });
                    const cotisationsParFamille = {};
                    filtered.forEach(cotisation => {
                      const familleId = cotisation.famille_id;
                      if (!cotisationsParFamille[familleId]) {
                        cotisationsParFamille[familleId] = {
                          famille: cotisation.famille,
                          cotisations: []
                        };
                      }
                      cotisationsParFamille[familleId].cotisations.push(cotisation);
                    });
                    const famillesIds = Object.keys(cotisationsParFamille);
                    const totalPages = getTotalPages(famillesIds, itemsPerPage);
                    return (
                      <Pagination 
                        currentPage={currentPageCotisationsFamilles} 
                        totalPages={totalPages} 
                        onPageChange={(page) => {
                          setCurrentPageCotisationsFamilles(page);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }} 
                      />
                    );
                  })()}
                </>
              );
            })()}
          </div>
          </>
        )}

        {/* Interface de gestion des membres de famille */}
        {showGestionMembres && famillePourMembres && (
          <div className="card" style={{ marginBottom: '20px', background: '#f8f9fa' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3>👨‍👩‍👧‍👦 Gestion des membres - Famille: {famillePourMembres.nom_famille}</h3>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowGestionMembres(false);
                  setFamillePourMembres(null);
                  setMembresFamille([]);
                  setNouveauMembre({ nom: '', prenom: '', date_naissance: '', lien: '' });
                }}
              >
                ✖️ Fermer
              </button>
            </div>
            
            {/* Formulaire pour ajouter un membre */}
            <div style={{ marginBottom: '20px', padding: '15px', background: '#fff', borderRadius: '5px', border: '1px solid #ddd' }}>
              <h4 style={{ marginBottom: '15px', color: '#2c3e50' }}>Ajouter un nouveau membre</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Nom *</label>
                  <input
                    type="text"
                    value={nouveauMembre.nom}
                    onChange={(e) => setNouveauMembre({ ...nouveauMembre, nom: e.target.value })}
                    placeholder="Nom"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Prénom *</label>
                  <input
                    type="text"
                    value={nouveauMembre.prenom}
                    onChange={(e) => setNouveauMembre({ ...nouveauMembre, prenom: e.target.value })}
                    placeholder="Prénom"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Date de naissance</label>
                  <input
                    type="date"
                    value={nouveauMembre.date_naissance}
                    onChange={(e) => setNouveauMembre({ ...nouveauMembre, date_naissance: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Lien de parenté</label>
                  <select
                    value={nouveauMembre.lien}
                    onChange={(e) => setNouveauMembre({ ...nouveauMembre, lien: e.target.value })}
                  >
                    <option value="">Sélectionner</option>
                    <option value="Époux/Épouse">Époux/Épouse</option>
                    <option value="Enfant">Enfant</option>
                    <option value="Parent">Parent</option>
                    <option value="Frère/Sœur">Frère/Sœur</option>
                    <option value="Autre">Autre</option>
                  </select>
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1', marginTop: '15px' }}>
                  <WebcamCapture
                    key={`membre-famille-photo-${webcamKeyMembreFamille}`}
                    onCapture={handlePhotoMembreFamilleCapture}
                    label="Photo du membre"
                    fieldName="photo"
                    photoType="passeport"
                  />
                </div>
                <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', gridColumn: '1 / -1' }}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleAddMembreFamilleSubmit}
                    style={{ width: '100%' }}
                  >
                    ➕ Enregistrer
                  </button>
                </div>
              </div>
            </div>

            {/* Liste des membres existants */}
            <div>
              <h4 style={{ marginBottom: '15px', color: '#2c3e50' }}>Membres de la famille ({membresFamille.length})</h4>
              {membresFamille.length > 0 ? (
                <div className="table-wrapper">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Photo</th>
                        <th>Nom</th>
                        <th>Prénom</th>
                        <th>Date de naissance</th>
                        <th>Lien de parenté</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {membresFamille.map((membre) => (
                        <tr key={membre.id}>
                          <td>
                            {membre.photo ? (
                              <img 
                                src={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${membre.photo}`} 
                                alt={`${membre.nom} ${membre.prenom}`}
                                style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #ddd' }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  if (e.target.nextSibling) {
                                    e.target.nextSibling.style.display = 'inline';
                                  }
                                }}
                              />
                            ) : null}
                            {!membre.photo && (
                              <span style={{ color: '#999', fontSize: '12px' }}>Pas de photo</span>
                            )}
                          </td>
                          <td>{membre.nom}</td>
                          <td>{membre.prenom}</td>
                          <td>{membre.date_naissance || '-'}</td>
                          <td>{membre.lien || '-'}</td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-danger"
                              onClick={() => handleDeleteMembreFamille(membre.id)}
                              style={{ padding: '5px 10px', fontSize: '12px' }}
                              disabled={!isPrincipal()}
                              title={!isPrincipal() ? 'Seul l\'administrateur principal peut supprimer' : 'Supprimer ce membre'}
                            >
                              🗑️ Supprimer
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#999', fontStyle: 'italic', background: '#fff', borderRadius: '5px', border: '1px solid #ddd' }}>
                  Aucun membre enregistré pour cette famille
                </div>
              )}
            </div>
          </div>
        )}

        {/* Modal de détails de la famille */}
        {showFamilleDetails && selectedFamilleDetails && (
          <div className="modal-overlay" onClick={() => setShowFamilleDetails(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', padding: 0 }}>
              <div className="modal-header">
                <h2>👨‍👩‍👧‍👦 Détails de la famille: {selectedFamilleDetails.nom_famille}</h2>
                <button className="modal-close" onClick={() => setShowFamilleDetails(false)}>✕</button>
              </div>
              <div className="modal-body" style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
                {/* Informations de la famille */}
                <div style={{ marginBottom: '30px' }}>
                  <h3 style={{ marginBottom: '15px', color: '#2c3e50', borderBottom: '2px solid #2c3e50', paddingBottom: '10px' }}>
                    📋 Informations de la famille
                  </h3>
                  
                  {/* Photo du chef de famille */}
                  {selectedFamilleDetails.photo_chef && (
                    <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                      <div style={{ display: 'inline-block', padding: '15px', background: '#f8f9fa', borderRadius: '8px', border: '2px solid #2c3e50' }}>
                        <strong style={{ display: 'block', color: '#666', fontSize: '12px', textTransform: 'uppercase', marginBottom: '10px' }}>
                          📷 Photo du chef de famille
                        </strong>
                        <img 
                          src={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${selectedFamilleDetails.photo_chef}`} 
                          alt={`Photo de ${selectedFamilleDetails.chef_famille_nom}`}
                          style={{ 
                            maxWidth: '250px', 
                            maxHeight: '300px', 
                            border: '2px solid #ddd', 
                            borderRadius: '5px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            objectFit: 'cover'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'block';
                          }}
                        />
                        <p style={{ display: 'none', color: '#999', fontStyle: 'italic', marginTop: '10px' }}>
                          Photo non disponible
                        </p>
                      </div>
                    </div>
                  )}
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                    <div style={{ padding: '10px', background: '#f8f9fa', borderRadius: '5px' }}>
                      <strong style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase' }}>Numéro de famille</strong>
                      <p style={{ margin: '5px 0 0 0', fontSize: '16px', fontWeight: 'bold', color: '#2c3e50' }}>
                        {selectedFamilleDetails.numero_famille || '-'}
                      </p>
                    </div>
                    <div style={{ padding: '10px', background: '#f8f9fa', borderRadius: '5px' }}>
                      <strong style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase' }}>Nom de la famille</strong>
                      <p style={{ margin: '5px 0 0 0', fontSize: '16px', fontWeight: 'bold', color: '#2c3e50' }}>
                        {selectedFamilleDetails.nom_famille || '-'}
                      </p>
                    </div>
                    <div style={{ padding: '10px', background: '#f8f9fa', borderRadius: '5px' }}>
                      <strong style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase' }}>Chef de famille</strong>
                      <p style={{ margin: '5px 0 0 0', fontSize: '16px', fontWeight: 'bold', color: '#2c3e50' }}>
                        {selectedFamilleDetails.chef_famille_nom || '-'}
                      </p>
                    </div>
                    <div style={{ padding: '10px', background: '#f8f9fa', borderRadius: '5px' }}>
                      <strong style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase' }}>Adresse</strong>
                      <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#2c3e50' }}>
                        {selectedFamilleDetails.adresse || '-'}
                      </p>
                    </div>
                    <div style={{ padding: '10px', background: '#f8f9fa', borderRadius: '5px' }}>
                      <strong style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase' }}>Quartier</strong>
                      <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#2c3e50' }}>
                        {selectedFamilleDetails.quartier || '-'}
                      </p>
                    </div>
                    <div style={{ padding: '10px', background: '#f8f9fa', borderRadius: '5px' }}>
                      <strong style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase' }}>Commune</strong>
                      <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#2c3e50' }}>
                        {selectedFamilleDetails.commune || '-'}
                      </p>
                    </div>
                    <div style={{ padding: '10px', background: '#f8f9fa', borderRadius: '5px' }}>
                      <strong style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase' }}>Téléphone</strong>
                      <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#2c3e50' }}>
                        {selectedFamilleDetails.telephone || '-'}
                      </p>
                    </div>
                    <div style={{ padding: '10px', background: '#f8f9fa', borderRadius: '5px' }}>
                      <strong style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase' }}>Nombre de membres</strong>
                      <p style={{ margin: '5px 0 0 0', fontSize: '16px', fontWeight: 'bold', color: '#2c3e50' }}>
                        {selectedFamilleDetails.nombre_membres || (selectedFamilleDetails.membres?.length || 0)}
                      </p>
                    </div>
                    <div style={{ padding: '10px', background: '#f8f9fa', borderRadius: '5px', gridColumn: '1 / -1' }}>
                      <strong style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase' }}>Observations</strong>
                      <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#2c3e50', whiteSpace: 'pre-wrap' }}>
                        {selectedFamilleDetails.observations || '-'}
                      </p>
                    </div>
                    <div style={{ padding: '10px', background: '#f8f9fa', borderRadius: '5px' }}>
                      <strong style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase' }}>Date d'enregistrement</strong>
                      <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#2c3e50' }}>
                        {selectedFamilleDetails.created_at ? new Date(selectedFamilleDetails.created_at).toLocaleDateString('fr-FR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : '-'}
                      </p>
                    </div>
                    <div style={{ padding: '10px', background: '#f8f9fa', borderRadius: '5px' }}>
                      <strong style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase' }}>Statut</strong>
                      <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: selectedFamilleDetails.statut === 'Active' ? '#28a745' : '#dc3545', fontWeight: 'bold' }}>
                        {selectedFamilleDetails.statut || 'Active'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Liste des membres de la famille */}
                <div>
                  <h3 style={{ marginBottom: '15px', color: '#2c3e50', borderBottom: '2px solid #2c3e50', paddingBottom: '10px' }}>
                    👥 Membres de la famille ({selectedFamilleDetails.membres?.length || 0})
                  </h3>
                  {selectedFamilleDetails.membres && selectedFamilleDetails.membres.length > 0 ? (
                    <div className="table-wrapper" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      <table className="table" style={{ fontSize: '14px' }}>
                        <thead style={{ position: 'sticky', top: 0, background: '#f8f9fa', color: '#000', zIndex: 1, borderBottom: '2px solid #2c3e50' }}>
                          <tr>
                            <th style={{ padding: '12px', textAlign: 'left', color: '#000', fontWeight: 'bold' }}>Photo</th>
                            <th style={{ padding: '12px', textAlign: 'left', color: '#000', fontWeight: 'bold' }}>Nom</th>
                            <th style={{ padding: '12px', textAlign: 'left', color: '#000', fontWeight: 'bold' }}>Prénom</th>
                            <th style={{ padding: '12px', textAlign: 'left', color: '#000', fontWeight: 'bold' }}>Date de naissance</th>
                            <th style={{ padding: '12px', textAlign: 'left', color: '#000', fontWeight: 'bold' }}>Lien de parenté</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedFamilleDetails.membres.map((membre) => (
                            <tr key={membre.id} style={{ borderBottom: '1px solid #eee' }}>
                              <td style={{ padding: '12px' }}>
                                {membre.photo ? (
                                  <img 
                                    src={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${membre.photo}`} 
                                    alt={`${membre.nom} ${membre.prenom}`}
                                    style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #ddd' }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      if (e.target.nextSibling) {
                                        e.target.nextSibling.style.display = 'inline';
                                      }
                                    }}
                                  />
                                ) : null}
                                {!membre.photo && (
                                  <span style={{ color: '#999', fontSize: '12px' }}>Pas de photo</span>
                                )}
                              </td>
                              <td style={{ padding: '12px' }}>{membre.nom || '-'}</td>
                              <td style={{ padding: '12px' }}>{membre.prenom || '-'}</td>
                              <td style={{ padding: '12px' }}>
                                {membre.date_naissance 
                                  ? new Date(membre.date_naissance).toLocaleDateString('fr-FR', {
                                      year: 'numeric',
                                      month: 'long',
                                      day: 'numeric'
                                    })
                                  : '-'}
                              </td>
                              <td style={{ padding: '12px' }}>{membre.lien || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#999', fontStyle: 'italic', background: '#f8f9fa', borderRadius: '5px', border: '1px solid #ddd' }}>
                      Aucun membre enregistré pour cette famille
                    </div>
                  )}
                </div>

              </div>
              
              <div className="modal-footer" style={{ padding: '15px 20px', borderTop: '1px solid #ddd', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowFamilleDetails(false);
                  }}
                >
                  Fermer
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setShowFamilleDetails(false);
                    handleGestionMembres(selectedFamilleDetails);
                  }}
                >
                  👨‍👩‍👧‍👦 Gérer les membres
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'cotisations' && (
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
              <h2 style={{ margin: 0 }}>Liste des cotisations ({cotisations.length})</h2>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', width: '100%', alignItems: 'center' }}>
                <div style={{ position: 'relative', flex: '1', minWidth: '200px', maxWidth: '300px' }}>
                  <input
                    type="text"
                    placeholder="🔍 Rechercher une cotisation..."
                    value={searchCotisations}
                    onChange={(e) => {
                      setSearchCotisations(e.target.value);
                      setCurrentPageCotisations(1);
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 40px 10px 15px',
                      border: '2px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'border-color 0.3s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#2c3e50'}
                    onBlur={(e) => e.target.style.borderColor = '#ddd'}
                  />
                  {searchCotisations && (
                    <button
                      onClick={() => setSearchCotisations('')}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '18px',
                        color: '#999'
                      }}
                      title="Effacer la recherche"
                    >
                      ×
                    </button>
                  )}
                </div>
                <div style={{ minWidth: '200px', maxWidth: '300px' }}>
                  <select
                    value={selectedAssociationCotisations}
                    onChange={(e) => {
                      setSelectedAssociationCotisations(e.target.value);
                      setCurrentPageCotisations(1);
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 15px',
                      border: '2px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'border-color 0.3s',
                      backgroundColor: 'white'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#2c3e50'}
                    onBlur={(e) => e.target.style.borderColor = '#ddd'}
                  >
                    <option value="">Toutes les associations</option>
                    {associationsCotisations.map(asso => (
                      <option key={asso} value={asso}>{asso}</option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button
                    className="btn btn-primary"
                    onClick={handlePrintCotisations}
                    style={{ fontSize: '14px', padding: '8px 15px' }}
                    title="Imprimer la liste"
                  >
                    🖨️ Imprimer
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={handleDownloadPDFCotisations}
                    style={{ fontSize: '14px', padding: '8px 15px' }}
                    title="Télécharger en PDF"
                  >
                    📥 Télécharger PDF
                  </button>
                </div>
              </div>
            </div>
            {(searchCotisations || selectedAssociationCotisations) && (
              <div style={{ marginBottom: '15px', color: '#666', fontSize: '14px' }}>
                {(() => {
                  const filtered = cotisations.filter(c => {
                    if (selectedAssociationCotisations && c.membre?.association !== selectedAssociationCotisations) return false;
                    if (!searchCotisations) return true;
                    const search = searchCotisations.toLowerCase();
                    const moisNoms = [
                      'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
                      'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
                    ];
                    const moisNom = moisNoms[c.mois - 1] || '';
                    return (
                      c.membre?.numero_matricule?.toLowerCase().includes(search) ||
                      c.membre?.nom?.toLowerCase().includes(search) ||
                      c.membre?.prenom?.toLowerCase().includes(search) ||
                      moisNom.includes(search) ||
                      c.annee?.toString().includes(search) ||
                      c.statut?.toLowerCase().includes(search) ||
                      c.montant?.toString().includes(search)
                    );
                  });
                  return `${filtered.length} cotisation(s) ${selectedAssociationCotisations ? `de l'association "${selectedAssociationCotisations}"` : ''} ${searchCotisations ? 'trouvée(s)' : ''}`;
                })()}
              </div>
            )}
            
            {(() => {
              // Filtrer les cotisations selon la recherche et l'association
              const filteredCotisations = cotisations.filter(cotisation => {
                // Filtre par association
                if (selectedAssociationCotisations && cotisation.membre?.association !== selectedAssociationCotisations) {
                  return false;
                }
                
                // Filtre par recherche
                if (!searchCotisations) return true;
                const search = searchCotisations.toLowerCase();
                const moisNoms = [
                  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
                  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
                ];
                const moisNom = moisNoms[cotisation.mois - 1] || '';
                return (
                  cotisation.membre?.numero_matricule?.toLowerCase().includes(search) ||
                  cotisation.membre?.nom?.toLowerCase().includes(search) ||
                  cotisation.membre?.prenom?.toLowerCase().includes(search) ||
                  moisNom.includes(search) ||
                  cotisation.annee?.toString().includes(search) ||
                  cotisation.statut?.toLowerCase().includes(search) ||
                  cotisation.montant?.toString().includes(search)
                );
              });

              if (filteredCotisations.length === 0) {
                return (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                    <p style={{ fontSize: '16px' }}>
                      {searchCotisations ? 'Aucune cotisation trouvée' : 'Aucune cotisation enregistrée'}
                    </p>
                  </div>
                );
              }

              // Grouper les cotisations par membre
              const cotisationsParMembre = {};
              filteredCotisations.forEach(cotisation => {
                const membreId = cotisation.membre_id;
                if (!cotisationsParMembre[membreId]) {
                  cotisationsParMembre[membreId] = {
                    membre: cotisation.membre,
                    cotisations: []
                  };
                }
                cotisationsParMembre[membreId].cotisations.push(cotisation);
              });

              // Trier les membres par nom
              const membresIds = Object.keys(cotisationsParMembre).sort((a, b) => {
                const membreA = cotisationsParMembre[a].membre;
                const membreB = cotisationsParMembre[b].membre;
                const nomA = `${membreA?.nom || ''} ${membreA?.prenom || ''}`.trim();
                const nomB = `${membreB?.nom || ''} ${membreB?.prenom || ''}`.trim();
                return nomA.localeCompare(nomB);
              });

              const moisNoms = [
                'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
                'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
              ];

              const paginatedMembresIds = paginate(membresIds, currentPageCotisations, itemsPerPage);
              return (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {paginatedMembresIds.map(membreId => {
                    const { membre, cotisations: cotisationsMembre } = cotisationsParMembre[membreId];
                    const isExpanded = membresExpandes[membreId] || false;
                    const cotisationsPayees = cotisationsMembre.filter(c => c.statut === 'paye').length;
                    const cotisationsImpayees = cotisationsMembre.filter(c => c.statut === 'impaye').length;
                    const totalMontant = cotisationsMembre.reduce((sum, c) => sum + parseFloat(c.montant || 0), 0);

                    return (
                      <div
                        key={membreId}
                        style={{
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          backgroundColor: '#fff'
                        }}
                      >
                        <div
                          style={{
                            padding: '15px',
                            backgroundColor: cotisationsImpayees > 0 ? '#fff3cd' : '#d4edda',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            transition: 'background-color 0.2s'
                          }}
                          onClick={() => setMembresExpandes(prev => ({ ...prev, [membreId]: !isExpanded }))}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = cotisationsImpayees > 0 ? '#ffe69c' : '#c3e6cb'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = cotisationsImpayees > 0 ? '#fff3cd' : '#d4edda'}
                        >
                          <div style={{ flex: 1 }}>
                            <h3 style={{ margin: 0, fontSize: '16px', color: '#2c3e50' }}>
                              {membre?.numero_matricule || '-'} - {membre?.nom || ''} {membre?.prenom || ''}
                            </h3>
                            <div style={{ fontSize: '13px', color: '#666', marginTop: '5px' }}>
                              {cotisationsMembre.length} cotisation(s) • 
                              <span style={{ color: '#28a745', marginLeft: '5px' }}>{cotisationsPayees} payée(s)</span> • 
                              <span style={{ color: '#dc3545', marginLeft: '5px' }}>{cotisationsImpayees} impayée(s)</span> • 
                              <span style={{ fontWeight: 'bold', marginLeft: '5px' }}>
                                Total: {totalMontant.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF
                              </span>
                            </div>
                          </div>
                          <div style={{ fontSize: '20px', color: '#666', marginLeft: '15px' }}>
                            {isExpanded ? '▼' : '▶'}
                          </div>
                        </div>

                        {isExpanded && (
                          <div style={{ padding: '15px', backgroundColor: '#f8f9fa' }}>
                            <div className="table-wrapper" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                              <table className="table" style={{ margin: 0 }}>
                                <thead>
                                  <tr>
                                    <th style={{ whiteSpace: 'nowrap' }}>Mois/Année</th>
                                    <th style={{ whiteSpace: 'nowrap' }}>Semaine</th>
                                    <th style={{ whiteSpace: 'nowrap' }}>Montant</th>
                                    <th style={{ whiteSpace: 'nowrap' }}>Statut</th>
                                    <th style={{ whiteSpace: 'nowrap' }}>Date paiement</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {cotisationsMembre
                                    .sort((a, b) => {
                                      if (a.annee !== b.annee) return b.annee - a.annee;
                                      if (a.mois !== b.mois) return b.mois - a.mois;
                                      return (a.semaine || 0) - (b.semaine || 0);
                                    })
                                    .map(cotisation => (
                                      <tr key={cotisation.id} style={{
                                        backgroundColor: cotisation.statut === 'paye' ? '#d4edda' : 'transparent'
                                      }}>
                                        <td data-label="Mois/Année" style={{ whiteSpace: 'nowrap' }}>
                                          {moisNoms[cotisation.mois - 1]} {cotisation.annee}
                                        </td>
                                        <td data-label="Semaine" style={{ whiteSpace: 'nowrap' }}>
                                          {cotisation.semaine ? `Semaine ${cotisation.semaine}` : '-'}
                                        </td>
                                        <td data-label="Montant" style={{ whiteSpace: 'nowrap' }}>
                                          {parseFloat(cotisation.montant).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF
                                        </td>
                                        <td data-label="Statut" style={{ whiteSpace: 'nowrap' }}>
                                          {cotisation.statut === 'paye' ? (
                                            <span className="badge badge-success">Payée</span>
                                          ) : (
                                            <span className="badge badge-danger">Impayée</span>
                                          )}
                                        </td>
                                        <td data-label="Date paiement" style={{ whiteSpace: 'nowrap' }}>
                                          {cotisation.date_paiement ? new Date(cotisation.date_paiement).toLocaleDateString('fr-FR') : '-'}
                                        </td>
                                      </tr>
                                    ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  </div>
                  {(() => {
                    const filtered = cotisations.filter(c => {
                      if (selectedAssociationCotisations && c.membre?.association !== selectedAssociationCotisations) {
                        return false;
                      }
                      if (!searchCotisations) return true;
                      const search = searchCotisations.toLowerCase();
                      return (
                        c.membre?.numero_matricule?.toLowerCase().includes(search) ||
                        c.membre?.nom?.toLowerCase().includes(search) ||
                        c.membre?.prenom?.toLowerCase().includes(search)
                      );
                    });
                    const cotisationsParMembre = {};
                    filtered.forEach(cotisation => {
                      const membreId = cotisation.membre_id;
                      if (!cotisationsParMembre[membreId]) {
                        cotisationsParMembre[membreId] = {
                          membre: cotisation.membre,
                          cotisations: []
                        };
                      }
                      cotisationsParMembre[membreId].cotisations.push(cotisation);
                    });
                    const membresIds = Object.keys(cotisationsParMembre);
                    const totalPages = getTotalPages(membresIds, itemsPerPage);
                    return (
                      <Pagination 
                        currentPage={currentPageCotisations} 
                        totalPages={totalPages} 
                        onPageChange={(page) => {
                          setCurrentPageCotisations(page);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }} 
                      />
                    );
                  })()}
                </>
              );
            })()}
          </div>
        )}

        {activeTab === 'paiements' && (
          <div className="card">
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              flexWrap: 'wrap',
              gap: '15px'
            }}>
              <h2 style={{ margin: 0 }}>Liste des paiements ({paiementsCombines.length})</h2>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', width: '100%', alignItems: 'center' }}>
                <div style={{ position: 'relative', flex: '1', minWidth: '200px', maxWidth: '300px' }}>
                  <input
                    type="text"
                    placeholder="🔍 Rechercher un paiement..."
                    value={searchPaiements}
                    onChange={(e) => {
                      setSearchPaiements(e.target.value);
                      setCurrentPagePaiements(1);
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 40px 10px 15px',
                      border: '2px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'border-color 0.3s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#2c3e50'}
                    onBlur={(e) => e.target.style.borderColor = '#ddd'}
                  />
                  {searchPaiements && (
                    <button
                      onClick={() => setSearchPaiements('')}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '18px',
                        color: '#999'
                      }}
                      title="Effacer la recherche"
                    >
                      ×
                    </button>
                  )}
                </div>
                <div style={{ minWidth: '200px', maxWidth: '300px' }}>
                  <select
                    value={selectedAssociationPaiements}
                    onChange={(e) => {
                      setSelectedAssociationPaiements(e.target.value);
                      setCurrentPagePaiements(1);
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 15px',
                      border: '2px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'border-color 0.3s',
                      backgroundColor: 'white'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#2c3e50'}
                    onBlur={(e) => e.target.style.borderColor = '#ddd'}
                  >
                    <option value="">Toutes les associations</option>
                    {associationsPaiements.map(asso => (
                      <option key={asso} value={asso}>{asso}</option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button
                    className="btn btn-primary"
                    onClick={handlePrintPaiements}
                    style={{ fontSize: '14px', padding: '8px 15px' }}
                    title="Imprimer la liste"
                  >
                    🖨️ Imprimer
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={handleDownloadPDFPaiements}
                    style={{ fontSize: '14px', padding: '8px 15px' }}
                    title="Télécharger en PDF"
                  >
                    📥 Télécharger PDF
                  </button>
                </div>
              </div>
            </div>
            {(searchPaiements || selectedAssociationPaiements) && (
              <div style={{ marginBottom: '15px', color: '#666', fontSize: '14px' }}>
                {(() => {
                  const filtered = paiementsCombines.filter(p => {
                    // Filtre par association (pour membres) ou commune (pour familles)
                    if (selectedAssociationPaiements) {
                      if (p.source_type === 'membre' && p.membre?.association !== selectedAssociationPaiements) return false;
                      if (p.source_type === 'famille' && p.famille?.commune !== selectedAssociationPaiements) return false;
                    }
                    if (!searchPaiements) return true;
                    const search = searchPaiements.toLowerCase();
                    const typePaiement = p.type_paiement === 'cotisation_initial' ? 'cotisation initiale' :
                                      p.type_paiement === 'cotisation_mensuelle' ? 'cotisation mensuelle' :
                                      p.type_paiement === 'cotisation_famille' ? 'cotisation famille' :
                                      p.type_paiement === 'autre' ? 'frais de carte' : '';
                    const semaineText = p.semaine ? `semaine ${p.semaine}` : '';
                    // Recherche adaptée pour membres et familles
                    const nomMembre = p.membre ? `${p.membre.nom || ''} ${p.membre.prenom || ''}`.trim().toLowerCase() : '';
                    const nomFamille = p.famille ? `${p.famille.nom_famille || ''} ${p.famille.chef_famille_nom || ''}`.trim().toLowerCase() : '';
                    const matriculeMembre = p.membre?.numero_matricule?.toLowerCase() || '';
                    const numeroFamille = p.famille?.numero_famille?.toLowerCase() || '';
                    return (
                      nomMembre.includes(search) ||
                      nomFamille.includes(search) ||
                      matriculeMembre.includes(search) ||
                      numeroFamille.includes(search) ||
                      p.date_paiement?.toLowerCase().includes(search) ||
                      p.montant?.toString().includes(search) ||
                      typePaiement.includes(search) ||
                      semaineText.includes(search) ||
                      p.numero_recu?.toLowerCase().includes(search)
                    );
                  });
                  return `${filtered.length} paiement(s) ${selectedAssociationPaiements ? `de l'association "${selectedAssociationPaiements}"` : ''} ${searchPaiements ? 'trouvé(s)' : ''}`;
                })()}
              </div>
            )}
            
            {(() => {
              // Filtrer les paiements selon la recherche et l'association
              const filteredPaiements = paiementsCombines.filter(paiement => {
                // Filtre par association (pour membres) ou commune (pour familles)
                if (selectedAssociationPaiements) {
                  if (paiement.source_type === 'membre' && paiement.membre?.association !== selectedAssociationPaiements) {
                  return false;
                  }
                  if (paiement.source_type === 'famille' && paiement.famille?.commune !== selectedAssociationPaiements) {
                    return false;
                  }
                }
                
                // Filtre par recherche
                if (!searchPaiements) return true;
                const search = searchPaiements.toLowerCase();
                const typePaiement = paiement.type_paiement === 'cotisation_initial' ? 'cotisation initiale' :
                                  paiement.type_paiement === 'cotisation_mensuelle' ? 'cotisation mensuelle' :
                                  paiement.type_paiement === 'cotisation_famille' ? 'cotisation famille' :
                                  paiement.type_paiement === 'autre' ? 'frais de carte' : '';
                const semaineText = paiement.semaine ? `semaine ${paiement.semaine}` : '';
                // Recherche adaptée pour membres et familles
                const nomMembre = paiement.membre ? `${paiement.membre.nom || ''} ${paiement.membre.prenom || ''}`.trim().toLowerCase() : '';
                const nomFamille = paiement.famille ? `${paiement.famille.nom_famille || ''} ${paiement.famille.chef_famille_nom || ''}`.trim().toLowerCase() : '';
                const matriculeMembre = paiement.membre?.numero_matricule?.toLowerCase() || '';
                const numeroFamille = paiement.famille?.numero_famille?.toLowerCase() || '';
                return (
                  nomMembre.includes(search) ||
                  nomFamille.includes(search) ||
                  matriculeMembre.includes(search) ||
                  numeroFamille.includes(search) ||
                  paiement.date_paiement?.toLowerCase().includes(search) ||
                  paiement.montant?.toString().includes(search) ||
                  typePaiement.includes(search) ||
                  semaineText.includes(search) ||
                  paiement.numero_recu?.toLowerCase().includes(search)
                );
              });

              if (filteredPaiements.length === 0) {
                return (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                    <p style={{ fontSize: '16px' }}>
                      {searchPaiements ? 'Aucun paiement trouvé' : 'Aucun paiement enregistré'}
                    </p>
                  </div>
                );
              }

              // Grouper les paiements par source (membre ou famille)
              const paiementsParSource = {};
              filteredPaiements.forEach(paiement => {
                const sourceKey = paiement.source_type === 'membre' 
                  ? `membre_${paiement.membre_id}` 
                  : `famille_${paiement.famille_id}`;
                if (!paiementsParSource[sourceKey]) {
                  paiementsParSource[sourceKey] = {
                    source: paiement.source,
                    source_type: paiement.source_type,
                    membre: paiement.membre,
                    famille: paiement.famille,
                    paiements: []
                  };
                }
                paiementsParSource[sourceKey].paiements.push(paiement);
              });

              // Trier les sources par nom
              const sourceKeys = Object.keys(paiementsParSource).sort((a, b) => {
                const sourceA = paiementsParSource[a];
                const sourceB = paiementsParSource[b];
                let nomA = '';
                let nomB = '';
                if (sourceA.source_type === 'membre') {
                  nomA = `${sourceA.membre?.nom || ''} ${sourceA.membre?.prenom || ''}`.trim();
                } else {
                  nomA = `${sourceA.famille?.nom_famille || ''} ${sourceA.famille?.chef_famille_nom || ''}`.trim();
                }
                if (sourceB.source_type === 'membre') {
                  nomB = `${sourceB.membre?.nom || ''} ${sourceB.membre?.prenom || ''}`.trim();
                } else {
                  nomB = `${sourceB.famille?.nom_famille || ''} ${sourceB.famille?.chef_famille_nom || ''}`.trim();
                }
                return nomA.localeCompare(nomB);
              });

              const paginatedSourceKeys = paginate(sourceKeys, currentPagePaiements, itemsPerPage);
              return (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {paginatedSourceKeys.map(sourceKey => {
                    const { membre, famille, source_type, paiements: paiementsSource } = paiementsParSource[sourceKey];
                    const isExpanded = membresExpandesPaiements[sourceKey] || false;
                    const totalMontant = paiementsSource.reduce((sum, p) => sum + parseFloat(p.montant || 0), 0);
                    const cotisationsInitiales = paiementsSource.filter(p => p.type_paiement === 'cotisation_initial').length;
                    const cotisationsMensuelles = paiementsSource.filter(p => p.type_paiement === 'cotisation_mensuelle').length;
                    const cotisationsFamilles = paiementsSource.filter(p => p.type_paiement === 'cotisation_famille').length;
                    const autres = paiementsSource.filter(p => p.type_paiement === 'autre').length;

                    // Déterminer le nom et l'identifiant selon le type
                    const nom = source_type === 'membre' 
                      ? `${membre?.nom || ''} ${membre?.prenom || ''}`.trim()
                      : `${famille?.nom_famille || ''} (${famille?.chef_famille_nom || ''})`.trim();
                    const identifiant = source_type === 'membre' 
                      ? membre?.numero_matricule || '-'
                      : famille?.numero_famille || '-';
                    const bgColor = source_type === 'membre' ? '#e3f2fd' : '#fff3e0';
                    const hoverColor = source_type === 'membre' ? '#bbdefb' : '#ffe0b2';

                    return (
                      <div
                        key={sourceKey}
                        style={{
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          backgroundColor: '#fff'
                        }}
                      >
                        <div
                          style={{
                            padding: '15px',
                            backgroundColor: bgColor,
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            transition: 'background-color 0.2s'
                          }}
                          onClick={() => setMembresExpandesPaiements(prev => ({ ...prev, [sourceKey]: !isExpanded }))}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = hoverColor}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = bgColor}
                        >
                          <div style={{ flex: 1 }}>
                            <h3 style={{ margin: 0, fontSize: '16px', color: '#2c3e50' }}>
                              {identifiant} - {nom} {source_type === 'famille' && <span style={{ fontSize: '12px', color: '#666' }}>(Famille)</span>}
                            </h3>
                            <div style={{ fontSize: '13px', color: '#666', marginTop: '5px' }}>
                              {paiementsSource.length} paiement(s) • 
                              {cotisationsInitiales > 0 && <span style={{ marginLeft: '5px' }}>{cotisationsInitiales} initiale(s)</span>}
                              {cotisationsMensuelles > 0 && <span style={{ marginLeft: '5px' }}>{cotisationsMensuelles} mensuelle(s)</span>}
                              {cotisationsFamilles > 0 && <span style={{ marginLeft: '5px' }}>{cotisationsFamilles} famille(s)</span>}
                              {autres > 0 && <span style={{ marginLeft: '5px' }}>{autres} autre(s)</span>}
                              <span style={{ fontWeight: 'bold', marginLeft: '10px', color: '#1565c0' }}>
                                Total: {totalMontant.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF
                              </span>
                            </div>
                          </div>
                          <div style={{ fontSize: '20px', color: '#666', marginLeft: '15px' }}>
                            {isExpanded ? '▼' : '▶'}
                          </div>
                        </div>

                        {isExpanded && (
                          <div style={{ padding: '15px', backgroundColor: '#f8f9fa' }}>
                            <div className="table-wrapper" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                              <table className="table" style={{ margin: 0 }}>
                                <thead>
                                  <tr>
                                    <th style={{ whiteSpace: 'nowrap' }}>Date</th>
                                    <th style={{ whiteSpace: 'nowrap' }}>Montant</th>
                                    <th style={{ whiteSpace: 'nowrap' }}>Type</th>
                                    {source_type === 'membre' && <th style={{ whiteSpace: 'nowrap' }}>Semaine</th>}
                                    <th style={{ whiteSpace: 'nowrap' }}>N° Reçu</th>
                                    <th style={{ whiteSpace: 'nowrap' }}>Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {paiementsSource
                                    .sort((a, b) => {
                                      const dateA = new Date(a.date_paiement);
                                      const dateB = new Date(b.date_paiement);
                                      return dateB - dateA; // Plus récent en premier
                                    })
                                    .map(paiement => (
                                      <tr key={paiement.id}>
                                        <td data-label="Date" style={{ whiteSpace: 'nowrap' }}>
                                          {paiement.date_paiement ? new Date(paiement.date_paiement).toLocaleDateString('fr-FR') : '-'}
                                        </td>
                                        <td data-label="Montant" style={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>
                                          {parseFloat(paiement.montant).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF
                                        </td>
                                        <td data-label="Type" style={{ whiteSpace: 'nowrap' }}>
                                          {paiement.type_paiement === 'cotisation_initial' && 'Cotisation initiale'}
                                          {paiement.type_paiement === 'cotisation_mensuelle' && 'Cotisation mensuelle'}
                                          {paiement.type_paiement === 'cotisation_famille' && 'Cotisation famille'}
                                          {paiement.type_paiement === 'autre' && 'Frais de carte'}
                                        </td>
                                        {source_type === 'membre' && (
                                        <td data-label="Semaine" style={{ whiteSpace: 'nowrap' }}>
                                          {paiement.semaine ? `Semaine ${paiement.semaine}` : '-'}
                                        </td>
                                        )}
                                        <td data-label="N° Reçu" style={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                                          {paiement.numero_recu}
                                        </td>
                                        <td data-label="Actions" style={{ whiteSpace: 'nowrap' }}>
                                          <button
                                            className="btn btn-danger"
                                            onClick={async () => {
                                              const sourceNom = paiement.source_type === 'membre'
                                                ? `${paiement.membre?.nom || ''} ${paiement.membre?.prenom || ''}`.trim() || 'ce membre'
                                                : `${paiement.famille?.nom_famille || ''} (${paiement.famille?.chef_famille_nom || ''})`.trim() || 'cette famille';
                                              const montant = parseFloat(paiement.montant).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
                                              const message = `Êtes-vous sûr de vouloir supprimer le paiement de ${montant} CDF pour ${sourceNom} (Reçu: ${paiement.numero_recu}) ?\n\n⚠️ Cette action remettra les cotisations associées en statut "impayé".`;
                                              
                                              if (!window.confirm(message)) {
                                                return;
                                              }
                                              
                                              try {
                                                let response;
                                                if (paiement.source_type === 'famille') {
                                                  response = await deletePaiementFamille(paiement.id);
                                                } else {
                                                  response = await deletePaiement(paiement.id);
                                                }
                                                const message = response.data?.cotisations_affectees > 0 
                                                  ? `Le paiement a été supprimé. ${response.data.cotisations_affectees} cotisation(s) remise(s) en statut "impayé".`
                                                  : 'Le paiement a été supprimé avec succès.';
                                                
                                                notificationService.success('Paiement supprimé', message);
                                                
                                                // Recharger les données pour mettre à jour les listes de paiements et cotisations
                                                await loadData();
                                                
                                                // Si on est sur l'onglet cotisations, s'assurer qu'il est visible
                                                if (activeTab !== 'cotisations' && response.data.cotisations_affectees > 0) {
                                                  // Optionnel : basculer vers l'onglet cotisations pour voir les changements
                                                  // setActiveTab('cotisations');
                                                }
                                              } catch (error) {
                                                notificationService.error('Erreur', error.response?.data?.message || 'Erreur lors de la suppression du paiement');
                                              }
                                            }}
                                            style={{
                                              fontSize: '12px',
                                              padding: '5px 10px'
                                            }}
                                            title="Supprimer ce paiement"
                                          >
                                            🗑️ Supprimer
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {(() => {
                  // Utiliser la même logique de filtrage que plus haut
                  const filteredPaiements = paiementsCombines.filter(paiement => {
                    if (selectedAssociationPaiements) {
                      if (paiement.source_type === 'membre' && paiement.membre?.association !== selectedAssociationPaiements) {
                      return false;
                      }
                      if (paiement.source_type === 'famille' && paiement.famille?.commune !== selectedAssociationPaiements) {
                        return false;
                      }
                    }
                    if (!searchPaiements) return true;
                    const search = searchPaiements.toLowerCase();
                    const typePaiement = paiement.type_paiement === 'cotisation_initial' ? 'cotisation initiale' :
                                      paiement.type_paiement === 'cotisation_mensuelle' ? 'cotisation mensuelle' :
                                      paiement.type_paiement === 'cotisation_famille' ? 'cotisation famille' :
                                      paiement.type_paiement === 'autre' ? 'frais de carte' : '';
                    const semaineText = paiement.semaine ? `semaine ${paiement.semaine}` : '';
                    const nomMembre = paiement.membre ? `${paiement.membre.nom || ''} ${paiement.membre.prenom || ''}`.trim().toLowerCase() : '';
                    const nomFamille = paiement.famille ? `${paiement.famille.nom_famille || ''} ${paiement.famille.chef_famille_nom || ''}`.trim().toLowerCase() : '';
                    const matriculeMembre = paiement.membre?.numero_matricule?.toLowerCase() || '';
                    const numeroFamille = paiement.famille?.numero_famille?.toLowerCase() || '';
                    return (
                      nomMembre.includes(search) ||
                      nomFamille.includes(search) ||
                      matriculeMembre.includes(search) ||
                      numeroFamille.includes(search) ||
                      paiement.date_paiement?.toLowerCase().includes(search) ||
                      paiement.montant?.toString().includes(search) ||
                      typePaiement.includes(search) ||
                      semaineText.includes(search) ||
                      paiement.numero_recu?.toLowerCase().includes(search)
                    );
                  });
                  // Grouper par source (membre ou famille)
                  const paiementsParSource = {};
                  filteredPaiements.forEach(paiement => {
                    const sourceKey = paiement.source_type === 'membre' 
                      ? `membre_${paiement.membre_id}` 
                      : `famille_${paiement.famille_id}`;
                    if (!paiementsParSource[sourceKey]) {
                      paiementsParSource[sourceKey] = {
                        source: paiement.source,
                        source_type: paiement.source_type,
                        membre: paiement.membre,
                        famille: paiement.famille,
                        paiements: []
                      };
                    }
                    paiementsParSource[sourceKey].paiements.push(paiement);
                  });
                  const sourceKeys = Object.keys(paiementsParSource);
                  const totalPages = getTotalPages(sourceKeys, itemsPerPage);
                  return (
                    <Pagination 
                      currentPage={currentPagePaiements} 
                      totalPages={totalPages} 
                      onPageChange={(page) => {
                        setCurrentPagePaiements(page);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }} 
                    />
                  );
                })()}
                </>
              );
            })()}
          </div>
        )}

        {activeTab === 'montants' && (
          <div>
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
                <h2 style={{ flex: '1 1 100%', marginBottom: '10px' }}>Gestion des montants mensuels</h2>
                <button 
                  className="btn btn-success"
                  onClick={() => {
                    setEditingMontant(null);
                    setMontantForm({
                      mois: new Date().getMonth() + 1,
                      annee: new Date().getFullYear(),
                      montant_mensuel: ''
                    });
                    setShowMontantForm(true);
                  }}
                  style={{ flex: '1 1 auto', minWidth: '150px' }}
                >
                  + Fixer un montant mensuel
                </button>
              </div>

              {showMontantForm && (
                <div className="card" style={{ marginBottom: '20px', background: '#f8f9fa' }}>
                  <h3 style={{ marginBottom: '15px' }}>{editingMontant ? 'Modifier le montant mensuel' : 'Fixer le montant mensuel'}</h3>
                  <form onSubmit={handleMontantSubmit}>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Mois *</label>
                        <select
                          value={montantForm.mois}
                          onChange={(e) => setMontantForm({ ...montantForm, mois: parseInt(e.target.value) })}
                          required
                          disabled={!!editingMontant}
                        >
                          <option value="1">Janvier</option>
                          <option value="2">Février</option>
                          <option value="3">Mars</option>
                          <option value="4">Avril</option>
                          <option value="5">Mai</option>
                          <option value="6">Juin</option>
                          <option value="7">Juillet</option>
                          <option value="8">Août</option>
                          <option value="9">Septembre</option>
                          <option value="10">Octobre</option>
                          <option value="11">Novembre</option>
                          <option value="12">Décembre</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Année *</label>
                        <input
                          type="number"
                          value={montantForm.annee}
                          onChange={(e) => setMontantForm({ ...montantForm, annee: parseInt(e.target.value) })}
                          min="2020"
                          max="2100"
                          required
                          disabled={!!editingMontant}
                        />
                      </div>
                      <div className="form-group">
                        <label>Montant mensuel (CDF) *</label>
                        <input
                          type="number"
                          step="0.01"
                          value={montantForm.montant_mensuel}
                          onChange={(e) => setMontantForm({ ...montantForm, montant_mensuel: e.target.value })}
                          placeholder="Ex: 10000"
                          required
                        />
                        <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                          Ce montant sera divisé en 4 semaines ({(parseFloat(montantForm.montant_mensuel) || 0) / 4} CDF par semaine)
                        </small>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                      <button type="submit" className="btn btn-primary">
                        {editingMontant ? 'Modifier le montant' : 'Fixer et générer les cotisations'}
                      </button>
                      <button 
                        type="button" 
                        className="btn btn-secondary"
                        onClick={() => {
                          setShowMontantForm(false);
                          setEditingMontant(null);
                          setMontantForm({
                            mois: new Date().getMonth() + 1,
                            annee: new Date().getFullYear(),
                            montant_mensuel: ''
                          });
                        }}
                      >
                        Annuler
                      </button>
                    </div>
                  </form>
                </div>
              )}

              <div className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                  <h3 style={{ margin: 0 }}>Historique des montants mensuels</h3>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button
                      className="btn btn-primary"
                      onClick={handlePrintMontants}
                      style={{ fontSize: '14px', padding: '8px 15px' }}
                      title="Imprimer la liste"
                    >
                      🖨️ Imprimer
                    </button>
                    <button
                      className="btn btn-success"
                      onClick={handleDownloadPDFMontants}
                      style={{ fontSize: '14px', padding: '8px 15px' }}
                      title="Télécharger en PDF"
                    >
                      📥 Télécharger PDF
                    </button>
                  </div>
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <input
                    type="text"
                    placeholder="🔍 Rechercher un montant (mois, année, montant)..."
                    value={searchMontants}
                    onChange={(e) => setSearchMontants(e.target.value)}
                    style={{
                      width: '100%',
                      maxWidth: '500px',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px'
                    }}
                  />
                  {searchMontants && (
                    <button
                      onClick={() => setSearchMontants('')}
                      style={{
                        marginLeft: '10px',
                        padding: '10px 15px',
                        background: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      ✕ Effacer
                    </button>
                  )}
                </div>
                <div className="table-wrapper" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th style={{ whiteSpace: 'nowrap' }}>Mois/Année</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Montant mensuel</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Montant hebdomadaire</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Date de création</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const filteredMontants = montantsMensuels.filter(montant => {
                          if (!searchMontants) return true;
                          const search = searchMontants.toLowerCase();
                          const moisNoms = [
                            'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
                            'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
                          ];
                          const moisNom = moisNoms[montant.mois - 1] || '';
                          return (
                            moisNom.includes(search) ||
                            montant.annee?.toString().includes(search) ||
                            montant.montant_mensuel?.toString().includes(search) ||
                            montant.montant_hebdomadaire?.toString().includes(search)
                          );
                        });
                        if (filteredMontants.length === 0) {
                          return (
                            <tr>
                              <td colSpan="5" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                                {searchMontants ? 'Aucun montant trouvé' : 'Aucun montant mensuel fixé'}
                              </td>
                            </tr>
                          );
                        }
                        const paginatedMontants = paginate(filteredMontants, currentPageMontants, itemsPerPage);
                        return paginatedMontants.map(montant => {
                          const moisNoms = [
                            'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
                            'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
                          ];
                          return (
                            <tr key={montant.id}>
                              <td data-label="Mois/Année">{moisNoms[montant.mois - 1]} {montant.annee}</td>
                              <td data-label="Montant mensuel">{parseFloat(montant.montant_mensuel).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF</td>
                              <td data-label="Montant hebdomadaire">{parseFloat(montant.montant_hebdomadaire).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF</td>
                              <td data-label="Date de création">{new Date(montant.created_at).toLocaleDateString('fr-FR')}</td>
                              <td data-label="Actions">
                                <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                                  {isPrincipal() && (
                                    <button
                                      type="button"
                                      className="btn btn-primary"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleEditMontant(montant);
                                      }}
                                      style={{ fontSize: '12px', padding: '5px 10px' }}
                                      title="Modifier ce montant mensuel"
                                    >
                                      ✏️ Modifier
                                    </button>
                                  )}
                                  {isPrincipal() && (
                                    <button
                                      type="button"
                                      className="btn btn-danger"
                                      onClick={async (e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log('🔘 Bouton supprimer cliqué pour montant:', montant);
                                        try {
                                          await handleDeleteMontant(montant);
                                        } catch (error) {
                                          console.error('❌ Erreur dans onClick du bouton:', error);
                                          notificationService.error('Erreur', 'Une erreur est survenue lors de la suppression');
                                        }
                                      }}
                                      style={{ fontSize: '12px', padding: '5px 10px' }}
                                      title="Supprimer ce montant mensuel"
                                    >
                                      🗑️ Supprimer
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
                {(() => {
                  const filteredMontants = montantsMensuels.filter(montant => {
                    if (!searchMontants) return true;
                    const search = searchMontants.toLowerCase();
                    const moisNoms = [
                      'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
                      'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
                    ];
                    const moisNom = moisNoms[montant.mois - 1] || '';
                    return (
                      moisNom.includes(search) ||
                      montant.annee?.toString().includes(search) ||
                      montant.montant_mensuel?.toString().includes(search) ||
                      montant.montant_hebdomadaire?.toString().includes(search)
                    );
                  });
                  const totalPages = getTotalPages(filteredMontants, itemsPerPage);
                  return (
                    <Pagination 
                      currentPage={currentPageMontants} 
                      totalPages={totalPages} 
                      onPageChange={(page) => {
                        setCurrentPageMontants(page);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }} 
                    />
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'montants-familles' && (
          <div>
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
                <h2 style={{ flex: '1 1 100%', marginBottom: '10px' }}>Gestion des montants mensuels des familles</h2>
                <button 
                  className="btn btn-success"
                  onClick={() => {
                    setEditingMontantFamille(null);
                    setMontantFamilleForm({
                      mois: new Date().getMonth() + 1,
                      annee: new Date().getFullYear(),
                      montant_mensuel: ''
                    });
                    setShowMontantFamilleForm(true);
                  }}
                  style={{ flex: '1 1 auto', minWidth: '150px' }}
                >
                  + Fixer un montant mensuel famille
                </button>
              </div>

              {showMontantFamilleForm && (
                <div className="card" style={{ marginBottom: '20px', background: '#f8f9fa' }}>
                  <h3 style={{ marginBottom: '15px' }}>{editingMontantFamille ? 'Modifier le montant mensuel famille' : 'Fixer le montant mensuel famille'}</h3>
                  <form onSubmit={handleMontantFamilleSubmit}>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Mois *</label>
                        <select
                          value={montantFamilleForm.mois}
                          onChange={(e) => setMontantFamilleForm({ ...montantFamilleForm, mois: parseInt(e.target.value) })}
                          required
                          disabled={!!editingMontantFamille}
                        >
                          <option value="1">Janvier</option>
                          <option value="2">Février</option>
                          <option value="3">Mars</option>
                          <option value="4">Avril</option>
                          <option value="5">Mai</option>
                          <option value="6">Juin</option>
                          <option value="7">Juillet</option>
                          <option value="8">Août</option>
                          <option value="9">Septembre</option>
                          <option value="10">Octobre</option>
                          <option value="11">Novembre</option>
                          <option value="12">Décembre</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Année *</label>
                        <input
                          type="number"
                          value={montantFamilleForm.annee}
                          onChange={(e) => setMontantFamilleForm({ ...montantFamilleForm, annee: parseInt(e.target.value) })}
                          min="2020"
                          max="2100"
                          required
                          disabled={!!editingMontantFamille}
                        />
                      </div>
                      <div className="form-group">
                        <label>Montant mensuel (CDF) *</label>
                        <input
                          type="number"
                          step="0.01"
                          value={montantFamilleForm.montant_mensuel}
                          onChange={(e) => setMontantFamilleForm({ ...montantFamilleForm, montant_mensuel: e.target.value })}
                          placeholder="Ex: 20000"
                          required
                        />
                        <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                          Ce montant sera payé une seule fois par famille pour le mois sélectionné
                        </small>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                      <button type="submit" className="btn btn-primary">
                        {editingMontantFamille ? 'Modifier le montant' : 'Fixer et générer les cotisations'}
                      </button>
                      <button 
                        type="button" 
                        className="btn btn-secondary"
                        onClick={() => {
                          setShowMontantFamilleForm(false);
                          setEditingMontantFamille(null);
                          setMontantFamilleForm({
                            mois: new Date().getMonth() + 1,
                            annee: new Date().getFullYear(),
                            montant_mensuel: ''
                          });
                        }}
                      >
                        Annuler
                      </button>
                    </div>
                  </form>
                </div>
              )}

              <div className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                  <h3 style={{ margin: 0 }}>Historique des montants mensuels des familles</h3>
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <input
                    type="text"
                    placeholder="🔍 Rechercher un montant (mois, année, montant)..."
                    value={searchMontantsFamilles}
                    onChange={(e) => setSearchMontantsFamilles(e.target.value)}
                    style={{
                      width: '100%',
                      maxWidth: '500px',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px'
                    }}
                  />
                  {searchMontantsFamilles && (
                    <button
                      onClick={() => setSearchMontantsFamilles('')}
                      style={{
                        marginLeft: '10px',
                        padding: '10px 15px',
                        background: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      ✕ Effacer
                    </button>
                  )}
                </div>
                <div className="table-wrapper" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th style={{ whiteSpace: 'nowrap' }}>Mois/Année</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Montant mensuel</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Date de création</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const filteredMontants = montantsMensuelsFamilles.filter(montant => {
                          if (!searchMontantsFamilles) return true;
                          const search = searchMontantsFamilles.toLowerCase();
                          const moisNoms = [
                            'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
                            'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
                          ];
                          const moisNom = moisNoms[montant.mois - 1] || '';
                          return (
                            moisNom.includes(search) ||
                            montant.annee?.toString().includes(search) ||
                            montant.montant_mensuel?.toString().includes(search)
                          );
                        });
                        if (filteredMontants.length === 0) {
                          return (
                            <tr>
                              <td colSpan="4" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                                {searchMontantsFamilles ? 'Aucun montant trouvé' : 'Aucun montant mensuel famille fixé'}
                              </td>
                            </tr>
                          );
                        }
                        const paginatedMontants = paginate(filteredMontants, currentPageMontantsFamilles, itemsPerPage);
                        return paginatedMontants.map(montant => {
                          const moisNoms = [
                            'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
                            'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
                          ];
                          return (
                            <tr key={montant.id}>
                              <td data-label="Mois/Année">{moisNoms[montant.mois - 1]} {montant.annee}</td>
                              <td data-label="Montant mensuel">{parseFloat(montant.montant_mensuel).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF</td>
                              <td data-label="Date de création">{new Date(montant.created_at).toLocaleDateString('fr-FR')}</td>
                              <td data-label="Actions">
                                <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                                  {isPrincipal() && (
                                    <button
                                      type="button"
                                      className="btn btn-primary"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleEditMontantFamille(montant);
                                      }}
                                      style={{ fontSize: '12px', padding: '5px 10px' }}
                                      title="Modifier ce montant mensuel famille"
                                    >
                                      ✏️ Modifier
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
                {(() => {
                  const filteredMontants = montantsMensuelsFamilles.filter(montant => {
                    if (!searchMontantsFamilles) return true;
                    const search = searchMontantsFamilles.toLowerCase();
                    const moisNoms = [
                      'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
                      'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
                    ];
                    const moisNom = moisNoms[montant.mois - 1] || '';
                    return (
                      moisNom.includes(search) ||
                      montant.annee?.toString().includes(search) ||
                      montant.montant_mensuel?.toString().includes(search)
                    );
                  });
                  const totalPages = getTotalPages(filteredMontants, itemsPerPage);
                  return (
                    <Pagination 
                      currentPage={currentPageMontantsFamilles} 
                      totalPages={totalPages} 
                      onPageChange={(page) => {
                        setCurrentPageMontantsFamilles(page);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }} 
                    />
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'centres-sante' && (
          <div>
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
                <h2 style={{ flex: '1 1 100%', marginBottom: '10px' }}>Gestion des centres de santé</h2>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setEditingCentreSante(null);
                    setCentreSanteForm({
                      nom: '',
                      adresse: '',
                      commune: '',
                      telephone: ''
                    });
                    setShowCentreSanteForm(true);
                  }}
                >
                  + Ajouter un centre de santé
                </button>
              </div>

              {showCentreSanteForm && (
                <div className="card" style={{ marginBottom: '20px', background: '#f8f9fa' }}>
                  <h3>{editingCentreSante ? 'Modifier le centre de santé' : 'Créer un centre de santé'}</h3>
                  <form onSubmit={handleCentreSanteSubmit}>
                    <div className="form-row">
                      <div className="form-group" style={{ flex: '1 1 100%' }}>
                        <label>Nom du centre de santé *</label>
                        <input
                          type="text"
                          value={centreSanteForm.nom}
                          onChange={(e) => setCentreSanteForm({ ...centreSanteForm, nom: e.target.value })}
                          required
                          placeholder="Ex: Centre de Santé Saint GASPARD"
                        />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group" style={{ flex: '1 1 100%' }}>
                        <label>Adresse</label>
                        <input
                          type="text"
                          value={centreSanteForm.adresse}
                          onChange={(e) => setCentreSanteForm({ ...centreSanteForm, adresse: e.target.value })}
                          placeholder="Adresse complète"
                        />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Commune</label>
                        <select
                          value={centreSanteForm.commune}
                          onChange={(e) => setCentreSanteForm({ ...centreSanteForm, commune: e.target.value })}
                        >
                          <option value="">Sélectionner une commune</option>
                          <option value="MAKISO">MAKISO</option>
                          <option value="KABONDO">KABONDO</option>
                          <option value="MANGOBO">MANGOBO</option>
                          <option value="LUBUNGA">LUBUNGA</option>
                          <option value="TSHOPO">TSHOPO</option>
                          <option value="KISANGANI">KISANGANI</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Téléphone</label>
                        <input
                          type="text"
                          value={centreSanteForm.telephone}
                          onChange={(e) => setCentreSanteForm({ ...centreSanteForm, telephone: e.target.value })}
                          placeholder="+243 XXX XXX XXX"
                        />
                      </div>
                    </div>
                    <div>
                      <button type="submit" className="btn btn-primary">Enregistrer</button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => {
                          setShowCentreSanteForm(false);
                          setEditingCentreSante(null);
                        }}
                        style={{ marginLeft: '10px' }}
                      >
                        Annuler
                      </button>
                    </div>
                  </form>
                </div>
              )}

              <div className="card">
                <h3>Liste des centres de santé</h3>
                <div style={{ marginBottom: '15px' }}>
                  <input
                    type="text"
                    placeholder="🔍 Rechercher un centre de santé..."
                    value={searchCentresSante}
                    onChange={(e) => setSearchCentresSante(e.target.value)}
                    style={{
                      width: '100%',
                      maxWidth: '500px',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div className="table-wrapper">
                  <table className="table">
                    <thead>
                      <tr>
                        <th style={{ whiteSpace: 'nowrap' }}>Nom</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Commune</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Adresse</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Téléphone</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginate(
                        centresSante.filter(centre => {
                          if (!searchCentresSante) return true;
                          const search = searchCentresSante.toLowerCase();
                          return (
                            centre.nom?.toLowerCase().includes(search) ||
                            centre.commune?.toLowerCase().includes(search) ||
                            centre.adresse?.toLowerCase().includes(search) ||
                            centre.telephone?.toLowerCase().includes(search)
                          );
                        }),
                        currentPageCentresSante,
                        itemsPerPage
                      ).map(centre => (
                          <tr key={centre.id}>
                            <td data-label="Nom" style={{ whiteSpace: 'nowrap' }}>{centre.nom}</td>
                            <td data-label="Commune" style={{ whiteSpace: 'nowrap' }}>{centre.commune || '-'}</td>
                            <td data-label="Adresse" style={{ whiteSpace: 'nowrap' }}>{centre.adresse || '-'}</td>
                            <td data-label="Téléphone" style={{ whiteSpace: 'nowrap' }}>{centre.telephone || '-'}</td>
                            <td data-label="Actions" style={{ whiteSpace: 'nowrap' }}>
                              {isPrincipal() && (
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => {
                                    setEditingCentreSante(centre);
                                    setCentreSanteForm({
                                      nom: centre.nom,
                                      adresse: centre.adresse || '',
                                      commune: centre.commune || '',
                                      telephone: centre.telephone || ''
                                    });
                                    setShowCentreSanteForm(true);
                                  }}
                                  style={{ marginRight: '5px' }}
                                  title="Modifier"
                                >
                                  ✏️
                                </button>
                              )}
                              {isPrincipal() && (
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={async () => {
                                    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le centre de santé "${centre.nom}" ?`)) {
                                      return;
                                    }
                                    try {
                                      await deleteCentreSante(centre.id);
                                      loadData();
                                      notificationService.success('Succès', 'Centre de santé supprimé');
                                    } catch (error) {
                                      notificationService.error('Erreur', error.response?.data?.message || 'Erreur lors de la suppression');
                                    }
                                  }}
                                  title="Supprimer"
                                >
                                  🗑️
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      {centresSante.filter(centre => {
                        if (!searchCentresSante) return true;
                        const search = searchCentresSante.toLowerCase();
                        return (
                          centre.nom?.toLowerCase().includes(search) ||
                          centre.commune?.toLowerCase().includes(search) ||
                          centre.adresse?.toLowerCase().includes(search) ||
                          centre.telephone?.toLowerCase().includes(search) ||
                          centre.email?.toLowerCase().includes(search)
                        );
                      }).length === 0 && (
                        <tr>
                          <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>
                            {searchCentresSante ? 'Aucun centre de santé trouvé' : 'Aucun centre de santé enregistré'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {(() => {
                  const filteredCentresSante = centresSante.filter(centre => {
                    if (!searchCentresSante) return true;
                    const search = searchCentresSante.toLowerCase();
                    return (
                      centre.nom?.toLowerCase().includes(search) ||
                      centre.commune?.toLowerCase().includes(search) ||
                      centre.adresse?.toLowerCase().includes(search) ||
                      centre.telephone?.toLowerCase().includes(search)
                    );
                  });
                  const totalPages = getTotalPages(filteredCentresSante, itemsPerPage);
                  return (
                    <Pagination 
                      currentPage={currentPageCentresSante} 
                      totalPages={totalPages} 
                      onPageChange={(page) => {
                        setCurrentPageCentresSante(page);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }} 
                    />
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'declaration-centres' && (
          <div>
            <div className="card">
              <h2 style={{ marginBottom: '20px' }}>🏥 Déclaration des centres de santé aux membres</h2>
              
              <div className="alert alert-info" style={{ marginBottom: '20px' }}>
                <strong>Instructions :</strong> Sélectionnez une association, une commune, précisez le nombre de membres à déclarer, puis choisissez le centre de santé. 
                Le système assignera automatiquement le centre de santé aux membres correspondants qui n'ont pas encore de centre assigné.
              </div>

              <form onSubmit={async (e) => {
                e.preventDefault();
                try {
                  if (!declarationForm.association || !declarationForm.commune || !declarationForm.nombreMembres || !declarationForm.centre_sante) {
                    notificationService.error('Erreur', 'Veuillez remplir tous les champs');
                    return;
                  }

                  const nombreMembres = parseInt(declarationForm.nombreMembres);
                  if (isNaN(nombreMembres) || nombreMembres <= 0) {
                    notificationService.error('Erreur', 'Le nombre de membres doit être un nombre positif');
                    return;
                  }

                  // Vérifier que le nombre de membres à déclarer ne dépasse pas l'effectif disponible
                  if (nombreMembres > membresDisponibles) {
                    notificationService.error('Erreur', `Le nombre de membres à déclarer (${nombreMembres}) ne peut pas être supérieur à l'effectif disponible (${membresDisponibles} membre(s))`);
                    return;
                  }

                  // Préparer les données pour l'API
                  // Si "Toutes les associations" est sélectionné, envoyer une valeur spéciale ou null
                  const isToutesAssociations = declarationForm.association === '__TOUTES__';
                  
                  // Appeler l'API pour déclarer les centres
                  const response = await declarerCentres({
                    association: isToutesAssociations ? null : declarationForm.association,
                    toutes_associations: isToutesAssociations,
                    commune: declarationForm.commune,
                    nombre_membres: nombreMembres,
                    centre_sante: declarationForm.centre_sante
                  });

                  const messageSucces = isToutesAssociations 
                    ? `Centre de santé assigné à ${response.data.membres_assignes} membre(s) de toutes les associations`
                    : `Centre de santé assigné à ${response.data.membres_assignes} membre(s)`;
                  notificationService.success('Succès', messageSucces);
                  
                  // Réinitialiser le formulaire
                  setDeclarationForm({
                    association: '',
                    commune: '',
                    nombreMembres: '',
                    centre_sante: ''
                  });
                  setMembresDisponibles(0);

                  // Recharger les données
                  loadData();
                } catch (error) {
                  notificationService.error('Erreur', error.response?.data?.message || 'Erreur lors de la déclaration');
                }
              }}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Association *</label>
                    <select
                      value={declarationForm.association}
                      onChange={(e) => {
                        const newAssociation = e.target.value;
                        // Réinitialiser la commune et le nombre de membres quand l'association change
                        setDeclarationForm({ 
                          ...declarationForm, 
                          association: newAssociation, 
                          commune: '', // Réinitialiser la commune
                          nombreMembres: '' 
                        });
                        // Réinitialiser les membres disponibles
                        setMembresDisponibles(0);
                      }}
                      required
                    >
                      <option value="">Sélectionner une association</option>
                      <option value="__TOUTES__" style={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }}>📋 Toutes les associations</option>
                      {[...new Set(membres.map(m => m.association).filter(Boolean))].sort().map(asso => (
                        <option key={asso} value={asso}>{asso}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Commune *</label>
                    <select
                      value={declarationForm.commune}
                      onChange={(e) => {
                        const newCommune = e.target.value;
                        setDeclarationForm({ ...declarationForm, commune: newCommune, nombreMembres: '' });
                        // Le recalcul sera fait automatiquement par le useEffect
                      }}
                      required
                      disabled={!declarationForm.association}
                    >
                      <option value="">
                        {declarationForm.association 
                          ? 'Sélectionner une commune' 
                          : 'Sélectionnez d\'abord une association'}
                      </option>
                      {declarationForm.association && (() => {
                        // Si "Toutes les associations" est sélectionné, afficher toutes les communes
                        const isToutesAssociations = declarationForm.association === '__TOUTES__';
                        const communesAssociation = [...new Set(
                          membres
                            .filter(m => {
                              if (isToutesAssociations) {
                                return m.commune; // Toutes les communes de tous les membres
                              }
                              return m.association === declarationForm.association && m.commune;
                            })
                            .map(m => m.commune)
                        )].sort();
                        
                        return communesAssociation.map(commune => (
                          <option key={commune} value={commune}>{commune}</option>
                        ));
                      })()}
                    </select>
                    {declarationForm.association && declarationForm.association !== '__TOUTES__' && (() => {
                      const communesAssociation = [...new Set(
                        membres
                          .filter(m => m.association === declarationForm.association && m.commune)
                          .map(m => m.commune)
                      )];
                      if (communesAssociation.length === 0) {
                        return (
                          <small style={{ color: '#dc3545', display: 'block', marginTop: '5px' }}>
                            ⚠️ Aucun membre de cette association n'a de commune enregistrée
                          </small>
                        );
                      }
                      return null;
                    })()}
                  </div>
                </div>

                {/* Affichage du nombre de membres disponibles */}
                {declarationForm.association && declarationForm.commune && (() => {
                  const isToutesAssociations = declarationForm.association === '__TOUTES__';
                  
                  const membresCommune = membres.filter(m => {
                    const mCommune = (m.commune || '').trim();
                    return mCommune.toLowerCase() === declarationForm.commune.trim().toLowerCase();
                  });
                  
                  const membresAssociationCommune = membres.filter(m => {
                    const mAssociation = (m.association || '').trim();
                    const mCommune = (m.commune || '').trim();
                    const matchAssociation = isToutesAssociations || mAssociation.toLowerCase() === declarationForm.association.trim().toLowerCase();
                    return matchAssociation && mCommune.toLowerCase() === declarationForm.commune.trim().toLowerCase();
                  });
                  
                  const membresAvecCentre = membresAssociationCommune.filter(m => m.centre_sante);
                  
                  // Compter les associations uniques dans la commune si "Toutes" est sélectionné
                  const associationsUniques = isToutesAssociations 
                    ? [...new Set(membresCommune.filter(m => m.association).map(m => m.association))]
                    : [];
                  
                  return (
                    <div className="alert alert-info" style={{ 
                      marginBottom: '20px', 
                      padding: '15px',
                      backgroundColor: isToutesAssociations ? '#e8f5e9' : '#e7f3ff',
                      border: isToutesAssociations ? '1px solid #4caf50' : '1px solid #b3d9ff',
                      borderRadius: '5px'
                    }}>
                      {isToutesAssociations ? (
                        <>
                          <strong>📊 Effectif disponible :</strong> {membresDisponibles} membre(s) de <strong style={{ color: '#2e7d32' }}>TOUTES LES ASSOCIATIONS</strong> 
                          {' '}dans la commune <strong>"{declarationForm.commune}"</strong> sans centre de santé assigné.
                          {associationsUniques.length > 0 && (
                            <div style={{ marginTop: '10px', fontSize: '13px', color: '#1b5e20' }}>
                              <strong>📋 Associations concernées ({associationsUniques.length}) :</strong> {associationsUniques.join(', ')}
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <strong>📊 Effectif disponible :</strong> {membresDisponibles} membre(s) de l'association <strong>"{declarationForm.association}"</strong> 
                          {' '}dans la commune <strong>"{declarationForm.commune}"</strong> sans centre de santé assigné.
                        </>
                      )}
                      
                      {membresDisponibles === 0 && membresAvecCentre.length > 0 && (
                        <div style={{ marginTop: '15px', fontSize: '13px' }}>
                          <div style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '5px' }}>
                            <strong>⚠️ Tous les membres {isToutesAssociations ? 'de cette commune' : 'de cette association et commune'} ont déjà un centre de santé assigné :</strong>
                            <ul style={{ marginTop: '8px', marginBottom: '0', paddingLeft: '20px' }}>
                              {membresAvecCentre.slice(0, 10).map(m => (
                                <li key={m.id} style={{ marginBottom: '5px' }}>
                                  <strong>{m.nom} {m.prenom}</strong> {isToutesAssociations && m.association ? `(${m.association})` : ''} - Centre : <strong>{m.centre_sante}</strong>
                                </li>
                              ))}
                              {membresAvecCentre.length > 10 && (
                                <li style={{ fontStyle: 'italic', color: '#666' }}>... et {membresAvecCentre.length - 10} autre(s)</li>
                              )}
                            </ul>
                          </div>
                          <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
                            <strong>📋 Statistiques détaillées :</strong>
                            <br />
                            • <strong>Total membres de cette commune :</strong> {membresCommune.length}
                            <br />
                            • <strong>Membres avec centre assigné :</strong> {membresAvecCentre.length}
                            <br />
                            • <strong>Membres disponibles (sans centre) :</strong> {membresDisponibles}
                          </div>
                        </div>
                      )}
                      
                      {membresDisponibles === 0 && membresAvecCentre.length === 0 && (
                        <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
                          <strong>💡 Vérification :</strong> Aucun membre trouvé {isToutesAssociations ? '' : `correspondant exactement à l'association "${declarationForm.association}" et `}dans la commune <strong>"{declarationForm.commune}"</strong>.
                          <br />
                          <strong>Total membres de cette commune :</strong> {membresCommune.length}
                        </div>
                      )}
                    </div>
                  );
                })()}

                <div className="form-row">
                  <div className="form-group">
                    <label>Nombre de membres à déclarer *</label>
                    <input
                      type="number"
                      min="1"
                      max={membresDisponibles > 0 ? membresDisponibles : undefined}
                      value={declarationForm.nombreMembres}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Permettre la saisie libre, la validation se fera à la soumission
                        setDeclarationForm({ ...declarationForm, nombreMembres: value });
                      }}
                      required
                      placeholder="Ex: 10"
                      disabled={!declarationForm.association || !declarationForm.commune || membresDisponibles === 0}
                      style={{
                        borderColor: declarationForm.nombreMembres && parseInt(declarationForm.nombreMembres) > membresDisponibles ? '#dc3545' : undefined,
                        backgroundColor: (!declarationForm.association || !declarationForm.commune || membresDisponibles === 0) ? '#f0f0f0' : undefined
                      }}
                    />
                    <small style={{ 
                      color: declarationForm.nombreMembres && parseInt(declarationForm.nombreMembres) > membresDisponibles ? '#dc3545' : '#666', 
                      display: 'block', 
                      marginTop: '5px' 
                    }}>
                      {!declarationForm.association || !declarationForm.commune ? (
                        'Sélectionnez d\'abord une association et une commune'
                      ) : membresDisponibles === 0 ? (
                        <span style={{ color: '#dc3545' }}>
                          ⚠️ Aucun membre disponible pour cette association et cette commune
                        </span>
                      ) : declarationForm.nombreMembres && parseInt(declarationForm.nombreMembres) > membresDisponibles ? (
                        <span style={{ color: '#dc3545', fontWeight: 'bold' }}>
                          ⚠️ Le nombre ne peut pas dépasser {membresDisponibles} membre(s) disponible(s)
                        </span>
                      ) : (
                        `Nombre de membres qui seront assignés à ce centre de santé (max: ${membresDisponibles})`
                      )}
                    </small>
                  </div>

                  <div className="form-group">
                    <label>Centre de santé *</label>
                    <select
                      value={declarationForm.centre_sante}
                      onChange={(e) => setDeclarationForm({ ...declarationForm, centre_sante: e.target.value })}
                      required
                    >
                      <option value="">Sélectionner un centre de santé</option>
                      {centresSante
                        .filter(centre => centre.nom)
                        .map(centre => (
                          <option key={centre.id} value={centre.nom}>
                            {centre.nom}
                            {centre.commune ? ` - ${centre.commune}` : ''}
                            {centre.adresse ? ` (${centre.adresse})` : ''}
                          </option>
                        ))}
                    </select>
                    {centresSante.length === 0 && (
                      <small style={{ color: '#dc3545', display: 'block', marginTop: '5px' }}>
                        Aucun centre de santé disponible. Créez-en un dans l'onglet "Centres de santé".
                      </small>
                    )}
                  </div>
                </div>

                <div style={{ marginTop: '20px' }}>
                  <button type="submit" className="btn btn-primary" style={{ minWidth: '200px' }}>
                    ✅ Valider la déclaration
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={() => {
                      setDeclarationForm({
                        association: '',
                        commune: '',
                        nombreMembres: '',
                        centre_sante: ''
                      });
                      setMembresDisponibles(0);
                    }}
                    style={{ marginLeft: '10px' }}
                  >
                    Réinitialiser
                  </button>
                </div>
              </form>
            </div>

            {/* Interface pour déclarer les centres de santé aux familles */}
            <div className="card" style={{ marginTop: '30px' }}>
              <h2 style={{ marginBottom: '20px' }}>👨‍👩‍👧‍👦 Déclaration des centres de santé aux familles</h2>
              
              <div className="alert alert-info" style={{ marginBottom: '20px' }}>
                <strong>Instructions :</strong> Sélectionnez une famille par son nom, puis choisissez le centre de santé. 
                Le système assignera automatiquement le centre de santé à la famille sélectionnée.
              </div>

              <form onSubmit={async (e) => {
                e.preventDefault();
                try {
                  if (!declarationFormFamilles.nom_famille || !declarationFormFamilles.centre_sante) {
                    notificationService.error('Erreur', 'Veuillez remplir tous les champs');
                    return;
                  }

                  // Appeler l'API pour déclarer le centre à la famille
                  console.log('📤 Envoi de la requête avec:', {
                    nom_famille: declarationFormFamilles.nom_famille,
                    centre_sante: declarationFormFamilles.centre_sante
                  });
                  const response = await declarerCentresFamilles({
                    nom_famille: declarationFormFamilles.nom_famille?.trim() || declarationFormFamilles.nom_famille,
                    centre_sante: declarationFormFamilles.centre_sante?.trim() || declarationFormFamilles.centre_sante
                  });

                  notificationService.success('Succès', `Centre de santé assigné à la famille "${declarationFormFamilles.nom_famille}"`);
                  
                  // Réinitialiser le formulaire
                  setDeclarationFormFamilles({
                    nom_famille: '',
                    centre_sante: ''
                  });
                  setFamillesDisponibles(0);

                  // Recharger les données
                  loadData();
                } catch (error) {
                  notificationService.error('Erreur', error.response?.data?.message || 'Erreur lors de la déclaration');
                }
              }}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Nom de la famille *</label>
                    <select
                      value={declarationFormFamilles.nom_famille}
                      onChange={(e) => {
                        const newNomFamille = e.target.value;
                        setDeclarationFormFamilles({ ...declarationFormFamilles, nom_famille: newNomFamille });
                      }}
                      required
                    >
                      <option value="">Sélectionner une famille</option>
                      {(Array.isArray(familles) ? familles : []).filter(f => f.statut === 'active').sort((a, b) => {
                        const nomA = (a.nom_famille || '').toLowerCase();
                        const nomB = (b.nom_famille || '').toLowerCase();
                        return nomA.localeCompare(nomB);
                      }).map(famille => (
                        <option key={famille.id} value={famille.nom_famille}>
                          {famille.nom_famille} {famille.numero_famille ? `(Numéro: ${famille.numero_famille})` : ''} {famille.commune ? `- ${famille.commune}` : ''}
                        </option>
                      ))}
                    </select>
                    {familles.length === 0 && (
                      <small style={{ color: '#dc3545', display: 'block', marginTop: '5px' }}>
                        ⚠️ Aucune famille enregistrée
                      </small>
                    )}
                  </div>
                </div>

                {/* Affichage des informations de la famille sélectionnée */}
                {declarationFormFamilles.nom_famille && (() => {
                  const familleSelectionnee = (Array.isArray(familles) ? familles : []).find(f => {
                    const fNom = (f.nom_famille || '').trim();
                    return fNom.toLowerCase() === declarationFormFamilles.nom_famille.trim().toLowerCase() && f.statut === 'active';
                  });
                  
                  if (!familleSelectionnee) {
                    return null;
                  }
                  
                  const aDejaUnCentre = familleSelectionnee.centre_sante && familleSelectionnee.centre_sante.trim() !== '';
                  
                  return (
                    <div className="alert alert-info" style={{ 
                      marginBottom: '20px', 
                      padding: '15px',
                      backgroundColor: aDejaUnCentre ? '#fff3cd' : '#e7f3ff',
                      border: `1px solid ${aDejaUnCentre ? '#ffc107' : '#b3d9ff'}`,
                      borderRadius: '5px'
                    }}>
                      {aDejaUnCentre ? (
                        <div>
                          <strong>⚠️ Cette famille a déjà un centre de santé assigné :</strong>
                          <div style={{ marginTop: '10px', fontSize: '14px' }}>
                            <strong>Famille :</strong> {familleSelectionnee.nom_famille}
                            <br />
                            <strong>Numéro :</strong> {familleSelectionnee.numero_famille || '-'}
                            <br />
                            <strong>Chef de famille :</strong> {familleSelectionnee.chef_famille_nom || '-'}
                            <br />
                            <strong>Commune :</strong> {familleSelectionnee.commune || '-'}
                            <br />
                            <strong>Centre de santé actuel :</strong> <span style={{ color: '#dc3545', fontWeight: 'bold' }}>{familleSelectionnee.centre_sante}</span>
                          </div>
                          <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
                            Si vous sélectionnez un nouveau centre de santé, il remplacera le centre actuel.
                          </div>
                        </div>
                      ) : (
                        <div>
                          <strong>📊 Informations de la famille sélectionnée :</strong>
                          <div style={{ marginTop: '10px', fontSize: '14px' }}>
                            <strong>Famille :</strong> {familleSelectionnee.nom_famille}
                            <br />
                            <strong>Numéro :</strong> {familleSelectionnee.numero_famille || '-'}
                            <br />
                            <strong>Chef de famille :</strong> {familleSelectionnee.chef_famille_nom || '-'}
                            <br />
                            <strong>Commune :</strong> {familleSelectionnee.commune || '-'}
                            <br />
                            <strong>Centre de santé :</strong> <span style={{ color: '#dc3545', fontWeight: 'bold' }}>Aucun</span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                <div className="form-row">
                  <div className="form-group">
                    <label>Centre de santé *</label>
                    <select
                      value={declarationFormFamilles.centre_sante}
                      onChange={(e) => setDeclarationFormFamilles({ ...declarationFormFamilles, centre_sante: e.target.value })}
                      required
                      disabled={!declarationFormFamilles.nom_famille}
                      style={{
                        backgroundColor: !declarationFormFamilles.nom_famille ? '#f0f0f0' : undefined
                      }}
                    >
                      <option value="">
                        {!declarationFormFamilles.nom_famille 
                          ? 'Sélectionnez d\'abord une famille' 
                          : 'Sélectionner un centre de santé'}
                      </option>
                      {centresSante.map(centre => (
                        <option key={centre.id} value={centre.nom}>
                          {centre.nom}
                          {centre.adresse ? ` (${centre.adresse})` : ''}
                        </option>
                      ))}
                    </select>
                    {centresSante.length === 0 && (
                      <small style={{ color: '#dc3545', display: 'block', marginTop: '5px' }}>
                        Aucun centre de santé disponible. Créez-en un dans l'onglet "Centres de santé".
                      </small>
                    )}
                  </div>
                </div>

                <div style={{ marginTop: '20px' }}>
                  <button type="submit" className="btn btn-primary" style={{ minWidth: '200px' }}>
                    ✅ Valider la déclaration
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={() => {
                      setDeclarationFormFamilles({
                        nom_famille: '',
                        centre_sante: ''
                      });
                      setFamillesDisponibles(0);
                    }}
                    style={{ marginLeft: '10px' }}
                  >
                    Réinitialiser
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {activeTab === 'rapports' && (
          <div>
            <RapportsFinanciers />
            
            <div style={{ marginTop: '50px', paddingTop: '30px', borderTop: '2px solid #e9ecef' }}>
              <h2 style={{ marginBottom: '30px', color: '#2c3e50' }}>📄 Rapports et Exportations (Anciens)</h2>
            
            {/* Rapport 1: Membres avec leurs cotisations */}
            <div className="card" style={{ marginBottom: '30px' }}>
              <h3 style={{ marginBottom: '20px', color: '#2c3e50' }}>📋 Liste des membres avec leurs paiements de cotisation</h3>
              <form onSubmit={(e) => {
                e.preventDefault();
                handlePrintMembresCotisations();
              }}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Association *</label>
                    <select
                      value={rapportCotisationsForm.association}
                      onChange={(e) => setRapportCotisationsForm({ ...rapportCotisationsForm, association: e.target.value })}
                      required
                    >
                      <option value="">Sélectionner une association</option>
                      {[...new Set(membres.map(m => m.association).filter(Boolean))].sort().map(assoc => (
                        <option key={assoc} value={assoc}>{assoc}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Commune *</label>
                    <select
                      value={rapportCotisationsForm.commune}
                      onChange={(e) => setRapportCotisationsForm({ ...rapportCotisationsForm, commune: e.target.value })}
                      required
                    >
                      <option value="">Sélectionner une commune</option>
                      <option value="toutes">Toutes les communes</option>
                      {rapportCotisationsForm.association && [...new Set(membres.filter(m => m.association === rapportCotisationsForm.association).map(m => m.commune).filter(Boolean))].sort().map(commune => (
                        <option key={commune} value={commune}>{commune}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Période *</label>
                    <select
                      value={rapportCotisationsForm.periodeType}
                      onChange={(e) => setRapportCotisationsForm({ ...rapportCotisationsForm, periodeType: e.target.value })}
                      required
                    >
                      <option value="mois">Tout le mois</option>
                      <option value="semaine">Semaine spécifique</option>
                    </select>
                  </div>
                  {rapportCotisationsForm.periodeType === 'mois' ? (
                    <>
                      <div className="form-group">
                        <label>Mois *</label>
                        <select
                          value={rapportCotisationsForm.mois}
                          onChange={(e) => setRapportCotisationsForm({ ...rapportCotisationsForm, mois: parseInt(e.target.value) })}
                          required
                        >
                          <option value="1">Janvier</option>
                          <option value="2">Février</option>
                          <option value="3">Mars</option>
                          <option value="4">Avril</option>
                          <option value="5">Mai</option>
                          <option value="6">Juin</option>
                          <option value="7">Juillet</option>
                          <option value="8">Août</option>
                          <option value="9">Septembre</option>
                          <option value="10">Octobre</option>
                          <option value="11">Novembre</option>
                          <option value="12">Décembre</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Année *</label>
                        <input
                          type="number"
                          value={rapportCotisationsForm.annee}
                          onChange={(e) => setRapportCotisationsForm({ ...rapportCotisationsForm, annee: parseInt(e.target.value) })}
                          min="2020"
                          max="2100"
                          required
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="form-group">
                        <label>Mois *</label>
                        <select
                          value={rapportCotisationsForm.mois}
                          onChange={(e) => setRapportCotisationsForm({ ...rapportCotisationsForm, mois: parseInt(e.target.value) })}
                          required
                        >
                          <option value="1">Janvier</option>
                          <option value="2">Février</option>
                          <option value="3">Mars</option>
                          <option value="4">Avril</option>
                          <option value="5">Mai</option>
                          <option value="6">Juin</option>
                          <option value="7">Juillet</option>
                          <option value="8">Août</option>
                          <option value="9">Septembre</option>
                          <option value="10">Octobre</option>
                          <option value="11">Novembre</option>
                          <option value="12">Décembre</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Année *</label>
                        <input
                          type="number"
                          value={rapportCotisationsForm.annee}
                          onChange={(e) => setRapportCotisationsForm({ ...rapportCotisationsForm, annee: parseInt(e.target.value) })}
                          min="2020"
                          max="2100"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Semaine *</label>
                        <select
                          value={rapportCotisationsForm.semaine}
                          onChange={(e) => setRapportCotisationsForm({ ...rapportCotisationsForm, semaine: parseInt(e.target.value) })}
                          required
                        >
                          <option value="1">Semaine 1</option>
                          <option value="2">Semaine 2</option>
                          <option value="3">Semaine 3</option>
                          <option value="4">Semaine 4</option>
                        </select>
                      </div>
                    </>
                  )}
                  <div className="form-group">
                    <label>Statut *</label>
                    <select
                      value={rapportCotisationsForm.statut}
                      onChange={(e) => setRapportCotisationsForm({ ...rapportCotisationsForm, statut: e.target.value })}
                      required
                    >
                      <option value="tous">Tous</option>
                      <option value="paye">Payé</option>
                      <option value="impaye">Impayé</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <button type="submit" className="btn btn-primary">
                    🖨️ Imprimer
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-success"
                    onClick={handleDownloadPDFMembresCotisations}
                  >
                    📥 Télécharger PDF
                  </button>
                </div>
              </form>
            </div>

            {/* Rapport 2: Membres récoltés */}
            <div className="card">
              <h3 style={{ marginBottom: '20px', color: '#2c3e50' }}>👥 Liste des membres récoltés</h3>
              <form onSubmit={(e) => {
                e.preventDefault();
                handlePrintMembresRecoltes();
              }}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Association *</label>
                    <select
                      value={rapportMembresRecoltesForm.association}
                      onChange={(e) => setRapportMembresRecoltesForm({ ...rapportMembresRecoltesForm, association: e.target.value })}
                      required
                    >
                      <option value="">Sélectionner une association</option>
                      {[...new Set(membres.map(m => m.association).filter(Boolean))].sort().map(assoc => (
                        <option key={assoc} value={assoc}>{assoc}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Commune *</label>
                    <select
                      value={rapportMembresRecoltesForm.commune}
                      onChange={(e) => setRapportMembresRecoltesForm({ ...rapportMembresRecoltesForm, commune: e.target.value })}
                      required
                    >
                      <option value="">Sélectionner une commune</option>
                      <option value="toutes">Toutes les communes</option>
                      {rapportMembresRecoltesForm.association && [...new Set(membres.filter(m => m.association === rapportMembresRecoltesForm.association).map(m => m.commune).filter(Boolean))].sort().map(commune => (
                        <option key={commune} value={commune}>{commune}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <button type="submit" className="btn btn-primary">
                    🖨️ Imprimer
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-success"
                    onClick={handleDownloadPDFMembresRecoltes}
                  >
                    📥 Télécharger PDF
                  </button>
                </div>
              </form>
            </div>

            {/* Section Export Excel/CSV */}
            <div className="card" style={{ marginTop: '30px' }}>
              <h3 style={{ marginBottom: '20px', color: '#2c3e50' }}>📊 Export de Données (Excel/CSV)</h3>
              <form onSubmit={(e) => {
                e.preventDefault();
                handleExport();
              }}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Type de données à exporter *</label>
                    <select
                      value={exportForm.type}
                      onChange={(e) => setExportForm({ ...exportForm, type: e.target.value })}
                      required
                    >
                      <option value="membres">Membres</option>
                      <option value="paiements">Paiements</option>
                      <option value="cotisations">Cotisations</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Format *</label>
                    <select
                      value={exportForm.format}
                      onChange={(e) => setExportForm({ ...exportForm, format: e.target.value })}
                      required
                    >
                      <option value="excel">Excel (.xlsx)</option>
                      <option value="csv">CSV (.csv)</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Association</label>
                    <select
                      value={exportForm.association}
                      onChange={(e) => setExportForm({ ...exportForm, association: e.target.value })}
                    >
                      <option value="">Toutes les associations</option>
                      {[...new Set(membres.map(m => m.association).filter(Boolean))].sort().map(assoc => (
                        <option key={assoc} value={assoc}>{assoc}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Commune</label>
                    <select
                      value={exportForm.commune}
                      onChange={(e) => setExportForm({ ...exportForm, commune: e.target.value })}
                    >
                      <option value="">Toutes les communes</option>
                      <option value="toutes">Toutes les communes</option>
                      {exportForm.association && [...new Set(membres.filter(m => m.association === exportForm.association).map(m => m.commune).filter(Boolean))].sort().map(commune => (
                        <option key={commune} value={commune}>{commune}</option>
                      ))}
                      {!exportForm.association && [...new Set(membres.map(m => m.commune).filter(Boolean))].sort().map(commune => (
                        <option key={commune} value={commune}>{commune}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Date de début</label>
                    <input
                      type="date"
                      value={exportForm.dateDebut}
                      onChange={(e) => setExportForm({ ...exportForm, dateDebut: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Date de fin</label>
                    <input
                      type="date"
                      value={exportForm.dateFin}
                      onChange={(e) => setExportForm({ ...exportForm, dateFin: e.target.value })}
                    />
                  </div>
                </div>
                <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
                  <button
                    type="submit"
                    className="btn btn-primary"
                  >
                    📥 Exporter
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setExportForm({
                      type: 'membres',
                      association: '',
                      commune: '',
                      dateDebut: '',
                      dateFin: '',
                      format: 'excel'
                    })}
                  >
                    🔄 Réinitialiser
                  </button>
                </div>
              </form>
            </div>
            </div>
          </div>
        )}

        {activeTab === 'backup' && (
          <div>
            <h2 style={{ marginBottom: '30px', color: '#2c3e50' }}>💾 Gestion des Sauvegardes</h2>
            
            {/* Créer une sauvegarde */}
            <div className="card" style={{ marginBottom: '30px' }}>
              <h3 style={{ marginBottom: '20px', color: '#2c3e50' }}>Créer une Sauvegarde</h3>
              <p style={{ marginBottom: '20px', color: '#666' }}>
                Créez une sauvegarde complète de la base de données et des fichiers uploadés. 
                La sauvegarde sera disponible en format SQL et ZIP.
              </p>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCreateBackup}
                style={{ padding: '12px 24px', fontSize: '16px' }}
              >
                💾 Créer une Sauvegarde Maintenant
              </button>
            </div>

            {/* Planification des sauvegardes automatiques */}
            <div className="card" style={{ marginBottom: '30px' }}>
              <h3 style={{ marginBottom: '20px', color: '#2c3e50' }}>Planification des Sauvegardes Automatiques</h3>
              {loadingSchedule ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
                  <p>Chargement de la configuration...</p>
                </div>
              ) : (
                <form onSubmit={(e) => {
                  e.preventDefault();
                  handleUpdateSchedule();
                }}>
                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={backupSchedule.enabled}
                          onChange={(e) => setBackupSchedule({ ...backupSchedule, enabled: e.target.checked })}
                          style={{ marginRight: '8px' }}
                        />
                        Activer les sauvegardes automatiques
                      </label>
                    </div>
                  </div>
                  {backupSchedule.enabled && (
                    <>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Fréquence *</label>
                          <select
                            value={backupSchedule.frequency}
                            onChange={(e) => setBackupSchedule({ ...backupSchedule, frequency: e.target.value })}
                            required
                          >
                            <option value="daily">Quotidienne</option>
                            <option value="weekly">Hebdomadaire (Dimanche)</option>
                            <option value="monthly">Mensuelle (1er du mois)</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Heure *</label>
                          <input
                            type="time"
                            value={backupSchedule.time}
                            onChange={(e) => setBackupSchedule({ ...backupSchedule, time: e.target.value })}
                            required
                          />
                        </div>
                      </div>
                    </>
                  )}
                  <div style={{ marginTop: '20px' }}>
                    <button type="submit" className="btn btn-primary">
                      💾 Enregistrer la Planification
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Liste des sauvegardes */}
            <div className="card">
              <h3 style={{ marginBottom: '20px', color: '#2c3e50' }}>Liste des Sauvegardes</h3>
              {loadingBackups ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
                  <p>Chargement des sauvegardes...</p>
                </div>
              ) : backups.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  <p style={{ fontSize: '18px', marginBottom: '10px' }}>📦 Aucune sauvegarde disponible</p>
                  <p style={{ fontSize: '14px', color: '#999' }}>
                    Créez votre première sauvegarde en cliquant sur le bouton ci-dessus.
                  </p>
                </div>
              ) : (
                <>
                  <div className="table-wrapper" style={{ overflowX: 'auto' }}>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Nom du fichier</th>
                          <th>Type</th>
                          <th>Taille</th>
                          <th>Date de création</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginate(backups, currentPageBackups, itemsPerPage).map((backup, index) => (
                          <tr key={index}>
                            <td>{backup.filename}</td>
                            <td>
                              <span style={{
                                padding: '4px 8px',
                                borderRadius: '4px',
                                backgroundColor: backup.type === 'zip' ? '#007bff' : '#28a745',
                                color: 'white',
                                fontSize: '12px',
                                fontWeight: '500'
                              }}>
                                {backup.type === 'zip' ? 'ZIP' : 'SQL'}
                              </span>
                            </td>
                            <td>{(backup.size / 1024 / 1024).toFixed(2)} MB</td>
                            <td>{new Date(backup.date).toLocaleString('fr-FR')}</td>
                            <td>
                              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-primary"
                                  onClick={() => handleDownloadBackup(backup.filename)}
                                  title="Télécharger"
                                >
                                  📥 Télécharger
                                </button>
                                {backup.type === 'sql' && (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-warning"
                                    onClick={() => handleRestoreBackup(backup.filename)}
                                    title="Restaurer"
                                    style={{ backgroundColor: '#ffc107', color: '#000', border: 'none' }}
                                  >
                                    🔄 Restaurer
                                  </button>
                                )}
                                {isPrincipal() && (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-danger"
                                    onClick={() => handleDeleteBackup(backup.filename)}
                                    title="Supprimer"
                                  >
                                    🗑️ Supprimer
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {(() => {
                    const totalPages = getTotalPages(backups, itemsPerPage);
                    return (
                      <Pagination 
                        currentPage={currentPageBackups} 
                        totalPages={totalPages} 
                        onPageChange={(page) => {
                          setCurrentPageBackups(page);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }} 
                      />
                    );
                  })()}
                </>
              )}
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div>
            <h2 style={{ marginBottom: '30px', color: '#2c3e50' }}>⚙️ Paramètres Système</h2>
            
            {loadingSettings ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
                <p>Chargement des paramètres...</p>
              </div>
            ) : (
              <>
                {/* Configuration Générale */}
                <div className="card" style={{ marginBottom: '30px' }}>
                  <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #3498db', paddingBottom: '10px' }}>
                    📋 Configuration Générale
                  </h3>
                  <div className="form-row">
                    <div className="form-group" style={{ flex: '1' }}>
                      <label>Nom de l'organisation *</label>
                      <input
                        type="text"
                        value={settingsForm.general?.organization_name?.value || settingsForm.general?.organization_name || ''}
                        onChange={(e) => setSettingsForm({
                          ...settingsForm,
                          general: {
                            ...settingsForm.general,
                            organization_name: { ...settingsForm.general?.organization_name, value: e.target.value }
                          }
                        })}
                        placeholder="MUTU NDE ESPOIR ASBL"
                      />
                    </div>
                    <div className="form-group" style={{ flex: '1' }}>
                      <label>Logo de l'organisation</label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {/* Aperçu du logo actuel */}
                        {(settingsForm.general?.organization_logo?.value || settingsForm.general?.organization_logo) && (
                          <div style={{ marginBottom: '10px' }}>
                            <label style={{ fontSize: '12px', color: '#666', marginBottom: '5px', display: 'block' }}>
                              Logo actuel:
                            </label>
                            <img
                              src={settingsForm.general?.organization_logo?.value || settingsForm.general?.organization_logo || '/logo.png'}
                              alt="Logo actuel"
                              style={{
                                maxWidth: '200px',
                                maxHeight: '100px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                padding: '5px',
                                backgroundColor: '#fff'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                        {/* Input file pour upload */}
                        <div>
                          <input
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/gif,image/svg+xml,image/webp"
                            onChange={handleLogoUpload}
                            disabled={uploadingLogo}
                            style={{
                              padding: '8px',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              width: '100%',
                              cursor: uploadingLogo ? 'not-allowed' : 'pointer'
                            }}
                          />
                          <p style={{ fontSize: '11px', color: '#999', marginTop: '5px', marginBottom: 0 }}>
                            Formats acceptés: JPEG, PNG, GIF, SVG, WebP (max 2MB)
                          </p>
                          {uploadingLogo && (
                            <p style={{ fontSize: '12px', color: '#3498db', marginTop: '5px', marginBottom: 0 }}>
                              ⏳ Upload en cours...
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group" style={{ flex: '1' }}>
                      <label>Adresse</label>
                      <input
                        type="text"
                        value={settingsForm.general?.organization_address?.value || settingsForm.general?.organization_address || ''}
                        onChange={(e) => setSettingsForm({
                          ...settingsForm,
                          general: {
                            ...settingsForm.general,
                            organization_address: { ...settingsForm.general?.organization_address, value: e.target.value }
                          }
                        })}
                        placeholder="Adresse complète"
                      />
                    </div>
                    <div className="form-group" style={{ flex: '1' }}>
                      <label>Téléphone</label>
                      <input
                        type="text"
                        value={settingsForm.general?.organization_phone?.value || settingsForm.general?.organization_phone || ''}
                        onChange={(e) => setSettingsForm({
                          ...settingsForm,
                          general: {
                            ...settingsForm.general,
                            organization_phone: { ...settingsForm.general?.organization_phone, value: e.target.value }
                          }
                        })}
                        placeholder="+243 XXX XXX XXX"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={settingsForm.general?.organization_email?.value || settingsForm.general?.organization_email || ''}
                      onChange={(e) => setSettingsForm({
                        ...settingsForm,
                        general: {
                          ...settingsForm.general,
                          organization_email: { ...settingsForm.general?.organization_email, value: e.target.value }
                        }
                      })}
                      placeholder="contact@mundes.org"
                    />
                  </div>
                  <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
                    <button className="btn btn-primary" onClick={() => handleSaveSettings('general')}>
                      💾 Enregistrer
                    </button>
                    <button className="btn btn-secondary" onClick={() => handleResetSettings('general')}>
                      🔄 Réinitialiser
                    </button>
                  </div>
                </div>

                {/* Paramètres SMS */}
                <div className="card" style={{ marginBottom: '30px' }}>
                  <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #3498db', paddingBottom: '10px' }}>
                    📱 Paramètres SMS
                  </h3>
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input
                        type="checkbox"
                        checked={settingsForm.sms?.sms_enabled?.value !== undefined ? settingsForm.sms.sms_enabled.value : settingsForm.sms?.sms_enabled || false}
                        onChange={(e) => setSettingsForm({
                          ...settingsForm,
                          sms: {
                            ...settingsForm.sms,
                            sms_enabled: { ...settingsForm.sms?.sms_enabled, value: e.target.checked }
                          }
                        })}
                      />
                      Activer les notifications SMS
                    </label>
                  </div>
                  <div className="form-row">
                    <div className="form-group" style={{ flex: '1' }}>
                      <label>Fournisseur SMS</label>
                      <select
                        value={settingsForm.sms?.sms_provider?.value || settingsForm.sms?.sms_provider || ''}
                        onChange={(e) => setSettingsForm({
                          ...settingsForm,
                          sms: {
                            ...settingsForm.sms,
                            sms_provider: { ...settingsForm.sms?.sms_provider, value: e.target.value }
                          }
                        })}
                        style={{ width: '100%', padding: '8px' }}
                      >
                        <option value="">— Choisir —</option>
                        <option value="infobip">Infobip</option>
                        <option value="twilio">Twilio</option>
                        <option value="nexmo">Nexmo / Vonage</option>
                      </select>
                    </div>
                    <div className="form-group" style={{ flex: '1' }}>
                      <label>ID Expéditeur / Numéro expéditeur</label>
                      <input
                        type="text"
                        value={settingsForm.sms?.sms_sender_id?.value || settingsForm.sms?.sms_sender_id || ''}
                        onChange={(e) => setSettingsForm({
                          ...settingsForm,
                          sms: {
                            ...settingsForm.sms,
                            sms_sender_id: { ...settingsForm.sms?.sms_sender_id, value: e.target.value }
                          }
                        })}
                        placeholder="Infobip: 447491163443 | Twilio: numéro"
                      />
                    </div>
                  </div>
                  {(settingsForm.sms?.sms_provider?.value || settingsForm.sms?.sms_provider || '').toLowerCase() === 'infobip' && (
                    <div className="form-group" style={{ marginTop: '10px' }}>
                      <label>Infobip – URL API (host)</label>
                      <input
                        type="text"
                        value={settingsForm.sms?.infobip_base_url?.value ?? settingsForm.sms?.infobip_base_url ?? 'z43rd6.api.infobip.com'}
                        onChange={(e) => setSettingsForm({
                          ...settingsForm,
                          sms: {
                            ...settingsForm.sms,
                            infobip_base_url: { ...settingsForm.sms?.infobip_base_url, value: e.target.value }
                          }
                        })}
                        placeholder="z43rd6.api.infobip.com"
                        style={{ width: '100%', padding: '8px' }}
                      />
                    </div>
                  )}
                  <div className="form-row">
                    <div className="form-group" style={{ flex: '1' }}>
                      <label>Clé API</label>
                      <input
                        type="password"
                        value={settingsForm.sms?.sms_api_key?.value || settingsForm.sms?.sms_api_key || ''}
                        onChange={(e) => setSettingsForm({
                          ...settingsForm,
                          sms: {
                            ...settingsForm.sms,
                            sms_api_key: { ...settingsForm.sms?.sms_api_key, value: e.target.value }
                          }
                        })}
                        placeholder="Clé API SMS"
                      />
                    </div>
                    <div className="form-group" style={{ flex: '1' }}>
                      <label>Secret API</label>
                      <input
                        type="password"
                        value={settingsForm.sms?.sms_api_secret?.value || settingsForm.sms?.sms_api_secret || ''}
                        onChange={(e) => setSettingsForm({
                          ...settingsForm,
                          sms: {
                            ...settingsForm.sms,
                            sms_api_secret: { ...settingsForm.sms?.sms_api_secret, value: e.target.value }
                          }
                        })}
                        placeholder="Secret API SMS"
                      />
                    </div>
                  </div>
                  <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
                    <button className="btn btn-primary" onClick={() => handleSaveSettings('sms')}>
                      💾 Enregistrer
                    </button>
                    <button className="btn btn-secondary" onClick={() => handleResetSettings('sms')}>
                      🔄 Réinitialiser
                    </button>
                  </div>
                </div>

                {/* Configuration des Montants */}
                <div className="card" style={{ marginBottom: '30px' }}>
                  <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #3498db', paddingBottom: '10px' }}>
                    💵 Configuration des Montants par Défaut
                  </h3>
                  <div className="form-group">
                    <label>Carte de Membre (CDF)</label>
                    <input
                      type="number"
                      value={settingsForm.amounts?.default_carte_membre?.value !== undefined ? settingsForm.amounts.default_carte_membre.value : settingsForm.amounts?.default_carte_membre || 5000}
                      onChange={(e) => setSettingsForm({
                        ...settingsForm,
                        amounts: {
                          ...settingsForm.amounts,
                          default_carte_membre: { ...settingsForm.amounts?.default_carte_membre, value: parseFloat(e.target.value) || 0 }
                        }
                      })}
                      min="0"
                      step="100"
                    />
                  </div>
                  <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
                    <button className="btn btn-primary" onClick={() => handleSaveSettings('amounts')}>
                      💾 Enregistrer
                    </button>
                    <button className="btn btn-secondary" onClick={() => handleResetSettings('amounts')}>
                      🔄 Réinitialiser
                    </button>
                  </div>
                </div>

                {/* Paramètres d'Impression */}
                <div className="card" style={{ marginBottom: '30px' }}>
                  <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #3498db', paddingBottom: '10px' }}>
                    🖨️ Paramètres d'Impression
                  </h3>
                  <div className="form-group">
                    <label>Titre de l'en-tête</label>
                    <input
                      type="text"
                      value={settingsForm.print?.print_header_title?.value || settingsForm.print?.print_header_title || 'MUTU NDE ESPOIR ASBL'}
                      onChange={(e) => setSettingsForm({
                        ...settingsForm,
                        print: {
                          ...settingsForm.print,
                          print_header_title: { ...settingsForm.print?.print_header_title, value: e.target.value }
                        }
                      })}
                      placeholder="MUTU NDE ESPOIR ASBL"
                    />
                  </div>
                  <div className="form-group">
                    <label>Sous-titre de l'en-tête</label>
                    <input
                      type="text"
                      value={settingsForm.print?.print_header_subtitle?.value || settingsForm.print?.print_header_subtitle || 'Système de gestion'}
                      onChange={(e) => setSettingsForm({
                        ...settingsForm,
                        print: {
                          ...settingsForm.print,
                          print_header_subtitle: { ...settingsForm.print?.print_header_subtitle, value: e.target.value }
                        }
                      })}
                      placeholder="Système de gestion"
                    />
                  </div>
                  <div className="form-group">
                    <label>Texte du pied de page</label>
                    <input
                      type="text"
                      value={settingsForm.print?.print_footer_text?.value || settingsForm.print?.print_footer_text || 'Document généré par MUTU NDE ESPOIR ASBL - Système de gestion'}
                      onChange={(e) => setSettingsForm({
                        ...settingsForm,
                        print: {
                          ...settingsForm.print,
                          print_footer_text: { ...settingsForm.print?.print_footer_text, value: e.target.value }
                        }
                      })}
                      placeholder="Texte du pied de page"
                    />
                  </div>
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input
                        type="checkbox"
                        checked={settingsForm.print?.print_show_logo?.value !== undefined ? settingsForm.print.print_show_logo.value : settingsForm.print?.print_show_logo !== false}
                        onChange={(e) => setSettingsForm({
                          ...settingsForm,
                          print: {
                            ...settingsForm.print,
                            print_show_logo: { ...settingsForm.print?.print_show_logo, value: e.target.checked }
                          }
                        })}
                      />
                      Afficher le logo sur les documents imprimés
                    </label>
                    <p style={{ fontSize: '11px', color: '#999', marginTop: '5px', marginBottom: 0, fontStyle: 'italic', marginLeft: '25px' }}>
                      ⓘ Le logo utilisé sera celui configuré dans "Configuration Générale"
                    </p>
                  </div>
                  <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
                    <button className="btn btn-primary" onClick={() => handleSaveSettings('print')}>
                      💾 Enregistrer
                    </button>
                    <button className="btn btn-secondary" onClick={() => handleResetSettings('print')}>
                      🔄 Réinitialiser
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'site' && (
          <div>
            <h2 style={{ marginBottom: '30px', color: '#2c3e50' }}>🌐 Gestion du site internet</h2>
            <p style={{ color: '#666', marginBottom: '16px', maxWidth: '600px' }}>
              Configurez les informations affichées sur le site public. Choisissez une option ci-dessous.
            </p>

            <nav className="site-section-menu" style={{ display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: '2px solid #e0e0e0', paddingBottom: '0', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => setSiteSection('infos')}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderBottom: siteSection === 'infos' ? '3px solid #3498db' : '3px solid transparent',
                  background: siteSection === 'infos' ? '#f0f8ff' : 'transparent',
                  color: siteSection === 'infos' ? '#2c3e50' : '#666',
                  fontWeight: siteSection === 'infos' ? 600 : 400,
                  cursor: 'pointer',
                  borderRadius: '4px 4px 0 0',
                  marginBottom: '-2px'
                }}
              >
                📋 Informations du site
              </button>
              <button
                type="button"
                onClick={() => setSiteSection('index')}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderBottom: siteSection === 'index' ? '3px solid #3498db' : '3px solid transparent',
                  background: siteSection === 'index' ? '#f0f8ff' : 'transparent',
                  color: siteSection === 'index' ? '#2c3e50' : '#666',
                  fontWeight: siteSection === 'index' ? 600 : 400,
                  cursor: 'pointer',
                  borderRadius: '4px 4px 0 0',
                  marginBottom: '-2px'
                }}
              >
                🏠 Page d'accueil
              </button>
              <button
                type="button"
                onClick={() => setSiteSection('historique')}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderBottom: siteSection === 'historique' ? '3px solid #3498db' : '3px solid transparent',
                  background: siteSection === 'historique' ? '#f0f8ff' : 'transparent',
                  color: siteSection === 'historique' ? '#2c3e50' : '#666',
                  fontWeight: siteSection === 'historique' ? 600 : 400,
                  cursor: 'pointer',
                  borderRadius: '4px 4px 0 0',
                  marginBottom: '-2px'
                }}
              >
                📜 Page Historique
              </button>
              <button
                type="button"
                onClick={() => setSiteSection('apropos')}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderBottom: siteSection === 'apropos' ? '3px solid #3498db' : '3px solid transparent',
                  background: siteSection === 'apropos' ? '#f0f8ff' : 'transparent',
                  color: siteSection === 'apropos' ? '#2c3e50' : '#666',
                  fontWeight: siteSection === 'apropos' ? 600 : 400,
                  cursor: 'pointer',
                  borderRadius: '4px 4px 0 0',
                  marginBottom: '-2px'
                }}
              >
                📄 Page À propos
              </button>
              <button
                type="button"
                onClick={() => setSiteSection('contact')}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderBottom: siteSection === 'contact' ? '3px solid #3498db' : '3px solid transparent',
                  background: siteSection === 'contact' ? '#f0f8ff' : 'transparent',
                  color: siteSection === 'contact' ? '#2c3e50' : '#666',
                  fontWeight: siteSection === 'contact' ? 600 : 400,
                  cursor: 'pointer',
                  borderRadius: '4px 4px 0 0',
                  marginBottom: '-2px'
                }}
              >
                📧 Page Contact
              </button>
              <button
                type="button"
                onClick={() => setSiteSection('activites')}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderBottom: siteSection === 'activites' ? '3px solid #3498db' : '3px solid transparent',
                  background: siteSection === 'activites' ? '#f0f8ff' : 'transparent',
                  color: siteSection === 'activites' ? '#2c3e50' : '#666',
                  fontWeight: siteSection === 'activites' ? 600 : 400,
                  cursor: 'pointer',
                  borderRadius: '4px 4px 0 0',
                  marginBottom: '-2px'
                }}
              >
                📰 Page Activités
              </button>
            </nav>

            {loadingSettings ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
                <p>Chargement...</p>
              </div>
            ) : (
              <>
                {siteSection === 'infos' && (
              <div className="card" style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #3498db', paddingBottom: '10px' }}>
                  Informations du site
                </h3>
                <div className="form-row">
                  <div className="form-group" style={{ flex: '1' }}>
                    <label>Nom du site *</label>
                    <input
                      type="text"
                      value={settingsForm.site?.site_name?.value ?? settingsForm.site?.site_name ?? systemSettings?.site?.site_name?.value ?? 'MUTU NDE ESPOIR ASBL'}
                      onChange={(e) => setSettingsForm({
                        ...settingsForm,
                        site: {
                          ...settingsForm.site,
                          site_name: { ...settingsForm.site?.site_name, value: e.target.value }
                        }
                      })}
                      placeholder="MUTU NDE ESPOIR ASBL"
                    />
                  </div>
                  <div className="form-group" style={{ flex: '1' }}>
                    <label>Slogan / courte description</label>
                    <input
                      type="text"
                      value={settingsForm.site?.site_tagline?.value ?? settingsForm.site?.site_tagline ?? ''}
                      onChange={(e) => setSettingsForm({
                        ...settingsForm,
                        site: {
                          ...settingsForm.site,
                          site_tagline: { ...settingsForm.site?.site_tagline, value: e.target.value }
                        }
                      })}
                      placeholder="Ex: Mutuelle de santé solidaire"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group" style={{ flex: '1' }}>
                    <label>Email de contact public</label>
                    <input
                      type="email"
                      value={settingsForm.site?.site_email?.value ?? settingsForm.site?.site_email ?? ''}
                      onChange={(e) => setSettingsForm({
                        ...settingsForm,
                        site: { ...settingsForm.site, site_email: { ...settingsForm.site?.site_email, value: e.target.value } }
                      })}
                      placeholder="contact@mundes.org"
                    />
                  </div>
                  <div className="form-group" style={{ flex: '1' }}>
                    <label>Téléphone de contact public</label>
                    <input
                      type="text"
                      value={settingsForm.site?.site_phone?.value ?? settingsForm.site?.site_phone ?? ''}
                      onChange={(e) => setSettingsForm({
                        ...settingsForm,
                        site: { ...settingsForm.site, site_phone: { ...settingsForm.site?.site_phone, value: e.target.value } }
                      })}
                      placeholder="+243 XXX XXX XXX"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Adresse affichée sur le site</label>
                  <input
                    type="text"
                    value={settingsForm.site?.site_address?.value ?? settingsForm.site?.site_address ?? ''}
                    onChange={(e) => setSettingsForm({
                      ...settingsForm,
                      site: { ...settingsForm.site, site_address: { ...settingsForm.site?.site_address, value: e.target.value } }
                    })}
                    placeholder="Adresse complète"
                  />
                </div>
                <div className="form-group">
                  <label>Logo du site</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                    {(settingsForm.site?.site_logo?.value ?? settingsForm.site?.site_logo ?? settingsForm.general?.organization_logo?.value ?? '/logo.png') && (
                      <img
                        src={settingsForm.site?.site_logo?.value ?? settingsForm.site?.site_logo ?? settingsForm.general?.organization_logo?.value ?? '/logo.png'}
                        alt="Logo"
                        style={{ maxWidth: '120px', maxHeight: '60px', objectFit: 'contain', border: '1px solid #ddd', borderRadius: '4px' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <div>
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/gif,image/svg+xml,image/webp"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const isSvg = file.type === 'image/svg+xml';
                          if (isSvg) {
                            (async () => {
                              setUploadingLogo(true);
                              try {
                                const formData = new FormData();
                                formData.append('logo', file);
                                const response = await uploadLogo(formData);
                                if (response?.data?.success && response.data.logoPath) {
                                  setSettingsForm(prev => ({ ...prev, site: { ...prev.site, site_logo: { ...prev.site?.site_logo, value: response.data.logoPath } } }));
                                  await loadSystemSettings();
                                  notificationService.success('Succès', 'Logo mis à jour');
                                }
                              } catch (err) {
                                notificationService.error('Erreur', err.response?.data?.message || 'Erreur upload');
                              } finally {
                                setUploadingLogo(false);
                              }
                            })();
                          } else {
                            setImageCropModal({
                              open: true,
                              file,
                              aspect: 1,
                              title: 'Rogner et compresser le logo',
                              onConfirm: async (processedFile) => {
                                setUploadingLogo(true);
                                try {
                                  const formData = new FormData();
                                  formData.append('logo', processedFile);
                                  const response = await uploadLogo(formData);
                                  if (response?.data?.success && response.data.logoPath) {
                                    setSettingsForm(prev => ({ ...prev, site: { ...prev.site, site_logo: { ...prev.site?.site_logo, value: response.data.logoPath } } }));
                                    await loadSystemSettings();
                                    notificationService.success('Succès', 'Logo mis à jour (rogné et compressé)');
                                  }
                                } catch (err) {
                                  notificationService.error('Erreur', err.response?.data?.message || 'Erreur upload');
                                } finally {
                                  setUploadingLogo(false);
                                  setImageCropModal(prev => ({ ...prev, open: false, file: null, onConfirm: null }));
                                }
                              }
                            });
                          }
                          e.target.value = '';
                        }}
                        disabled={uploadingLogo}
                        style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px', cursor: uploadingLogo ? 'not-allowed' : 'pointer' }}
                      />
                      <p style={{ fontSize: '11px', color: '#999', marginTop: '4px', marginBottom: 0 }}>JPEG, PNG, GIF, SVG, WebP — rognage et compression pour images (hors SVG)</p>
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input
                      type="checkbox"
                      checked={settingsForm.site?.maintenance_mode?.value === true || settingsForm.site?.maintenance_mode === true}
                      onChange={(e) => setSettingsForm({
                        ...settingsForm,
                        site: { ...settingsForm.site, maintenance_mode: { ...settingsForm.site?.maintenance_mode, value: e.target.checked } }
                      })}
                    />
                    Mode maintenance (afficher une page « Site en maintenance » au public)
                  </label>
                </div>
                <div className="form-group">
                  <label>Mentions légales / CGU (texte libre)</label>
                  <textarea
                    rows={6}
                    value={settingsForm.site?.legal_mentions?.value ?? settingsForm.site?.legal_mentions ?? ''}
                    onChange={(e) => setSettingsForm({
                      ...settingsForm,
                      site: { ...settingsForm.site, legal_mentions: { ...settingsForm.site?.legal_mentions, value: e.target.value } }
                    })}
                    placeholder="Mentions légales, conditions d'utilisation, politique de confidentialité..."
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', resize: 'vertical' }}
                  />
                </div>
              </div>
                )}

                {siteSection === 'index' && (
              <div className="card" style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #3498db', paddingBottom: '10px' }}>
                  🏠 Page d'accueil (/)
                </h3>
                <div className="form-group">
                  <label>Description sous le slogan (hero)</label>
                  <textarea
                    rows={3}
                    value={settingsForm.site?.index_hero_description?.value ?? settingsForm.site?.index_hero_description ?? ''}
                    onChange={(e) => setSettingsForm({ ...settingsForm, site: { ...settingsForm.site, index_hero_description: { ...settingsForm.site?.index_hero_description, value: e.target.value } } })}
                    placeholder="Une mutuelle de solidarité..."
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', resize: 'vertical' }}
                  />
                </div>
                <h4 style={{ marginTop: '20px', marginBottom: '12px', color: '#2c3e50' }}>Section Qui Sommes-Nous ?</h4>
                <div className="form-group">
                  <label>Titre</label>
                  <input type="text" value={settingsForm.site?.index_about_title?.value ?? settingsForm.site?.index_about_title ?? 'Qui Sommes-Nous ?'} onChange={(e) => setSettingsForm({ ...settingsForm, site: { ...settingsForm.site, index_about_title: { ...settingsForm.site?.index_about_title, value: e.target.value } } })} style={{ width: '100%', padding: '8px' }} />
                </div>
                <div className="form-group">
                  <label>Paragraphe 1</label>
                  <textarea rows={2} value={settingsForm.site?.index_about_p1?.value ?? settingsForm.site?.index_about_p1 ?? ''} onChange={(e) => setSettingsForm({ ...settingsForm, site: { ...settingsForm.site, index_about_p1: { ...settingsForm.site?.index_about_p1, value: e.target.value } } })} style={{ width: '100%', padding: '8px' }} />
                </div>
                <div className="form-group">
                  <label>Paragraphe 2</label>
                  <textarea rows={2} value={settingsForm.site?.index_about_p2?.value ?? settingsForm.site?.index_about_p2 ?? ''} onChange={(e) => setSettingsForm({ ...settingsForm, site: { ...settingsForm.site, index_about_p2: { ...settingsForm.site?.index_about_p2, value: e.target.value } } })} style={{ width: '100%', padding: '8px' }} />
                </div>
                <div className="form-group">
                  <label>Image de la section Qui Sommes-Nous (page d'accueil)</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    {(settingsForm.site?.index_about_image?.value ?? settingsForm.site?.index_about_image ?? '/rdc.png') && (
                      <img src={settingsForm.site?.index_about_image?.value ?? settingsForm.site?.index_about_image ?? '/rdc.png'} alt="Aperçu" style={{ maxWidth: '120px', maxHeight: '80px', objectFit: 'contain', border: '1px solid #ddd', borderRadius: '6px' }} onError={(e) => { e.target.style.display = 'none'; }} />
                    )}
                    <input type="text" placeholder="URL de l'image (ex: /rdc.png)" value={settingsForm.site?.index_about_image?.value ?? settingsForm.site?.index_about_image ?? ''} onChange={(e) => setSettingsForm({ ...settingsForm, site: { ...settingsForm.site, index_about_image: { ...settingsForm.site?.index_about_image, value: e.target.value } } })} style={{ flex: '1', minWidth: '200px', padding: '8px' }} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input type="file" accept="image/jpeg,image/jpg,image/png,image/gif,image/webp" onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setImageCropModal({
                        open: true,
                        file,
                        aspect: undefined,
                        title: 'Rogner et compresser l\'image (section Qui Sommes-Nous)',
                        onConfirm: async (processedFile) => {
                          try {
                            const formData = new FormData();
                            formData.append('image', processedFile);
                            const response = await uploadIndexImage(formData);
                            if (response?.data?.success && response.data.imagePath) {
                              setSettingsForm(prev => ({ ...prev, site: { ...prev.site, index_about_image: { ...prev.site?.index_about_image, value: response.data.imagePath } } }));
                              notificationService.success('Succès', 'Image envoyée (rognée et compressée).');
                            }
                          } catch (err) {
                            notificationService.error('Erreur', err.response?.data?.message || 'Erreur lors de l\'envoi.');
                          } finally {
                            setImageCropModal(prev => ({ ...prev, open: false, file: null, onConfirm: null }));
                          }
                        }
                      });
                      e.target.value = '';
                    }} style={{ padding: '6px', fontSize: '13px' }} />
                    <span style={{ fontSize: '12px', color: '#666' }}>ou choisir un fichier (rogner + compresser)</span>
                  </div>
                </div>
                <h4 style={{ marginTop: '20px', marginBottom: '12px', color: '#2c3e50' }}>Section Nos Services</h4>
                <div className="form-group">
                  <label>Titre</label>
                  <input type="text" value={settingsForm.site?.index_services_title?.value ?? settingsForm.site?.index_services_title ?? 'Nos Services'} onChange={(e) => setSettingsForm({ ...settingsForm, site: { ...settingsForm.site, index_services_title: { ...settingsForm.site?.index_services_title, value: e.target.value } } })} style={{ width: '100%', padding: '8px' }} />
                </div>
                <div className="form-group">
                  <label>Services (icône, titre, description) — retirer ou ajouter</label>
                  {(() => {
                    let arr = settingsForm.site?.index_services?.value ?? settingsForm.site?.index_services;
                    if (typeof arr === 'string') { try { arr = JSON.parse(arr); } catch (e) { arr = []; } }
                    if (!Array.isArray(arr)) arr = [];
                    return (
                      <>
                        {arr.map((item, i) => (
                          <div key={i} style={{ marginBottom: '12px', padding: '12px', border: '1px solid #eee', borderRadius: '8px', background: '#fafafa', position: 'relative' }}>
                            <button type="button" onClick={() => setSettingsForm(prev => ({ ...prev, site: { ...prev.site, index_services: { ...prev.site?.index_services, value: arr.filter((_, idx) => idx !== i) } } }))} style={{ position: 'absolute', top: '8px', right: '8px', padding: '4px 10px', fontSize: '12px', background: '#e74c3c', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Retirer</button>
                            <input type="text" placeholder="Icône (emoji)" value={item?.icon ?? ''} onChange={(e) => { const n = [...arr]; n[i] = { ...n[i], icon: e.target.value }; setSettingsForm(prev => ({ ...prev, site: { ...prev.site, index_services: { ...prev.site?.index_services, value: n } } })); }} style={{ width: '60px', marginRight: '8px', padding: '6px' }} />
                            <input type="text" placeholder="Titre" value={item?.title ?? ''} onChange={(e) => { const n = [...arr]; n[i] = { ...n[i], title: e.target.value }; setSettingsForm(prev => ({ ...prev, site: { ...prev.site, index_services: { ...prev.site?.index_services, value: n } } })); }} style={{ flex: 1, minWidth: '180px', padding: '6px', marginRight: '8px' }} />
                            <textarea placeholder="Description" value={item?.description ?? ''} onChange={(e) => { const n = [...arr]; n[i] = { ...n[i], description: e.target.value }; setSettingsForm(prev => ({ ...prev, site: { ...prev.site, index_services: { ...prev.site?.index_services, value: n } } })); }} rows={2} style={{ width: '100%', marginTop: '8px', padding: '8px' }} />
                          </div>
                        ))}
                        <button type="button" onClick={() => setSettingsForm(prev => ({ ...prev, site: { ...prev.site, index_services: { ...prev.site?.index_services, value: [...(Array.isArray(prev.site?.index_services?.value) ? prev.site.index_services.value : []), { icon: '•', title: '', description: '' }] } } }))} style={{ padding: '8px 14px', fontSize: '13px', background: '#3498db', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>+ Ajouter un service</button>
                      </>
                    );
                  })()}
                </div>
                <h4 style={{ marginTop: '20px', marginBottom: '12px', color: '#2c3e50' }}>Section Nos Valeurs</h4>
                <div className="form-group">
                  <label>Titre</label>
                  <input type="text" value={settingsForm.site?.index_values_title?.value ?? settingsForm.site?.index_values_title ?? 'Nos Valeurs'} onChange={(e) => setSettingsForm({ ...settingsForm, site: { ...settingsForm.site, index_values_title: { ...settingsForm.site?.index_values_title, value: e.target.value } } })} style={{ width: '100%', padding: '8px' }} />
                </div>
                <div className="form-group">
                  <label>Valeurs (titre, description)</label>
                  {(() => {
                    let arr = settingsForm.site?.index_values?.value ?? settingsForm.site?.index_values;
                    if (typeof arr === 'string') { try { arr = JSON.parse(arr); } catch (e) { arr = []; } }
                    if (!Array.isArray(arr)) arr = [];
                    return (
                      <>
                        {arr.map((item, i) => (
                          <div key={i} style={{ marginBottom: '12px', padding: '12px', border: '1px solid #eee', borderRadius: '8px', background: '#fafafa', position: 'relative' }}>
                            <button type="button" onClick={() => setSettingsForm(prev => ({ ...prev, site: { ...prev.site, index_values: { ...prev.site?.index_values, value: arr.filter((_, idx) => idx !== i) } } }))} style={{ position: 'absolute', top: '8px', right: '8px', padding: '4px 10px', fontSize: '12px', background: '#e74c3c', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Retirer</button>
                            <input type="text" placeholder="Titre (ex: Solidarité)" value={item?.title ?? ''} onChange={(e) => { const n = [...arr]; n[i] = { ...n[i], title: e.target.value }; setSettingsForm(prev => ({ ...prev, site: { ...prev.site, index_values: { ...prev.site?.index_values, value: n } } })); }} style={{ width: '100%', padding: '6px', marginBottom: '6px' }} />
                            <textarea placeholder="Description" value={item?.description ?? ''} onChange={(e) => { const n = [...arr]; n[i] = { ...n[i], description: e.target.value }; setSettingsForm(prev => ({ ...prev, site: { ...prev.site, index_values: { ...prev.site?.index_values, value: n } } })); }} rows={2} style={{ width: '100%', padding: '8px' }} />
                          </div>
                        ))}
                        <button type="button" onClick={() => setSettingsForm(prev => ({ ...prev, site: { ...prev.site, index_values: { ...prev.site?.index_values, value: [...(Array.isArray(prev.site?.index_values?.value) ? prev.site.index_values.value : []), { title: '', description: '' }] } } }))} style={{ padding: '8px 14px', fontSize: '13px', background: '#3498db', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>+ Ajouter une valeur</button>
                      </>
                    );
                  })()}
                </div>
                <h4 style={{ marginTop: '20px', marginBottom: '12px', color: '#2c3e50' }}>Section Devenez Membre</h4>
                <div className="form-group">
                  <label>Titre</label>
                  <input type="text" value={settingsForm.site?.index_membership_title?.value ?? settingsForm.site?.index_membership_title ?? 'Devenez Membre'} onChange={(e) => setSettingsForm({ ...settingsForm, site: { ...settingsForm.site, index_membership_title: { ...settingsForm.site?.index_membership_title, value: e.target.value } } })} style={{ width: '100%', padding: '8px' }} />
                </div>
                <div className="form-group">
                  <label>Sous-titre avantages</label>
                  <input type="text" value={settingsForm.site?.index_membership_benefits_title?.value ?? settingsForm.site?.index_membership_benefits_title ?? 'Avantages de l\'adhésion'} onChange={(e) => setSettingsForm({ ...settingsForm, site: { ...settingsForm.site, index_membership_benefits_title: { ...settingsForm.site?.index_membership_benefits_title, value: e.target.value } } })} style={{ width: '100%', padding: '8px' }} />
                </div>
                <div className="form-group">
                  <label>Liste des avantages (un par ligne ou retirer/ajouter)</label>
                  {(() => {
                    let arr = settingsForm.site?.index_membership_benefits?.value ?? settingsForm.site?.index_membership_benefits;
                    if (typeof arr === 'string') { try { arr = JSON.parse(arr); } catch (e) { arr = []; } }
                    if (!Array.isArray(arr)) arr = [];
                    return (
                      <>
                        {arr.map((item, i) => (
                          <div key={i} style={{ marginBottom: '8px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <input type="text" value={typeof item === 'string' ? item : (item?.text ?? item?.label ?? '')} onChange={(e) => { const n = [...arr]; n[i] = e.target.value; setSettingsForm(prev => ({ ...prev, site: { ...prev.site, index_membership_benefits: { ...prev.site?.index_membership_benefits, value: n } } })); }} style={{ flex: 1, padding: '6px 8px' }} placeholder="Avantage" />
                            <button type="button" onClick={() => setSettingsForm(prev => ({ ...prev, site: { ...prev.site, index_membership_benefits: { ...prev.site?.index_membership_benefits, value: arr.filter((_, idx) => idx !== i) } } }))} style={{ padding: '4px 10px', fontSize: '12px', background: '#e74c3c', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Retirer</button>
                          </div>
                        ))}
                        <button type="button" onClick={() => setSettingsForm(prev => ({ ...prev, site: { ...prev.site, index_membership_benefits: { ...prev.site?.index_membership_benefits, value: [...(Array.isArray(prev.site?.index_membership_benefits?.value) ? prev.site.index_membership_benefits.value : []), ''] } } }))} style={{ padding: '8px 14px', fontSize: '13px', background: '#3498db', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', marginTop: '8px' }}>+ Ajouter un avantage</button>
                      </>
                    );
                  })()}
                </div>
                <div className="form-group">
                  <label>Texte d'appel (CTA)</label>
                  <input type="text" value={settingsForm.site?.index_membership_cta?.value ?? settingsForm.site?.index_membership_cta ?? ''} onChange={(e) => setSettingsForm({ ...settingsForm, site: { ...settingsForm.site, index_membership_cta: { ...settingsForm.site?.index_membership_cta, value: e.target.value } } })} style={{ width: '100%', padding: '8px' }} placeholder="Rejoignez notre communauté..." />
                </div>
              </div>
                )}

                {siteSection === 'apropos' && (
              <div className="card" style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #3498db', paddingBottom: '10px' }}>📄 Page À propos (/apropos)</h3>
                <div className="form-row">
                  <div className="form-group" style={{ flex: 1 }}><label>Titre de la page</label><input type="text" value={settingsForm.site?.apropos_title?.value ?? settingsForm.site?.apropos_title ?? 'À Propos de Nous'} onChange={(e) => setSettingsForm({ ...settingsForm, site: { ...settingsForm.site, apropos_title: { ...settingsForm.site?.apropos_title, value: e.target.value } } })} style={{ width: '100%', padding: '8px' }} /></div>
                  <div className="form-group" style={{ flex: 1 }}><label>Sous-titre</label><input type="text" value={settingsForm.site?.apropos_subtitle?.value ?? settingsForm.site?.apropos_subtitle ?? ''} onChange={(e) => setSettingsForm({ ...settingsForm, site: { ...settingsForm.site, apropos_subtitle: { ...settingsForm.site?.apropos_subtitle, value: e.target.value } } })} style={{ width: '100%', padding: '8px' }} /></div>
                </div>
                <h4 style={{ marginTop: '20px', marginBottom: '10px', color: '#2c3e50' }}>Section Mission</h4>
                <div className="form-group"><label>Titre</label><input type="text" value={settingsForm.site?.apropos_mission_title?.value ?? settingsForm.site?.apropos_mission_title ?? 'Notre Mission'} onChange={(e) => setSettingsForm({ ...settingsForm, site: { ...settingsForm.site, apropos_mission_title: { ...settingsForm.site?.apropos_mission_title, value: e.target.value } } })} style={{ width: '100%', padding: '8px' }} /></div>
                <div className="form-group"><label>Paragraphe 1</label><textarea rows={2} value={settingsForm.site?.apropos_mission_p1?.value ?? settingsForm.site?.apropos_mission_p1 ?? ''} onChange={(e) => setSettingsForm({ ...settingsForm, site: { ...settingsForm.site, apropos_mission_p1: { ...settingsForm.site?.apropos_mission_p1, value: e.target.value } } })} style={{ width: '100%', padding: '8px' }} /></div>
                <div className="form-group"><label>Paragraphe 2</label><textarea rows={2} value={settingsForm.site?.apropos_mission_p2?.value ?? settingsForm.site?.apropos_mission_p2 ?? ''} onChange={(e) => setSettingsForm({ ...settingsForm, site: { ...settingsForm.site, apropos_mission_p2: { ...settingsForm.site?.apropos_mission_p2, value: e.target.value } } })} style={{ width: '100%', padding: '8px' }} /></div>
                <h4 style={{ marginTop: '20px', marginBottom: '10px', color: '#2c3e50' }}>Section Vision</h4>
                <div className="form-group"><label>Titre</label><input type="text" value={settingsForm.site?.apropos_vision_title?.value ?? settingsForm.site?.apropos_vision_title ?? 'Notre Vision'} onChange={(e) => setSettingsForm({ ...settingsForm, site: { ...settingsForm.site, apropos_vision_title: { ...settingsForm.site?.apropos_vision_title, value: e.target.value } } })} style={{ width: '100%', padding: '8px' }} /></div>
                <div className="form-group"><label>Paragraphe 1</label><textarea rows={2} value={settingsForm.site?.apropos_vision_p1?.value ?? settingsForm.site?.apropos_vision_p1 ?? ''} onChange={(e) => setSettingsForm({ ...settingsForm, site: { ...settingsForm.site, apropos_vision_p1: { ...settingsForm.site?.apropos_vision_p1, value: e.target.value } } })} style={{ width: '100%', padding: '8px' }} /></div>
                <div className="form-group"><label>Paragraphe 2</label><textarea rows={2} value={settingsForm.site?.apropos_vision_p2?.value ?? settingsForm.site?.apropos_vision_p2 ?? ''} onChange={(e) => setSettingsForm({ ...settingsForm, site: { ...settingsForm.site, apropos_vision_p2: { ...settingsForm.site?.apropos_vision_p2, value: e.target.value } } })} style={{ width: '100%', padding: '8px' }} /></div>
                <h4 style={{ marginTop: '20px', marginBottom: '10px', color: '#2c3e50' }}>Valeurs (icône, titre, description)</h4>
                {(() => {
                  let arr = settingsForm.site?.apropos_values?.value ?? settingsForm.site?.apropos_values;
                  if (typeof arr === 'string') { try { arr = JSON.parse(arr); } catch (e) { arr = []; } }
                  if (!Array.isArray(arr)) arr = [];
                  return (
                    <>
                      {arr.map((item, i) => (
                        <div key={i} style={{ marginBottom: '12px', padding: '12px', border: '1px solid #eee', borderRadius: '8px', background: '#fafafa', position: 'relative' }}>
                          <button type="button" onClick={() => setSettingsForm(prev => ({ ...prev, site: { ...prev.site, apropos_values: { ...prev.site?.apropos_values, value: arr.filter((_, idx) => idx !== i) } } }))} style={{ position: 'absolute', top: '8px', right: '8px', padding: '4px 10px', fontSize: '12px', background: '#e74c3c', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Retirer</button>
                          <input type="text" placeholder="Icône" value={item?.icon ?? ''} onChange={(e) => { const n = [...arr]; n[i] = { ...n[i], icon: e.target.value }; setSettingsForm(prev => ({ ...prev, site: { ...prev.site, apropos_values: { ...prev.site?.apropos_values, value: n } } })); }} style={{ width: '50px', marginRight: '8px', padding: '6px' }} />
                          <input type="text" placeholder="Titre" value={item?.title ?? ''} onChange={(e) => { const n = [...arr]; n[i] = { ...n[i], title: e.target.value }; setSettingsForm(prev => ({ ...prev, site: { ...prev.site, apropos_values: { ...prev.site?.apropos_values, value: n } } })); }} style={{ width: '180px', marginRight: '8px', padding: '6px' }} />
                          <textarea placeholder="Description" value={item?.description ?? ''} onChange={(e) => { const n = [...arr]; n[i] = { ...n[i], description: e.target.value }; setSettingsForm(prev => ({ ...prev, site: { ...prev.site, apropos_values: { ...prev.site?.apropos_values, value: n } } })); }} rows={2} style={{ width: '100%', marginTop: '8px', padding: '8px' }} />
                        </div>
                      ))}
                      <button type="button" onClick={() => setSettingsForm(prev => ({ ...prev, site: { ...prev.site, apropos_values: { ...prev.site?.apropos_values, value: [...(Array.isArray(prev.site?.apropos_values?.value) ? prev.site.apropos_values.value : []), { icon: '•', title: '', description: '' }] } } }))} style={{ padding: '8px 14px', fontSize: '13px', background: '#3498db', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>+ Ajouter une valeur</button>
                    </>
                  );
                })()}
                <h4 style={{ marginTop: '20px', marginBottom: '10px', color: '#2c3e50' }}>Section Équipe</h4>
                <div className="form-group"><label>Titre</label><input type="text" value={settingsForm.site?.apropos_team_title?.value ?? settingsForm.site?.apropos_team_title ?? 'Notre Équipe'} onChange={(e) => setSettingsForm({ ...settingsForm, site: { ...settingsForm.site, apropos_team_title: { ...settingsForm.site?.apropos_team_title, value: e.target.value } } })} style={{ width: '100%', padding: '8px' }} /></div>
                <div className="form-group"><label>Texte d'introduction</label><textarea rows={2} value={settingsForm.site?.apropos_team_intro?.value ?? settingsForm.site?.apropos_team_intro ?? ''} onChange={(e) => setSettingsForm({ ...settingsForm, site: { ...settingsForm.site, apropos_team_intro: { ...settingsForm.site?.apropos_team_intro, value: e.target.value } } })} style={{ width: '100%', padding: '8px' }} /></div>
                <div className="form-group"><label>Membres (nom, rôle, description, photo)</label>
                  {(() => {
                    let arr = settingsForm.site?.apropos_team?.value ?? settingsForm.site?.apropos_team;
                    if (typeof arr === 'string') { try { arr = JSON.parse(arr); } catch (e) { arr = []; } }
                    if (!Array.isArray(arr)) arr = [];
                    return (
                      <>
                        {arr.map((item, i) => (
                          <div key={i} style={{ marginBottom: '12px', padding: '12px', border: '1px solid #eee', borderRadius: '8px', background: '#fafafa', position: 'relative' }}>
                            <button type="button" onClick={() => setSettingsForm(prev => ({ ...prev, site: { ...prev.site, apropos_team: { ...prev.site?.apropos_team, value: arr.filter((_, idx) => idx !== i) } } }))} style={{ position: 'absolute', top: '8px', right: '8px', padding: '4px 10px', fontSize: '12px', background: '#e74c3c', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Retirer</button>
                            <input type="text" placeholder="Nom" value={item?.name ?? ''} onChange={(e) => { const n = [...arr]; n[i] = { ...n[i], name: e.target.value }; setSettingsForm(prev => ({ ...prev, site: { ...prev.site, apropos_team: { ...prev.site?.apropos_team, value: n } } })); }} style={{ width: '100%', padding: '6px', marginBottom: '6px' }} />
                            <div className="form-row" style={{ marginBottom: '8px' }}>
                              <input type="text" placeholder="Rôle" value={item?.role ?? ''} onChange={(e) => { const n = [...arr]; n[i] = { ...n[i], role: e.target.value }; setSettingsForm(prev => ({ ...prev, site: { ...prev.site, apropos_team: { ...prev.site?.apropos_team, value: n } } })); }} style={{ flex: 1, padding: '6px', marginRight: '8px' }} />
                              <input type="text" placeholder="Photo (URL manuel)" value={item?.photo ?? ''} onChange={(e) => { const n = [...arr]; n[i] = { ...n[i], photo: e.target.value }; setSettingsForm(prev => ({ ...prev, site: { ...prev.site, apropos_team: { ...prev.site?.apropos_team, value: n } } })); }} style={{ flex: 1, padding: '6px' }} />
                            </div>
                            <div style={{ marginBottom: '8px' }}>
                              <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>Ou envoyer une photo :</label>
                              {(item?.photo) && (
                                <img src={item.photo} alt="" style={{ maxWidth: '60px', maxHeight: '60px', objectFit: 'cover', borderRadius: '4px', marginRight: '8px', verticalAlign: 'middle', border: '1px solid #ddd' }} onError={(e) => { e.target.style.display = 'none'; }} />
                              )}
                              <input
                                type="file"
                                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  const arrCopy = [...arr];
                                  const memberIndex = i;
                                  setImageCropModal({
                                    open: true,
                                    file,
                                    aspect: 1,
                                    title: 'Rogner et compresser la photo du membre',
                                    onConfirm: async (processedFile) => {
                                      try {
                                        const formData = new FormData();
                                        formData.append('photo', processedFile);
                                        const response = await uploadTeamPhoto(formData);
                                        if (response?.data?.success && response.data.photoPath) {
                                          setSettingsForm(prev => {
                                            const raw = prev.site?.apropos_team?.value ?? prev.site?.apropos_team;
                                            const nextArr = Array.isArray(raw) ? [...raw] : (typeof raw === 'string' ? (() => { try { return JSON.parse(raw); } catch (e) { return []; } })() : []);
                                            nextArr[memberIndex] = { ...(nextArr[memberIndex] || {}), photo: response.data.photoPath };
                                            return { ...prev, site: { ...prev.site, apropos_team: { ...prev.site?.apropos_team, value: nextArr } } };
                                          });
                                          notificationService.success('Succès', 'Photo envoyée (rognée et compressée).');
                                        }
                                      } catch (err) {
                                        notificationService.error('Erreur', err.response?.data?.message || 'Erreur lors de l\'envoi.');
                                      } finally {
                                        setImageCropModal(prev => ({ ...prev, open: false, file: null, onConfirm: null }));
                                      }
                                    }
                                  });
                                  e.target.value = '';
                                }}
                                style={{ padding: '4px', fontSize: '12px' }}
                              />
                              <span style={{ fontSize: '11px', color: '#999', marginLeft: '8px' }}>JPEG, PNG, GIF, WebP — rognage carré + compression</span>
                            </div>
                            <textarea placeholder="Description" value={item?.description ?? ''} onChange={(e) => { const n = [...arr]; n[i] = { ...n[i], description: e.target.value }; setSettingsForm(prev => ({ ...prev, site: { ...prev.site, apropos_team: { ...prev.site?.apropos_team, value: n } } })); }} rows={2} style={{ width: '100%', marginTop: '6px', padding: '8px' }} />
                          </div>
                        ))}
                        <button type="button" onClick={() => setSettingsForm(prev => ({ ...prev, site: { ...prev.site, apropos_team: { ...prev.site?.apropos_team, value: [...(Array.isArray(prev.site?.apropos_team?.value) ? prev.site.apropos_team.value : []), { name: '', role: '', description: '', photo: '/logo.png' }] } } }))} style={{ padding: '8px 14px', fontSize: '13px', background: '#3498db', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>+ Ajouter un membre</button>
                      </>
                    );
                  })()}
                </div>
                <h4 style={{ marginTop: '20px', marginBottom: '10px', color: '#2c3e50' }}>Section Localisation</h4>
                <div className="form-group"><label>Titre</label><input type="text" value={settingsForm.site?.apropos_location_title?.value ?? settingsForm.site?.apropos_location_title ?? 'Notre Localisation'} onChange={(e) => setSettingsForm({ ...settingsForm, site: { ...settingsForm.site, apropos_location_title: { ...settingsForm.site?.apropos_location_title, value: e.target.value } } })} style={{ width: '100%', padding: '8px' }} /></div>
                <div className="form-group"><label>Texte</label><textarea rows={3} value={settingsForm.site?.apropos_location_text?.value ?? settingsForm.site?.apropos_location_text ?? ''} onChange={(e) => setSettingsForm({ ...settingsForm, site: { ...settingsForm.site, apropos_location_text: { ...settingsForm.site?.apropos_location_text, value: e.target.value } } })} style={{ width: '100%', padding: '8px' }} /></div>
              </div>
                )}

                {siteSection === 'historique' && (
              <div className="card" style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #3498db', paddingBottom: '10px' }}>
                  📜 Page Historique (/historique)
                </h3>
                <div className="form-row">
                  <div className="form-group" style={{ flex: '1' }}>
                    <label>Titre de la page</label>
                    <input
                      type="text"
                      value={settingsForm.site?.historique_title?.value ?? settingsForm.site?.historique_title ?? 'Notre Historique'}
                      onChange={(e) => setSettingsForm({
                        ...settingsForm,
                        site: { ...settingsForm.site, historique_title: { ...settingsForm.site?.historique_title, value: e.target.value } }
                      })}
                      placeholder="Notre Historique"
                    />
                  </div>
                  <div className="form-group" style={{ flex: '1' }}>
                    <label>Sous-titre</label>
                    <input
                      type="text"
                      value={settingsForm.site?.historique_subtitle?.value ?? settingsForm.site?.historique_subtitle ?? ''}
                      onChange={(e) => setSettingsForm({
                        ...settingsForm,
                        site: { ...settingsForm.site, historique_subtitle: { ...settingsForm.site?.historique_subtitle, value: e.target.value } }
                      })}
                      placeholder="Un parcours de solidarité et d'engagement"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Timeline (année, titre, texte)</label>
                  <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>Vous pouvez retirer des étapes ou en ajouter.</p>
                  {(() => {
                    let arr = settingsForm.site?.historique_timeline?.value ?? settingsForm.site?.historique_timeline;
                    if (typeof arr === 'string') {
                      try { arr = JSON.parse(arr); } catch (e) { arr = []; }
                    }
                    if (!Array.isArray(arr)) arr = [];
                    return (
                      <>
                        {arr.map((item, i) => (
                          <div key={i} style={{ marginBottom: '16px', padding: '12px', border: '1px solid #eee', borderRadius: '8px', background: '#fafafa', position: 'relative' }}>
                            <button
                              type="button"
                              onClick={() => {
                                const next = arr.filter((_, idx) => idx !== i);
                                setSettingsForm(prev => ({ ...prev, site: { ...prev.site, historique_timeline: { ...prev.site?.historique_timeline, value: next } } }));
                              }}
                              title="Retirer cette étape"
                              style={{ position: 'absolute', top: '8px', right: '8px', padding: '4px 10px', fontSize: '12px', background: '#e74c3c', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                            >
                              Retirer
                            </button>
                            <div className="form-row" style={{ marginBottom: '8px' }}>
                              <input
                                type="text"
                                placeholder="Année (ex: 2015)"
                                value={item?.year ?? ''}
                                onChange={(e) => {
                                  const next = [...arr];
                                  next[i] = { ...next[i], year: e.target.value };
                                  setSettingsForm(prev => ({ ...prev, site: { ...prev.site, historique_timeline: { ...prev.site?.historique_timeline, value: next } } }));
                                }}
                                style={{ width: '120px', marginRight: '8px', padding: '6px 8px' }}
                              />
                              <input
                                type="text"
                                placeholder="Titre"
                                value={item?.title ?? ''}
                                onChange={(e) => {
                                  const next = [...arr];
                                  next[i] = { ...next[i], title: e.target.value };
                                  setSettingsForm(prev => ({ ...prev, site: { ...prev.site, historique_timeline: { ...prev.site?.historique_timeline, value: next } } }));
                                }}
                                style={{ flex: 1, padding: '6px 8px' }}
                              />
                            </div>
                            <textarea
                              placeholder="Texte"
                              value={item?.text ?? ''}
                              onChange={(e) => {
                                const next = [...arr];
                                next[i] = { ...next[i], text: e.target.value };
                                setSettingsForm(prev => ({ ...prev, site: { ...prev.site, historique_timeline: { ...prev.site?.historique_timeline, value: next } } }));
                              }}
                              rows={2}
                              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                            />
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => {
                            const current = settingsForm.site?.historique_timeline?.value ?? settingsForm.site?.historique_timeline;
                            let arr = Array.isArray(current) ? current : (typeof current === 'string' ? (() => { try { return JSON.parse(current); } catch (e) { return []; } })() : []);
                            setSettingsForm(prev => ({ ...prev, site: { ...prev.site, historique_timeline: { ...prev.site?.historique_timeline, value: [...arr, { year: '', title: '', text: '' }] } } }));
                          }}
                          style={{ padding: '8px 14px', fontSize: '13px', background: '#3498db', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        >
                          + Ajouter une étape
                        </button>
                      </>
                    );
                  })()}
                </div>
                <div className="form-group">
                  <label>Titre de la section « Réalisations »</label>
                  <input
                    type="text"
                    value={settingsForm.site?.historique_achievements_title?.value ?? settingsForm.site?.historique_achievements_title ?? 'Nos Réalisations'}
                    onChange={(e) => setSettingsForm({
                      ...settingsForm,
                      site: { ...settingsForm.site, historique_achievements_title: { ...settingsForm.site?.historique_achievements_title, value: e.target.value } }
                    })}
                    placeholder="Nos Réalisations"
                  />
                </div>
                <div className="form-group">
                  <label>Réalisations (icône, titre, texte)</label>
                  <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>Vous pouvez retirer des réalisations ou en ajouter.</p>
                  {(() => {
                    let arr = settingsForm.site?.historique_achievements?.value ?? settingsForm.site?.historique_achievements;
                    if (typeof arr === 'string') {
                      try { arr = JSON.parse(arr); } catch (e) { arr = []; }
                    }
                    if (!Array.isArray(arr)) arr = [];
                    return (
                      <>
                        {arr.map((item, i) => (
                          <div key={i} style={{ marginBottom: '12px', padding: '12px', border: '1px solid #eee', borderRadius: '8px', background: '#fafafa', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-start', position: 'relative' }}>
                            <button
                              type="button"
                              onClick={() => {
                                const next = arr.filter((_, idx) => idx !== i);
                                setSettingsForm(prev => ({ ...prev, site: { ...prev.site, historique_achievements: { ...prev.site?.historique_achievements, value: next } } }));
                              }}
                              title="Retirer cette réalisation"
                              style={{ position: 'absolute', top: '8px', right: '8px', padding: '4px 10px', fontSize: '12px', background: '#e74c3c', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                            >
                              Retirer
                            </button>
                            <input
                              type="text"
                              placeholder="Icône (emoji)"
                              value={item?.icon ?? ''}
                              onChange={(e) => {
                                const next = [...arr];
                                next[i] = { ...next[i], icon: e.target.value };
                                setSettingsForm(prev => ({ ...prev, site: { ...prev.site, historique_achievements: { ...prev.site?.historique_achievements, value: next } } }));
                              }}
                              style={{ width: '60px', padding: '6px 8px', textAlign: 'center' }}
                            />
                            <input
                              type="text"
                              placeholder="Titre"
                              value={item?.title ?? ''}
                              onChange={(e) => {
                                const next = [...arr];
                                next[i] = { ...next[i], title: e.target.value };
                                setSettingsForm(prev => ({ ...prev, site: { ...prev.site, historique_achievements: { ...prev.site?.historique_achievements, value: next } } }));
                              }}
                              style={{ flex: 1, minWidth: '140px', padding: '6px 8px' }}
                            />
                            <input
                              type="text"
                              placeholder="Texte"
                              value={item?.text ?? ''}
                              onChange={(e) => {
                                const next = [...arr];
                                next[i] = { ...next[i], text: e.target.value };
                                setSettingsForm(prev => ({ ...prev, site: { ...prev.site, historique_achievements: { ...prev.site?.historique_achievements, value: next } } }));
                              }}
                              style={{ flex: 2, minWidth: '200px', padding: '6px 8px' }}
                            />
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => {
                            const current = settingsForm.site?.historique_achievements?.value ?? settingsForm.site?.historique_achievements;
                            let arr = Array.isArray(current) ? current : (typeof current === 'string' ? (() => { try { return JSON.parse(current); } catch (e) { return []; } })() : []);
                            setSettingsForm(prev => ({ ...prev, site: { ...prev.site, historique_achievements: { ...prev.site?.historique_achievements, value: [...arr, { icon: '•', title: '', text: '' }] } } }));
                          }}
                          style={{ padding: '8px 14px', fontSize: '13px', background: '#3498db', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        >
                          + Ajouter une réalisation
                        </button>
                      </>
                    );
                  })()}
                </div>
              </div>
                )}

                {siteSection === 'contact' && (
              <div className="card" style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #3498db', paddingBottom: '10px' }}>
                  📧 Page Contact (/contact)
                </h3>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '16px' }}>Adresse, email et téléphone affichés sur la page proviennent des <strong>Informations du site</strong>. Ici vous configurez les titres, libellés et textes spécifiques à la page Contact.</p>
                <div className="form-row">
                  <div className="form-group" style={{ flex: '1' }}>
                    <label>Titre de la page</label>
                    <input type="text" value={settingsForm.site?.contact_title?.value ?? settingsForm.site?.contact_title ?? 'Contactez-Nous'} onChange={(e) => setSettingsForm({ ...settingsForm, site: { ...settingsForm.site, contact_title: { ...settingsForm.site?.contact_title, value: e.target.value } } })} style={{ width: '100%', padding: '8px' }} />
                  </div>
                  <div className="form-group" style={{ flex: '1' }}>
                    <label>Sous-titre</label>
                    <input type="text" value={settingsForm.site?.contact_subtitle?.value ?? settingsForm.site?.contact_subtitle ?? 'Nous sommes là pour vous aider'} onChange={(e) => setSettingsForm({ ...settingsForm, site: { ...settingsForm.site, contact_subtitle: { ...settingsForm.site?.contact_subtitle, value: e.target.value } } })} style={{ width: '100%', padding: '8px' }} />
                  </div>
                </div>
                <h4 style={{ marginTop: '20px', marginBottom: '12px', color: '#2c3e50' }}>Section Informations de contact</h4>
                <div className="form-group">
                  <label>Titre de la section</label>
                  <input type="text" value={settingsForm.site?.contact_info_section_title?.value ?? settingsForm.site?.contact_info_section_title ?? 'Informations de Contact'} onChange={(e) => setSettingsForm({ ...settingsForm, site: { ...settingsForm.site, contact_info_section_title: { ...settingsForm.site?.contact_info_section_title, value: e.target.value } } })} style={{ width: '100%', padding: '8px' }} />
                </div>
                <div className="form-row" style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                  <div className="form-group" style={{ flex: '1', minWidth: '140px' }}>
                    <label>Libellé Adresse</label>
                    <input type="text" value={settingsForm.site?.contact_address_label?.value ?? settingsForm.site?.contact_address_label ?? 'Adresse'} onChange={(e) => setSettingsForm({ ...settingsForm, site: { ...settingsForm.site, contact_address_label: { ...settingsForm.site?.contact_address_label, value: e.target.value } } })} style={{ width: '100%', padding: '8px' }} />
                  </div>
                  <div className="form-group" style={{ flex: '1', minWidth: '140px' }}>
                    <label>Libellé Email</label>
                    <input type="text" value={settingsForm.site?.contact_email_label?.value ?? settingsForm.site?.contact_email_label ?? 'Email'} onChange={(e) => setSettingsForm({ ...settingsForm, site: { ...settingsForm.site, contact_email_label: { ...settingsForm.site?.contact_email_label, value: e.target.value } } })} style={{ width: '100%', padding: '8px' }} />
                  </div>
                  <div className="form-group" style={{ flex: '1', minWidth: '140px' }}>
                    <label>Libellé Téléphone</label>
                    <input type="text" value={settingsForm.site?.contact_phone_label?.value ?? settingsForm.site?.contact_phone_label ?? 'Téléphone'} onChange={(e) => setSettingsForm({ ...settingsForm, site: { ...settingsForm.site, contact_phone_label: { ...settingsForm.site?.contact_phone_label, value: e.target.value } } })} style={{ width: '100%', padding: '8px' }} />
                  </div>
                  <div className="form-group" style={{ flex: '1', minWidth: '160px' }}>
                    <label>Libellé Heures d'ouverture</label>
                    <input type="text" value={settingsForm.site?.contact_hours_label?.value ?? settingsForm.site?.contact_hours_label ?? "Heures d'Ouverture"} onChange={(e) => setSettingsForm({ ...settingsForm, site: { ...settingsForm.site, contact_hours_label: { ...settingsForm.site?.contact_hours_label, value: e.target.value } } })} style={{ width: '100%', padding: '8px' }} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Heures d'ouverture (une ligne par créneau)</label>
                  <textarea rows={3} value={settingsForm.site?.contact_hours_text?.value ?? settingsForm.site?.contact_hours_text ?? ''} onChange={(e) => setSettingsForm({ ...settingsForm, site: { ...settingsForm.site, contact_hours_text: { ...settingsForm.site?.contact_hours_text, value: e.target.value } } })} placeholder="Lundi - Vendredi: 8h00 - 17h00 (une ligne par créneau)" style={{ width: '100%', padding: '8px' }} />
                </div>
                <h4 style={{ marginTop: '20px', marginBottom: '12px', color: '#2c3e50' }}>Réseaux sociaux</h4>
                <div className="form-group">
                  <label>Titre section</label>
                  <input type="text" value={settingsForm.site?.contact_social_title?.value ?? settingsForm.site?.contact_social_title ?? 'Suivez-Nous'} onChange={(e) => setSettingsForm({ ...settingsForm, site: { ...settingsForm.site, contact_social_title: { ...settingsForm.site?.contact_social_title, value: e.target.value } } })} style={{ width: '100%', padding: '8px' }} />
                </div>
                <div className="form-group">
                  <label>Liens (nom, URL, icône emoji) — retirer ou ajouter</label>
                  {(() => {
                    let arr = settingsForm.site?.contact_social_links?.value ?? settingsForm.site?.contact_social_links;
                    if (typeof arr === 'string') { try { arr = JSON.parse(arr); } catch (e) { arr = []; } }
                    if (!Array.isArray(arr)) arr = [];
                    return (
                      <>
                        {arr.map((item, i) => (
                          <div key={i} style={{ marginBottom: '12px', padding: '12px', border: '1px solid #eee', borderRadius: '8px', background: '#fafafa', position: 'relative', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                            <button type="button" onClick={() => setSettingsForm(prev => ({ ...prev, site: { ...prev.site, contact_social_links: { ...prev.site?.contact_social_links, value: arr.filter((_, idx) => idx !== i) } } }))} style={{ position: 'absolute', top: '8px', right: '8px', padding: '4px 10px', fontSize: '12px', background: '#e74c3c', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Retirer</button>
                            <input type="text" placeholder="Nom (ex: Facebook)" value={item?.name ?? ''} onChange={(e) => { const n = [...arr]; n[i] = { ...n[i], name: e.target.value }; setSettingsForm(prev => ({ ...prev, site: { ...prev.site, contact_social_links: { ...prev.site?.contact_social_links, value: n } } })); }} style={{ width: '120px', padding: '6px' }} />
                            <input type="text" placeholder="URL" value={item?.url ?? ''} onChange={(e) => { const n = [...arr]; n[i] = { ...n[i], url: e.target.value }; setSettingsForm(prev => ({ ...prev, site: { ...prev.site, contact_social_links: { ...prev.site?.contact_social_links, value: n } } })); }} style={{ flex: 1, minWidth: '200px', padding: '6px' }} />
                            <input type="text" placeholder="Icône (emoji)" value={item?.icon ?? ''} onChange={(e) => { const n = [...arr]; n[i] = { ...n[i], icon: e.target.value }; setSettingsForm(prev => ({ ...prev, site: { ...prev.site, contact_social_links: { ...prev.site?.contact_social_links, value: n } } })); }} style={{ width: '60px', padding: '6px', textAlign: 'center' }} />
                          </div>
                        ))}
                        <button type="button" onClick={() => setSettingsForm(prev => ({ ...prev, site: { ...prev.site, contact_social_links: { ...prev.site?.contact_social_links, value: [...(Array.isArray(prev.site?.contact_social_links?.value) ? prev.site.contact_social_links.value : []), { name: '', url: '', icon: '🔗' }] } } }))} style={{ padding: '8px 14px', fontSize: '13px', background: '#3498db', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>+ Ajouter un lien</button>
                      </>
                    );
                  })()}
                </div>
                <h4 style={{ marginTop: '20px', marginBottom: '12px', color: '#2c3e50' }}>Formulaire de contact</h4>
                <div className="form-group">
                  <label>Titre du formulaire</label>
                  <input type="text" value={settingsForm.site?.contact_form_title?.value ?? settingsForm.site?.contact_form_title ?? 'Envoyez-Nous un Message'} onChange={(e) => setSettingsForm({ ...settingsForm, site: { ...settingsForm.site, contact_form_title: { ...settingsForm.site?.contact_form_title, value: e.target.value } } })} style={{ width: '100%', padding: '8px' }} />
                </div>
                <div className="form-row">
                  <div className="form-group" style={{ flex: '1' }}>
                    <label>Titre message de succès</label>
                    <input type="text" value={settingsForm.site?.contact_success_title?.value ?? settingsForm.site?.contact_success_title ?? 'Message Envoyé !'} onChange={(e) => setSettingsForm({ ...settingsForm, site: { ...settingsForm.site, contact_success_title: { ...settingsForm.site?.contact_success_title, value: e.target.value } } })} style={{ width: '100%', padding: '8px' }} />
                  </div>
                  <div className="form-group" style={{ flex: '1' }}>
                    <label>Texte message de succès</label>
                    <input type="text" value={settingsForm.site?.contact_success_text?.value ?? settingsForm.site?.contact_success_text ?? "Nous vous répondrons dans les plus brefs délais."} onChange={(e) => setSettingsForm({ ...settingsForm, site: { ...settingsForm.site, contact_success_text: { ...settingsForm.site?.contact_success_text, value: e.target.value } } })} style={{ width: '100%', padding: '8px' }} />
                  </div>
                </div>
              </div>
                )}

                {siteSection === 'activites' && (
              <div className="card" style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #3498db', paddingBottom: '10px' }}>
                  📰 Page Activités (/activites)
                </h3>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '16px' }}>Publiez les actualités et activités de MUNDES. Seules les activités <strong>publiées</strong> sont visibles sur la page publique.</p>
                {loadingSiteActivities ? (
                  <div style={{ textAlign: 'center', padding: '24px' }}><div className="spinner" style={{ margin: '0 auto 12px' }}></div><p>Chargement…</p></div>
                ) : (
                  <>
                    <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                      <strong>Liste des activités</strong>
                      <button type="button" className="btn btn-primary" onClick={() => { setSiteActivityForm({ id: null, title: '', summary: '', content: '', image_url: '', activity_date: '', published: true }); setShowSiteActivityForm(true); }}>
                        + Ajouter une activité
                      </button>
                    </div>
                    {siteActivities.length === 0 ? (
                      <p style={{ color: '#666', padding: '20px 0' }}>Aucune activité. Cliquez sur « Ajouter une activité » pour en créer une.</p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {siteActivities.map((a) => (
                          <div key={a.id} style={{ padding: '14px', border: '1px solid #e0e0e0', borderRadius: '8px', background: a.published ? '#fff' : '#f9f9f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                            <div>
                              <span style={{ fontWeight: 600, color: '#2c3e50' }}>{a.title}</span>
                              {a.activity_date && <span style={{ marginLeft: '10px', fontSize: '13px', color: '#666' }}>{new Date(a.activity_date).toLocaleDateString('fr-FR')}</span>}
                              {!a.published && <span style={{ marginLeft: '8px', fontSize: '12px', background: '#f0f0f0', padding: '2px 8px', borderRadius: '4px' }}>Brouillon</span>}
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button type="button" className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: '13px' }} onClick={() => { setSiteActivityForm({ id: a.id, title: a.title, summary: a.summary || '', content: a.content || '', image_url: a.image_url || '', activity_date: a.activity_date || '', published: !!a.published }); setShowSiteActivityForm(true); }}>Modifier</button>
                              <button type="button" className="btn btn-danger" style={{ padding: '6px 12px', fontSize: '13px' }} onClick={() => { if (window.confirm('Supprimer cette activité ?')) deleteSiteActivity(a.id).then(() => loadSiteActivitiesList()).catch(() => notificationService.error('Erreur', 'Suppression impossible.')); }}>Supprimer</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
                )}

                <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
                  <button className="btn btn-primary" onClick={() => handleSaveSettings('site')}>
                    💾 Enregistrer les modifications
                  </button>
                  <button className="btn btn-secondary" onClick={() => handleResetSettings('site')}>
                    🔄 Réinitialiser la section site
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'impressions' && (
          <div>
            <h2 style={{ marginBottom: '24px', color: '#2c3e50' }}>🖨️ Impressions — listes et rapports</h2>
            <p style={{ color: '#666', marginBottom: '28px', maxWidth: '700px' }}>
              Ouvrez une liste dans une nouvelle fenêtre pour l'imprimer ou l'enregistrer en PDF. Les filtres (association, période, etc.) peuvent être appliqués depuis les onglets correspondants avant d'imprimer, ou utilisez les options par défaut ci-dessous.
            </p>
            <div className="card" style={{ marginBottom: '24px' }}>
              <h3 style={{ marginBottom: '16px', color: '#2c3e50', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>Listes des membres</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                <button type="button" className="btn btn-primary" onClick={() => { const w = window.open('/admin/membres/print', '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes'); if (!w) notificationService.error('Erreur', 'Veuillez autoriser les pop-ups pour imprimer.'); }}>
                  🖨️ Imprimer la liste des membres
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => { const params = new URLSearchParams(); if (selectedAssociation) params.append('association', selectedAssociation); if (selectedTypeMembre) params.append('type', selectedTypeMembre); if (searchTerm) params.append('search', searchTerm); const w = window.open(`/admin/membres/print?${params.toString()}`, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes'); if (!w) notificationService.error('Erreur', 'Veuillez autoriser les pop-ups pour imprimer.'); }}>
                  Imprimer avec les filtres actuels (Membres)
                </button>
              </div>
            </div>
            <div className="card" style={{ marginBottom: '24px' }}>
              <h3 style={{ marginBottom: '16px', color: '#2c3e50', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>Liste des cotisations (toutes)</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                <button type="button" className="btn btn-primary" onClick={() => { const w = window.open('/admin/cotisations/print', '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes'); if (!w) notificationService.error('Erreur', 'Veuillez autoriser les pop-ups pour imprimer.'); }}>
                  🖨️ Imprimer la liste des cotisations
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => { const params = new URLSearchParams(); if (selectedAssociationCotisations) params.append('association', selectedAssociationCotisations); if (searchCotisations) params.append('search', searchCotisations); const w = window.open(`/admin/cotisations/print?${params.toString()}`, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes'); if (!w) notificationService.error('Erreur', 'Veuillez autoriser les pop-ups pour imprimer.'); }}>
                  Imprimer avec les filtres actuels (Cotisations)
                </button>
              </div>
            </div>
            <div className="card" style={{ marginBottom: '24px', borderLeft: '4px solid #28a745' }}>
              <h3 style={{ marginBottom: '16px', color: '#28a745', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>Cotisations payées</h3>
              <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>Liste des cotisations déjà payées.</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                <button type="button" className="btn btn-success" onClick={() => { const params = new URLSearchParams(); params.append('statut', 'paye'); const w = window.open(`/admin/cotisations/print?${params.toString()}`, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes'); if (!w) notificationService.error('Erreur', 'Veuillez autoriser les pop-ups pour imprimer.'); }}>
                  🖨️ Imprimer la liste des cotisations payées
                </button>
              </div>
            </div>
            <div className="card" style={{ marginBottom: '24px', borderLeft: '4px solid #dc3545' }}>
              <h3 style={{ marginBottom: '16px', color: '#dc3545', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>Cotisations impayées</h3>
              <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>Liste des cotisations en attente de paiement.</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                <button type="button" className="btn btn-danger" onClick={() => { const params = new URLSearchParams(); params.append('statut', 'impaye'); const w = window.open(`/admin/cotisations/print?${params.toString()}`, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes'); if (!w) notificationService.error('Erreur', 'Veuillez autoriser les pop-ups pour imprimer.'); }}>
                  🖨️ Imprimer la liste des cotisations impayées
                </button>
              </div>
            </div>
            <div className="card" style={{ marginBottom: '24px' }}>
              <h3 style={{ marginBottom: '16px', color: '#2c3e50', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>Membres et évolution des cotisations</h3>
              <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>Rapport combinant les membres et leurs cotisations sur une période (mois, année ou semaine).</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                <button type="button" className="btn btn-primary" onClick={() => { const now = new Date(); const params = new URLSearchParams(); params.append('periodeType', 'mois'); params.append('mois', now.getMonth() + 1); params.append('annee', now.getFullYear()); params.append('association', ''); params.append('commune', ''); params.append('statut', 'tous'); const w = window.open(`/admin/rapports/membres-cotisations/print?${params.toString()}`, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes'); if (!w) notificationService.error('Erreur', 'Veuillez autoriser les pop-ups pour imprimer.'); }}>
                  🖨️ Imprimer (mois en cours)
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => { const params = new URLSearchParams(); params.append('association', rapportCotisationsForm.association || ''); params.append('commune', rapportCotisationsForm.commune || ''); params.append('periodeType', rapportCotisationsForm.periodeType || 'mois'); params.append('mois', rapportCotisationsForm.mois || new Date().getMonth() + 1); params.append('annee', rapportCotisationsForm.annee || new Date().getFullYear()); if (rapportCotisationsForm.periodeType === 'semaine') params.append('semaine', rapportCotisationsForm.semaine || 1); params.append('statut', rapportCotisationsForm.statut || 'tous'); const w = window.open(`/admin/rapports/membres-cotisations/print?${params.toString()}`, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes'); if (!w) notificationService.error('Erreur', 'Veuillez autoriser les pop-ups pour imprimer.'); }}>
                  Imprimer avec les filtres du rapport (Rapports)
                </button>
              </div>
            </div>
            <div className="card" style={{ marginBottom: '24px' }}>
              <h3 style={{ marginBottom: '16px', color: '#2c3e50', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>Autres listes</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                <button type="button" className="btn btn-secondary" onClick={() => { const w = window.open('/admin/paiements/print', '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes'); if (!w) notificationService.error('Erreur', 'Veuillez autoriser les pop-ups pour imprimer.'); }}>
                  Liste des paiements
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => { const w = window.open('/admin/montants/print', '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes'); if (!w) notificationService.error('Erreur', 'Veuillez autoriser les pop-ups pour imprimer.'); }}>
                  Liste des montants mensuels
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => { const w = window.open('/admin/recolteurs/print', '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes'); if (!w) notificationService.error('Erreur', 'Veuillez autoriser les pop-ups pour imprimer.'); }}>
                  Liste des récolteurs
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => { const w = window.open('/admin/familles/print', '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes'); if (!w) notificationService.error('Erreur', 'Veuillez autoriser les pop-ups pour imprimer.'); }}>
                  Liste des familles
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => { const params = new URLSearchParams(); params.append('association', rapportMembresRecoltesForm.association || ''); params.append('commune', rapportMembresRecoltesForm.commune || ''); const w = window.open(`/admin/rapports/membres-recoltes/print?${params.toString()}`, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes'); if (!w) notificationService.error('Erreur', 'Veuillez autoriser les pop-ups pour imprimer.'); }}>
                  Membres récoltés (rapport)
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'activities' && (
          <div>
            <h2 style={{ marginBottom: '30px', color: '#2c3e50' }}>📋 Journal des Activités</h2>
            
            {/* Filtres */}
            <div className="card" style={{ marginBottom: '20px' }}>
              <h3 style={{ marginBottom: '15px', color: '#2c3e50' }}>Filtres</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Type d'action</label>
                  <select
                    value={activityFilters.action_type}
                    onChange={(e) => {
                      setActivityFilters({ ...activityFilters, action_type: e.target.value });
                    }}
                  >
                    <option value="">Tous</option>
                    <option value="create">Création</option>
                    <option value="update">Modification</option>
                    <option value="delete">Suppression</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Type d'entité</label>
                  <select
                    value={activityFilters.entity_type}
                    onChange={(e) => {
                      setActivityFilters({ ...activityFilters, entity_type: e.target.value });
                    }}
                  >
                    <option value="">Toutes</option>
                    <option value="membre">Membre</option>
                    <option value="admin">Administrateur</option>
                    <option value="recolteur">Récolteur</option>
                    <option value="cotisation">Cotisation</option>
                    <option value="paiement">Paiement</option>
                    <option value="montant_mensuel">Montant mensuel</option>
                    <option value="centre_sante">Centre de santé</option>
                  </select>
                </div>
                <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <button
                    className="btn btn-primary"
                    onClick={loadActivities}
                    style={{ marginRight: '10px' }}
                  >
                    🔍 Filtrer
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setActivityFilters({ action_type: '', entity_type: '' });
                      loadActivities();
                    }}
                  >
                    🔄 Réinitialiser
                  </button>
                </div>
              </div>
            </div>

            {/* Liste des activités */}
            <div className="card">
              {loadingActivities ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
                  <p>Chargement des activités...</p>
                </div>
              ) : activities.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  <p style={{ fontSize: '18px', marginBottom: '10px' }}>📋 Aucune activité enregistrée</p>
                  <p style={{ fontSize: '14px', color: '#999' }}>
                    {activities.length === 0 && !loadingActivities ? 
                      'Les activités seront enregistrées automatiquement lors des modifications et suppressions.' : 
                      'Aucune activité trouvée pour les filtres sélectionnés.'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="table-wrapper" style={{ overflowX: 'auto' }}>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Date/Heure</th>
                          <th>Administrateur</th>
                          <th>Action</th>
                          <th>Entité</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginate(activities, currentPageActivities, itemsPerPage).map(activity => {
                          const actionColors = {
                            create: '#28a745',
                            update: '#ffc107',
                            delete: '#dc3545'
                          };
                          const actionLabels = {
                            create: 'Création',
                            update: 'Modification',
                            delete: 'Suppression'
                          };
                          const entityLabels = {
                            membre: 'Membre',
                            admin: 'Administrateur',
                            recolteur: 'Récolteur',
                            cotisation: 'Cotisation',
                            paiement: 'Paiement',
                            montant_mensuel: 'Montant mensuel',
                            centre_sante: 'Centre de santé'
                          };
                          
                          return (
                            <tr key={activity.id}>
                              <td>{new Date(activity.created_at).toLocaleString('fr-FR')}</td>
                              <td>{activity.admin?.nom_complet || activity.admin?.username || 'N/A'}</td>
                              <td>
                                <span style={{
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  backgroundColor: actionColors[activity.action_type] || '#6c757d',
                                  color: 'white',
                                  fontSize: '12px',
                                  fontWeight: '500'
                                }}>
                                  {actionLabels[activity.action_type] || activity.action_type}
                                </span>
                              </td>
                              <td>{entityLabels[activity.entity_type] || activity.entity_type}</td>
                              <td>{activity.description}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {(() => {
                    const totalPages = getTotalPages(activities, itemsPerPage);
                    return (
                      <Pagination 
                        currentPage={currentPageActivities} 
                        totalPages={totalPages} 
                        onPageChange={(page) => {
                          setCurrentPageActivities(page);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }} 
                      />
                    );
                  })()}
                </>
              )}
            </div>
          </div>
        )}

        {activeTab === 'notifications' && (
          <div>
            <h2 style={{ marginBottom: '30px', color: '#2c3e50' }}>📱 Notifications aux Membres</h2>
            
            {/* Boutons d'action */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
              <button 
                className="btn btn-primary"
                onClick={() => setShowSendNotificationModal(true)}
              >
                📤 Envoyer des Notifications
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setEditingTemplate(null);
                  setTemplateForm({
                    name: '',
                    category: 'custom',
                    subject: '',
                    message: '',
                    variables: '',
                    is_active: true
                  });
                  setShowTemplateForm(true);
                }}
              >
                ➕ Créer un Modèle
              </button>
            </div>

            {/* Modèles de Messages */}
            <div className="card" style={{ marginBottom: '30px' }}>
              <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #3498db', paddingBottom: '10px' }}>
                📝 Modèles de Messages Prédéfinis
              </h3>
              {loadingTemplates ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
                  <p>Chargement des modèles...</p>
                </div>
              ) : !Array.isArray(messageTemplates) || messageTemplates.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  <p>Aucun modèle de message créé.</p>
                  <p style={{ fontSize: '14px', color: '#999' }}>Créez votre premier modèle pour commencer.</p>
                </div>
              ) : (
                <>
                  <div className="table-wrapper" style={{ overflowX: 'auto' }}>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Nom</th>
                          <th>Catégorie</th>
                          <th>Message</th>
                          <th>Statut</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.isArray(messageTemplates) && messageTemplates.length > 0 ? (
                          paginate(messageTemplates, currentPageMessageTemplates, itemsPerPage).map(template => (
                            <tr key={template.id}>
                            <td style={{ fontWeight: 'bold' }}>{template.name}</td>
                            <td>
                              {template.category === 'payment_reminder' && '💳 Rappel Paiement'}
                              {template.category === 'welcome' && '👋 Bienvenue'}
                              {template.category === 'payment_confirmation' && '✅ Confirmation Paiement'}
                              {template.category === 'custom' && '📝 Personnalisé'}
                            </td>
                            <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {template.message.substring(0, 100)}...
                            </td>
                            <td>
                              {template.is_active ? (
                                <span style={{ color: '#28a745', fontWeight: 'bold' }}>✓ Actif</span>
                              ) : (
                                <span style={{ color: '#dc3545', fontWeight: 'bold' }}>✗ Inactif</span>
                              )}
                            </td>
                            <td>
                              <div style={{ display: 'flex', gap: '5px' }}>
                                {isPrincipal() && (
                                  <button
                                    className="btn btn-sm btn-primary"
                                    onClick={() => handleEditTemplate(template)}
                                    title="Modifier"
                                  >
                                    ✏️
                                  </button>
                                )}
                                {isPrincipal() && (
                                  <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() => handleDeleteTemplate(template.id)}
                                    title="Supprimer"
                                  >
                                    🗑️
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                              {loadingTemplates ? 'Chargement...' : 'Aucun modèle de message trouvé'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {(() => {
                    const totalPages = getTotalPages(messageTemplates || [], itemsPerPage);
                    return (
                      <Pagination 
                        currentPage={currentPageMessageTemplates} 
                        totalPages={totalPages} 
                        onPageChange={(page) => {
                          setCurrentPageMessageTemplates(page);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }} 
                      />
                    );
                  })()}
                </>
              )}
            </div>

            {/* Historique des Notifications */}
            <div className="card">
              <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #3498db', paddingBottom: '10px' }}>
                📋 Historique des Notifications Envoyées
              </h3>
              {loadingNotificationHistory ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
                  <p>Chargement de l'historique...</p>
                </div>
              ) : !Array.isArray(notificationHistory) || notificationHistory.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  <p>Aucune notification envoyée.</p>
                </div>
              ) : (
                <>
                  <div className="table-wrapper" style={{ overflowX: 'auto' }}>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Type</th>
                          <th>Destinataires</th>
                          <th>Envoyés</th>
                          <th>Échoués</th>
                          <th>Statut</th>
                          <th>Envoyé par</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.isArray(notificationHistory) && notificationHistory.length > 0 ? (
                          paginate(notificationHistory, currentPageNotificationHistory, itemsPerPage).map(history => (
                            <tr key={history.id}>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {new Date(history.sent_at).toLocaleString('fr-FR')}
                              </td>
                              <td>
                                {history.type === 'sms' && '📱 SMS'}
                                {history.type === 'email' && '📧 Email'}
                                {history.type === 'push' && '🔔 Push'}
                              </td>
                              <td>{history.total_recipients}</td>
                              <td style={{ color: '#28a745', fontWeight: 'bold' }}>{history.sent_count}</td>
                              <td style={{ color: '#dc3545', fontWeight: 'bold' }}>{history.failed_count}</td>
                              <td>
                                {history.status === 'sent' && <span style={{ color: '#28a745', fontWeight: 'bold' }}>✓ Envoyé</span>}
                                {history.status === 'partial' && <span style={{ color: '#ffc107', fontWeight: 'bold' }}>⚠ Partiel</span>}
                                {history.status === 'failed' && <span style={{ color: '#dc3545', fontWeight: 'bold' }}>✗ Échoué</span>}
                                {history.status === 'pending' && <span style={{ color: '#17a2b8', fontWeight: 'bold' }}>⏳ En attente</span>}
                              </td>
                              <td>{history.sender?.nom_complet || history.sender?.username || '-'}</td>
                              <td>
                                <button
                                  className="btn btn-sm btn-info"
                                  onClick={() => {
                                    // Afficher les détails de la notification
                                    alert(`Message: ${history.message}\n\nDestinataires: ${history.total_recipients}\nEnvoyés: ${history.sent_count}\nÉchoués: ${history.failed_count}`);
                                  }}
                                  title="Voir détails"
                                >
                                  👁️
                                </button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="8" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                              {loadingNotificationHistory ? 'Chargement...' : 'Aucune notification trouvée'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {(() => {
                    const totalPages = getTotalPages(notificationHistory || [], itemsPerPage);
                    return (
                      <Pagination 
                        currentPage={currentPageNotificationHistory} 
                        totalPages={totalPages} 
                        onPageChange={(page) => {
                          setCurrentPageNotificationHistory(page);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }} 
                      />
                    );
                  })()}
                </>
              )}
            </div>
          </div>
        )}

        {activeTab === 'contact' && (
          <div>
            <h2 style={{ marginBottom: '8px', color: '#2c3e50' }}>📧 Gestion des mails professionnels</h2>
            <p style={{ marginBottom: '24px', color: '#666', fontSize: '14px' }}>
              Réponses envoyées depuis <strong>infomundes@mundes.org</strong>
            </p>
            
            {/* Filtres */}
            <div className="card" style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ flex: '1', minWidth: '200px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Filtrer par statut:</label>
                  <select
                    value={contactMessageFilters.statut}
                    onChange={(e) => setContactMessageFilters({ ...contactMessageFilters, statut: e.target.value, page: 1 })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Tous les statuts</option>
                    <option value="nouveau">Nouveau</option>
                    <option value="lu">Lu</option>
                    <option value="repondu">Répondu</option>
                    <option value="archive">Archivé</option>
                  </select>
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                  <button
                    className="btn btn-secondary"
                    onClick={loadContactMessages}
                    disabled={loadingContactMessages}
                  >
                    🔄 Actualiser
                  </button>
                </div>
              </div>
            </div>

            {/* Liste des messages */}
            <div className="card">
              {loadingContactMessages ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
                  <p>Chargement des messages...</p>
                </div>
              ) : contactMessages.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  <p>Aucun message de contact trouvé.</p>
                </div>
              ) : (
                <>
                  <div className="table-wrapper" style={{ overflowX: 'auto' }}>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Nom</th>
                          <th>Email</th>
                          <th>Téléphone</th>
                          <th>Sujet</th>
                          <th>Message</th>
                          <th>Statut</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginate(contactMessages, currentPageContactMessages, itemsPerPage).map((msg) => (
                        <tr key={msg.id}>
                          <td>{new Date(msg.created_at).toLocaleDateString('fr-FR')}</td>
                          <td><strong>{msg.nom}</strong></td>
                          <td>{msg.email}</td>
                          <td>{msg.telephone || '-'}</td>
                          <td>{msg.sujet}</td>
                          <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {msg.message}
                          </td>
                          <td>
                            <span className={`badge ${
                              msg.statut === 'nouveau' ? 'badge-warning' :
                              msg.statut === 'lu' ? 'badge-info' :
                              msg.statut === 'repondu' ? 'badge-success' :
                              'badge-secondary'
                            }`}>
                              {msg.statut === 'nouveau' ? '🆕 Nouveau' :
                               msg.statut === 'lu' ? '👁️ Lu' :
                               msg.statut === 'repondu' ? '✅ Répondu' :
                               '📦 Archivé'}
                            </span>
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                              <button
                                className="btn btn-sm btn-primary"
                                onClick={async () => {
                                  try {
                                    const response = await getContactMessage(msg.id);
                                    setSelectedContactMessage(response.data);
                                  } catch (error) {
                                    console.error('Erreur:', error);
                                    notificationService.error('Erreur', 'Impossible de charger le message.');
                                  }
                                }}
                                title="Voir les détails"
                              >
                                👁️
                              </button>
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => {
                                  setSelectedContactMessage(msg);
                                  setContactResponse(msg.reponse || '');
                                  setShowContactResponseModal(true);
                                }}
                                title="Répondre"
                              >
                                ✉️
                              </button>
                              <select
                                value={msg.statut}
                                onChange={async (e) => {
                                  try {
                                    await updateContactMessageStatus(msg.id, e.target.value);
                                    await loadContactMessages();
                                    notificationService.success('Succès', 'Statut mis à jour.');
                                  } catch (error) {
                                    notificationService.error('Erreur', 'Impossible de mettre à jour le statut.');
                                  }
                                }}
                                style={{
                                  padding: '5px 10px',
                                  fontSize: '12px',
                                  borderRadius: '5px',
                                  border: '1px solid #ddd'
                                }}
                                title="Changer le statut"
                              >
                                <option value="nouveau">Nouveau</option>
                                <option value="lu">Lu</option>
                                <option value="repondu">Répondu</option>
                                <option value="archive">Archivé</option>
                              </select>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={async () => {
                                  if (window.confirm('Êtes-vous sûr de vouloir supprimer ce message ?')) {
                                    try {
                                      await deleteContactMessage(msg.id);
                                      await loadContactMessages();
                                      notificationService.success('Succès', 'Message supprimé.');
                                    } catch (error) {
                                      notificationService.error('Erreur', 'Impossible de supprimer le message.');
                                    }
                                  }
                                }}
                                title="Supprimer"
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {(() => {
                    const totalPages = getTotalPages(contactMessages, itemsPerPage);
                    return (
                      <Pagination 
                        currentPage={currentPageContactMessages} 
                        totalPages={totalPages} 
                        onPageChange={(page) => {
                          setCurrentPageContactMessages(page);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }} 
                      />
                    );
                  })()}
                </>
              )}
            </div>
          </div>
        )}

        {/* Modals pour la section contact */}
        {activeTab === 'contact' && (
          <>
            {/* Modal pour voir les détails */}
            {selectedContactMessage && !showContactResponseModal && (
              <div className="modal-overlay" onClick={() => setSelectedContactMessage(null)}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                  <div className="modal-header">
                    <h3>Détails du Message</h3>
                    <button className="modal-close" onClick={() => setSelectedContactMessage(null)}>✕</button>
                  </div>
                  <div className="modal-body">
                    <div style={{ marginBottom: '20px' }}>
                      <strong>Date:</strong> {new Date(selectedContactMessage.created_at).toLocaleString('fr-FR')}
                    </div>
                    <div style={{ marginBottom: '20px' }}>
                      <strong>Nom:</strong> {selectedContactMessage.nom}
                    </div>
                    <div style={{ marginBottom: '20px' }}>
                      <strong>Email:</strong> {selectedContactMessage.email}
                    </div>
                    {selectedContactMessage.telephone && (
                      <div style={{ marginBottom: '20px' }}>
                        <strong>Téléphone:</strong> {selectedContactMessage.telephone}
                      </div>
                    )}
                    <div style={{ marginBottom: '20px' }}>
                      <strong>Sujet:</strong> {selectedContactMessage.sujet}
                    </div>
                    <div style={{ marginBottom: '20px' }}>
                      <strong>Message:</strong>
                      <div style={{ 
                        marginTop: '10px', 
                        padding: '15px', 
                        background: '#f5f5f5', 
                        borderRadius: '5px',
                        whiteSpace: 'pre-wrap'
                      }}>
                        {selectedContactMessage.message}
                      </div>
                    </div>
                    {selectedContactMessage.reponse && (
                      <div style={{ marginBottom: '20px' }}>
                        <strong>Réponse:</strong>
                        <div style={{ 
                          marginTop: '10px', 
                          padding: '15px', 
                          background: '#e8f5e9', 
                          borderRadius: '5px',
                          whiteSpace: 'pre-wrap'
                        }}>
                          {selectedContactMessage.reponse}
                        </div>
                        {selectedContactMessage.date_reponse && (
                          <div style={{ marginTop: '5px', fontSize: '12px', color: '#666' }}>
                            Répondu le: {new Date(selectedContactMessage.date_reponse).toLocaleString('fr-FR')}
                          </div>
                        )}
                        {selectedContactMessage.reponse_envoyee_par_email && selectedContactMessage.date_envoi_email && (
                          <div style={{ marginTop: '5px', fontSize: '12px', color: '#2e7d32' }}>
                            ✓ Email envoyé le {new Date(selectedContactMessage.date_envoi_email).toLocaleString('fr-FR')}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        setContactResponse(selectedContactMessage.reponse || '');
                        setShowContactResponseModal(true);
                      }}
                    >
                      {selectedContactMessage.reponse ? 'Modifier la réponse' : 'Ajouter une réponse'}
                    </button>
                    {selectedContactMessage.reponse && (
                      <button
                        className="btn btn-secondary"
                        onClick={async () => {
                          try {
                            const res = await sendContactReplyEmail(selectedContactMessage.id);
                            notificationService.success('Email', res.data?.emailSent ? 'Email renvoyé avec succès.' : res.data?.message || 'Vérifier la config SMTP.');
                            const r = await getContactMessage(selectedContactMessage.id);
                            setSelectedContactMessage(r.data);
                            await loadContactMessages();
                          } catch (e) {
                            notificationService.error('Erreur', 'Impossible de renvoyer l\'email.');
                          }
                        }}
                      >
                        📤 Renvoyer l'email
                      </button>
                    )}
                    <button className="btn btn-secondary" onClick={() => setSelectedContactMessage(null)}>
                      Fermer
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Modal pour répondre */}
            {showContactResponseModal && selectedContactMessage && (
              <div className="modal-overlay" onClick={() => {
                setShowContactResponseModal(false);
                setContactResponse('');
              }}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                  <div className="modal-header">
                    <h3>Répondre au Message</h3>
                    <button className="modal-close" onClick={() => {
                      setShowContactResponseModal(false);
                      setContactResponse('');
                    }}>✕</button>
                  </div>
                  <div className="modal-body">
                    <div style={{ marginBottom: '12px', padding: '10px', background: '#e3f2fd', borderRadius: '6px', fontSize: '13px' }}>
                      Réponse envoyée depuis <strong>infomundes@mundes.org</strong> vers l'adresse du contact.
                    </div>
                    <div style={{ marginBottom: '20px' }}>
                      <strong>De:</strong> {selectedContactMessage.nom} ({selectedContactMessage.email})
                    </div>
                    <div style={{ marginBottom: '20px' }}>
                      <strong>Sujet:</strong> {selectedContactMessage.sujet}
                    </div>
                    <div style={{ marginBottom: '20px' }}>
                      <strong>Message original:</strong>
                      <div style={{ 
                        marginTop: '10px', 
                        padding: '15px', 
                        background: '#f5f5f5', 
                        borderRadius: '5px',
                        whiteSpace: 'pre-wrap',
                        maxHeight: '150px',
                        overflowY: 'auto'
                      }}>
                        {selectedContactMessage.message}
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Votre réponse *</label>
                      <textarea
                        value={contactResponse}
                        onChange={(e) => setContactResponse(e.target.value)}
                        rows="8"
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: '1px solid #ddd',
                          borderRadius: '5px',
                          fontSize: '14px',
                          fontFamily: 'inherit'
                        }}
                        placeholder="Tapez votre réponse ici..."
                        required
                      />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      className="btn btn-primary"
                      onClick={async () => {
                        if (!contactResponse.trim()) {
                          alert('Veuillez saisir une réponse.');
                          return;
                        }
                        try {
                          const res = await addContactMessageResponse(selectedContactMessage.id, contactResponse);
                          await loadContactMessages();
                          setShowContactResponseModal(false);
                          setContactResponse('');
                          setSelectedContactMessage(null);
                          const emailSent = res.data?.emailSent === true;
                          notificationService.success(
                            'Succès',
                            emailSent
                              ? 'Réponse enregistrée et envoyée par email (infomundes@mundes.org).'
                              : (res.data?.message || 'Réponse enregistrée. Vérifier la config SMTP pour l\'envoi par email.')
                          );
                        } catch (error) {
                          notificationService.error('Erreur', 'Impossible d\'ajouter la réponse.');
                        }
                      }}
                    >
                      Enregistrer la réponse
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowContactResponseModal(false);
                        setContactResponse('');
                      }}
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

          {selectedMembre && (
          <CarteMembre 
          membre={selectedMembre} 
          onClose={() => setSelectedMembre(null)} 
        />
      )}

      {selectedFamilleCarte && (
        <CarteFamille 
          famille={selectedFamilleCarte} 
          onClose={() => setSelectedFamilleCarte(null)} 
        />
      )}

      {selectedPersonneBon && typePersonneBon && (
        <BonTraitement
          personne={selectedPersonneBon}
          type={typePersonneBon}
          cotisations={cotisationsBon}
          onClose={() => {
            setSelectedPersonneBon(null);
            setTypePersonneBon(null);
            setCotisationsBon([]);
          }}
        />
      )}

      {selectedMembreFiche && (
        <FicheAdhesion 
          membre={selectedMembreFiche} 
          onClose={() => setSelectedMembreFiche(null)} 
        />
      )}

      {selectedMembreDetail && (
        <DetailMembre 
          membre={selectedMembreDetail} 
          onClose={() => setSelectedMembreDetail(null)} 
        />
      )}

      {selectedRecolteur && recolteurStats && (
        <DetailRecolteur
          recolteur={recolteurStats.recolteur || selectedRecolteur}
          stats={recolteurStats.statistiques}
          membres={recolteurStats.membres || []}
          recolteurId={selectedRecolteur.id}
          onClose={() => {
            setSelectedRecolteur(null);
            setRecolteurStats(null);
          }}
        />
      )}

      {/* Modal de confirmation de restauration avec mot de passe */}
      {showRestoreModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '10px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#dc3545' }}>
              ⚠️ Confirmation de Restauration
            </h3>
            <div style={{ marginBottom: '20px' }}>
              <p style={{ color: '#666', marginBottom: '15px' }}>
                <strong>ATTENTION :</strong> La restauration de la sauvegarde <strong>"{restoreFilename}"</strong> va remplacer <strong>TOUTES</strong> les données actuelles de la base de données.
              </p>
              <p style={{ color: '#dc3545', fontWeight: 'bold', marginBottom: '15px' }}>
                Cette action est <strong>IRRÉVERSIBLE</strong>.
              </p>
              <p style={{ color: '#666', marginBottom: '20px' }}>
                Pour confirmer cette action, veuillez entrer votre mot de passe administrateur :
              </p>
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                  Mot de passe administrateur *
                </label>
                <input
                  type="password"
                  value={restorePassword}
                  onChange={(e) => setRestorePassword(e.target.value)}
                  placeholder="Entrez votre mot de passe"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '15px',
                    boxSizing: 'border-box'
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && restorePassword) {
                      handleConfirmRestore();
                    }
                  }}
                  autoFocus
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowRestoreModal(false);
                  setRestorePassword('');
                  setRestoreFilename('');
                }}
                disabled={verifyingPassword}
              >
                Annuler
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleConfirmRestore}
                disabled={!restorePassword || verifyingPassword}
                style={{ 
                  opacity: (!restorePassword || verifyingPassword) ? 0.6 : 1,
                  cursor: (!restorePassword || verifyingPassword) ? 'not-allowed' : 'pointer'
                }}
              >
                {verifyingPassword ? 'Vérification...' : '🔄 Confirmer la Restauration'}
              </button>
            </div>
          </div>
        </div>
        )}

        {/* Modale pour créer/modifier un modèle */}
        {showTemplateForm && (
          <div className="modal-overlay" onClick={() => setShowTemplateForm(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', display: 'flex', flexDirection: 'column', padding: 0 }}>
              <div className="modal-header">
                <h2>{editingTemplate ? '✏️ Modifier le Modèle' : '➕ Créer un Modèle'}</h2>
                <button className="modal-close" onClick={() => setShowTemplateForm(false)}>✕</button>
              </div>
              <form onSubmit={editingTemplate ? handleUpdateTemplate : handleCreateTemplate} className="modal-body" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div className="form-group">
                  <label>Nom du modèle *</label>
                  <input
                    type="text"
                    value={templateForm.name}
                    onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                    required
                    placeholder="Ex: Rappel de paiement"
                  />
                </div>
                <div className="form-group">
                  <label>Catégorie *</label>
                  <select
                    value={templateForm.category}
                    onChange={(e) => setTemplateForm({ ...templateForm, category: e.target.value })}
                    required
                  >
                    <option value="custom">📝 Personnalisé</option>
                    <option value="payment_reminder">💳 Rappel Paiement</option>
                    <option value="welcome">👋 Bienvenue</option>
                    <option value="payment_confirmation">✅ Confirmation Paiement</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Sujet (optionnel)</label>
                  <input
                    type="text"
                    value={templateForm.subject}
                    onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                    placeholder="Sujet du message"
                  />
                </div>
                <div className="form-group">
                  <label>Message *</label>
                  <textarea
                    value={templateForm.message}
                    onChange={(e) => setTemplateForm({ ...templateForm, message: e.target.value })}
                    required
                    rows="6"
                    placeholder="Ex: Bonjour {nom}, votre cotisation de {montant} CDF est due le {date}."
                  />
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    Variables disponibles: {'{nom}'}, {'{prenom}'}, {'{montant}'}, {'{date}'}, {'{association}'}
                  </p>
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={templateForm.is_active}
                      onChange={(e) => setTemplateForm({ ...templateForm, is_active: e.target.checked })}
                    />
                    {' '}Modèle actif
                  </label>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowTemplateForm(false)}>
                    Annuler
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingTemplate ? '💾 Enregistrer' : '➕ Créer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modale pour envoyer des notifications */}
        {showSendNotificationModal && (
          <div className="modal-overlay" onClick={() => setShowSendNotificationModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', padding: 0 }}>
              <div className="modal-header">
                <h2>📤 Envoyer des Notifications</h2>
                <button className="modal-close" onClick={() => setShowSendNotificationModal(false)}>✕</button>
              </div>
              <div className="modal-body" style={{ flex: 1, overflowY: 'auto' }}>
                <div className="form-group">
                  <label>Sélectionner un modèle (optionnel)</label>
                  <select
                    value={selectedTemplate?.id || ''}
                    onChange={(e) => {
                      const template = messageTemplates.find(t => t.id === parseInt(e.target.value));
                      setSelectedTemplate(template || null);
                      if (template) {
                        setNotificationMessage(template.message || '');
                      }
                    }}
                  >
                    <option value="">-- Aucun modèle --</option>
                    {Array.isArray(messageTemplates) && messageTemplates.filter(t => t.is_active).map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name} ({template.category})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Message *</label>
                  <textarea
                    value={notificationMessage}
                    onChange={(e) => setNotificationMessage(e.target.value)}
                    required
                    rows="6"
                    placeholder="Entrez le message à envoyer..."
                  />
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    Variables disponibles: {'{nom}'}, {'{prenom}'}, {'{montant}'}, {'{date}'}, {'{association}'}
                  </p>
                </div>
                <div className="form-group">
                  <label>Sélectionner les membres *</label>
                  <div style={{ border: '1px solid #ddd', borderRadius: '5px', padding: '10px', maxHeight: '300px', overflowY: 'auto' }}>
                    <div style={{ marginBottom: '10px' }}>
                      <input
                        type="text"
                        placeholder="Rechercher un membre..."
                        style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ccc' }}
                        onChange={(e) => {
                          // Filtrer les membres visibles (fonctionnalité à implémenter)
                          e.target.value.toLowerCase();
                        }}
                      />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                      {membres.filter(m => m.telephone).map(membre => {
                        const isSelected = selectedMembers.some(sm => sm.id === membre.id);
                        return (
                          <label key={membre.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '5px', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedMembers([...selectedMembers, membre]);
                                } else {
                                  setSelectedMembers(selectedMembers.filter(sm => sm.id !== membre.id));
                                }
                              }}
                            />
                            <span>
                              {membre.nom} {membre.prenom} - {membre.telephone}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                    {membres.filter(m => m.telephone).length === 0 && (
                      <p style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
                        Aucun membre avec un numéro de téléphone trouvé.
                      </p>
                    )}
                  </div>
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    {selectedMembers.length} membre(s) sélectionné(s)
                  </p>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowSendNotificationModal(false);
                    setSelectedTemplate(null);
                    setSelectedMembers([]);
                    setNotificationMessage('');
                  }}
                  disabled={sendingNotification}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSendNotification}
                  disabled={sendingNotification || selectedMembers.length === 0 || !notificationMessage.trim()}
                >
                  {sendingNotification ? '⏳ Envoi en cours...' : '📤 Envoyer'}
                </button>
              </div>
            </div>
          </div>
        )}

      <ImageCropCompressModal
        open={imageCropModal.open}
        imageFile={imageCropModal.file}
        onConfirm={(file) => { imageCropModal.onConfirm?.(file); }}
        onCancel={() => setImageCropModal(prev => ({ ...prev, open: false, file: null, onConfirm: null }))}
        aspectRatio={imageCropModal.aspect}
        title={imageCropModal.title}
      />

      {showSiteActivityForm && (
        <div className="modal-overlay" onClick={() => setShowSiteActivityForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '640px', maxHeight: '90vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h3>{siteActivityForm.id ? 'Modifier l\'activité' : 'Nouvelle activité'}</h3>
              <button type="button" className="modal-close" onClick={() => setShowSiteActivityForm(false)} aria-label="Fermer">×</button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              <div className="form-group" style={{ marginBottom: '14px' }}>
                <label>Titre *</label>
                <input type="text" value={siteActivityForm.title} onChange={(e) => setSiteActivityForm(prev => ({ ...prev, title: e.target.value }))} placeholder="Titre de l'activité" style={{ width: '100%', padding: '10px' }} />
              </div>
              <div className="form-group" style={{ marginBottom: '14px' }}>
                <label>Résumé (optionnel, pour la liste)</label>
                <textarea rows={2} value={siteActivityForm.summary} onChange={(e) => setSiteActivityForm(prev => ({ ...prev, summary: e.target.value }))} placeholder="Court résumé affiché dans la liste" style={{ width: '100%', padding: '10px' }} />
              </div>
              <div className="form-group" style={{ marginBottom: '14px' }}>
                <label>Contenu *</label>
                <textarea rows={6} value={siteActivityForm.content} onChange={(e) => setSiteActivityForm(prev => ({ ...prev, content: e.target.value }))} placeholder="Texte complet de l'activité" style={{ width: '100%', padding: '10px' }} />
              </div>
              <div className="form-group" style={{ marginBottom: '14px' }}>
                <label>Image (URL ou upload — rognage et compression)</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
                  {siteActivityForm.image_url && <img src={siteActivityForm.image_url} alt="" style={{ maxWidth: '100px', maxHeight: '60px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #ddd' }} onError={(e) => { e.target.style.display = 'none'; }} />}
                  <input type="text" value={siteActivityForm.image_url} onChange={(e) => setSiteActivityForm(prev => ({ ...prev, image_url: e.target.value }))} placeholder="/uploads/activities/..." style={{ flex: 1, minWidth: '200px', padding: '8px' }} />
                  <input type="file" accept="image/jpeg,image/jpg,image/png,image/gif,image/webp" onChange={(e) => { const f = e.target.files?.[0]; if (!f) return; setImageCropModal({ open: true, file: f, aspect: undefined, title: 'Rogner et compresser l\'image de l\'activité', onConfirm: async (processedFile) => { try { const fd = new FormData(); fd.append('image', processedFile); const res = await uploadActivityImage(fd); if (res?.data?.imagePath) { setSiteActivityForm(prev => ({ ...prev, image_url: res.data.imagePath })); notificationService.success('Succès', 'Image envoyée (rognée et compressée).'); } } catch (err) { notificationService.error('Erreur', err.response?.data?.message || 'Erreur upload.'); } finally { setImageCropModal(prev => ({ ...prev, open: false, file: null, onConfirm: null })); } } }); e.target.value = ''; }} style={{ padding: '6px', fontSize: '13px' }} />
                </div>
              </div>
              <div className="form-group" style={{ marginBottom: '14px' }}>
                <label>Date de l'activité (optionnel)</label>
                <input type="date" value={siteActivityForm.activity_date || ''} onChange={(e) => setSiteActivityForm(prev => ({ ...prev, activity_date: e.target.value || '' }))} style={{ padding: '8px' }} />
              </div>
              <div className="form-group" style={{ marginBottom: '14px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={siteActivityForm.published} onChange={(e) => setSiteActivityForm(prev => ({ ...prev, published: e.target.checked }))} />
                  Publié (visible sur la page publique)
                </label>
              </div>
            </div>
            <div className="modal-footer" style={{ padding: '16px 20px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setShowSiteActivityForm(false)}>Annuler</button>
              <button type="button" className="btn btn-primary" onClick={async () => { if (!siteActivityForm.title?.trim() || !siteActivityForm.content?.trim()) { notificationService.error('Erreur', 'Titre et contenu requis.'); return; } try { if (siteActivityForm.id) await updateSiteActivity(siteActivityForm.id, { title: siteActivityForm.title.trim(), summary: siteActivityForm.summary || null, content: siteActivityForm.content.trim(), image_url: siteActivityForm.image_url || null, activity_date: siteActivityForm.activity_date || null, published: siteActivityForm.published }); else await createSiteActivity({ title: siteActivityForm.title.trim(), summary: siteActivityForm.summary || null, content: siteActivityForm.content.trim(), image_url: siteActivityForm.image_url || null, activity_date: siteActivityForm.activity_date || null, published: siteActivityForm.published }); notificationService.success('Succès', siteActivityForm.id ? 'Activité mise à jour.' : 'Activité créée.'); setShowSiteActivityForm(false); loadSiteActivitiesList(); } catch (err) { notificationService.error('Erreur', err.response?.data?.message || 'Erreur enregistrement.'); }} }>
                {siteActivityForm.id ? 'Enregistrer' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}

      <AdminInstallPrompt />
      <Footer />
          </div>
          </React.Fragment>
        </main>
      </div>
      </div>
    </React.Fragment>
  );
};

export default DashboardAdmin;

